import { useEffect, useState, useCallback, useMemo, Fragment } from "react";
import { useAuth } from "../context/AuthContext";
import { useSearchParams } from "react-router-dom";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Label } from "../components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { 
  RefreshCcw,
  Search,
  Download,
  ChevronDown,
  Building2,
  Globe,
  User,
  Users,
  Baby,
  BarChart3,
  PieChart,
  FileText,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Eye,
  Percent,
  Receipt,
  Plus,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Target,
  Calendar,
  Check,
  Loader2,
  Trash2,
  Edit2,
  Mail,
  AlertTriangle,
  CheckCircle,
  CheckCircle2,
  Clock,
  X,
  XCircle,
  LogOut,
  Activity,
  Unlock,
  Bell,
  ChevronsUpDown,
} from "lucide-react";
import { formatCurrency, formatNumber } from "../lib/utils";
import * as XLSX from "xlsx";
import { exportStyledXlsx } from "../lib/excelExport";
import { useIsMobile } from "../lib/useIsMobile";
import MobilePortfolioHero from "../components/MobilePortfolioHero";
import { CashflowStatusCard } from "./Portfolio/summary/CashflowStatusCard";
import { SubCategoryAllocationCard } from "./Portfolio/summary/SubCategoryAllocationCard";
import { FamilySipOverviewCard } from "./Portfolio/summary/FamilySipOverviewCard";
import { FamilyAllocationCard } from "./Portfolio/summary/FamilyAllocationCard";
import MobileHoldingsCards from "../components/MobileHoldingsCards";
import MobileTransactionsCards from "../components/MobileTransactionsCards";
import MobileTabDropdown from "../components/MobileTabDropdown";
import ReportingPreferencesPanel from "./ReportingPreferencesPanel";
import { toTitleCase } from "../lib/formatName";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Portfolio = () => {
  const { api, user, isClient, token } = useAuth();
  const [searchParams] = useSearchParams();
  const clientIdParam = searchParams.get("client");
  
  // Helper function to format dates → always returns DD-MM-YYYY (Indian standard)
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';

    // Handle YYYYMMDD format (e.g., "19980522")
    if (/^\d{8}$/.test(dateStr)) {
      const year = dateStr.substring(0, 4);
      const month = dateStr.substring(4, 6);
      const day = dateStr.substring(6, 8);
      return `${day}-${month}-${year}`;
    }

    // Handle DD-MM-YYYY or D-M-YYYY (the canonical CAMS/Karvy format stored
    // in transaction_master). Passing these into `new Date(str)` reinterprets
    // them as US MM-DD-YYYY, swapping day↔month (e.g. "08-05-2023" → "05-08-2023").
    // Detect and pass through normalized to zero-padded DD-MM-YYYY.
    let m = /^(\d{1,2})-(\d{1,2})-(\d{4})$/.exec(dateStr);
    if (m) {
      const dd = m[1].padStart(2, '0');
      const mm = m[2].padStart(2, '0');
      return `${dd}-${mm}-${m[3]}`;
    }

    // Handle DD/MM/YYYY
    m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(dateStr);
    if (m) {
      const dd = m[1].padStart(2, '0');
      const mm = m[2].padStart(2, '0');
      return `${dd}-${mm}-${m[3]}`;
    }

    // Handle YYYY-MM-DD (ISO short)
    m = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateStr);
    if (m) return `${m[3]}-${m[2]}-${m[1]}`;

    // Fallback for full ISO timestamps etc.
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    return `${String(date.getDate()).padStart(2, '0')}-${String(date.getMonth() + 1).padStart(2, '0')}-${date.getFullYear()}`;
  };
  
  // Helper function to convert text to Title Case
  // Name title-casing comes from the shared `lib/formatName` util so every
  // page renders consistently. The duplicate local helper has been removed.

  // Helper function to get Financial Year from date string (YYYYMMDD or YYYY-MM-DD)
  const getFinancialYear = (dateStr) => {
    if (!dateStr) return null;
    let year, month;
    if (dateStr.includes('-')) {
      const parts = dateStr.split('-');
      if (parts[0].length === 4) {
        year = parseInt(parts[0]);
        month = parseInt(parts[1]);
      } else {
        year = parseInt(parts[2]);
        month = parseInt(parts[1]);
      }
    } else if (dateStr.length === 8) {
      year = parseInt(dateStr.substring(0, 4));
      month = parseInt(dateStr.substring(4, 6));
    } else {
      return null;
    }
    // Indian FY: Apr-Mar. If month >= 4 (April), FY is year-(year+1), else (year-1)-year
    if (month >= 4) {
      return `FY ${year}-${(year + 1).toString().slice(-2)}`;
    } else {
      return `FY ${year - 1}-${year.toString().slice(-2)}`;
    }
  };

  // Helper: check if a trade_date string falls within [from, to] custom range.
  // trade_date is stored as "DD-MM-YYYY" or "YYYY-MM-DD"; range inputs are HTML
  // <input type="date"> which emit "YYYY-MM-DD". Empty range endpoints = open.
  const isWithinDateRange = (dateStr, fromYMD, toYMD) => {
    if (!fromYMD && !toYMD) return true;
    if (!dateStr) return false;
    let y, m, d;
    const s = String(dateStr);
    if (s.includes("-")) {
      const parts = s.split("-");
      if (parts[0].length === 4) { y = parts[0]; m = parts[1]; d = parts[2]; }
      else { y = parts[2]; m = parts[1]; d = parts[0]; }
    } else if (s.length === 8) {
      y = s.substring(0, 4); m = s.substring(4, 6); d = s.substring(6, 8);
    } else {
      return false;
    }
    const ymd = `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    if (fromYMD && ymd < fromYMD) return false;
    if (toYMD && ymd > toYMD) return false;
    return true;
  };

  // Helper function to check if asset is equity type
  const isEquityAsset = (assetType) => {
    if (!assetType) return false;
    const type = assetType.toUpperCase();
    return type.includes('EQUITY') || type.includes('HYBRID') || type === 'ELSS';
  };

  // Helper function to calculate holding period in months
  const getHoldingPeriodMonths = (purchaseDate, saleDate) => {
    if (!purchaseDate || !saleDate) return 0;
    const parseDate = (d) => {
      if (d.includes('-')) {
        const parts = d.split('-');
        if (parts[0].length === 4) return new Date(parts[0], parts[1] - 1, parts[2]);
        return new Date(parts[2], parts[1] - 1, parts[0]);
      }
      return new Date(d.substring(0, 4), d.substring(4, 6) - 1, d.substring(6, 8));
    };
    const purchase = parseDate(purchaseDate);
    const sale = parseDate(saleDate);
    const months = (sale.getFullYear() - purchase.getFullYear()) * 12 + (sale.getMonth() - purchase.getMonth());
    return months;
  };

  // Helper function to determine if it's Long Term Capital Gain
  // Equity: >12 months, Debt: >36 months (for purchases before Apr 2023)
  const isLongTerm = (purchaseDate, saleDate, assetType) => {
    const months = getHoldingPeriodMonths(purchaseDate, saleDate);
    if (isEquityAsset(assetType)) {
      return months > 12;
    }
    return months > 36; // Debt
  };
  
  const [activeTab, setActiveTab] = useState("summary");
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(clientIdParam || "");
  const [familyContext, setFamilyContext] = useState(null);
  // Family Aggregate (Clubbed) view — when the search-selected client is a
  // Group Head with multiple mapped members, this defaults to TRUE so the
  // portfolio shows the aggregated family book. Chips on the banner toggle
  // it: clicking "All Family" → true (with drillDownPan reset), clicking
  // any member chip → false + sets `drillDownPan` to that member's PAN.
  const [familyAggregate, setFamilyAggregate] = useState(false);
  // Drill-down PAN — independent of the top-bar search. When set (because
  // a member chip was clicked) the portfolio data fetchers view that PAN
  // INSTEAD of the search-selected one, but the search box, dropdown and
  // the family banner CONTINUE to reflect the original head selection.
  // Cleared whenever the top-bar search changes the head.
  const [drillDownPan, setDrillDownPan] = useState("");
  // For multi-bank PAN splits (proprietary vs personal on same PAN) the
  // client dropdown uses a synthetic id `PAN::BANK::<bank_no>`. We strip
  // the suffix for path-style portfolio lookups and pass it as a query
  // parameter so the backend can scope results to the right bank.
  const _splitParts = String(selectedClient || "").split("::BANK::");
  const _searchClientPan = _splitParts[0] || "";
  // The PAN actually driving DATA fetches: drillDownPan overrides when set.
  const selectedClientPan = drillDownPan || _searchClientPan;
  // The HEAD PAN — i.e. who the search/dropdown is currently pointing at.
  // Used to load familyContext (banner content) so chips stay stable when
  // drilling into a single member.
  const searchClientPan = _searchClientPan;
  const selectedClientBank = _splitParts[1] || "";
  const bankQueryStr = selectedClientBank ? `?bank_account=${encodeURIComponent(selectedClientBank)}` : "";
  const [loading, setLoading] = useState(true);
  const [clientSearchTerm, setClientSearchTerm] = useState("");
  const [clientDropdownOpen, setClientDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  
  // Folio Details search/filter
  const [folioSearchTerm, setFolioSearchTerm] = useState("");
  const [folioDetailsData, setFolioDetailsData] = useState([]);
  const [purchaseSearchTerm, setPurchaseSearchTerm] = useState("");

  // All Transactions state
  const [allTransactions, setAllTransactions] = useState([]);
  const [allTransactionsLoading, setAllTransactionsLoading] = useState(false);
  const [allTransactionsSearch, setAllTransactionsSearch] = useState("");
  const [allTransactionsSort, setAllTransactionsSort] = useState({ key: "trade_date", direction: "desc" });
  const [allTxnDupFilter, setAllTxnDupFilter] = useState("all"); // "all" | "duplicates"
  const [allTxnPage, setAllTxnPage] = useState(1);
  // Date range + FY quick-pick filter for All Transactions
  const [allTxnDateFrom, setAllTxnDateFrom] = useState(""); // YYYY-MM-DD
  const [allTxnDateTo, setAllTxnDateTo] = useState("");     // YYYY-MM-DD
  const [allTxnFY, setAllTxnFY] = useState("");             // e.g. "2024-25" or "" (custom/all)
  const ALL_TXN_PAGE_SIZE = 500;

  // Portfolio Mix (Equity/Debt/Hybrid) — iteration 37, per-client
  const [portfolioMix, setPortfolioMix] = useState(null);
  useEffect(() => {
    // Only fetch when we have a real client PAN in view.
    if (!selectedClientPan || !api) { setPortfolioMix(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const resp = await api.get(`/dashboard/portfolio-mix?pan=${encodeURIComponent(selectedClientPan)}`);
        if (!cancelled) setPortfolioMix(resp.data || null);
      } catch (e) {
        if (!cancelled) setPortfolioMix(null);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedClientPan, api]);


  // AMC lookup map (ISIN / amfi_code / product_code → AMC name) for AMC-Wise Breakdown
  const [amcLookup, setAmcLookup] = useState({ by_isin: {}, by_amfi_code: {}, by_product_code: {} });
  useEffect(() => {
    let cancel = false;
    api.get("/scheme-amc-map")
      .then(res => { if (!cancel) setAmcLookup(res.data || {}); })
      .catch(() => {});
    return () => { cancel = true; };
  }, [api]);
  
  // Server-side search for transaction tabs (searches across all pages)
  // Removed - no longer using pagination or search
  
  // Sub-account filter
  const [selectedSubAccount, setSelectedSubAccount] = useState("all");
  const [selectedMinorKey, setSelectedMinorKey] = useState(null); // Track specific sub-account by uniqueKey
  const [selectedBankAccType, setSelectedBankAccType] = useState("all"); // NRE/NRO filter for NRI clients
  
  // Helper: compute sub-account uniqueKey for a folio item (mirrors the grouping logic)
  // NOTE: 'PROPERITER' covers a common misspelling of 'PROPRIETOR' seen in
  // KARVY feeds (e.g. tax_status="PROPERITERSHIP") which would otherwise
  // slip into the individual bucket and surface as a duplicate Sub-Account
  // capsule for the same legal entity (Venktesh Multimedia Services case).
  const FIRM_TAX_KEYWORDS = ['HUF', 'PROPRIETOR', 'PROPERITER', 'PARTNERSHIP', 'BODY CORPORATE', 'COMPANY', 'TRUST', 'SOCIETY', 'AOP', 'BOI', 'LLP'];
  const cleanField = (val) => (val && val !== 'None' && val !== 'null') ? val.trim() : '';
  // Parse DD-MM-YYYY date strings to timestamps
  const parseDateStr = (s) => {
    if (!s) return 0;
    const parts = s.split('-');
    if (parts.length === 3 && parts[0].length <= 2) {
      return new Date(`${parts[2]}-${parts[1]}-${parts[0]}`).getTime() || 0;
    }
    return new Date(s).getTime() || 0;
  };
  // Convert "YYYY-MM-DD" (HTML <input type=date>) → ms timestamp; "" → null
  const ymdToMs = (s) => {
    if (!s) return null;
    const t = new Date(`${s}T00:00:00`).getTime();
    return Number.isNaN(t) ? null : t;
  };
  // Convert a stored trade_date string ("DD-MM-YYYY" | "YYYY-MM-DD" | "YYYYMMDD")
  // into a real JS Date so xlsx-js-style writes it as a proper date cell (not a
  // string). Without this, Excel sorts "07-09-2026" BEFORE "31-08-2026"
  // lexicographically, which made Ankur's Sep-2026 STP entries look truncated
  // after 31-Aug when the user sorted by Trade Date.
  const tradeDateToJsDate = (s) => {
    if (!s) return null;
    const str = String(s).trim();
    // DD-MM-YYYY (Karvy/CAMS canonical) or D-M-YYYY
    let m = /^(\d{1,2})-(\d{1,2})-(\d{4})$/.exec(str);
    if (m) return new Date(parseInt(m[3], 10), parseInt(m[2], 10) - 1, parseInt(m[1], 10));
    // YYYY-MM-DD (ISO short)
    m = /^(\d{4})-(\d{1,2})-(\d{1,2})/.exec(str);
    if (m) return new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10));
    // YYYYMMDD (Karvy raw)
    m = /^(\d{4})(\d{2})(\d{2})$/.exec(str);
    if (m) return new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10));
    const d = new Date(str);
    return Number.isNaN(d.getTime()) ? null : d;
  };
  // Return [fromMs, toMs] for a given Indian FY label like "2024-25"
  // (2024-04-01 → 2025-03-31 inclusive). Returns [null, null] if invalid.
  const fyToRangeMs = (fyLabel) => {
    if (!fyLabel) return [null, null];
    const m = /^(\d{4})-(\d{2,4})$/.exec(fyLabel);
    if (!m) return [null, null];
    const startYr = parseInt(m[1], 10);
    const from = new Date(`${startYr}-04-01T00:00:00`).getTime();
    const to = new Date(`${startYr + 1}-03-31T23:59:59`).getTime();
    return [from, to];
  };
  // Helper: resolve NRE/NRO from bank_acc_type first, then fall back to tax_status parsing
  const resolveNriBankType = (item) => {
    const bat = (item.bank_acc_type || item.bank_account_type || '').toUpperCase().trim();
    if (bat === 'NRO') return 'NRO';
    if (bat === 'NRE') return 'NRE';
    // Fallback: parse tax_status
    const taxStatus = (item.tax_status || '').toUpperCase();
    if (taxStatus.includes('NON') || taxStatus.includes('NRO')) return 'NRO';
    if (taxStatus.includes('REPATRI') || taxStatus.includes('NRE')) return 'NRE';
    return 'OTHER';
  };

  const getSubAccountKey = (item) => {
    const taxStatus = (item.tax_status || '').toUpperCase();
    const holderName = (item.first_holder || '').trim().toUpperCase();
    const pan = cleanField(item.first_holder_pan) || cleanField(item.pan_1) || '';
    const guardianPan = cleanField(item.guardian_pan);
    const isMinor = !!guardianPan || taxStatus.includes('MINOR');
    const isNri = taxStatus.includes('NRI');
    const isFirm = FIRM_TAX_KEYWORDS.some(kw => taxStatus.includes(kw));
    const pattern = item.holding_pattern || 'self_single';
    
    if (isMinor) return `${holderName}|${guardianPan}`;
    if (isNri) {
      const bankType = resolveNriBankType(item);
      return `${pan}|${bankType}`;
    }
    if (isFirm) return `${holderName}|${pan}`;
    // Individual: split by holding pattern (self vs joint)
    if (pattern === 'joint_holder' || pattern === 'as_joint') {
      const secondHolder = cleanField(item.second_holder).toUpperCase();
      return `JOINT|${pan}|${secondHolder}`;
    }
    return `INDIVIDUAL|${pan}`;
  };
  
  // Portfolio data
  const [portfolioData, setPortfolioData] = useState([]);
  const [totalFolios, setTotalFolios] = useState(0);
  const [holdingPatternCounts, setHoldingPatternCounts] = useState({});
  const isMobile = useIsMobile();
  
  // Sorting state
  const [sortConfig, setSortConfig] = useState({ key: "market_value", direction: "desc" });
  
  // Goals state
  const [goals, setGoals] = useState([]);
  const [showCreateGoalDialog, setShowCreateGoalDialog] = useState(false);
  const [showEditGoalDialog, setShowEditGoalDialog] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);
  const [newGoalName, setNewGoalName] = useState("");
  const [newGoalDate, setNewGoalDate] = useState("");
  const [newGoalAmount, setNewGoalAmount] = useState("");
  const [creatingGoal, setCreatingGoal] = useState(false);
  const [schemeGoals, setSchemeGoals] = useState({}); // Map of ISIN -> [{goal, percentage}]
  const [schemeAllocations, setSchemeAllocations] = useState({}); // Map of ISIN -> total allocated %
  const [assigningPercentage, setAssigningPercentage] = useState(100); // Default percentage for new assignments

  // Goal color palette - consistent across capsules and badges
  const goalColors = [
    { border: "border-rose-200", bg: "bg-rose-50", text: "text-rose-600", accent: "text-rose-400", progress: "bg-rose-500", badge: "bg-rose-100 text-rose-700" },
    { border: "border-emerald-200", bg: "bg-emerald-50", text: "text-emerald-600", accent: "text-emerald-500", progress: "bg-emerald-500", badge: "bg-emerald-100 text-emerald-700" },
    { border: "border-cyan-200", bg: "bg-cyan-50", text: "text-cyan-600", accent: "text-cyan-500", progress: "bg-cyan-500", badge: "bg-cyan-100 text-cyan-700" },
    { border: "border-purple-200", bg: "bg-purple-50", text: "text-purple-600", accent: "text-purple-500", progress: "bg-purple-500", badge: "bg-purple-100 text-purple-700" },
    { border: "border-amber-200", bg: "bg-amber-50", text: "text-amber-600", accent: "text-amber-500", progress: "bg-amber-500", badge: "bg-amber-100 text-amber-700" },
  ];

  // Selected goal filter state
  const [selectedGoalFilter, setSelectedGoalFilter] = useState("all");

  // Get color for a goal based on its index
  const getGoalColor = (goalId) => {
    const idx = goals.findIndex(g => g.id === goalId);
    return goalColors[idx >= 0 ? idx % goalColors.length : 0];
  };

  // Sub-account types for filtering - counts will be updated dynamically
  const subAccountTypes = [
    { key: "all", label: "All", icon: Building2, color: "bg-teal-600", patternKey: "all" },
    { key: "self_single", label: "Self (Single)", icon: User, color: "bg-teal-600", patternKey: "self_single" },
    { key: "joint_holder", label: "Joint Holder", icon: Users, color: "bg-blue-500", patternKey: "joint_holder" },
    { key: "as_joint", label: "As Joint (2nd/3rd)", icon: Users, color: "bg-cyan-500", patternKey: "as_joint" },
    { key: "minor_guardian", label: "Minor (as Guardian)", icon: Baby, color: "bg-purple-500", patternKey: "minor_guardian" },
    { key: "self_minor", label: "Self (Minor)", icon: Baby, color: "bg-amber-500", patternKey: "self_minor" },
  ];

  // Ownership pattern legend
  const ownershipLegend = [
    { label: "Self (Single)", color: "bg-teal-600", key: "self_single" },
    { label: "Joint Holder", color: "bg-blue-500", key: "joint_holder" },
    { label: "As Joint (2nd/3rd)", color: "bg-cyan-500", key: "as_joint" },
    { label: "Minor (as Guardian)", color: "bg-purple-500", key: "minor_guardian" },
    { label: "Self (Minor)", color: "bg-amber-500", key: "self_minor" },
  ];

  // Tabs configuration
  const allTabs = [
    { key: "summary", label: "Summary", icon: BarChart3 },
    { key: "scheme_breakdown", label: "Scheme Wise Breakdown", icon: PieChart },
    { key: "reporting_prefs", label: "Reporting Preferences", icon: Bell },
    { key: "folio_details", label: "Folio Details", icon: FileText },
    { key: "all_transactions", label: "All Transactions", icon: RefreshCcw },
    { key: "failed_transactions", label: "Failed Transactions", icon: XCircle },
    { key: "active_units", label: "Active Units", icon: TrendingUp },
    { key: "sold_units", label: "Sold Units", icon: TrendingUp },
    { key: "idcw_payouts", label: "Dividend Payout", icon: DollarSign },
    { key: "junk_units", label: "Non Financial", icon: Trash2 },
    { key: "segregated", label: "Segregated", icon: AlertTriangle },
    { key: "unit_mismatch", label: "Unit Mismatch", icon: AlertTriangle },
    { key: "provisional_sell", label: "Provisional Sell", icon: Target },
    { key: "provisional_buy", label: "Provisional Buy", icon: Target },
    { key: "exit_free", label: "Exit Free Investments", icon: CheckCircle2 },
  ];
  
  // Check if selected client is exited (zero balance)
  const selectedClientData = clients.find(c => c.id === selectedClient);
  const isExitedClient = selectedClientData?.is_exited || selectedClientData?.market_value === 0;
  
  // Filter tabs:
  //  - Active Units / Junk Units / Unit Mismatch hidden for exited clients
  //  - Provisional Sell + Exit Free Investments hidden when "All Family"
  //    (clubbed) is active — these views are individual-PAN only because the
  //    underlying tax simulation requires per-PAN FY headroom & residency.
  const tabs = allTabs.filter(t => {
    if (isExitedClient && ["active_units", "junk_units", "unit_mismatch"].includes(t.key)) return false;
    if (familyAggregate && ["provisional_sell", "provisional_buy", "exit_free"].includes(t.key)) return false;
    return true;
  });

  // If user is on Provisional Sell / Exit Free and toggles into "All Family"
  // clubbed mode, gracefully fall back to Summary (those tabs require an
  // individual PAN).
  useEffect(() => {
    if (familyAggregate && ["provisional_sell", "provisional_buy", "exit_free"].includes(activeTab)) {
      setActiveTab("summary");
    }
  }, [familyAggregate, activeTab]);

  // Transaction state
  const [transactions, setTransactions] = useState([]);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [transactionPage, setTransactionPage] = useState(1);
  const [transactionTotal, setTransactionTotal] = useState(0);
  const [transactionTotalPages, setTransactionTotalPages] = useState(0);
  const [selectedFolioForTxn, setSelectedFolioForTxn] = useState("");

  // Junk transactions state
  const [junkTransactions, setJunkTransactions] = useState([]);
  const [junkLoading, setJunkLoading] = useState(false);
  const [junkPage, setJunkPage] = useState(1);
  const [junkTotal, setJunkTotal] = useState(0);
  const [junkTotalPages, setJunkTotalPages] = useState(0);
  const [selectedFolioForJunk, setSelectedFolioForJunk] = useState("");
  const [junkCount, setJunkCount] = useState(0);
  const [relatedCount, setRelatedCount] = useState(0);
  const [junkSortConfig, setJunkSortConfig] = useState({ key: "transaction_date", direction: "desc" });
  const [unitMismatchSortConfig, setUnitMismatchSortConfig] = useState({ key: "difference", direction: "desc" });
  const [junkFolios, setJunkFolios] = useState([]); // All folios for dropdown

  // Segregated Portfolios state (non-financial/junk entries from unit reconciliation)
  const [segregatedPortfolios, setSegregatedPortfolios] = useState([]);
  const [segregatedLoading, setSegregatedLoading] = useState(false);

  // Sold Units - now sourced from allTransactions via soldFromAllTxn memo
  const [selectedFolioForSold, setSelectedFolioForSold] = useState("");
  const [soldSearchTerm, setSoldSearchTerm] = useState("");
  const [soldSortField, setSoldSortField] = useState("gain_loss");
  const [soldSortDir, setSoldSortDir] = useState("desc");
  const [selectedFYForSold, setSelectedFYForSold] = useState("all"); // Financial Year filter
  // Custom date range filter for Sold Units (NRI users on calendar-year tax cycles).
  // When either date is set, this filter is applied on top of FY / folio filters.
  const [soldDateFrom, setSoldDateFrom] = useState(""); // YYYY-MM-DD
  const [soldDateTo, setSoldDateTo] = useState("");     // YYYY-MM-DD
  const [soldUnitsSortConfig, setSoldUnitsSortConfig] = useState({ key: "transaction_date", direction: "desc" });
  const [summarySubTab, setSummarySubTab] = useState("purchase"); // purchase | sold - for Scheme Wise Breakdown tab
  // As-on-date valuation for Scheme Wise Breakdown — empty = live/today.
  const [schemeAsOnDate, setSchemeAsOnDate] = useState("");
  const [schemeAsOfNav, setSchemeAsOfNav] = useState({}); // {amfi_code: {nav, nav_date}}
  const [schemeAsOfLoading, setSchemeAsOfLoading] = useState(false);

  // Non-financial - now sourced from allTransactions (junk_units tab)

  // Active units state
  const [activeUnitsTransactions, setActiveUnitsTransactions] = useState([]);
  const [activeUnitsLoading, setActiveUnitsLoading] = useState(false);
  const [activeUnitsPage, setActiveUnitsPage] = useState(1);
  const [activeUnitsTotal, setActiveUnitsTotal] = useState(0);
  const [activeUnitsTotalPages, setActiveUnitsTotalPages] = useState(0);
  // Category-capsule filter (Wealth / Tax / Short Term / Thematic / Foreign / Commodities / All)
  // driven by /portfolio-strategy/holdings-categorized/{pan}.
  const [categoryHoldings, setCategoryHoldings] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState("All");
  const [selectedFolioForActiveUnits, setSelectedFolioForActiveUnits] = useState("");
  // Multi-fund selection on Active Units (parity with All Transactions UX).
  // When non-empty, takes precedence over `selectedFolioForActiveUnits` for
  // in-memory filtering. We keep the single-folio state around for the
  // pre-warming fetch path so the backend isn't called per-folio.
  const [selectedFoliosForActiveUnits, setSelectedFoliosForActiveUnits] = useState([]);
  const [activeUnitsSearch, setActiveUnitsSearch] = useState("");
  const [activeUnitsReconciliation, setActiveUnitsReconciliation] = useState(null);
  const [selectedHoldingPeriodForActiveUnits, setSelectedHoldingPeriodForActiveUnits] = useState("all");
  const [activeUnitsSortConfig, setActiveUnitsSortConfig] = useState({ key: "transaction_date", direction: "desc" });
  const [unitReconciliation, setUnitReconciliation] = useState(null);
  const [activeUnitsFolios, setActiveUnitsFolios] = useState([]); // All folios for dropdown

  // ── Active Units global rebuild (moved here from Reset DB) ───────────────
  const [rebuildingActiveUnits, setRebuildingActiveUnits] = useState(false);
  const [activeUnitsRebuildStatus, setActiveUnitsRebuildStatus] = useState(null);
  const [forceResettingActiveUnitsLock, setForceResettingActiveUnitsLock] = useState(false);

  const fetchActiveUnitsRebuildStatus = useCallback(async () => {
    if (!token) return;
    try {
      const r = await fetch(`${API_URL}/api/active-units/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.ok) setActiveUnitsRebuildStatus(await r.json());
    } catch (_e) { /* ignore */ }
  }, [token]);

  const handleRebuildActiveUnits = useCallback(async () => {
    if (!token) return;
    if (!window.confirm(
      "Rebuild Active Units?\n\n" +
      "Recomputes the FIFO snapshot of holdings across ALL clients. Takes ~60–120 seconds."
    )) return;
    setRebuildingActiveUnits(true);
    try {
      const resp = await fetch(`${API_URL}/api/active-units/rebuild`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await resp.json();
      if (!resp.ok) {
        toast.error(data.detail || "Rebuild failed to start");
        setRebuildingActiveUnits(false);
        return;
      }
      toast.info("Active Units rebuild started in background");
      const poll = async () => {
        try {
          const r = await fetch(`${API_URL}/api/active-units/status`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (!r.ok) return;
          const j = await r.json();
          setActiveUnitsRebuildStatus(j);
          if (j.status === "completed") {
            toast.success(`Active Units rebuilt: ${(j.rows_in_collection || 0).toLocaleString()} rows`);
            setRebuildingActiveUnits(false);
            return;
          }
          if (j.status === "failed") {
            toast.error(j.error || "Active Units rebuild failed");
            setRebuildingActiveUnits(false);
            return;
          }
          setTimeout(poll, 4000);
        } catch (_e) { setTimeout(poll, 6000); }
      };
      setTimeout(poll, 2000);
    } catch (e) {
      toast.error("Rebuild failed to start", { description: String(e) });
      setRebuildingActiveUnits(false);
    }
  }, [token]);

  const handleForceResetActiveUnitsLock = useCallback(async () => {
    if (!token) return;
    if (!window.confirm(
      "Force-reset Active Units lock?\n\n" +
      "Use only when the rebuild is permanently 'running'. Only releases the lock; no data touched."
    )) return;
    setForceResettingActiveUnitsLock(true);
    try {
      const r = await fetch(`${API_URL}/api/active-units/force-reset`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await r.json();
      if (!r.ok) {
        toast.error(data.detail || "Force-reset failed");
        return;
      }
      toast.success(`Lock cleared (was: ${data.previous_status || "unknown"}). You can rebuild now.`);
      await fetchActiveUnitsRebuildStatus();
    } catch (e) {
      toast.error("Force-reset failed", { description: String(e) });
    } finally {
      setForceResettingActiveUnitsLock(false);
    }
  }, [token, fetchActiveUnitsRebuildStatus]);

  // Per-PAN active_units rebuild — light/fast (~5-10s).
  // Used to refresh a single client's holdings without a full 300k-row pass.
  const [rebuildingPan, setRebuildingPan] = useState(false);
  const handleRebuildActiveUnitsForPan = useCallback(async () => {
    const panInput = window.prompt(
      "Rebuild Active Units for a single PAN\n\n" +
      "Enter the client PAN (e.g. ABCDE1234F). The rebuild touches only " +
      "this client's rows — ~5-10s. Dashboard cache is NOT refreshed."
    );
    if (!panInput) return;
    const pan = panInput.trim().toUpperCase();
    if (!/^[A-Z]{5}\d{4}[A-Z]$/.test(pan)) {
      toast.error("Invalid PAN format. Expected 5 letters + 4 digits + 1 letter (e.g. ABCDE1234F).");
      return;
    }
    setRebuildingPan(true);
    try {
      const r = await fetch(`${API_URL}/api/active-units/rebuild-pan?pan=${encodeURIComponent(pan)}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await r.json();
      if (!r.ok) {
        toast.error(data.detail || "Per-PAN rebuild failed");
        return;
      }
      toast.success(
        `Rebuilt ${data.client_name || pan}: ${data.inserted} rows inserted, ${data.deleted} deleted.`,
        { duration: 6000 }
      );
    } catch (e) {
      toast.error("Per-PAN rebuild failed", { description: String(e) });
    } finally {
      setRebuildingPan(false);
    }
  }, [token]);

  // IDCW/Dividend Payout - now sourced from allTransactions via dividendFromAllTxn memo
  const [selectedFYForIdcw, setSelectedFYForIdcw] = useState("all"); // Financial Year filter for IDCW

  // Provisional Sell — UI state
  const [psBucket, setPsBucket] = useState("all"); // "all" or LT5/Y3_5/Y2_3/Y1_2/LT1
  const [psSellAmount, setPsSellAmount] = useState("");
  const [psSellMode, setPsSellMode] = useState("bucket_priority"); // bucket_specific | bucket_priority | tax_optimized
  const [psBucketPriority, setPsBucketPriority] = useState("LT5,Y3_5,Y2_3,Y1_2,LT1");
  const [psExcludeLocked, setPsExcludeLocked] = useState(true);
  const [psSimulation, setPsSimulation] = useState(null);
  const [psComparison, setPsComparison] = useState(null); // multi-mode side-by-side comparison
  const [psExpandedGroups, setPsExpandedGroups] = useState({});
  const [psBucketsSelected, setPsBucketsSelected] = useState(["LT5"]); // multi-select for bucket_specific mode
  // Investor's marginal income-tax slab (percent). Used to tax DEBT-STCG.
  // Default 30 % = top slab. User can override per simulation.
  const [psDebtSlabPct, setPsDebtSlabPct] = useState(30);
  // Tax regime — affects which marginal slabs are available in the dropdown.
  const [psTaxRegime, setPsTaxRegime] = useState("new"); // "new" | "old"
  // NRI mode — when ON, MF taxes are treated as TDS deducted by AMC at source:
  //   • LTCG exemption (₹1.25 L) does NOT apply at TDS stage (claim refund via ITR)
  //   • 4% health-&-education cess always applied
  //   • Surcharge tier (0/10/15/25/37 %) configurable per client income bracket
  const [psNriOverride, setPsNriOverride] = useState(null); // null = auto-detect, true/false = manual
  const [psNriSurchargePct, setPsNriSurchargePct] = useState(0); // 0 / 10 / 15 / 25 / 37

  // Exit Free Investments filters (added 2026-02-07)
  const [efAssetClass, setEfAssetClass] = useState("all"); // all | equity | debt | elss
  const [efSearch, setEfSearch] = useState("");
  const [efSortKey, setEfSortKey] = useState("bucket"); // bucket | gain | current_value | cagr | holding_years
  const [efSortDir, setEfSortDir] = useState("desc");   // asc | desc
  // Clubbed view: expanded group keys (folio + scheme + bucket).
  const [expandedExitFreeGroups, setExpandedExitFreeGroups] = useState({});


  // Family member PAN list — derived from familyContext when Clubbed View
  // (familyAggregate) is active AND no single-member drill-down. Used by
  // the dedicated paginated fetchers (active-units / unit-reconciliation /
  // raw-transactions / junk) to fan out across every family member.
  // When the user drills into one member (drillDownPan set), this returns
  // null so each fetcher falls back to single-PAN mode.
  const familyMemberPans = useMemo(() => {
    if (!familyAggregate || drillDownPan) return null;
    if (!familyContext?.is_group_head) return null;
    const arr = (familyContext.family_members || [])
      .map(m => (m.pan || "").toUpperCase())
      .filter(p => p && p.length === 10);
    return arr.length > 1 ? arr : null;
  }, [familyAggregate, drillDownPan, familyContext]);

  // Family-aware lookups for the per-row "Client Name" column shown next
  // to Folio No on all tabs when Clubbed Family view is active.
  const showFamilyClientCol = !!(
    familyAggregate
    && familyContext?.is_group_head
    && (familyContext?.family_members || []).length > 1
    && !drillDownPan
  );
  const panToClientName = useMemo(() => {
    const m = {};
    (familyContext?.family_members || []).forEach((mem) => {
      const pan = (mem.pan || "").toUpperCase();
      if (!pan) return;
      const raw = mem.name || pan;
      const titled = raw.replace(/\b([A-Z]+)\b/g, (w) => (
        /^\([A-Z]+\)?$/.test(w) || w.length <= 3
          ? w
          : w.charAt(0) + w.slice(1).toLowerCase()
      ));
      m[pan] = titled;
    });
    return m;
  }, [familyContext]);
  const folioToClientName = useMemo(() => {
    if (!showFamilyClientCol) return {};
    const m = {};
    (allTransactions || []).forEach((t) => {
      const folio = t?.folio_no;
      const pan = (t?._family_pan || "").toUpperCase();
      if (!folio || !pan || m[folio]) return;
      const name = panToClientName[pan];
      if (name) m[folio] = name;
    });
    return m;
  }, [showFamilyClientCol, allTransactions, panToClientName]);
  const resolveRowClientName = (row) => {
    if (!showFamilyClientCol) return "";
    const pan = (row?._family_pan || row?.investor_pan || row?.pan_no || row?.first_holder_pan || "").toUpperCase();
    if (pan && panToClientName[pan]) return panToClientName[pan];
    if (row?.folio_no && folioToClientName[row.folio_no]) return folioToClientName[row.folio_no];
    return "—";
  };

  // Fetch unit reconciliation for selected client (family-aware).
  const fetchUnitReconciliation = useCallback(async () => {
    if (!selectedClient) return;
    try {
      const pans = familyMemberPans || [selectedClientPan];
      const results = await Promise.all(
        pans.map(p => api.get(`/reconciliation/units/${encodeURIComponent(p)}`).catch(() => ({ data: null })))
      );
      if (pans.length === 1) {
        setUnitReconciliation(results[0]?.data ?? null);
        return;
      }
      // Merge per-PAN reconciliation responses. Each typically returns an
      // array of folio-level rows OR a `{rows: [...]}` shape — handle both.
      const merged = { rows: [] };
      for (const r of results) {
        const data = r?.data;
        if (!data) continue;
        if (Array.isArray(data)) merged.rows.push(...data);
        else if (Array.isArray(data.rows)) merged.rows.push(...data.rows);
        else if (Array.isArray(data.reconciliation)) merged.rows.push(...data.reconciliation);
      }
      setUnitReconciliation(merged);
    } catch (error) {
      console.error("Error fetching unit reconciliation:", error);
      setUnitReconciliation(null);
    }
  }, [selectedClient, selectedClientPan, familyMemberPans]);

  // Fetch transactions (raw, paginated) — family-aware in Clubbed View.
  const fetchTransactions = useCallback(async (page = 1, folioNo = "") => {
    if (!selectedClient) return;

    setTransactionsLoading(true);
    try {
      const buildUrl = (pan) => {
        let u = `/transactions-raw/${encodeURIComponent(pan)}?page=${page}&page_size=50`;
        if (folioNo) u += `&folio_no=${folioNo}`;
        return u;
      };
      const pans = familyMemberPans || [selectedClientPan];
      if (pans.length === 1) {
        const response = await api.get(buildUrl(pans[0]));
        setTransactions(response.data.transactions || []);
        setTransactionTotal(response.data.total || 0);
        setTransactionTotalPages(response.data.total_pages || 0);
        setTransactionPage(page);
      } else {
        const results = await Promise.all(
          pans.map(p => api.get(buildUrl(p)).catch(() => ({ data: { transactions: [], total: 0 } })))
        );
        const merged = [];
        let totalSum = 0;
        for (const r of results) {
          const d = r?.data || {};
          merged.push(...(d.transactions || []));
          totalSum += d.total || 0;
        }
        setTransactions(merged);
        setTransactionTotal(totalSum);
        setTransactionTotalPages(Math.max(1, Math.ceil(totalSum / 50)));
        setTransactionPage(page);
      }
    } catch (error) {
      console.error("Failed to fetch transactions:", error);
      setTransactions([]);
    } finally {
      setTransactionsLoading(false);
    }
  }, [selectedClient, selectedClientPan, familyMemberPans, api]);

  // Fetch junk transactions (family-aware in Clubbed View).
  const fetchJunkTransactions = useCallback(async (page = 1, folioNo = "", searchQuery = "") => {
    if (!selectedClient) return;

    setJunkLoading(true);
    try {
      const buildUrl = (pan) => {
        let u = `/transactions/junk/${encodeURIComponent(pan)}?page=${page}&page_size=50`;
        if (folioNo) u += `&folio_no=${folioNo}`;
        if (searchQuery) u += `&search=${encodeURIComponent(searchQuery)}`;
        return u;
      };
      const pans = familyMemberPans || [selectedClientPan];
      if (pans.length === 1) {
        const response = await api.get(buildUrl(pans[0]));
        setJunkTransactions(response.data.transactions || []);
        setJunkTotal(response.data.total || 0);
        setJunkTotalPages(response.data.total_pages || 0);
        setJunkPage(page);
        setJunkCount(response.data.junk_count || 0);
        setRelatedCount(response.data.related_count || 0);
        if (!folioNo && response.data.unique_folios) {
          setJunkFolios(response.data.unique_folios);
        }
      } else {
        const results = await Promise.all(
          pans.map(p => api.get(buildUrl(p)).catch(() => ({ data: { transactions: [], total: 0, unique_folios: [], junk_count: 0, related_count: 0 } })))
        );
        const merged = [];
        const folioSet = new Set();
        let totalSum = 0, junkSum = 0, relatedSum = 0;
        for (const r of results) {
          const d = r?.data || {};
          merged.push(...(d.transactions || []));
          totalSum += d.total || 0;
          junkSum += d.junk_count || 0;
          relatedSum += d.related_count || 0;
          for (const f of (d.unique_folios || [])) folioSet.add(f);
        }
        setJunkTransactions(merged);
        setJunkTotal(totalSum);
        setJunkTotalPages(Math.max(1, Math.ceil(totalSum / 50)));
        setJunkPage(page);
        setJunkCount(junkSum);
        setRelatedCount(relatedSum);
        if (!folioNo) setJunkFolios(Array.from(folioSet));
      }
    } catch (error) {
      console.error("Failed to fetch junk transactions:", error);
      setJunkTransactions([]);
    } finally {
      setJunkLoading(false);
    }
  }, [selectedClient, selectedClientPan, familyMemberPans, api]);

  // Fetch segregated portfolios for a client.
  const fetchSegregatedPortfolios = useCallback(async () => {
    if (!selectedClient) return;
    
    setSegregatedLoading(true);
    try {
      const response = await api.get(`/portfolio/${selectedClient}/segregated`);
      setSegregatedPortfolios(response.data.entries || []);
    } catch (error) {
      console.error("Failed to fetch segregated portfolios:", error);
      setSegregatedPortfolios([]);
    } finally {
      setSegregatedLoading(false);
    }
  }, [selectedClient, api]);

  // Sold, Non-Financial, and IDCW now sourced from allTransactions (no standalone API calls)

  // Fetch active units transactions (family-aware in Clubbed View).
  const fetchActiveUnitsTransactions = useCallback(async (page = 1, folioNo = "", pageSize = 50, searchQuery = "") => {
    if (!selectedClient) return;

    setActiveUnitsLoading(true);
    try {
      const buildUrl = (pan) => {
        let u = `/transactions/active/${encodeURIComponent(pan)}?page=${page}&page_size=${pageSize}`;
        if (folioNo) u += `&folio_no=${folioNo}`;
        if (searchQuery) u += `&search=${encodeURIComponent(searchQuery)}`;
        return u;
      };
      const pans = familyMemberPans || [selectedClientPan];
      if (pans.length === 1) {
        const response = await api.get(buildUrl(pans[0]));
        setActiveUnitsTransactions(response.data.transactions || []);
        setActiveUnitsTotal(response.data.total || 0);
        setActiveUnitsTotalPages(response.data.total_pages || 0);
        setActiveUnitsPage(page);
        setActiveUnitsReconciliation(response.data.reconciliation || null);
        if (!folioNo && response.data.unique_folios) {
          setActiveUnitsFolios(response.data.unique_folios);
        }
      } else {
        // Family-aggregate: fan out across each member's PAN, merge txns,
        // sum totals, union unique_folios. Per-PAN pagination is preserved
        // (each member returns up to page_size rows for the requested page).
        const results = await Promise.all(
          pans.map(p => api.get(buildUrl(p)).catch(() => ({ data: { transactions: [], total: 0, unique_folios: [] } })))
        );
        const merged = [];
        const folioSet = new Set();
        let totalSum = 0;
        for (const r of results) {
          const d = r?.data || {};
          merged.push(...(d.transactions || []));
          totalSum += d.total || 0;
          for (const f of (d.unique_folios || [])) folioSet.add(f);
        }
        setActiveUnitsTransactions(merged);
        setActiveUnitsTotal(totalSum);
        setActiveUnitsTotalPages(Math.max(1, Math.ceil(totalSum / pageSize)));
        setActiveUnitsPage(page);
        setActiveUnitsReconciliation(null);
        if (!folioNo) setActiveUnitsFolios(Array.from(folioSet));
      }
    } catch (error) {
      console.error("Failed to fetch active units transactions:", error);
      setActiveUnitsTransactions([]);
    } finally {
      setActiveUnitsLoading(false);
    }
  }, [selectedClient, selectedClientPan, familyMemberPans, api]);

  // Fetch transactions when tab is selected
  useEffect(() => {
    if (activeTab === "transaction_details" && selectedClient) {
      fetchTransactions(1, selectedFolioForTxn);
    }
  }, [activeTab, selectedClient, selectedFolioForTxn]);

  // Fetch portfolio data and reconciliation when client or family scope changes
  useEffect(() => {
    if (selectedClient) {
      fetchPortfolioData();
      fetchUnitReconciliation();
    }
  }, [selectedClient, selectedClientPan, familyMemberPans, fetchUnitReconciliation]);

  // Family-mapping context follows the SEARCH-selected client (NOT the
  // drill-down PAN). This means when the user clicks a member chip the
  // banner + chip list stay anchored to the Head — only the data viewer
  // shifts to that member. Backend `/family-mapping/by-pan/{pan}` enforces
  // role-based access (privileged roles may query any PAN; plain clients
  // can only query their own).
  useEffect(() => {
    if (!searchClientPan) return;
    // Reset drill-down whenever the search/dropdown picks a different client.
    setDrillDownPan("");
    let cancelled = false;
    (async () => {
      try {
        const fm = await api.get(`/family-mapping/by-pan/${encodeURIComponent(searchClientPan)}`);
        if (!cancelled) {
          const ctx = fm.data || null;
          setFamilyContext(ctx);
          // Auto-enable Clubbed Family View when the SEARCH-selected client
          // IS the Group Head with ≥2 members. Non-head members get an
          // individual view (no banner is rendered — see the banner JSX
          // guard `familyContext.is_group_head`).
          if (ctx && ctx.is_group_head && (ctx.family_members || []).length > 1) {
            setFamilyAggregate(true);
          } else {
            setFamilyAggregate(false);
          }
        }
      } catch (_e) {
        if (!cancelled) {
          setFamilyContext(null);
          setFamilyAggregate(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [searchClientPan]);

  // Reset all in-tab search/filter state when the client changes so the
  // user starts each client's portfolio with a clean slate (otherwise an
  // ISIN/folio typed for one client carries over and silently hides
  // matches for the next client).
  useEffect(() => {
    setSearchTerm("");
    setFolioSearchTerm("");
    setPurchaseSearchTerm("");
    setAllTransactionsSearch("");
    setSoldSearchTerm("");
    setSelectedFolioForTxn("");
    setSelectedFolioForSold("");
    setSelectedSubAccount("all");
    setSelectedMinorKey(null);
    setSelectedBankAccType("all");
    setSelectedGoalFilter("all");
    setAllTxnPage(1);
    setAllTxnDupFilter("all");
    // Provisional Sell — reset so the Target Sell Amount + stale simulation
    // results don't leak between clients (the entered amount and the picker
    // selection are by definition client-specific).
    setPsSellAmount("");
    setPsSimulation(null);
    setPsComparison(null);
    setPsExpandedGroups({});
    setPsNriOverride(null);
    setPsNriSurchargePct(0);
    // Category capsule filter resets on client switch.
    setCategoryHoldings(null);
    setCategoryFilter("All");
  }, [selectedClient]);

  // Fetch category-tagged holdings for the capsule filter (Active Units tab only).
  useEffect(() => {
    if (activeTab !== "active_units" || !selectedClientPan) {
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/portfolio-strategy/holdings-categorized/${selectedClientPan}`);
        if (!cancelled) setCategoryHoldings(data);
      } catch {
        if (!cancelled) setCategoryHoldings(null);
      }
    })();
    return () => { cancelled = true; };
  }, [activeTab, selectedClientPan, api]);

  // Fetch transactions when tab requires it and data isn't loaded yet.
  // `allTxnClientId` is part of the dep array so that when an in-flight fetch
  // completes (and sets allTxnClientId to its key) the effect re-evaluates —
  // important for the Clubbed Family flow where familyAggregate flips from
  // false→true while the initial single-PAN fetch is still in progress; the
  // re-eval then triggers a fresh fetch against the family-aggregate URL.
  const [allTxnClientId, setAllTxnClientId] = useState("");
  useEffect(() => {
    const expectedKey = `${selectedClient}|${drillDownPan}|${familyAggregate ? "FAM" : "ONE"}`;
    if (["all_transactions", "failed_transactions", "sold_units", "idcw_payouts", "junk_units", "segregated", "scheme_breakdown", "active_units", "unit_mismatch", "summary", "provisional_sell", "exit_free"].includes(activeTab) && selectedClient && allTxnClientId !== expectedKey && !allTransactionsLoading) {
      setAllTransactionsLoading(true);
      setAllTransactions([]);
      // Clubbed Family View: when familyAggregate is on, fetch the merged
      // payload that the backend builds by iterating every family PAN.
      // Individual chips drill back down to the per-member endpoint.
      const txnUrl = familyAggregate
        ? `/portfolio/family-aggregate/${encodeURIComponent(selectedClientPan)}/all-transactions${bankQueryStr}`
        : `/portfolio/${encodeURIComponent(selectedClientPan)}/all-transactions${bankQueryStr}`;
      api.get(txnUrl)
        .then(res => {
          const txnData = res.data || {};
          setAllTransactions(txnData.transactions || []);
          setAllTxnClientId(expectedKey);
        })
        .catch(err => console.error("Failed to fetch all transactions:", err))
        .finally(() => setAllTransactionsLoading(false));
    }
  }, [activeTab, selectedClient, familyAggregate, drillDownPan, allTxnClientId]);


  // Fetch junk transactions when tab is selected
  useEffect(() => {
    if (activeTab === "junk_units" && selectedClient) {
      fetchJunkTransactions(1, selectedFolioForJunk);
    }
  }, [activeTab, selectedClient, selectedFolioForJunk]);

  // Fetch segregated portfolios when tab is selected
  useEffect(() => {
    if (activeTab === "segregated_portfolios" && selectedClient) {
      fetchSegregatedPortfolios();
    }
  }, [activeTab, selectedClient, fetchSegregatedPortfolios]);

  // Non-financial, sold, and IDCW tabs now source from allTransactions (loaded above)

  // Fetch active units transactions when tab is selected
  useEffect(() => {
    if (activeTab === "active_units") fetchActiveUnitsRebuildStatus();
  }, [activeTab, fetchActiveUnitsRebuildStatus]);

  useEffect(() => {
    if ((activeTab === "active_units" || activeTab === "unit_mismatch" || activeTab === "summary" || activeTab === "scheme_breakdown" || activeTab === "provisional_sell" || activeTab === "exit_free") && selectedClient) {
      // For unit_mismatch, summary, scheme_breakdown, provisional_sell and exit_free, fetch ALL active units with a large page size
      const pageSize = (activeTab === "unit_mismatch" || activeTab === "summary" || activeTab === "scheme_breakdown" || activeTab === "provisional_sell" || activeTab === "exit_free") ? 10000 : 50;
      fetchActiveUnitsTransactions(1, selectedFolioForActiveUnits, pageSize);
    }
  }, [activeTab, selectedClient, selectedFolioForActiveUnits]);

  // IDCW tab now sources from allTransactions (dividendFromAllTxn memo)

  useEffect(() => {
    fetchClients();
  }, [isClient, user]);

  // Clear active units state and fetch all transactions when client changes
  useEffect(() => {
    if (selectedClient) {
      // Clear active units state
      setActiveUnitsTransactions([]);
      setActiveUnitsTotal(0);
      setActiveUnitsTotalPages(0);
      setActiveUnitsReconciliation(null);
      setActiveUnitsFolios([]);

      // Fetch all transactions for the new client
      // IMPORTANT: honour Clubbed Family View. If we always hit the
      // single-PAN endpoint here, we clobber the family-aggregate
      // payload fetched by the tab-driven effect above — folios of
      // the other family members disappear from `allTransactions`,
      // and therefore from `activeUnitsCostMap`, breaking XIRR /
      // cost columns for every non-head member in Scheme Wise
      // Breakdown when "All Family" is selected.
      setAllTransactionsLoading(true);
      setAllTransactions([]);
      const txnUrl = familyAggregate
        ? `/portfolio/family-aggregate/${encodeURIComponent(selectedClientPan)}/all-transactions${bankQueryStr}`
        : `/portfolio/${encodeURIComponent(selectedClientPan)}/all-transactions${bankQueryStr}`;
      // Keep the cache key in the same shape as the tab-driven
      // effect above so the two effects don't fight each other.
      const clientKey = `${selectedClient}|${drillDownPan}|${familyAggregate ? "FAM" : "ONE"}`;
      api.get(txnUrl)
        .then(res => {
          const txnData = res.data || {};
          setAllTransactions(txnData.transactions || []);
          setAllTxnClientId(clientKey);
        })
        .catch(err => console.error("Failed to fetch all transactions:", err))
        .finally(() => setAllTransactionsLoading(false));
    }
  }, [selectedClient]);

  // Switch to "sold" tab for exited clients
  useEffect(() => {
    if (isExitedClient) {
      setSummarySubTab("sold");
    } else {
      setSummarySubTab("purchase");
    }
  }, [isExitedClient, selectedClient]);

  // Fetch goals (scoped to the currently selected client so each client
  // has their own goal list — switching clients reloads the right goals)
  const fetchGoals = useCallback(async () => {
    if (!selectedClient) { setGoals([]); setSchemeGoals({}); setSchemeAllocations({}); return; }
    try {
      const response = await api.get(`/goals?client_id=${encodeURIComponent(selectedClient)}`);
      const goalsData = response.data.goals || [];
      setGoals(goalsData);
      
      // Build holding -> goals mapping with percentages (using folio_no:isin as key)
      const mapping = {};
      const allocations = {};
      
      goalsData.forEach(goal => {
        const schemes = goal.schemes || [];
        schemes.forEach(scheme => {
          // Handle both old format (string) and new format (object with isin, folio_no, percentage)
          let holdingKey;
          let percentage;
          
          if (typeof scheme === 'string') {
            // Old format - just ISIN (for backward compatibility)
            holdingKey = scheme;
            percentage = 100;
          } else {
            // New format - folio_no + isin
            const folio = scheme.folio_no || '';
            const isin = scheme.isin || '';
            holdingKey = folio ? `${folio}:${isin}` : isin;
            percentage = scheme.percentage || 100;
          }
          
          if (!mapping[holdingKey]) mapping[holdingKey] = [];
          mapping[holdingKey].push({ goal, percentage, scheme });
          
          // Track total allocation per holding
          if (!allocations[holdingKey]) allocations[holdingKey] = 0;
          allocations[holdingKey] += percentage;
        });
      });
      
      setSchemeGoals(mapping);
      setSchemeAllocations(allocations);
    } catch (error) {
      console.error("Failed to fetch goals", error);
    }
  }, [api, selectedClient]);

  useEffect(() => {
    fetchGoals();
  }, [fetchGoals]);

  // Create new goal
  const handleCreateGoal = async () => {
    if (!newGoalName.trim() || !newGoalDate) {
      toast.error("Please enter goal name and expected date");
      return;
    }
    if (!selectedClient) {
      toast.error("Please select a client first");
      return;
    }
    
    setCreatingGoal(true);
    try {
      await api.post("/goals", {
        name: newGoalName.trim(),
        expected_date: newGoalDate,
        target_amount: parseFloat(newGoalAmount) || 0,
        client_id: selectedClient,  // scope goal to the currently selected client
      });
      
      toast.success("Goal created successfully");
      setShowCreateGoalDialog(false);
      setNewGoalName("");
      setNewGoalDate("");
      setNewGoalAmount("");
      fetchGoals();
    } catch (error) {
      toast.error("Failed to create goal");
    } finally {
      setCreatingGoal(false);
    }
  };

  // Edit goal
  const handleEditGoal = async () => {
    if (!editingGoal) return;
    
    setCreatingGoal(true);
    try {
      await api.put(`/goals/${editingGoal.id}`, {
        name: newGoalName.trim() || editingGoal.name,
        expected_date: newGoalDate || editingGoal.expected_date,
        target_amount: parseFloat(newGoalAmount) || editingGoal.target_amount || 0
      });
      
      toast.success("Goal updated successfully");
      setShowEditGoalDialog(false);
      setEditingGoal(null);
      setNewGoalName("");
      setNewGoalDate("");
      setNewGoalAmount("");
      fetchGoals();
    } catch (error) {
      toast.error("Failed to update goal");
    } finally {
      setCreatingGoal(false);
    }
  };

  // Open edit dialog
  const openEditGoalDialog = (goal) => {
    setEditingGoal(goal);
    setNewGoalName(goal.name);
    setNewGoalDate(goal.expected_date);
    setNewGoalAmount(goal.target_amount?.toString() || "");
    setShowEditGoalDialog(true);
  };

  // Calculate current value and achievement % for a goal
  const calculateGoalProgress = (goal) => {
    const schemes = goal.schemes || [];
    let currentValue = 0;
    
    // Sum up market values of all assigned holdings (weighted by percentage)
    schemes.forEach(scheme => {
      const isin = typeof scheme === 'string' ? scheme : scheme.isin;
      const folioNo = typeof scheme === 'string' ? '' : (scheme.folio_no || '');
      const percentage = typeof scheme === 'string' ? 100 : (scheme.percentage || 100);
      
      // Find the specific holding by folio_no + isin
      let holding;
      if (folioNo) {
        holding = portfolioData.find(h => h.folio_no === folioNo && h.isin === isin);
      } else {
        // Fallback for old format - sum all holdings with this ISIN
        holding = portfolioData.find(h => h.isin === isin);
      }
      
      if (holding?.market_value) {
        // Add weighted value based on percentage allocated
        currentValue += (holding.market_value * percentage) / 100;
      }
    });
    
    const targetAmount = goal.target_amount || 0;
    const achievementPercent = targetAmount > 0 ? Math.min((currentValue / targetAmount) * 100, 100) : 0;
    
    return {
      currentValue,
      targetAmount,
      achievementPercent: Math.round(achievementPercent)
    };
  };

  // Compute folio options with scheme details for dropdown
  const folioOptions = useMemo(() => {
    const folioMap = {};
    portfolioData.forEach(h => {
      if (h.folio_no) {
        if (!folioMap[h.folio_no]) {
          folioMap[h.folio_no] = {
            folio_no: h.folio_no,
            schemes: [],
            totalValue: 0
          };
        }
        folioMap[h.folio_no].schemes.push(h.scheme_name || toTitleCase(h.fund_desc) || h.scheme_code?.toUpperCase() || 'Unknown');
        folioMap[h.folio_no].totalValue += h.market_value || 0;
      }
    });
    return Object.values(folioMap).sort((a, b) => b.totalValue - a.totalValue);
  }, [portfolioData]);

  // Assign scheme to goal with percentage (using folio_no + isin)
  const handleAssignGoal = async (goalId, folioNo, isin, percentage = 100) => {
    try {
      const response = await api.post("/goals/assign", { goal_id: goalId, folio_no: folioNo, isin, percentage });
      toast.success(`Holding allocated ${percentage}% to goal`);
      fetchGoals();
      setAssigningPercentage(100); // Reset for next assignment
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to assign holding to goal";
      toast.error(message);
    }
  };

  // Unassign scheme from goal
  const handleUnassignGoal = async (goalId, folioNo, isin) => {
    try {
      await api.post("/goals/unassign", { goal_id: goalId, folio_no: folioNo, isin, percentage: 0 });
      toast.success("Holding removed from goal");
      fetchGoals();
    } catch (error) {
      toast.error("Failed to remove holding from goal");
    }
  };

  // Get holding key (folio_no:isin)
  const getHoldingKey = (folioNo, isin) => {
    return folioNo ? `${folioNo}:${isin}` : isin;
  };

  // Get remaining allocation for a holding
  const getRemainingAllocation = (holdingKey) => {
    const allocated = schemeAllocations[holdingKey] || 0;
    return Math.max(0, 100 - allocated);
  };

  // Check if a holding is fully allocated
  const isFullyAllocated = (holdingKey) => {
    return (schemeAllocations[holdingKey] || 0) >= 100;
  };

  // Delete goal
  const handleDeleteGoal = async (goalId) => {
    if (!window.confirm("Are you sure you want to delete this goal?")) return;
    try {
      await api.delete(`/goals/${goalId}`);
      toast.success("Goal deleted");
      fetchGoals();
    } catch (error) {
      toast.error("Failed to delete goal");
    }
  };

  const fetchClients = async () => {
    try {
      const response = await api.get("/clients-with-aum");
      setClients(response.data);
      if (clientIdParam) {
        setSelectedClient(clientIdParam);
      } else if (response.data.length > 0) {
        setSelectedClient(response.data[0].id);
      }
    } catch (error) {
      toast.error("Failed to load clients");
    } finally {
      setLoading(false);
    }
    // Family-mapping context for the Family Group banner is now driven by
    // the `selectedClientPan` useEffect below, so it always reflects the
    // currently-viewed client (not the logged-in user). No fetch here.
  };

  const fetchPortfolioData = async () => {
    if (!selectedClient) return;
    
    setLoading(true);
    try {
      // Family-aware fan-out: when Clubbed Family View is active (no drill-down),
      // fetch each member's holdings + folio-details and merge. Otherwise fall
      // back to the single-PAN path. Dedupe merged holdings/folios by
      // (folio_no + scheme_code) to prevent double-counting jointly-held folios
      // that surface across multiple members' investor_master rows.
      const pans = familyMemberPans || [selectedClientPan];
      let data = [];
      let folioDetailsData = [];
      if (pans.length === 1) {
        const [response, folioDetailsResponse] = await Promise.all([
          api.get(`/portfolio/${encodeURIComponent(pans[0])}${bankQueryStr}`),
          api.get(`/portfolio/${encodeURIComponent(pans[0])}/folio-details${bankQueryStr}`),
        ]);
        data = response.data || [];
        folioDetailsData = folioDetailsResponse.data || [];
      } else {
        const results = await Promise.all(
          pans.map(p => Promise.all([
            api.get(`/portfolio/${encodeURIComponent(p)}${bankQueryStr}`).catch(() => ({ data: [] })),
            api.get(`/portfolio/${encodeURIComponent(p)}/folio-details${bankQueryStr}`).catch(() => ({ data: [] })),
          ]))
        );
        const seenHoldings = new Set();
        const seenFolios = new Set();
        for (const [hRes, fdRes] of results) {
          for (const h of (hRes.data || [])) {
            const key = `${h.folio_no || ''}|${h.scheme_code || h.isin || h.scheme_name || ''}`;
            if (seenHoldings.has(key)) continue;
            seenHoldings.add(key);
            data.push(h);
          }
          for (const fd of (fdRes.data || [])) {
            const key = `${fd.folio_no || ''}|${fd.scheme_code || fd.isin || fd.scheme_name || ''}`;
            if (seenFolios.has(key)) continue;
            seenFolios.add(key);
            folioDetailsData.push(fd);
          }
        }
      }
      setPortfolioData(data);
      setFolioDetailsData(folioDetailsData);
      
      // Count unique holdings with units > 0 by folio_no + scheme_code combination
      const holdingsWithBalance = data.filter(item => (item.units || 0) > 0);
      const uniqueHoldings = new Set(holdingsWithBalance.map(item => `${item.folio_no}_${item.scheme_code}`)).size;
      // Total Folios = unique folios with visible data (units > 0 or transactions exist)
      const visibleFolios = new Set(data.filter(item => (item.units || 0) > 0 || (item.txn_count || 0) > 0).map(item => item.folio_no)).size;
      setTotalFolios(visibleFolios);
      
      // Calculate holding pattern counts and collect joint holder names
      const patternCounts = { all: 0, self_single: 0, joint_holder: 0, as_joint: 0, minor_guardian: 0, self_minor: 0 };
      const allFolioSet = new Set();
      const jointHolderNames = new Set();
      const guardianNames = new Set();
      
      // Sub-account grouping rules based on account type:
      // Individual: PAN only (name variants are the same person)
      // Minor: Name + Guardian PAN (each minor is separate)
      // Firm (HUF/Proprietorship/Partnership/Body Corporate/Company/Trust): Name + First Holder PAN
      // NRI: PAN + Bank Account Type (NRE/NRO)
      const subAccountsMap = {};
      
      const getAccountType = (item) => {
        const taxStatus = (item.tax_status || '').toUpperCase();
        const gp = cleanField(item.guardian_pan);
        if (gp || taxStatus.includes('MINOR')) return 'minor';
        if (taxStatus.includes('NRI')) return 'nri';
        if (FIRM_TAX_KEYWORDS.some(kw => taxStatus.includes(kw))) return 'firm';
        return 'individual';
      };
      
      const getNriBankType = (item) => resolveNriBankType(item);
      
      data.forEach(item => {
        if (item.folio_no && ((item.units || 0) > 0 || (item.txn_count || 0) > 0)) allFolioSet.add(item.folio_no);
        patternCounts.all = allFolioSet.size;
        const pattern = item.holding_pattern || "self_single";
        if (patternCounts[pattern] !== undefined) {
          patternCounts[pattern]++;
        }
        if (item.second_holder && item.second_holder.trim()) {
          jointHolderNames.add(item.second_holder.trim());
        }
        if (cleanField(item.guardian_name)) {
          guardianNames.add(item.guardian_name.trim());
        }
        
        const accountType = getAccountType(item);
        const holderName = (item.first_holder || '').trim();
        const pan = cleanField(item.first_holder_pan) || cleanField(item.pan_1) || '';
        const guardianPan = cleanField(item.guardian_pan);
        let uniqueKey, label, subType;
        
        if (accountType === 'minor') {
          uniqueKey = `${holderName.toUpperCase()}|${guardianPan}`;
          label = holderName;
          subType = 'minor';
        } else if (accountType === 'firm') {
          uniqueKey = `${holderName.toUpperCase()}|${pan}`;
          label = holderName;
          subType = 'firm';
        } else if (accountType === 'nri') {
          const bankType = getNriBankType(item);
          uniqueKey = `${pan}|${bankType}`;
          label = `${holderName} - ${bankType}`;
          subType = 'nri';
        } else {
          // Individual - split by holding pattern (self vs joint)
          const pattern = item.holding_pattern || 'self_single';
          if (pattern === 'joint_holder' || pattern === 'as_joint') {
            const secondHolder = cleanField(item.second_holder).toUpperCase();
            uniqueKey = `JOINT|${pan}|${secondHolder}`;
            label = cleanField(item.second_holder) || holderName;
            subType = 'joint';
          } else {
            uniqueKey = `INDIVIDUAL|${pan}`;
            label = holderName;
            subType = 'individual';
          }
        }
        
        if (!subAccountsMap[uniqueKey]) {
          subAccountsMap[uniqueKey] = {
            holderName: label,
            guardianName: item.guardian_name?.trim() || '',
            guardianPan: guardianPan,
            subType,
            count: 0,
            folioSet: new Set(),
            marketValue: 0
          };
        }
        // Only count folios that have visible data (units > 0 or transactions exist)
        if (item.folio_no && ((item.units || 0) > 0 || (item.txn_count || 0) > 0)) {
          subAccountsMap[uniqueKey].folioSet.add(item.folio_no);
        }
        subAccountsMap[uniqueKey].count++;
        subAccountsMap[uniqueKey].marketValue += (item.market_value || 0);
      });
      
      // Count NRE/NRO bank account types for NRI clients
      // NRI Repatriable = NRE, NRI Non-Repatriable = NRO
      const bankAccTypeCounts = { nre: 0, nro: 0 };
      const isNriClient = data.some(item => 
        item.tax_status?.toUpperCase().includes('NRI') || 
        item.tax_category?.toUpperCase().includes('NRI')
      );
      
      if (isNriClient) {
        data.forEach(item => {
          const bankType = resolveNriBankType(item);
          if (bankType === 'NRO') bankAccTypeCounts.nro++;
          else if (bankType === 'NRE') bankAccTypeCounts.nre++;
        });
      }
      patternCounts.bankAccTypeCounts = bankAccTypeCounts;
      patternCounts.isNriClient = isNriClient;
      
      // Convert subAccountsMap to array, sort by market value (highest first)
      // Filter out sub-accounts with zero visible folios
      const subAccounts = Object.entries(subAccountsMap)
        .map(([key, val]) => ({...val, uniqueKey: key, count: val.folioSet.size}))
        .filter(acct => acct.count > 0)
        .sort((a, b) => b.marketValue - a.marketValue);
      
      // Store additional info
      patternCounts.jointHolderNames = Array.from(jointHolderNames);
      patternCounts.guardianNames = Array.from(guardianNames);
      // Render sub-account capsules when there are 2+ sub-accounts OR when any
      // single sub-account is non-individual (joint/minor/firm/nri). This makes
      // the joint holder name (e.g. Anjali for Jagdish N Kulkarni) visible even
      // when ALL of the client's folios are under the same joint sub-account.
      const hasNonIndividual = subAccounts.some(a => a.subType && a.subType !== 'individual');
      patternCounts.minorAccounts = (subAccounts.length > 1 || hasNonIndividual) ? subAccounts : [];
      setHoldingPatternCounts(patternCounts);
      
    } catch (error) {
      toast.error("Failed to load portfolio data");
      setPortfolioData([]);
    } finally {
      setLoading(false);
    }
  };

  // Filter and sort clients: by market value (highest to lowest), then alphabetically for zero-value
  const filteredClients = clients
    .filter(client => {
      if (!clientSearchTerm) return true;
      const term = clientSearchTerm.toLowerCase();
      const matchesName = client.name?.toLowerCase().includes(term);
      const matchesPan = client.pan_no?.toLowerCase().includes(term);
      // Also search in associated names (minors, joint holders)
      const matchesSearchable = client.searchable_names?.toLowerCase().includes(term);
      return matchesName || matchesPan || matchesSearchable;
    })
    .sort((a, b) => {
      // If searching, prioritize exact matches in searchable_names (minor/joint holder match)
      if (clientSearchTerm) {
        const term = clientSearchTerm.toLowerCase();
        const aSearchable = a.searchable_names?.toLowerCase() || "";
        const bSearchable = b.searchable_names?.toLowerCase() || "";
        const aName = a.name?.toLowerCase() || "";
        const bName = b.name?.toLowerCase() || "";
        
        // Check if search term matches minor/joint holder name specifically (not main name)
        const aHasMinorMatch = aSearchable.includes(term) && !aName.includes(term);
        const bHasMinorMatch = bSearchable.includes(term) && !bName.includes(term);
        
        // Prioritize clients where the search matches a minor/associated name
        if (aHasMinorMatch && !bHasMinorMatch) return -1;
        if (bHasMinorMatch && !aHasMinorMatch) return 1;
      }
      
      const aValue = parseFloat(a.market_value) || 0;
      const bValue = parseFloat(b.market_value) || 0;
      
      // Both have market value - sort by value descending
      if (aValue > 0 && bValue > 0) {
        return bValue - aValue;
      }
      
      // One has value, one doesn't - value first
      if (aValue > 0 && bValue === 0) return -1;
      if (bValue > 0 && aValue === 0) return 1;
      
      // Both have zero value - sort alphabetically by name
      const aName = (a.name || "").toLowerCase();
      const bName = (b.name || "").toLowerCase();
      return aName.localeCompare(bName);
    });

  // Get selected client object
  const selectedClientObj = clients.find(c => c.id === selectedClient);
  
  // Filter portfolio data - only show holdings with units > 0 for scheme breakdown
  const holdingsWithBalance = useMemo(() => {
    const holdings = portfolioData.filter(item => (item.units || 0) > 0);

    // Exclude "ghost" Karvy holdings — folio+product combos whose transactions are
    // all Transfer Out (in-transit transfers where Karvy holdings feed hasn't updated
    // yet). These rows inflate Scheme Wise Breakdown even though net active = 0.
    // A holding is treated as Transfer Out iff: at least one Transfer Out txn exists
    // AND no Active Units txn exists for the same folio+product_code/isin key.
    const activeKeys = new Set();
    const transferOutKeys = new Set();
    allTransactions.forEach(t => {
      const pcOrIsin = (t.product_code || t.isin || t.scheme_code || "").trim();
      if (!t.folio_no || !pcOrIsin) return;
      const k = `${t.folio_no}:${pcOrIsin}`;
      if (t.reflecting_in === "Active Units") activeKeys.add(k);
      else if (t.reflecting_in === "Transfer Out") transferOutKeys.add(k);
    });
    const isGhostTransferOut = (h) => {
      const pcOrIsin = (h.product_code || h.scheme_code || h.isin || "").trim();
      if (!pcOrIsin) return false;
      const kByPC = `${h.folio_no}:${pcOrIsin}`;
      const kByIsin = h.isin ? `${h.folio_no}:${h.isin}` : null;
      const hasTransferOut = transferOutKeys.has(kByPC) || (kByIsin && transferOutKeys.has(kByIsin));
      const hasActive = activeKeys.has(kByPC) || (kByIsin && activeKeys.has(kByIsin));
      return hasTransferOut && !hasActive;
    };
    const filteredHoldings = holdings.filter(h => !isGhostTransferOut(h));

    // 2026-07-07 (user directive): REMOVED synthetic entries.
    // Previously, when a (folio, product) had CAMS active_units but NO
    // aum_master row, we injected a synthetic holding entry with
    // `_is_multi_broker: true` so it showed in Scheme Wise Breakdown.
    // Side effect: Portfolio > Unit Mismatch treated these as matched
    // (both sides saw the same units), which HID genuine multi-broker
    // mismatches from review.
    // Now: Scheme Wise Breakdown shows ONLY what aum_master actually
    // reports. Portfolio > Unit Mismatch will correctly flag folios
    // where active_units exists but AUM is absent (a_units > 0,
    // h_units = 0) — the classic multi-broker signature.
    return filteredHoldings;
  }, [portfolioData, allTransactions]);
  
  // For Folio Details - show ALL folios regardless of units (KYC info from MFSD211)
  const allFolioHoldings = portfolioData;
  
  // Apply sub-account filter
  const holdingsFilteredByPattern = useMemo(() => {
    let filtered = holdingsWithBalance;
    
    // First apply sub-account filter
    if (selectedSubAccount !== "all") {
      if (selectedMinorKey && selectedSubAccount === "sub_account_filter") {
        filtered = filtered.filter(item => getSubAccountKey(item) === selectedMinorKey);
      } else {
        filtered = filtered.filter(item => item.holding_pattern === selectedSubAccount);
      }
    }
    
    // Then apply NRE/NRO filter for NRI clients (using bank_acc_type)
    if (selectedBankAccType !== "all" && holdingPatternCounts.isNriClient) {
      filtered = filtered.filter(item => {
        const bankType = resolveNriBankType(item);
        if (selectedBankAccType === "nre") return bankType === 'NRE';
        if (selectedBankAccType === "nro") return bankType === 'NRO';
        return true;
      });
    }
    
    return filtered;
  }, [holdingsWithBalance, selectedSubAccount, selectedMinorKey, selectedBankAccType, holdingPatternCounts.isNriClient]);
  
  // Apply sub-account filter for all folios (Folio Details tab)
  const allFoliosFilteredByPattern = useMemo(() => {
    let filtered = allFolioHoldings;
    
    // First apply sub-account filter
    if (selectedSubAccount !== "all") {
      if (selectedMinorKey && selectedSubAccount === "sub_account_filter") {
        filtered = filtered.filter(item => getSubAccountKey(item) === selectedMinorKey);
      } else {
        filtered = filtered.filter(item => item.holding_pattern === selectedSubAccount);
      }
    }
    
    // Then apply NRE/NRO filter for NRI clients (using bank_acc_type)
    if (selectedBankAccType !== "all" && holdingPatternCounts.isNriClient) {
      filtered = filtered.filter(item => {
        const bankType = resolveNriBankType(item);
        if (selectedBankAccType === "nre") return bankType === 'NRE';
        if (selectedBankAccType === "nro") return bankType === 'NRO';
        return true;
      });
    }
    
    return filtered;
  }, [allFolioHoldings, selectedSubAccount, selectedMinorKey, selectedBankAccType, holdingPatternCounts.isNriClient]);
  
  // Folio Details from investor_master - filtered by sub-account pattern
  const folioDetailsFilteredByPattern = useMemo(() => {
    let filtered = folioDetailsData;
    if (selectedSubAccount !== "all") {
      if (selectedMinorKey && selectedSubAccount === "sub_account_filter") {
        filtered = filtered.filter(item => getSubAccountKey(item) === selectedMinorKey);
      } else {
        filtered = filtered.filter(item => item.holding_pattern === selectedSubAccount);
      }
    }
    if (selectedBankAccType !== "all" && holdingPatternCounts.isNriClient) {
      filtered = filtered.filter(item => {
        const bankType = resolveNriBankType(item);
        if (selectedBankAccType === "nre") return bankType === 'NRE';
        if (selectedBankAccType === "nro") return bankType === 'NRO';
        return true;
      });
    }
    return filtered;
  }, [folioDetailsData, selectedSubAccount, selectedMinorKey, selectedBankAccType, holdingPatternCounts.isNriClient]);
  
  // AUM per folio from portfolio data (for Folio Details tab)
  const folioAumMap = useMemo(() => {
    const map = {};
    portfolioData.forEach(h => {
      if (!map[h.folio_no]) map[h.folio_no] = 0;
      map[h.folio_no] += h.market_value || 0;
    });
    return map;
  }, [portfolioData]);
  
  // Get unique folio numbers for the filtered sub-account (used to filter transactions)
  const filteredFolioNos = useMemo(() => {
    const folios = new Set();
    allFoliosFilteredByPattern.forEach(item => {
      if (item.folio_no) folios.add(item.folio_no);
    });
    return Array.from(folios);
  }, [allFoliosFilteredByPattern]);
  
  // Filter transactions by sub-account (using filteredFolioNos)
  // Sold transactions now sourced from allTransactions
  const filteredSoldTransactions = useMemo(() => {
    let txns = allTransactions.filter(t => t.reflecting_in === "Sold Units");
    if (selectedSubAccount !== "all" && filteredFolioNos.length > 0) {
      txns = txns.filter(t => filteredFolioNos.includes(t.folio_no));
    }
    return txns;
  }, [allTransactions, filteredFolioNos, selectedSubAccount]);

  const filteredJunkTransactions = useMemo(() => {
    if (selectedSubAccount === "all") return junkTransactions;
    return junkTransactions.filter(t => filteredFolioNos.includes(t.folio_no));
  }, [junkTransactions, filteredFolioNos, selectedSubAccount]);

  // Active Units now sourced from allTransactions (transaction_master) with field mapping
  const filteredActiveUnits = useMemo(() => {
    let txns = allTransactions.filter(t => t.reflecting_in === "Active Units");
    if (selectedSubAccount !== "all" && filteredFolioNos.length > 0) {
      txns = txns.filter(t => filteredFolioNos.includes(t.folio_no));
    }
    // Multi-folio takes precedence over the legacy single-folio selector.
    if (selectedFoliosForActiveUnits.length > 0) {
      const set = new Set(selectedFoliosForActiveUnits);
      txns = txns.filter(t => set.has(t.folio_no));
    } else if (selectedFolioForActiveUnits) {
      txns = txns.filter(t => t.folio_no === selectedFolioForActiveUnits);
    }
    // Free-text search — same field set as the All Transactions search box.
    if (activeUnitsSearch.trim()) {
      const q = activeUnitsSearch.toLowerCase();
      txns = txns.filter(t =>
        (t.folio_no || "").toLowerCase().includes(q) ||
        (t.scheme_name || "").toLowerCase().includes(q) ||
        (t.scheme_code || t.product_code || "").toLowerCase().includes(q) ||
        (t.isin || "").toLowerCase().includes(q) ||
        (t.txn_no || "").toLowerCase().includes(q) ||
        (t.transaction_desc || t.description || "").toLowerCase().includes(q)
      );
    }
    // Map fields to match Active Units tab expectations
    return txns.map(t => ({
      ...t,
      transaction_date: t.trade_date || t.transaction_date,
      scheme_code: t.product_code || t.amfi_code || t.scheme_code,
      transaction_desc: t.description || t.transaction_desc,
      original_units: t.original_units || Math.abs(parseFloat(t.units || 0)),
      remaining_units: t.remaining_units != null ? t.remaining_units : Math.abs(parseFloat(t.units || 0)),
      units_sold: t.sold_units || t.units_sold || 0,
    }));
  }, [allTransactions, filteredFolioNos, selectedSubAccount, selectedFolioForActiveUnits, selectedFoliosForActiveUnits, activeUnitsSearch]);

  // Compute cost values from Active Units transactions (aggregated by folio+isin)
  // This is used by Scheme Wise Breakdown instead of portfolioData.cost_value
  const activeUnitsCostMap = useMemo(() => {
    const costMap = {};
    const now = new Date();
    filteredActiveUnits.forEach(txn => {
      // Mirror the (folio, product_code-first) join key used by the
      // Unit Reconciliation logic above. ISIN-first phantom-splits IDCW
      // variant switches (e.g. Axis ELSS 128TSDP K01149 ↔ K01156) and
      // makes the Portfolio cost / XIRR lookup miss on those holdings.
      const key = `${txn.folio_no}:${txn.scheme_code || txn.isin}`;
      if (!costMap[key]) {
        costMap[key] = { cost: 0, market: 0, cashflows: [], transactions: [] };
      }
      
      // Calculate cost value same as Active Units tab
      // PREFER `remaining_units` from the backend — it's already FIFO-matched
      // against every redemption / transfer-out / switch-out so it agrees with
      // aum_master.units (and therefore Client-Wise Summary). Falling back to
      // `units − units_sold` mis-counts when `sold_units` is null/missing on
      // partially-redeemed lots, inflating Current Market Value (the source
      // of the "Valentino Portfolio vs Client-Wise mismatch" report).
      const originalUnits = Math.abs(parseFloat(txn.units || txn.original_units || 0));
      let remainingUnits;
      if (txn.remaining_units != null && txn.remaining_units !== "") {
        remainingUnits = Math.max(0, parseFloat(txn.remaining_units) || 0);
      } else {
        const unitsSold = Math.abs(parseFloat(txn.units_sold || txn.sold_units || 0));
        remainingUnits = Math.max(0, originalUnits - unitsSold);
      }
      const stampDuty = parseFloat(txn.stamp_duty || 0);
      const proportionateStampDuty = originalUnits > 0 && stampDuty > 0
        ? (remainingUnits / originalUnits) * stampDuty
        : 0;
      const costValue = (remainingUnits * (txn.nav || 0)) + proportionateStampDuty;
      const currentNavObj = txn.current_nav;
      const currentNav = typeof currentNavObj === 'object' ? (currentNavObj?.nav || 0) : (currentNavObj || txn.nav || 0);
      const currentValue = remainingUnits * currentNav;
      
      costMap[key].cost += costValue;
      costMap[key].market += currentValue;
      costMap[key].transactions.push(txn);
      
      // Collect cashflows for XIRR: outflow (purchase) at purchase date
      const dateStr = txn.transaction_date || txn.trade_date;
      if (dateStr && costValue > 0) {
        let purchaseDate = null;
        if (dateStr.includes('-') && dateStr.length <= 10) {
          const parts = dateStr.split('-');
          if (parts[0].length === 2) purchaseDate = new Date(parts[2], parts[1] - 1, parts[0]); // DD-MM-YYYY
          else purchaseDate = new Date(dateStr);
        } else {
          purchaseDate = new Date(dateStr);
        }
        if (purchaseDate && !isNaN(purchaseDate.getTime())) {
          costMap[key].cashflows.push({ date: purchaseDate, amount: -costValue });
        }
      }
    });
    
    // Calculate XIRR for each holding using cashflows + current value as final inflow
    Object.values(costMap).forEach(entry => {
      if (entry.cashflows.length > 0 && entry.market > 0) {
        // Add current value as inflow today
        const allCashflows = [...entry.cashflows, { date: now, amount: entry.market }];
        // Simple annualized return as XIRR approximation
        const totalCost = entry.cost;
        const totalMarket = entry.market;
        // Weighted average purchase date
        let weightedDays = 0;
        let totalWeight = 0;
        allCashflows.forEach(cf => {
          if (cf.amount < 0) {
            const days = (now - cf.date) / (1000 * 60 * 60 * 24);
            weightedDays += days * Math.abs(cf.amount);
            totalWeight += Math.abs(cf.amount);
          }
        });
        const avgDays = totalWeight > 0 ? weightedDays / totalWeight : 0;
        const years = avgDays / 365.25;
        if (years > 0 && totalCost > 0) {
          const xirr = (Math.pow(totalMarket / totalCost, 1 / years) - 1) * 100;
          if (!isNaN(xirr) && isFinite(xirr)) {
            entry.xirr = xirr;
          }
        }
      }
    });
    
    return costMap;
  }, [filteredActiveUnits]);

  // ─────────────────────────────────────────────────────────────────────────────
  // Provisional Sell Intelligence Engine — operates entirely on filteredActiveUnits
  // (which is already FIFO-applied via backend). Buckets by tenure, ranks by CAGR
  // (worst first), supports bucket-priority + tax-optimized sell simulation.
  // ─────────────────────────────────────────────────────────────────────────────
  const provisionalSellLots = useMemo(() => {
    const parseDD = (ds) => {
      if (!ds) return null;
      const parts = String(ds).split('-');
      if (parts.length === 3 && parts[0].length === 2) return new Date(parts[2], parts[1] - 1, parts[0]);
      const d = new Date(ds);
      return isNaN(d.getTime()) ? null : d;
    };
    const isElssScheme = (name) => {
      const n = (name || "").toLowerCase();
      return n.includes("elss") || n.includes("tax saver") || n.includes("tax-saver");
    };
    const today = new Date();
    const out = [];
    filteredActiveUnits.forEach((t, idx) => {
      const remaining = parseFloat(t.remaining_units) || 0;
      if (remaining <= 0) return;
      const pNav = parseFloat(t.nav) || 0;
      const cNavObj = t.current_nav;
      const cNav = typeof cNavObj === "object" ? parseFloat(cNavObj?.nav || 0) : (parseFloat(cNavObj) || 0);
      if (pNav <= 0 || cNav <= 0) return;
      const purchaseDate = parseDD(t.trade_date || t.transaction_date);
      if (!purchaseDate) return;
      const holdingDays = Math.max(0, Math.floor((today - purchaseDate) / 86400000));
      const holdingYears = holdingDays / 365.25;
      let bucket;
      if (holdingDays >= 1825) bucket = "LT5";
      else if (holdingDays >= 1095) bucket = "Y3_5";
      else if (holdingDays >= 730) bucket = "Y2_3";
      else if (holdingDays >= 365) bucket = "Y1_2";
      else bucket = "LT1";

      const currentValue = remaining * cNav;
      const cost = remaining * pNav;
      const gain = currentValue - cost;
      const absReturnPct = pNav > 0 ? ((cNav - pNav) / pNav) * 100 : 0;
      const cagr = holdingYears > 0 && pNav > 0
        ? (Math.pow(cNav / pNav, 1 / holdingYears) - 1) * 100
        : null;

      const isElss = isElssScheme(t.scheme_name);
      const isLocked = isElss && holdingDays < 1095;
      const elssUnlockDate = isElss ? new Date(purchaseDate.getTime() + 1095 * 86400000) : null;

      const schemeNameLc = (t.scheme_name || "").toLowerCase();
      let isEquity;
      if (typeof t.is_equity === "boolean") isEquity = t.is_equity;
      else if (schemeNameLc.includes("debt") || schemeNameLc.includes("liquid") || schemeNameLc.includes("bond") || schemeNameLc.includes("gilt") || schemeNameLc.includes("income")) isEquity = false;
      else isEquity = true;

      out.push({
        lot_id: `${t.folio_no || ""}:${t.txn_no || idx}`,
        folio_no: t.folio_no || "",
        scheme_name: t.scheme_name || "",
        scheme_code: t.product_code || t.amfi_code || t.scheme_code || "",
        isin: t.isin || "",
        source: (String(t.source || "").toUpperCase().includes("WBR")) ? "CAMS" : "KARVY",
        txn_no: t.txn_no || "",
        transaction_date: t.trade_date || t.transaction_date || "",
        purchase_date_obj: purchaseDate,
        holding_days: holdingDays,
        holding_years: Number(holdingYears.toFixed(2)),
        bucket,
        purchase_nav: pNav,
        current_nav: cNav,
        remaining_units: remaining,
        cost_value: cost,
        current_value: currentValue,
        gain,
        absolute_return_pct: absReturnPct,
        cagr,
        is_equity: isEquity,
        is_elss: isElss,
        is_locked: isLocked,
        elss_unlock_date: elssUnlockDate,
      });
    });

    const byBucket = {};
    out.forEach((lot) => {
      if (!byBucket[lot.bucket]) byBucket[lot.bucket] = [];
      byBucket[lot.bucket].push(lot);
    });
    Object.values(byBucket).forEach((arr) => {
      arr.sort((a, b) => {
        const ac = a.cagr ?? -Infinity;
        const bc = b.cagr ?? -Infinity;
        if (ac !== bc) return ac - bc;
        return a.gain - b.gain;
      });
      arr.forEach((lot, i) => { lot.bucket_rank = i + 1; });
    });
    const overall = [...out].sort((a, b) => {
      const ac = a.cagr ?? -Infinity;
      const bc = b.cagr ?? -Infinity;
      if (ac !== bc) return ac - bc;
      return a.gain - b.gain;
    });
    overall.forEach((lot, i) => { lot.overall_rank = i + 1; });
    return out;
  }, [filteredActiveUnits]);

  const provisionalSellBuckets = useMemo(() => {
    const defs = [
      { key: "LT5",  label: "5+ Years",  color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
      { key: "Y3_5", label: "3-5 Years", color: "bg-teal-50 text-teal-700 border-teal-200" },
      { key: "Y2_3", label: "2-3 Years", color: "bg-blue-50 text-blue-700 border-blue-200" },
      { key: "Y1_2", label: "1-2 Years", color: "bg-amber-50 text-amber-700 border-amber-200" },
      { key: "LT1",  label: "< 1 Year",  color: "bg-rose-50 text-rose-700 border-rose-200" },
    ];
    return defs.map((d) => {
      const lots = provisionalSellLots.filter((l) => l.bucket === d.key);
      const value = lots.reduce((s, l) => s + l.current_value, 0);
      const cost = lots.reduce((s, l) => s + l.cost_value, 0);
      const gain = value - cost;
      const cagrs = lots.map((l) => l.cagr).filter((c) => c != null && isFinite(c));
      const avgCagr = cagrs.length ? cagrs.reduce((a, b) => a + b, 0) / cagrs.length : null;
      return { ...d, lots_count: lots.length, current_value: value, cost_value: cost, gain, avg_cagr: avgCagr };
    });
  }, [provisionalSellLots]);

  // Auto-sync default bucket priority + selected buckets to those that actually
  // contain lots for the current client. Avoids showing inapplicable buckets
  // (e.g. "5+ Years" when the client has never held longer than 18 months).
  useEffect(() => {
    const available = provisionalSellBuckets
      .filter((b) => b.lots_count > 0)
      .map((b) => b.key);
    if (available.length === 0) return;
    setPsBucketPriority((cur) => {
      const tokens = (cur || "").split(",").map((t) => t.trim()).filter(Boolean);
      const filtered = tokens.filter((t) => available.includes(t));
      const next = filtered.length ? filtered : available;
      const nextStr = next.join(",");
      return nextStr === cur ? cur : nextStr;
    });
    setPsBucketsSelected((cur) => {
      const filtered = (cur || []).filter((k) => available.includes(k));
      if (filtered.length === 0) return [available[0]];
      if (filtered.length === cur.length) return cur;
      return filtered;
    });
  }, [provisionalSellBuckets]);

  // The DRAIN QUEUE used by the simulator. Because FIFO is non-negotiable inside
  // a folio+scheme, the *effective* CAGR for ranking a scheme is the CAGR of its
  // oldest unsold lot (FIFO-front) — not the lowest-CAGR lot in the scheme.
  // This avoids the trap of "lowest CAGR newer lot drags whole scheme to the top
  // and FIFO drains better-performing older lots first."
  const psDrainQueue = useMemo(() => {
    const bucketOrder = { LT5: 0, Y3_5: 1, Y2_3: 2, Y1_2: 3, LT1: 4 };
    let pool = provisionalSellLots.filter((l) => !psExcludeLocked || !l.is_locked);

    if (psSellMode === "bucket_specific") {
      pool = pool.filter((l) => psBucketsSelected.includes(l.bucket));
    }

    // Group by folio+scheme; sort each group by purchase date ASC (FIFO front first).
    const groupKey = (l) => `${l.folio_no}::${l.scheme_code || l.isin || l.scheme_name}`;
    const groupMap = {};
    pool.forEach((l) => {
      const k = groupKey(l);
      if (!groupMap[k]) groupMap[k] = [];
      groupMap[k].push(l);
    });
    Object.values(groupMap).forEach((g) =>
      g.sort((a, b) => (a.purchase_date_obj?.getTime() || 0) - (b.purchase_date_obj?.getTime() || 0))
    );

    // Each group inherits the FIFO-front lot's bucket/CAGR/gain for ranking.
    const groupEntries = Object.values(groupMap).map((lots) => ({ front: lots[0], lots }));

    if (psSellMode === "bucket_specific") {
      // Pure CAGR-ASC across the selected buckets, using FIFO-front CAGR.
      groupEntries.sort((a, b) => (a.front.cagr ?? -Infinity) - (b.front.cagr ?? -Infinity));
    } else if (psSellMode === "bucket_priority") {
      const prio = psBucketPriority.split(",").map((s) => s.trim()).filter(Boolean);
      const idx = Object.fromEntries(prio.map((p, i) => [p, i]));
      groupEntries.sort((a, b) => {
        const ai = idx[a.front.bucket] ?? 999;
        const bi = idx[b.front.bucket] ?? 999;
        if (ai !== bi) return ai - bi;
        return (a.front.cagr ?? -Infinity) - (b.front.cagr ?? -Infinity);
      });
    } else {
      // tax_optimized
      groupEntries.sort((a, b) => {
        const bd = (bucketOrder[a.front.bucket] ?? 999) - (bucketOrder[b.front.bucket] ?? 999);
        if (bd !== 0) return bd;
        const aLoss = a.front.gain < 0 ? 0 : 1;
        const bLoss = b.front.gain < 0 ? 0 : 1;
        if (aLoss !== bLoss) return aLoss - bLoss;
        const aLong = a.front.is_equity ? a.front.holding_days >= 365 : a.front.holding_days >= 1095;
        const bLong = b.front.is_equity ? b.front.holding_days >= 365 : b.front.holding_days >= 1095;
        if (aLong !== bLong) return aLong ? -1 : 1;
        return (a.front.cagr ?? -Infinity) - (b.front.cagr ?? -Infinity);
      });
    }

    // Flatten: per group in rank order, walk lots in FIFO order.
    return groupEntries.flatMap((g) => g.lots);
  }, [provisionalSellLots, psSellMode, psBucketsSelected, psBucketPriority, psExcludeLocked]);

  // Sold entries from allTransactions (transaction_master) with FIFO details
  const soldFromAllTxn = useMemo(() => {
    let txns = allTransactions.filter(t => t.reflecting_in === "Sold Units");
    if (selectedSubAccount !== "all" && filteredFolioNos.length > 0) {
      txns = txns.filter(t => filteredFolioNos.includes(t.folio_no));
    }
    return txns;
  }, [allTransactions, filteredFolioNos, selectedSubAccount]);

  // Sold Units Summary — same grouping logic as the UI tab inside Scheme Wise
  // Breakdown > Sold Units Summary (groups by folio+scheme, computes %share + XIRR).
  // Exposed here so the Export button can dump it to xlsx.
  const soldUnitsSummary = useMemo(() => {
    const parseDDMMYYYY = (ds) => {
      if (!ds) return null;
      const parts = String(ds).split('-');
      if (parts.length === 3 && parts[0].length === 2) return new Date(parts[2], parts[1] - 1, parts[0]);
      return new Date(ds);
    };
    const groupedSoldMap = {};
    soldFromAllTxn.forEach(txn => {
      const key = `${txn.folio_no}:${txn.isin || txn.scheme_code}`;
      if (!groupedSoldMap[key]) {
        groupedSoldMap[key] = {
          folio_no: txn.folio_no,
          isin: txn.isin,
          scheme_code: txn.scheme_code,
          scheme_name: txn.scheme_name,
          source: txn.source,
          units: 0,
          total_cost_basis: 0,
          sale_amount: 0,
          gain_loss: 0,
          transaction_count: 0,
          cashflows: [],
          first_transaction_date: txn.trade_date || txn.transaction_date,
          last_transaction_date: txn.trade_date || txn.transaction_date,
        };
      }
      const saleAmt = Math.abs(parseFloat(txn.amount) || 0);
      const costBasis = parseFloat(txn.fifo_cost_value) || 0;
      groupedSoldMap[key].units += Math.abs(parseFloat(txn.units) || 0);
      groupedSoldMap[key].total_cost_basis += costBasis;
      groupedSoldMap[key].sale_amount += saleAmt;
      groupedSoldMap[key].gain_loss += (saleAmt - costBasis);
      groupedSoldMap[key].transaction_count += 1;
      if (Array.isArray(txn.fifo_details)) {
        txn.fifo_details.forEach(fd => {
          const buyDate = parseDDMMYYYY(fd.buy_date);
          const buyAmt = parseFloat(fd.buy_amount) || 0;
          if (buyDate && !isNaN(buyDate.getTime()) && buyAmt > 0) {
            groupedSoldMap[key].cashflows.push({ date: buyDate, amount: -buyAmt });
          }
        });
      }
      const sellDate = parseDDMMYYYY(txn.trade_date || txn.transaction_date);
      if (sellDate && !isNaN(sellDate.getTime()) && saleAmt > 0) {
        groupedSoldMap[key].cashflows.push({ date: sellDate, amount: saleAmt });
      }
    });

    const groupedSold = Object.values(groupedSoldMap);
    const totalSaleValueForCalc = groupedSold.reduce((s, t) => s + Math.abs(t.sale_amount || 0), 0);
    groupedSold.forEach(item => {
      item.sale_pct = totalSaleValueForCalc > 0 ? (Math.abs(item.sale_amount || 0) / totalSaleValueForCalc) * 100 : 0;
      let xirr = null;
      if (item.cashflows && item.cashflows.length >= 2) {
        const outflows = item.cashflows.filter(c => c.amount < 0);
        const inflows = item.cashflows.filter(c => c.amount > 0);
        if (outflows.length > 0 && inflows.length > 0) {
          const totalCost = outflows.reduce((s, c) => s + Math.abs(c.amount), 0);
          const totalSale = inflows.reduce((s, c) => s + c.amount, 0);
          let weightedDays = 0, totalWeight = 0;
          const lastSellDate = inflows.reduce((latest, c) => c.date > latest ? c.date : latest, inflows[0].date);
          outflows.forEach(c => {
            const days = (lastSellDate - c.date) / (1000 * 60 * 60 * 24);
            weightedDays += days * Math.abs(c.amount);
            totalWeight += Math.abs(c.amount);
          });
          const avgDays = totalWeight > 0 ? weightedDays / totalWeight : 0;
          const years = avgDays / 365.25;
          if (years > 0 && totalCost > 0) {
            xirr = (Math.pow(totalSale / totalCost, 1 / years) - 1) * 100;
            if (isNaN(xirr) || !isFinite(xirr)) xirr = null;
          }
        }
      }
      item.xirr = xirr;
    });
    return groupedSold;
  }, [soldFromAllTxn]);

  // Already-realized equity LTCG gain in the CURRENT financial year (Apr-Mar).
  // Used to shrink the ₹1.25 L LTCG-equity exemption inside the Sell Simulator,
  // so the system doesn't double-count the annual exemption against prior sells.
  const psFyRealized = useMemo(() => {
    const parseDD = (ds) => {
      if (!ds) return null;
      const parts = String(ds).split('-');
      if (parts.length === 3 && parts[0].length === 2) return new Date(parts[2], parts[1] - 1, parts[0]);
      const d = new Date(ds);
      return isNaN(d.getTime()) ? null : d;
    };
    const now = new Date();
    const fyStartYear = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1; // Apr = month 3
    const fyStart = new Date(fyStartYear, 3, 1);          // Apr 1
    const fyEnd = new Date(fyStartYear + 1, 2, 31, 23, 59, 59); // Mar 31
    const fyLabel = `FY ${String(fyStartYear).slice(-2)}-${String(fyStartYear + 1).slice(-2)}`;

    let ltcgEq = 0, stcgEq = 0, ltcgDebt = 0, stcgDebt = 0, salesCount = 0;
    soldFromAllTxn.forEach((t) => {
      const sellDate = parseDD(t.trade_date || t.transaction_date);
      if (!sellDate || sellDate < fyStart || sellDate > fyEnd) return;
      salesCount += 1;
      const saleAmt = Math.abs(parseFloat(t.amount) || 0);
      const isEquity = typeof t.is_equity === "boolean" ? t.is_equity : true;
      const rawLegs = Array.isArray(t.fifo_details) && t.fifo_details.length > 0
        ? t.fifo_details
        : null;
      if (rawLegs) {
        // Backend's fifo_details has buy_amount / units_matched but NOT a per-leg
        // sale value. Pro-rate the transaction's total sale amount across legs by
        // their buy_amount weight (equivalent to using a single sale NAV per txn).
        const totalCost = rawLegs.reduce((s, fd) => s + (parseFloat(fd.buy_amount) || 0), 0);
        const totalUnits = rawLegs.reduce((s, fd) => s + (parseFloat(fd.units_matched) || 0), 0);
        rawLegs.forEach((fd) => {
          const buyAmount = parseFloat(fd.buy_amount) || 0;
          const units = parseFloat(fd.units_matched) || 0;
          // Prefer unit-weighted; fall back to cost-weighted; fall back to even split.
          let salePortion;
          if (totalUnits > 0) salePortion = saleAmt * (units / totalUnits);
          else if (totalCost > 0) salePortion = saleAmt * (buyAmount / totalCost);
          else salePortion = saleAmt / rawLegs.length;
          const gain = salePortion - buyAmount;
          const buyDate = parseDD(fd.buy_date);
          const longTerm = buyDate
            ? ((sellDate - buyDate) / 86400000) >= (isEquity ? 365 : 1095)
            : true;
          if (isEquity && longTerm) ltcgEq += gain;
          else if (isEquity && !longTerm) stcgEq += gain;
          else if (!isEquity && longTerm) ltcgDebt += gain;
          else stcgDebt += gain;
        });
      } else {
        // Fallback when no fifo_details — use the transaction-level fifo_cost_value.
        const cost = parseFloat(t.fifo_cost_value) || 0;
        const gain = saleAmt - cost;
        // No buy date to determine LT/ST — assume long-term as the safer default.
        if (isEquity) ltcgEq += gain;
        else ltcgDebt += gain;
      }
    });

    return {
      fyLabel,
      sales_count: salesCount,
      ltcg_equity_gain: ltcgEq,
      stcg_equity_gain: stcgEq,
      ltcg_debt_gain: ltcgDebt,
      stcg_debt_gain: stcgDebt,
      remaining_eq_exempt: Math.max(0, 125000 - Math.max(0, ltcgEq)),
    };
  }, [soldFromAllTxn]);

  // Reusable sell-simulator engine. Implements the dynamic cross-fund FIFO
  // algorithm and returns a full result object — used by both "Run Simulation"
  // (single-mode) and "Compare All Modes" (3 runs side-by-side).
  const runSellSimulation = useCallback((mode, target, opts) => {
    const { bucketPriority, bucketsSelected, excludeLocked, fyExemptLeft, debtSlabRate,
            isNri = false, nriSurchargePct = 0 } = opts;
    // Default debt-STCG slab rate = 30 % (top marginal slab). Callers may pass
    // any number; we treat it as a percent (e.g. 30 -> 0.30).
    const slabRate = (typeof debtSlabRate === "number" && debtSlabRate > 0)
      ? debtSlabRate / 100
      : 0.30;
    let pool = provisionalSellLots.filter((l) => !excludeLocked || !l.is_locked);
    const priorityList = (bucketPriority || "").split(",").map((s) => s.trim()).filter(Boolean);
    const priorityIdx = Object.fromEntries(priorityList.map((p, i) => [p, i]));
    const bucketOrderTax = { LT5: 0, Y3_5: 1, Y2_3: 2, Y1_2: 3, LT1: 4 };

    if (mode === "bucket_specific") {
      pool = pool.filter((l) => (bucketsSelected || []).includes(l.bucket));
    } else if (mode === "bucket_priority") {
      pool = pool.filter((l) => priorityIdx[l.bucket] != null);
    }

    const groupKey = (l) => `${l.folio_no}::${l.scheme_code || l.isin || l.scheme_name}`;
    const fifoGroups = {};
    pool.forEach((l) => {
      const k = groupKey(l);
      if (!fifoGroups[k]) fifoGroups[k] = [];
      fifoGroups[k].push(l);
    });
    Object.values(fifoGroups).forEach((g) =>
      g.sort((a, b) => (a.purchase_date_obj?.getTime() || 0) - (b.purchase_date_obj?.getTime() || 0))
    );

    const headIdx = {};
    const lotRemainingUnits = {};
    Object.entries(fifoGroups).forEach(([k, lots]) => {
      headIdx[k] = 0;
      lots.forEach((l) => { lotRemainingUnits[l.lot_id] = l.remaining_units; });
    });

    const compareHeads = (a, b) => {
      if (mode === "bucket_priority") {
        const ai = priorityIdx[a.bucket] ?? 999;
        const bi = priorityIdx[b.bucket] ?? 999;
        if (ai !== bi) return ai - bi;
        const ac = a.cagr ?? -Infinity;
        const bc = b.cagr ?? -Infinity;
        if (ac !== bc) return ac - bc;
        return a.gain - b.gain;
      }
      if (mode === "tax_optimized") {
        const bd = (bucketOrderTax[a.bucket] ?? 999) - (bucketOrderTax[b.bucket] ?? 999);
        if (bd !== 0) return bd;
        const aLoss = a.gain < 0 ? 0 : 1;
        const bLoss = b.gain < 0 ? 0 : 1;
        if (aLoss !== bLoss) return aLoss - bLoss;
        const aLong = a.is_equity ? a.holding_days >= 365 : a.holding_days >= 1095;
        const bLong = b.is_equity ? b.holding_days >= 365 : b.holding_days >= 1095;
        if (aLong !== bLong) return aLong ? -1 : 1;
        return (a.cagr ?? -Infinity) - (b.cagr ?? -Infinity);
      }
      const ac = a.cagr ?? -Infinity;
      const bc = b.cagr ?? -Infinity;
      if (ac !== bc) return ac - bc;
      return a.gain - b.gain;
    };

    const activeHead = (k) => {
      const arr = fifoGroups[k];
      while (headIdx[k] < arr.length) {
        const lot = arr[headIdx[k]];
        if ((lotRemainingUnits[lot.lot_id] || 0) > 1e-6) return lot;
        headIdx[k] += 1;
      }
      return null;
    };

    const picks = [];
    let rem = target;
    let totalGain = 0, totalCostSold = 0, totalSold = 0;
    let stcgGainEquity = 0, stcgGainDebt = 0, ltcgGainEquity = 0, ltcgGainDebt = 0;
    let switchCount = 0;
    let lastFundKey = null;

    let safety = 100000;
    while (rem > 0.01 && safety-- > 0) {
      const candidates = [];
      for (const k of Object.keys(fifoGroups)) {
        const head = activeHead(k);
        if (head) candidates.push({ k, lot: head });
      }
      if (candidates.length === 0) break;

      candidates.sort((a, b) => compareHeads(a.lot, b.lot));
      const winner = candidates[0];
      const k = winner.k;
      const lot = winner.lot;

      if (lastFundKey != null && lastFundKey !== k) switchCount += 1;
      lastFundKey = k;

      const unitsAvail = lotRemainingUnits[lot.lot_id];
      const valueAvail = unitsAvail * lot.current_nav;
      let unitsToSell, value;
      if (valueAvail <= rem) {
        unitsToSell = unitsAvail;
        value = valueAvail;
      } else {
        unitsToSell = rem / lot.current_nav;
        value = unitsToSell * lot.current_nav;
      }
      const costPortion = unitsToSell * lot.purchase_nav;
      const gainPortion = value - costPortion;
      const longTerm = lot.is_equity ? lot.holding_days >= 365 : lot.holding_days >= 1095;
      if (gainPortion > 0) {
        if (!longTerm) {
          if (lot.is_equity) stcgGainEquity += gainPortion;
          else stcgGainDebt += gainPortion;
        }
        else if (lot.is_equity) ltcgGainEquity += gainPortion;
        else ltcgGainDebt += gainPortion;
      }

      const existing = picks.find((p) => p.lot_id === lot.lot_id);
      if (existing) {
        existing.units_to_sell += unitsToSell;
        existing.sell_value += value;
        existing.cost_portion += costPortion;
        existing.gain_portion += gainPortion;
      } else {
        picks.push({
          ...lot,
          units_to_sell: unitsToSell, sell_value: value,
          cost_portion: costPortion, gain_portion: gainPortion,
          long_term: longTerm, fifo_forced: false,
        });
      }
      lotRemainingUnits[lot.lot_id] = Math.max(0, unitsAvail - unitsToSell);
      if (lotRemainingUnits[lot.lot_id] <= 1e-6) headIdx[k] += 1;
      rem -= value;
      totalSold += value;
      totalCostSold += costPortion;
      totalGain += gainPortion;
    }

    // Tax rates (Indian Budget 2024 + subsequent updates):
    //   Equity STCG (≤12m)  : 20 %
    //   Equity LTCG (>12m)  : 12.5 % after ₹1.25 L exemption (per FY) — RESIDENT
    //   Debt   STCG (≤36m)  : at investor's marginal slab rate (configurable)
    //   Debt   LTCG (>36m)  : 12.5 % (post-Budget-2024 uniform rate)
    // NRI mode (TDS by AMC u/s 195):
    //   • Same headline rates BUT:
    //     - ₹1.25 L LTCG exemption does NOT apply at TDS stage
    //       (NRI claims refund via ITR if final liability is lower)
    //     - 4 % Health-&-Education Cess applied on (base + surcharge)
    //     - Surcharge by income bracket (0/10/15/25/37 %) — user-selectable
    //   • Debt STCG TDS = 30 % (top slab) — overrides the configured slabRate.
    const _nriMult = isNri
      ? (1 + (Number(nriSurchargePct) || 0) / 100) * 1.04
      : 1;
    const _debtStcgRate = isNri ? 0.30 : slabRate;
    const _ltcgExemptUsed = isNri ? 0 : Math.max(0, fyExemptLeft || 0);

    const stcgEquityTax = Math.max(0, stcgGainEquity) * 0.20 * _nriMult;
    const stcgDebtTax = Math.max(0, stcgGainDebt) * _debtStcgRate * _nriMult;
    const stcgTax = stcgEquityTax + stcgDebtTax;
    const ltcgExempt = _ltcgExemptUsed;
    const ltcgEquityTaxable = Math.max(0, ltcgGainEquity - ltcgExempt);
    const ltcgEquityTax = ltcgEquityTaxable * 0.125 * _nriMult;
    const ltcgDebtTax = Math.max(0, ltcgGainDebt) * 0.125 * _nriMult;
    const totalTax = stcgTax + ltcgEquityTax + ltcgDebtTax;
    const shortfall = Math.max(0, rem);
    const schemes = new Set(picks.map((p) => `${p.folio_no}::${p.scheme_code || p.isin || p.scheme_name}`));

    const stcgGain = stcgGainEquity + stcgGainDebt;
    return {
      mode, target, totalSold, totalCostSold, totalGain,
      stcgGain, stcgGainEquity, stcgGainDebt,
      ltcgGainEquity, ltcgGainDebt,
      stcgTax, stcgEquityTax, stcgDebtTax,
      ltcgEquityTax, ltcgDebtTax, totalTax,
      debtSlabRatePct: Math.round((_debtStcgRate) * 1000) / 10,
      shortfall, netProceeds: totalSold - totalTax,
      picks, switchCount, schemes_count: schemes.size,
      // NRI-specific diagnostics for the UI
      is_nri: !!isNri,
      nri_surcharge_pct: isNri ? (Number(nriSurchargePct) || 0) : 0,
      effective_rate_multiplier: _nriMult,
      ltcg_exemption_applied: _ltcgExemptUsed,
    };
  }, [provisionalSellLots]);


  // ─────────────────────────────────────────────────────────────────────────
  // Exit Free Investments — lots redeemable without exit load OR ELSS lock-in
  // (≥ 365 days holding AND not in active ELSS lock-in window).
  // Uses the same bucket sequence as Provisional Sell:
  //   LT5 (5+y), Y3_5 (3-5y), Y2_3 (2-3y), Y1_2 (1-2y).
  // ─────────────────────────────────────────────────────────────────────────
  const ELSS_REGEX = /ELSS|Tax\s*Saver|Tax-Saver|U\/S\s*80C|Section\s*80C/i;

  const exitFreeLots = useMemo(() => {
    const eligible = provisionalSellLots
      .filter((l) => l.bucket !== "LT1" && !l.is_locked)
      .map((l) => ({
        ...l,
        is_elss: ELSS_REGEX.test(l.scheme_name || ""),
        asset_class: ELSS_REGEX.test(l.scheme_name || "")
          ? "elss"
          : (l.is_equity ? "equity" : "debt"),
      }));
    const bucketRank = { LT5: 0, Y3_5: 1, Y2_3: 2, Y1_2: 3 };
    return [...eligible].sort((a, b) => {
      const br = (bucketRank[a.bucket] ?? 99) - (bucketRank[b.bucket] ?? 99);
      if (br !== 0) return br;
      return b.gain - a.gain;
    });
  }, [provisionalSellLots]);

  const exitFreeBuckets = useMemo(() => {
    const defs = [
      { key: "LT5",  label: "5+ Years",  color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
      { key: "Y3_5", label: "3-5 Years", color: "bg-teal-50 text-teal-700 border-teal-200" },
      { key: "Y2_3", label: "2-3 Years", color: "bg-blue-50 text-blue-700 border-blue-200" },
      { key: "Y1_2", label: "1-2 Years", color: "bg-amber-50 text-amber-700 border-amber-200" },
    ];
    return defs.map((d) => {
      const lots = exitFreeLots.filter((l) => l.bucket === d.key);
      const cur = lots.reduce((s, l) => s + l.current_value, 0);
      const cost = lots.reduce((s, l) => s + l.cost_value, 0);
      return {
        ...d,
        lots_count: lots.length,
        current_value: cur,
        cost_value: cost,
        gain: cur - cost,
      };
    });
  }, [exitFreeLots]);

  // Asset-class filter + search + user-chosen sort applied AFTER the
  // bucket-priority default sort. Drives the table render & summary cards.
  const exitFreeFilteredSorted = useMemo(() => {
    let arr = exitFreeLots;
    if (efAssetClass !== "all") {
      arr = arr.filter((l) => l.asset_class === efAssetClass);
    }
    if (efSearch.trim()) {
      const q = efSearch.trim().toLowerCase();
      arr = arr.filter((l) =>
        (l.scheme_name || "").toLowerCase().includes(q) ||
        (l.folio_no || "").toLowerCase().includes(q),
      );
    }
    const dir = efSortDir === "asc" ? 1 : -1;
    const bucketRank = { LT5: 0, Y3_5: 1, Y2_3: 2, Y1_2: 3 };
    const sorted = [...arr].sort((a, b) => {
      let av, bv;
      switch (efSortKey) {
        case "gain":           av = a.gain;           bv = b.gain;           break;
        case "current_value":  av = a.current_value;  bv = b.current_value;  break;
        case "cagr":           av = a.cagr ?? -1e9;   bv = b.cagr ?? -1e9;   break;
        case "holding_years":  av = a.holding_years;  bv = b.holding_years;  break;
        case "scheme":         return dir * (a.scheme_name || "").localeCompare(b.scheme_name || "");
        case "bucket":
        default: {
          const br = (bucketRank[a.bucket] ?? 99) - (bucketRank[b.bucket] ?? 99);
          if (br !== 0) return dir * br;
          return dir * (a.gain - b.gain);  // tiebreak
        }
      }
      return dir * (av - bv);
    });
    return sorted;
  }, [exitFreeLots, efAssetClass, efSearch, efSortKey, efSortDir]);

  // Live summary across whatever the user has filtered/searched.
  const exitFreeSummary = useMemo(() => {
    const lots = exitFreeFilteredSorted;
    const cur  = lots.reduce((s, l) => s + l.current_value, 0);
    const cost = lots.reduce((s, l) => s + l.cost_value, 0);
    const gain = cur - cost;
    const ecount = lots.filter((l) => l.asset_class === "equity").length;
    const dcount = lots.filter((l) => l.asset_class === "debt").length;
    const lcount = lots.filter((l) => l.asset_class === "elss").length;
    const schemes = new Set(lots.map((l) => l.scheme_name)).size;
    return {
      lots: lots.length,
      schemes,
      current_value: cur,
      cost_value: cost,
      gain,
      gain_pct: cost > 0 ? (gain / cost) * 100 : 0,
      equity_count: ecount,
      debt_count: dcount,
      elss_count: lcount,
    };
  }, [exitFreeFilteredSorted]);

  // Clubbed groups: one summary row per (folio_no + scheme_name + bucket).
  // Aggregates units/cost/value/gain across all lots in that group and
  // computes a cost-value-weighted CAGR. Preserves the parent table's
  // current sort order (groups are emitted in the order their first lot
  // appears in `exitFreeFilteredSorted`).
  const exitFreeGroups = useMemo(() => {
    const idx = new Map();
    const groups = [];
    exitFreeFilteredSorted.forEach((l) => {
      const k = `${l.folio_no || ""}|${l.scheme_name || ""}|${l.bucket || ""}`;
      let g = idx.get(k);
      if (!g) {
        g = {
          key: k,
          folio_no: l.folio_no,
          scheme_name: l.scheme_name,
          bucket: l.bucket,
          asset_class: l.asset_class,
          remaining_units: 0,
          cost_value: 0,
          current_value: 0,
          gain: 0,
          cagr_num: 0, // Σ(cagr × cost)
          cagr_den: 0, // Σ(cost)
          earliest_date: l.transaction_date,
          latest_date: l.transaction_date,
          max_yrs: l.holding_years || 0,
          min_yrs: l.holding_years || 0,
          lots: [],
        };
        idx.set(k, g);
        groups.push(g);
      }
      g.remaining_units += l.remaining_units || 0;
      g.cost_value += l.cost_value || 0;
      g.current_value += l.current_value || 0;
      g.gain += l.gain || 0;
      if (l.cagr != null && l.cost_value > 0) {
        g.cagr_num += l.cagr * l.cost_value;
        g.cagr_den += l.cost_value;
      }
      if (l.transaction_date && l.transaction_date < g.earliest_date) g.earliest_date = l.transaction_date;
      if (l.transaction_date && l.transaction_date > g.latest_date) g.latest_date = l.transaction_date;
      g.max_yrs = Math.max(g.max_yrs, l.holding_years || 0);
      g.min_yrs = Math.min(g.min_yrs, l.holding_years || 0);
      g.lots.push(l);
    });
    return groups.map((g) => ({
      ...g,
      cagr: g.cagr_den > 0 ? g.cagr_num / g.cagr_den : null,
    }));
  }, [exitFreeFilteredSorted]);

  // Per asset-class counts on the FULL (unfiltered) data → drives radio labels.
  const exitFreeClassCounts = useMemo(() => {
    return exitFreeLots.reduce((acc, l) => {
      acc[l.asset_class] = (acc[l.asset_class] || 0) + 1;
      acc.all++;
      return acc;
    }, { all: 0, equity: 0, debt: 0, elss: 0 });
  }, [exitFreeLots]);

  // ─────────────────────────────────────────────────────────────────────────
  // LTCG Harvest Engine — picks equity lots (>= 12 months) in the SAME drain
  // order the Run-Simulation engine (runSellSimulation) uses, so the picker's
  // promised ₹1.25 L gain exactly matches what the simulation will realise:
  //   1. Bucket priority order (user-controlled via psBucketPriority).
  //   2. Within a bucket, sort scheme-heads by worst CAGR first (tie-break: gain ASC).
  //   3. Within a (folio, scheme) FIFO group, drain the OLDEST lot first.
  //   4. LT1 (< 12 mo) is dropped — STCG lots can't fill the LTCG headroom.
  // When the next full lot would breach the remaining headroom, take a partial
  // unit count so the realised gain lands exactly on the cap.
  // ─────────────────────────────────────────────────────────────────────────
  const ltcgHarvest = useMemo(() => {
    const headroom = Math.max(0, psFyRealized?.remaining_eq_exempt || 0);
    if (headroom <= 0 || exitFreeLots.length === 0) {
      return { headroom, eligible_lots: 0, total_lots: 0, picks: [], harvested_gain: 0, sale_value: 0, cost_value: 0 };
    }
    // Use same priority list as the simulation; strip LT1 (STCG → can't help LTCG headroom).
    const priorityList = (psBucketPriority || "LT5,Y3_5,Y2_3,Y1_2")
      .split(",").map((s) => s.trim()).filter((b) => b && b !== "LT1");
    const priorityIdx = Object.fromEntries(priorityList.map((p, i) => [p, i]));
    const pool = exitFreeLots.filter((l) =>
      l.is_equity && l.gain > 0 && priorityIdx[l.bucket] != null,
    );
    if (pool.length === 0) {
      return { headroom, eligible_lots: 0, total_lots: exitFreeLots.length,
        picks: [], harvested_gain: 0, sale_value: 0, cost_value: 0,
        remaining_headroom: headroom };
    }
    // Build FIFO groups by (folio, scheme), oldest-first.
    const groupKey = (l) => `${l.folio_no}::${l.scheme_code || l.isin || l.scheme_name}`;
    const fifoGroups = {};
    pool.forEach((l) => {
      const k = groupKey(l);
      if (!fifoGroups[k]) fifoGroups[k] = [];
      fifoGroups[k].push(l);
    });
    Object.values(fifoGroups).forEach((g) =>
      g.sort((a, b) => (a.purchase_date_obj?.getTime() || 0) - (b.purchase_date_obj?.getTime() || 0))
    );
    const headIdx = {};
    Object.keys(fifoGroups).forEach((k) => { headIdx[k] = 0; });
    const activeHead = (k) => {
      const arr = fifoGroups[k];
      return headIdx[k] < arr.length ? arr[headIdx[k]] : null;
    };
    const compareHeads = (a, b) => {
      const ai = priorityIdx[a.bucket] ?? 999;
      const bi = priorityIdx[b.bucket] ?? 999;
      if (ai !== bi) return ai - bi;
      const ac = a.cagr ?? -Infinity;
      const bc = b.cagr ?? -Infinity;
      if (ac !== bc) return ac - bc;
      return a.gain - b.gain;
    };

    let remaining = headroom;
    const picks = [];
    let harvested = 0;
    let saleValue = 0;
    let costValue = 0;
    let safety = 100000;
    while (remaining > 0.01 && safety-- > 0) {
      const candidates = [];
      for (const k of Object.keys(fifoGroups)) {
        const head = activeHead(k);
        if (head) candidates.push({ k, lot: head });
      }
      if (candidates.length === 0) break;
      candidates.sort((a, b) => compareHeads(a.lot, b.lot));
      const winner = candidates[0];
      const lot = winner.lot;
      const fullGain = lot.gain;
      if (fullGain <= remaining + 0.01) {
        picks.push({
          ...lot,
          units_to_sell: lot.remaining_units,
          sale_value: lot.current_value,
          cost_used: lot.cost_value,
          gain_realized: fullGain,
          partial: false,
        });
        harvested += fullGain;
        saleValue += lot.current_value;
        costValue += lot.cost_value;
        remaining -= fullGain;
        headIdx[winner.k] += 1;
      } else {
        const gainPerUnit = lot.current_nav - lot.purchase_nav;
        if (gainPerUnit <= 0) { headIdx[winner.k] += 1; continue; }
        const units = Math.min(lot.remaining_units, remaining / gainPerUnit);
        if (units < 0.001) { headIdx[winner.k] += 1; continue; }
        const sale = units * lot.current_nav;
        const cost = units * lot.purchase_nav;
        picks.push({
          ...lot,
          units_to_sell: units,
          sale_value: sale,
          cost_used: cost,
          gain_realized: sale - cost,
          partial: true,
        });
        harvested += sale - cost;
        saleValue += sale;
        costValue += cost;
        remaining = 0;
      }
    }
    return {
      headroom,
      eligible_lots: pool.length,
      total_lots: exitFreeLots.length,
      picks,
      harvested_gain: harvested,
      sale_value: saleValue,
      cost_value: costValue,
      remaining_headroom: Math.max(0, headroom - harvested),
    };
  }, [exitFreeLots, psFyRealized, psBucketPriority]);


  // Dividend payout entries from allTransactions (transaction_master)
  const dividendFromAllTxn = useMemo(() => {
    let txns = allTransactions.filter(t => t.reflecting_in === "Dividend Payout");
    if (selectedSubAccount !== "all" && filteredFolioNos.length > 0) {
      txns = txns.filter(t => filteredFolioNos.includes(t.folio_no));
    }
    return txns;
  }, [allTransactions, filteredFolioNos, selectedSubAccount]);

  // Segregated entries from allTransactions (transaction_master)
  const segregatedFromAllTxn = useMemo(() => {
    let txns = allTransactions.filter(t => t.reflecting_in === "Segregated");
    if (selectedSubAccount !== "all" && filteredFolioNos.length > 0) {
      txns = txns.filter(t => filteredFolioNos.includes(t.folio_no));
    }
    return txns;
  }, [allTransactions, filteredFolioNos, selectedSubAccount]);

  const filteredTransactions = useMemo(() => {
    if (selectedSubAccount === "all") return transactions;
    return transactions.filter(t => filteredFolioNos.includes(t.folio_no));
  }, [transactions, filteredFolioNos, selectedSubAccount]);

  // Tab counts for badges
  const tabCounts = useMemo(() => {
    // All Transactions filtered by sub-account
    let allTxn = allTransactions;
    if (selectedSubAccount !== "all" && filteredFolioNos.length > 0) {
      allTxn = allTxn.filter(t => filteredFolioNos.includes(t.folio_no));
    }
    const failedCount = allTxn.filter(t => t.reflecting_in === "Failed Transactions").length;
    const nfCount = allTxn.filter(t => t.reflecting_in === "Non Financial").length;
    const divCount = allTxn.filter(t => t.reflecting_in === "Dividend Payout").length;
    const segCount = allTxn.filter(t => t.reflecting_in === "Segregated").length;
    const soldCount = allTxn.filter(t => t.reflecting_in === "Sold Units").length;
    const activeCount = allTxn.filter(t => t.reflecting_in === "Active Units").length;
    const fifoConsumedCount = allTxn.filter(t => t.reflecting_in === "FIFO Consumed").length;
    const transferOutCount = allTxn.filter(t => t.reflecting_in === "Transfer Out").length;
    const allTxnCount = allTxn.length - failedCount - nfCount - divCount - segCount - soldCount - activeCount - fifoConsumedCount - transferOutCount;
    // Sold Units count = sale entries + fully consumed purchases (excludes partial — those are in Active Units)
    return {
      all_transactions: allTransactions.length > 0 ? allTransactions.length : null,
      failed_transactions: allTransactions.length > 0 ? failedCount : null,
      non_financial: allTransactions.length > 0 ? nfCount : null,
      active_units: filteredActiveUnits.length || null,
      sold_units: allTransactions.length > 0 ? (soldCount + fifoConsumedCount) : null,
      idcw_payouts: dividendFromAllTxn.length || null,
      junk_units: allTransactions.length > 0 ? nfCount : null,
      segregated: segregatedFromAllTxn.length || null,
      transfer_out: allTransactions.length > 0 ? transferOutCount : null,
    };
  }, [allTransactions, filteredFolioNos, selectedSubAccount, filteredActiveUnits, soldFromAllTxn, dividendFromAllTxn, segregatedFromAllTxn]);

  // ── CSV Export ────────────────────────────────────────────────────────
  // One unified exporter that mirrors the current tab's filter chain
  // (financial-year filter, folio filter, sub-account filter, goal filter
  // etc.) so the downloaded file always matches what the user is seeing.
  const _escapeCsv = (v) => {
    if (v === null || v === undefined) return "";
    const s = String(v);
    if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };
  const downloadCsv = useCallback((filename, headers, rows) => {
    const csv = [
      headers.map(h => _escapeCsv(h.label || h.key)).join(","),
      ...rows.map(r => headers.map(h => _escapeCsv(
        typeof h.value === "function" ? h.value(r) : r[h.key]
      )).join(",")),
    ].join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, []);

  // Apply goal filter
  const holdingsFilteredByGoal = selectedGoalFilter === "all"
    ? holdingsFilteredByPattern
    : holdingsFilteredByPattern.filter(item => {
        // Check if this holding (folio_no + isin) is tagged to the selected goal
        const holdingKey = getHoldingKey(item.folio_no, item.isin);
        const goalAssignments = schemeGoals[holdingKey] || [];
        return goalAssignments.some(sg => sg.goal.id === selectedGoalFilter);
      });

  // Apply search filter on holdings
  const filteredPortfolioData = holdingsFilteredByGoal.filter(item => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      item.scheme_name?.toLowerCase().includes(term) ||
      item.folio_no?.toLowerCase().includes(term) ||
      item.scheme_code?.toLowerCase().includes(term)
    );
  });

  // Compute Unit Mismatch by comparing Active Units (transactions) with Scheme Wise Breakdown (holdings)
  const computedUnitMismatch = useMemo(() => {
    // ── Family-mode bug fix (2026-02-08) ──────────────────────────────
    // In `familyAggregate` mode both data sets contain rows from MULTIPLE
    // family members. The old key was `${folio_no}:${isin}`, which caused:
    //   • Active side  →  SUMMED units across all members' rows for that
    //                      (folio, isin) — over-counted.
    //   • Holdings side → OVERWROTE on each iteration, keeping only the
    //                      LAST member's row → under-counted.
    // The two sides could never reconcile → phantom mismatches even when
    // each individual member balanced perfectly. We now scope the key by
    // PAN so each member's (folio, isin) tuple is compared in isolation.
    const _pan = (r) => (r.client_pan || r.pan || r.investor_pan || "").toString().trim().toUpperCase();
    // IDCW-variant join fix (2026-06-23): Karvy / CAMS regularly issue
    // multiple ISINs per (folio, product_code) — e.g. Axis ELSS Tax Saver
    // Fund 128TSDP has K01149 (IDCW Payout) on the active_units side and
    // K01156 (IDCW Option) on the aum_master side for the SAME logical
    // holding. Joining on ISIN first phantom-splits a perfectly reconciled
    // 4,400.631-unit position into +4,400.631 / -4,400.631 ghost rows.
    // product_code is the most stable cross-source key — same fix the
    // backend `rebuild_unit_mismatch_cache` already applied for the same
    // reason (Aarti Hirendra Kumar / folio 91014390766 case).
    const _key = (r) => `${_pan(r)}|${r.folio_no || ""}:${r.scheme_code || r.isin || ""}`;

    // Build a map of Active Units totals by pan+folio+isin
    const activeUnitsMap = {};
    filteredActiveUnits.forEach(txn => {
      const key = _key(txn);
      if (!activeUnitsMap[key]) {
        activeUnitsMap[key] = {
          pan: _pan(txn),
          folio_no: txn.folio_no,
          isin: txn.isin,
          scheme_code: txn.scheme_code,
          scheme_name: txn.scheme_name,
          source: txn.source || 'KARVY',
          active_units: 0
        };
      }
      // Use remaining_units field directly from API response
      const remainingUnits = parseFloat(txn.remaining_units || 0);
      activeUnitsMap[key].active_units += remainingUnits;
    });

    // Build a map of Scheme Wise Breakdown (holdings) totals by pan+folio+isin.
    // Sum (not overwrite) so multiple rows per (pan, folio, isin) — which
    // can legitimately appear when CAMS/Karvy return per-plan or per-source
    // splits — aggregate the same way as the active-units map.
    // Exclude segregated portfolios — they never have active units.
    const holdingsMap = {};
    filteredPortfolioData.forEach(h => {
      if ((h.scheme_name || '').toLowerCase().includes('segregat')) return;
      const key = _key(h);
      if (!holdingsMap[key]) {
        holdingsMap[key] = {
          pan: _pan(h),
          folio_no: h.folio_no,
          isin: h.isin,
          scheme_code: h.scheme_code,
          scheme_name: h.scheme_name,
          source: h.source || 'KARVY',
          holding_units: 0,
        };
      }
      holdingsMap[key].holding_units += parseFloat(h.units || h.balance_units || 0);
    });

    // Compare and find mismatches
    const allKeys = new Set([...Object.keys(activeUnitsMap), ...Object.keys(holdingsMap)]);
    const matched = [];
    const mismatched = [];

    allKeys.forEach(key => {
      const active = activeUnitsMap[key] || { active_units: 0 };
      const holding = holdingsMap[key] || { holding_units: 0 };

      const activeUnits = active.active_units || 0;
      const holdingUnits = holding.holding_units || 0;
      const difference = activeUnits - holdingUnits;

      const item = {
        pan: active.pan || holding.pan,
        folio_no: active.folio_no || holding.folio_no,
        isin: active.isin || holding.isin,
        scheme_code: active.scheme_code || holding.scheme_code,
        scheme_name: active.scheme_name || holding.scheme_name,
        source: active.source || holding.source,
        active_units: activeUnits,
        holding_units: holdingUnits,
        difference: difference
      };

      if (Math.abs(difference) < 0.01) {
        matched.push(item);
      } else {
        mismatched.push(item);
      }
    });

    return {
      matched,
      mismatched,
      matched_count: matched.length,
      mismatched_count: mismatched.length
    };
  }, [filteredActiveUnits, filteredPortfolioData]);

  const sortedData = [...filteredPortfolioData]
    .filter(item => {
      if (!purchaseSearchTerm) return true;
      const term = purchaseSearchTerm.toLowerCase();
      return (
        item.folio_no?.toLowerCase().includes(term) ||
        item.scheme_name?.toLowerCase().includes(term) ||
        item.isin?.toLowerCase().includes(term) ||
        item.scheme_code?.toLowerCase().includes(term)
      );
    })
    .map(item => {
      // Override cost_value with value from Active Units calculation
      // Use product_code-first join (matches activeUnitsCostMap key above);
      // ISIN-first wrongly misses IDCW-variant switches.
      const key = `${item.folio_no}:${item.scheme_code || item.isin}`;
      const activeUnitsCost = activeUnitsCostMap[key];
      if (activeUnitsCost && activeUnitsCost.cost > 0) {
        const activeUnits = activeUnitsCost.transactions.reduce((sum, t) => {
          const rem = t.remaining_units != null ? t.remaining_units : Math.abs(parseFloat(t.units || 0));
          return sum + rem;
        }, 0);
        const holdingUnits = parseFloat(item.units || 0);
        // If active units < holdings and we have transaction data: multi-broker case
        // Replace balance with our active units
        const isMultiBroker = activeUnits > 0 && holdingUnits > 0 && activeUnits < holdingUnits * 0.99;
        return {
          ...item,
          cost_value: activeUnitsCost.cost,
          market_value: isMultiBroker ? activeUnitsCost.market : item.market_value,
          units: isMultiBroker ? activeUnits : item.units,
          xirr: activeUnitsCost.xirr != null ? activeUnitsCost.xirr : item.xirr,
          is_multi_broker: isMultiBroker,
          original_holdings_units: holdingUnits
        };
      }
      return item;
    })
    .sort((a, b) => {
    if (!sortConfig.key) return 0;
    
    let aVal = a[sortConfig.key];
    let bVal = b[sortConfig.key];
    
    // All numeric fields that should be sorted numerically
    if (["market_value", "units", "nav", "balance_units", "cost_value", "profit", "xirr", "holdings_pct"].includes(sortConfig.key)) {
      aVal = parseFloat(aVal) || 0;
      bVal = parseFloat(bVal) || 0;
    } else {
      aVal = (aVal || "").toString().toLowerCase();
      bVal = (bVal || "").toString().toLowerCase();
    }
    
    if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
    if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
    return 0;
  });

  // ─── As-on-date overlay for Scheme Wise Breakdown ────────────────────────
  // When `schemeAsOnDate` is set, replace each holding's `units` and
  // `market_value` with values computed AS OF that date by replaying
  // transactions ≤ date. Cost-basis is computed via **FIFO lot consumption**:
  //
  //   1. Sort all `add units` transactions chronologically into "lots".
  //   2. Sort all `minus units` transactions chronologically.
  //   3. Each sell consumes the oldest lot(s) first (true FIFO).
  //   4. The cost of the REMAINING units = Σ (lot.cost_per_unit × lot.units_left).
  //
  // A previous version used naive net-invested (`Σbuy − Σsell`), which went
  // to ₹0 whenever cumulative sells in INR ≥ cumulative buys — even when
  // units were still active (the unsold lots were appreciating). FIFO fixes
  // every such row in Valentino-style portfolios.
  //
  // KEY STRATEGY: indexing transactions vs holdings by `folio_no:<id>` —
  // try every available identifier (isin / scheme_code / product_code /
  // amfi_code), case-insensitively. Eliminates the "Total Cost = 0 because
  // holding has amfi_code but txn has only product_code" mismatch class.
  const schemeAsOfHoldings = useMemo(() => {
    if (!schemeAsOnDate) return sortedData;
    const cutoffMs = (() => {
      const t = new Date(`${schemeAsOnDate}T23:59:59`).getTime();
      return Number.isNaN(t) ? null : t;
    })();
    if (!cutoffMs) return sortedData;

    const keyFields = ["isin", "scheme_code", "product_code", "amfi_code"];

    // Step 1: bucket buys + sells by (folio, scheme-id-set) — each bucket is
    // SHARED across every identifier permutation the scheme has, so later
    // lookups by any identifier resolve to the same lot history.
    const bucketByKey = {};
    for (const t of allTransactions) {
      if (!t.folio_no) continue;
      const ms = parseDateStr(t.trade_date);
      if (!ms || ms > cutoffMs) continue;
      const treatment = (t.treatment || "").toLowerCase();
      if (treatment !== "add units" && treatment !== "minus units") continue;

      const cands = keyFields
        .map((f) => t[f])
        .filter((v) => v && String(v).trim())
        .map((v) => `${t.folio_no}:${String(v).trim().toUpperCase()}`);
      if (!cands.length) continue;

      let bucket = null;
      for (const k of cands) {
        if (bucketByKey[k]) { bucket = bucketByKey[k]; break; }
      }
      if (!bucket) bucket = { buys: [], sells: [] };
      for (const k of cands) bucketByKey[k] = bucket;

      const u = Math.abs(parseFloat(t.units || 0) || 0);
      const a = Math.abs(parseFloat(t.amount || 0) || 0);
      if (u <= 0) continue;
      if (treatment === "add units") {
        bucket.buys.push({ date: ms, units: u, amount: a });
      } else {
        bucket.sells.push({ date: ms, units: u });
      }
    }

    // Step 2: per-holding FIFO replay → remaining units + remaining cost.
    return sortedData
      .map((h) => {
        const lookupKeys = keyFields
          .map((f) => h[f])
          .filter((v) => v && String(v).trim())
          .map((v) => `${h.folio_no}:${String(v).trim().toUpperCase()}`);
        let bucket = null;
        for (const k of lookupKeys) {
          if (bucketByKey[k]) { bucket = bucketByKey[k]; break; }
        }
        if (!bucket) return null; // No txns ≤ date for this scheme

        // FIFO replay — work on a copy so re-runs are deterministic across
        // multiple holding-lookups that share a bucket (e.g. duplicate folio).
        const buys = bucket.buys
          .slice()
          .sort((a, b) => a.date - b.date)
          .map((b) => ({
            ...b,
            rem: b.units,
            cpu: b.units > 0 ? b.amount / b.units : 0,
          }));
        const sells = bucket.sells.slice().sort((a, b) => a.date - b.date);
        for (const s of sells) {
          let needed = s.units;
          for (const b of buys) {
            if (b.rem <= 0) continue;
            const consumed = Math.min(b.rem, needed);
            b.rem -= consumed;
            needed -= consumed;
            if (needed <= 0) break;
          }
        }
        let units = 0;
        let cost = 0;
        for (const b of buys) {
          if (b.rem > 0) {
            units += b.rem;
            cost += b.rem * b.cpu;
          }
        }
        if (units <= 0.0001) return null; // Fully redeemed by the cutoff

        const navInfo = schemeAsOfNav[String(h.amfi_code || "")] ||
                        schemeAsOfNav[String(h.scheme_code || "")] || null;
        const nav = navInfo?.nav || 0;
        const market = nav > 0 ? units * nav : 0;
        return {
          ...h,
          units,
          market_value: market,
          cost_value: cost,
          nav,
          nav_date: navInfo?.nav_date || null,
          nav_is_stale: nav > 0 && navInfo?.nav_date && navInfo.nav_date !== schemeAsOnDate,
          _as_of: schemeAsOnDate,
        };
      })
      .filter(Boolean);
  }, [schemeAsOnDate, schemeAsOfNav, sortedData, allTransactions]);

  // Build a STABLE key for the lookup effect so it doesn't refire on every
  // sort/render. `sortedData` is recreated every render (sort returns a new
  // array), which previously caused the effect → setState → re-render → effect
  // loop the user saw as "flickering". The amfi/scheme code set is the only
  // thing the lookup actually depends on, so we hash that.
  const schemeCodeFingerprint = useMemo(() => {
    const codes = new Set();
    for (const h of sortedData) {
      if (h.amfi_code) codes.add(String(h.amfi_code).trim());
      if (h.scheme_code) codes.add(String(h.scheme_code).trim());
    }
    return Array.from(codes).sort().join(",");
  }, [sortedData]);

  // Auto-fetch historical NAVs whenever the user picks a date or the in-view
  // scheme set genuinely changes.
  useEffect(() => {
    if (!schemeAsOnDate || !schemeCodeFingerprint) {
      setSchemeAsOfNav({});
      return;
    }
    const codes = schemeCodeFingerprint.split(",").filter(Boolean);
    if (!codes.length) {
      setSchemeAsOfNav({});
      return;
    }
    let cancelled = false;
    setSchemeAsOfLoading(true);
    api.get(`/nav-history/lookup?date=${schemeAsOnDate}&amfi_codes=${encodeURIComponent(codes.join(","))}`)
      .then((res) => {
        if (!cancelled) setSchemeAsOfNav(res.data?.schemes || {});
      })
      .catch((err) => {
        if (!cancelled) {
          console.error("nav-history lookup failed", err);
          setSchemeAsOfNav({});
        }
      })
      .finally(() => { if (!cancelled) setSchemeAsOfLoading(false); });
    return () => { cancelled = true; };
  }, [schemeAsOnDate, schemeCodeFingerprint, api]);

  // Calculate totals
  const totalMarketValue = sortedData.reduce((sum, item) => sum + (parseFloat(item.market_value) || 0), 0);
  // When the user has set an "As on Date", the Scheme Wise table reads from
  // `schemeAsOfHoldings` (computed above) and the totals must reflect that too.
  const schemeRowsForRender = schemeAsOnDate ? schemeAsOfHoldings : sortedData;
  const schemeTotalMarketValue = schemeAsOnDate
    ? schemeAsOfHoldings.reduce((s, h) => s + (parseFloat(h.market_value) || 0), 0)
    : totalMarketValue;

  const handleExportCurrentTab = useCallback(async () => {
    const cname = (selectedClientObj?.name || selectedClient || "client").replace(/[^A-Za-z0-9_-]+/g, "_");
    const ts = new Date().toISOString().slice(0, 10);

    // ── Pre-fetch any tab data that the user may not have visited yet ──────
    // Active Units lives in its own state; if the user clicked Export from
    // Summary / Folio Details, that array is empty so the Active Units sheet
    // would be blank. Same applies to the global All Transactions fetch.
    let activeUnitsForExport = activeUnitsTransactions;
    let allTxnForExport = allTransactions;
    try {
      if (selectedClient && (!allTxnForExport || allTxnForExport.length === 0)) {
        const r = await api.get(`/portfolio/${encodeURIComponent(selectedClientPan)}/all-transactions${bankQueryStr}`);
        allTxnForExport = r?.data?.transactions || [];
      }
    } catch (_e) { /* fall back */ }

    // Active Units source preference: derive from all-transactions (matches the
    // on-screen Active Units tab, includes CAMS + Karvy unified). The
    // `activeUnitsTransactions` state is Karvy-only and stays empty unless the
    // user visited that tab — so the all-txn derivation is more reliable.
    if (!activeUnitsForExport || activeUnitsForExport.length === 0) {
      activeUnitsForExport = (allTxnForExport || [])
        .filter(t => t.reflecting_in === "Active Units")
        .map(t => ({
          ...t,
          transaction_date: t.trade_date || t.transaction_date,
          scheme_code: t.product_code || t.amfi_code || t.scheme_code,
          transaction_desc: t.description || t.transaction_desc,
          original_units: t.original_units || Math.abs(parseFloat(t.units || 0)),
          remaining_units: t.remaining_units != null ? t.remaining_units : Math.abs(parseFloat(t.units || 0)),
          units_sold: t.sold_units || t.units_sold || 0,
        }));
    }

    // Derive the same sub-views the on-screen tabs use, off the freshest data.
    // Filters mirror the memos `soldFromAllTxn` / `dividendFromAllTxn` /
    // `segregatedFromAllTxn` / junk-from-allTxn so the export contains exactly
    // what the tab UI would display.
    const _txList = allTxnForExport || [];
    const _sold = _txList.filter(t => t.reflecting_in === "Sold Units");
    const _dividend = _txList.filter(t => t.reflecting_in === "Dividend Payout");
    const _segregated = _txList.filter(t => t.reflecting_in === "Segregated");
    const _junk = _txList.filter(t => t.reflecting_in === "Non Financial" || t.reflecting_in === "Junk Units");


    const patternLabel = (p) => {
      if (p === "joint_holder" || p === "as_joint") return "Joint Holder";
      if (p === "minor_guardian") return "Minor (Guardian)";
      if (p === "self_minor") return "Minor Account";
      return "Self";
    };
    const goalsLabel = (folioNo, isin) => {
      const list = schemeGoals[getHoldingKey(folioNo, isin)] || [];
      if (list.length === 0) return "";
      return list
        .map((sg) => `${sg.goal?.name || ""} (${sg.percentage || 0}%)`)
        .filter(Boolean)
        .join(", ");
    };
    const sourceLabel = (t) => ((t?.source || "").toUpperCase().includes("WBR") ? "CAMS" : "KARVY");
    const navVal = (t) => (typeof t.current_nav === "object" ? t.current_nav?.nav : t.current_nav);
    // Emit Trade Date as a real JS Date (see tradeDateToJsDate) so Excel
    // sorts it chronologically instead of lexicographically. Falls back to
    // the raw string only when parsing fails.
    const txnRow = (t) => {
      const rawTd = t.trade_date || t.transaction_date || "";
      const tdDate = tradeDateToJsDate(rawTd);
      return {
        "Trade Date": tdDate || rawTd,
        "Txn No": t.txn_no || "",
        "Folio": t.folio_no || "",
        "Scheme": t.scheme_name || "",
        "ISIN": t.isin || "",
        "Source": sourceLabel(t),
        "Bucket": t.reflecting_in || "",
        "Description": t.description || t.transaction_desc || "",
        "Treatment": t.treatment || "",
        "Units": parseFloat(t.units) || 0,
        "NAV": parseFloat(t.nav) || 0,
        "Amount": parseFloat(t.amount) || 0,
        "Stamp Duty": parseFloat(t.stamp_duty) || 0,
        "TDS": parseFloat(t.tds) || 0,
        "Current NAV": navVal(t) || "",
      };
    };
    // Sort transactions newest-first so the exported sheet's first data
    // row is the latest txn (matches the on-screen default sort direction).
    const sortByTradeDateDesc = (arr) => [...(arr || [])].sort((a, b) => {
      const ma = parseDateStr(a.trade_date || a.transaction_date);
      const mb = parseDateStr(b.trade_date || b.transaction_date);
      return mb - ma;
    });

    const sheetsToExport = [];
    let totalRowsExported = 0;
    const addSheet = (name, data) => {
      const safeName = String(name).substring(0, 31);
      const payload = !data || data.length === 0 ? [{ Note: "No data" }] : data;
      sheetsToExport.push({ name: safeName, rows: payload });
      if (data && data.length) totalRowsExported += data.length;
    };

    // Portfolio Summary (top-level totals — matches summary cards on screen)
    const totalCost = sortedData.reduce((s, h) => s + (parseFloat(h.cost_value) || 0), 0);
    const totalWithdrawn = soldUnitsSummary.reduce((s, t) => s + Math.abs(t.sale_amount || 0), 0);
    const totalDividend = dividendFromAllTxn.reduce((s, t) => s + Math.abs(parseFloat(t.amount) || 0), 0);
    const totalTds = filteredActiveUnits.reduce((s, t) => s + Math.abs(parseFloat(t.tds) || 0), 0);
    const summaryRows = [{
      "Client": selectedClientObj?.name || selectedClient || "",
      "PAN": selectedClientObj?.pan || "",
      "As Of": ts,
      "Total Cost (Invested)": Number(totalCost.toFixed(2)),
      "Total Market Value": Number(totalMarketValue.toFixed(2)),
      "Total Profit/Loss": Number((totalMarketValue - totalCost).toFixed(2)),
      "Total Withdrawn": Number(totalWithdrawn.toFixed(2)),
      "Total Dividend Payout": Number(totalDividend.toFixed(2)),
      "Total TDS": Number(totalTds.toFixed(2)),
      "Active Schemes": sortedData.length,
      "Sold Schemes": soldUnitsSummary.length,
    }];

    // Holding Period Summary (per-transaction bucketing — same logic as Summary tab)
    const parseDD = (ds) => {
      if (!ds) return null;
      const parts = String(ds).split('-');
      if (parts.length === 3 && parts[0].length === 2) return new Date(parts[2], parts[1] - 1, parts[0]);
      return new Date(ds);
    };
    const now = new Date();
    const periodDef = [
      { label: "5+ Years", min: 60 },
      { label: "3-5 Years", min: 36, max: 60 },
      { label: "2-3 Years", min: 24, max: 36 },
      { label: "1-2 Years", min: 12, max: 24 },
      { label: "Less than 1 Year", max: 12 },
    ];
    const periodBuckets = periodDef.map(p => ({ ...p, schemes: new Set(), txn: 0, cost: 0, market: 0 }));
    filteredActiveUnits.forEach(t => {
      const d = parseDD(t.transaction_date || t.trade_date);
      if (!d || isNaN(d.getTime())) return;
      const months = (now.getFullYear() - d.getFullYear()) * 12 + (now.getMonth() - d.getMonth());
      const bkt = periodBuckets.find(b => (!b.min || months >= b.min) && (!b.max || months < b.max));
      if (!bkt) return;
      const originalUnits = Math.abs(parseFloat(t.units || t.original_units || 0));
      const remainingUnits = t.remaining_units != null
        ? Math.max(0, parseFloat(t.remaining_units) || 0)
        : Math.max(0, originalUnits - Math.abs(parseFloat(t.units_sold || t.sold_units || 0)));
      const stampDuty = parseFloat(t.stamp_duty || 0);
      const propStamp = originalUnits > 0 && stampDuty > 0 ? (remainingUnits / originalUnits) * stampDuty : 0;
      const cost = remainingUnits * (parseFloat(t.nav) || 0) + propStamp;
      const cnav = navVal(t);
      const market = remainingUnits * (parseFloat(cnav) || parseFloat(t.nav) || 0);
      bkt.schemes.add(`${t.folio_no}:${t.isin || t.scheme_code}`);
      bkt.txn += 1;
      bkt.cost += cost;
      bkt.market += market;
    });
    const periodRows = periodBuckets.map(b => ({
      "Holding Period": b.label,
      "No. of Schemes": b.schemes.size,
      "Transactions": b.txn,
      "Cost Value (INR)": Number(b.cost.toFixed(2)),
      "Current Market Value (INR)": Number(b.market.toFixed(2)),
      "Gain/Loss (INR)": Number((b.market - b.cost).toFixed(2)),
      "Gain/Loss %": b.cost > 0 ? Number((((b.market - b.cost) / b.cost) * 100).toFixed(2)) : 0,
    }));

    // Scheme Wise — Active
    const activeRows = sortedData.map((h) => {
      const market = parseFloat(h.market_value) || 0;
      const cost = parseFloat(h.cost_value) || 0;
      return {
        Accounts: patternLabel(h.holding_pattern),
        "Linked Goals": goalsLabel(h.folio_no, h.isin),
        "Folio No.": h.folio_no || "",
        "Scheme Name": h.scheme_name || "",
        "Balance Units": parseFloat(h.units) || 0,
        "Total Cost Value (INR)": Number(cost.toFixed(2)),
        "Current Value (INR)": Number(market.toFixed(2)),
        "Profit/Loss": Number((market - cost).toFixed(2)),
        "% Holdings": totalMarketValue > 0 ? Number(((market / totalMarketValue) * 100).toFixed(2)) : 0,
        "XIRR %": h.xirr != null && !isNaN(h.xirr) ? Number(parseFloat(h.xirr).toFixed(2)) : null,
      };
    });

    // Scheme Wise — Sold
    const soldSummaryRows = soldUnitsSummary.map((t) => {
      const folioHolding = portfolioData.find((h) => h.folio_no === t.folio_no);
      return {
        Accounts: patternLabel(folioHolding?.holding_pattern || "self_single"),
        "Linked Goals": goalsLabel(t.folio_no, t.isin),
        "Folio No.": t.folio_no || "",
        "Scheme Name": t.scheme_name || "",
        "Units Sold": Math.abs(t.units || 0),
        "Total Cost Value (INR)": Number((t.total_cost_basis || 0).toFixed(2)),
        "Sale Value (INR)": Number(Math.abs(t.sale_amount || 0).toFixed(2)),
        "Profit/Loss": Number((t.gain_loss || 0).toFixed(2)),
        "% Share": Number((t.sale_pct || 0).toFixed(2)),
        "XIRR %": t.xirr != null && !isNaN(t.xirr) ? Number(parseFloat(t.xirr).toFixed(2)) : null,
      };
    });

    // Sold Units (respect FY + folio filter shown in the UI), but seeded from
    // the freshly-fetched all-txn list so the export isn't truncated when the
    // user hasn't browsed the Sold Units tab yet.
    let sUnits = _sold.length ? _sold : soldFromAllTxn;
    if (selectedFYForSold !== "all") sUnits = sUnits.filter(t => getFinancialYear(t.trade_date || t.transaction_date) === selectedFYForSold);
    if (selectedFolioForSold) sUnits = sUnits.filter(t => t.folio_no === selectedFolioForSold);
    if (soldDateFrom || soldDateTo) sUnits = sUnits.filter(t => isWithinDateRange(t.trade_date || t.transaction_date, soldDateFrom, soldDateTo));

    // Build sheets (in user-friendly order)
    addSheet("Portfolio Summary", summaryRows);
    addSheet("Holding Period Summary", periodRows);
    addSheet("Scheme Wise - Active", activeRows);
    addSheet("Scheme Wise - Sold", soldSummaryRows);
    addSheet("All Transactions", sortByTradeDateDesc(allTxnForExport).map(txnRow));
    addSheet("Active Units", sortByTradeDateDesc(activeUnitsForExport).map((t) => ({
      ...txnRow(t),
      "Remaining Units": parseFloat(t.remaining_units) || 0,
      "Units Sold": parseFloat(t.units_sold || t.sold_units) || 0,
    })));
    addSheet("Sold Units", sortByTradeDateDesc(sUnits).map((t) => ({
      ...txnRow(t),
      "FIFO Cost": parseFloat(t.fifo_cost_value) || 0,
      "GF Cost": parseFloat(t.grandfathered_cost_value) || 0,
      "P/L": Number((Math.abs(parseFloat(t.amount) || 0) - (t.fifo_cost_value || 0)).toFixed(2)),
      "Equity?": t.is_equity ? "Yes" : "No",
    })));
    addSheet("Dividend Payout", sortByTradeDateDesc(_dividend.length ? _dividend : (dividendFromAllTxn || [])).map(txnRow));
    addSheet("Non Financial", sortByTradeDateDesc(_junk.length ? _junk : (filteredJunkTransactions || [])).map(txnRow));
    addSheet("Segregated", sortByTradeDateDesc(_segregated.length ? _segregated : (segregatedFromAllTxn || [])).map(txnRow));
    addSheet("Failed Transactions", sortByTradeDateDesc((allTxnForExport || []).filter(t => t.reflecting_in === "Failed Transactions")).map(txnRow));
    addSheet("Transfer Out", sortByTradeDateDesc((allTxnForExport || []).filter(t => t.reflecting_in === "Transfer Out")).map(txnRow));
    addSheet("Folio Details", (folioDetailsFilteredByPattern || []).map(r => {
      const nomineeNames = [r.nominee_1, r.nominee_2, r.nominee_3]
        .filter(Boolean).filter(n => String(n).trim()).join("; ");
      return {
        "Folio": r.folio_no || "",
        "Source": r.source || "",
        "Scheme": r.scheme_name || "",
        "First Holder": r.first_holder || r.first_holder_name || "",
        "Second Holder": r.second_holder || r.second_holder_name || "",
        "Third Holder": r.third_holder || r.third_holder_name || "",
        "Guardian": r.guardian_name || "",
        "PAN (1st)": r.pan_1 || r.first_holder_pan || "",
        "Mobile": r.mobile || "",
        "Email": r.email || "",
        "City": r.city || "",
        "State": r.state || "",
        "Pincode": r.pincode || "",
        "Bank": r.bank_name || "",
        "Bank Account": r.bank_account || r.bank_account_no || "",
        "IFSC": r.bank_ifsc || r.ifsc_code || "",
        "Bank Type": r.bank_acc_type || "",
        "Nominees": nomineeNames,
        "Tax Status": r.tax_status || "",
      };
    }));
    addSheet("Unit Mismatch", ((computedUnitMismatch?.mismatched) || []).map(r => ({
      "Folio": r.folio_no || "",
      "Scheme": r.scheme_name || "",
      "Source": r.source || "",
      "Holding Units": r.holding_units || 0,
      "Active Units": r.active_units || 0,
      "Difference": r.difference || 0,
    })));

    const fname = `${cname}_portfolio_${ts}.xlsx`;
    exportStyledXlsx(sheetsToExport, fname);
    toast.success(`Exported ${totalRowsExported.toLocaleString()} rows across ${sheetsToExport.length} sheets`, {
      description: fname,
    });
  }, [
    allTransactions, filteredActiveUnits, activeUnitsTransactions, soldFromAllTxn,
    selectedFYForSold, selectedFolioForSold, soldDateFrom, soldDateTo, dividendFromAllTxn,
    filteredJunkTransactions, segregatedFromAllTxn,
    folioDetailsFilteredByPattern, computedUnitMismatch, selectedClient, selectedClientObj,
    sortedData, totalMarketValue, soldUnitsSummary, schemeGoals, portfolioData, api,
  ]);

  // ── Per-tab Excel export — exports only the visible/filtered rows of one tab.
  // Reuses the same row-mapping helpers used by the "Export Portfolio" button.
  const handleExportTabExcel = useCallback((tabKey) => {
    const cname = (selectedClientObj?.name || selectedClient || "client").replace(/[^A-Za-z0-9_-]+/g, "_");
    const ts = new Date().toISOString().slice(0, 10);
    const sourceLabel = (t) => ((t?.source || "").toUpperCase().includes("WBR") ? "CAMS" : "KARVY");
    const navVal = (t) => (typeof t.current_nav === "object" ? t.current_nav?.nav : t.current_nav);
    // Emit Trade Date as a real JS Date so Excel sorts chronologically
    // (see tradeDateToJsDate helper above). Falls back to the raw string
    // only when parsing fails.
    const txnRow = (t) => {
      const rawTd = t.trade_date || t.transaction_date || "";
      const tdDate = tradeDateToJsDate(rawTd);
      return {
        "Trade Date": tdDate || rawTd,
        "Txn No": t.txn_no || "",
        "Folio": t.folio_no || "",
        "Scheme": t.scheme_name || "",
        "ISIN": t.isin || "",
        "Source": sourceLabel(t),
        "Bucket": t.reflecting_in || "",
        "Description": t.description || t.transaction_desc || "",
        "Treatment": t.treatment || "",
        "Units": parseFloat(t.units) || 0,
        "NAV": parseFloat(t.nav) || 0,
        "Amount": parseFloat(t.amount) || 0,
        "Stamp Duty": parseFloat(t.stamp_duty) || 0,
        "TDS": parseFloat(t.tds) || 0,
        "Current NAV": navVal(t) || "",
      };
    };
    // Newest-first sort for the exported sheet (matches on-screen default).
    const sortByTradeDateDesc = (arr) => [...(arr || [])].sort((a, b) => {
      const ma = parseDateStr(a.trade_date || a.transaction_date);
      const mb = parseDateStr(b.trade_date || b.transaction_date);
      return mb - ma;
    });

    let sheetName = "";
    let rows = [];

    switch (tabKey) {
      case "folio_details": {
        sheetName = "Folio Details";
        rows = (folioDetailsFilteredByPattern || []).map((r) => {
          const nomineeNames = [r.nominee_1, r.nominee_2, r.nominee_3]
            .filter(Boolean).filter((n) => String(n).trim()).join("; ");
          return {
            "Folio": r.folio_no || "",
            "Source": r.source || "",
            "Scheme": r.scheme_name || "",
            "First Holder": r.first_holder || "",
            "Second Holder": r.second_holder || "",
            "Third Holder": r.third_holder || "",
            "Guardian": r.guardian_name || "",
            "PAN (1st)": r.pan_1 || "",
            "Mobile": r.mobile || "",
            "Email": r.email || "",
            "City": r.city || "",
            "State": r.state || "",
            "Pincode": r.pincode || "",
            "Bank": r.bank_name || "",
            "Bank Account": r.bank_account || "",
            "IFSC": r.bank_ifsc || "",
            "Bank Type": r.bank_acc_type || "",
            "Nominees": nomineeNames,
            "Tax Status": r.tax_status || "",
          };
        });
        break;
      }
      case "all_transactions": {
        sheetName = "All Transactions";
        const fromMs = ymdToMs(allTxnDateFrom);
        const toMs = allTxnDateTo ? new Date(`${allTxnDateTo}T23:59:59`).getTime() : null;
        let txns = [...(allTransactions || [])];
        if (selectedSubAccount !== "all" && filteredFolioNos.length > 0) {
          txns = txns.filter((t) => filteredFolioNos.includes(t.folio_no));
        }
        if (allTxnDupFilter === "duplicates") {
          // Filter to potential duplicates (same folio+isin+units+amount+date)
          const seen = {};
          txns.forEach((t) => {
            const k = `${t.folio_no}|${t.isin}|${t.units}|${t.amount}|${t.trade_date}`;
            seen[k] = (seen[k] || 0) + 1;
          });
          txns = txns.filter((t) => {
            const k = `${t.folio_no}|${t.isin}|${t.units}|${t.amount}|${t.trade_date}`;
            return seen[k] > 1;
          });
        }
        if (fromMs || toMs) {
          txns = txns.filter((t) => {
            const ms = parseDateStr(t.trade_date);
            if (!ms) return false;
            if (fromMs && ms < fromMs) return false;
            if (toMs && ms > toMs) return false;
            return true;
          });
        }
        rows = sortByTradeDateDesc(txns).map(txnRow);
        break;
      }
      case "failed_transactions": {
        sheetName = "Failed Transactions";
        rows = sortByTradeDateDesc((allTransactions || []).filter((t) => t.reflecting_in === "Failed Transactions")).map(txnRow);
        break;
      }
      case "active_units": {
        sheetName = "Active Units";
        rows = sortByTradeDateDesc(filteredActiveUnits).map((t) => ({
          ...txnRow(t),
          "Remaining Units": parseFloat(t.remaining_units) || 0,
          "Units Sold": parseFloat(t.units_sold || t.sold_units) || 0,
        }));
        break;
      }
      case "sold_units": {
        sheetName = "Sold Units";
        let sUnits = soldFromAllTxn || [];
        if (selectedFYForSold !== "all") {
          sUnits = sUnits.filter((t) => getFinancialYear(t.trade_date || t.transaction_date) === selectedFYForSold);
        }
        if (selectedFolioForSold) {
          sUnits = sUnits.filter((t) => t.folio_no === selectedFolioForSold);
        }
        if (soldDateFrom || soldDateTo) {
          sUnits = sUnits.filter((t) => isWithinDateRange(t.trade_date || t.transaction_date, soldDateFrom, soldDateTo));
        }
        rows = sortByTradeDateDesc(sUnits).map((t) => ({
          ...txnRow(t),
          "FIFO Cost": parseFloat(t.fifo_cost_value) || 0,
          "GF Cost": parseFloat(t.grandfathered_cost_value) || 0,
          "P/L": Number((Math.abs(parseFloat(t.amount) || 0) - (t.fifo_cost_value || 0)).toFixed(2)),
          "Equity?": t.is_equity ? "Yes" : "No",
        }));
        break;
      }
      case "idcw_payouts": {
        sheetName = "Dividend Payout";
        rows = sortByTradeDateDesc(dividendFromAllTxn).map(txnRow);
        break;
      }
      case "junk_units": {
        sheetName = "Non Financial";
        rows = sortByTradeDateDesc(filteredJunkTransactions).map(txnRow);
        break;
      }
      case "segregated": {
        sheetName = "Segregated";
        rows = sortByTradeDateDesc(segregatedFromAllTxn).map(txnRow);
        break;
      }
      case "transfer_out": {
        sheetName = "Transfer Out";
        rows = sortByTradeDateDesc((allTransactions || [])
          .filter((t) => t.reflecting_in === "Transfer Out")).map(txnRow);
        break;
      }
      case "unit_mismatch": {
        sheetName = "Unit Mismatch";
        rows = ((computedUnitMismatch?.mismatched) || []).map((r) => ({
          "Folio": r.folio_no || "",
          "Scheme": r.scheme_name || "",
          "Source": r.source || "",
          "Holding Units": r.holding_units || 0,
          "Active Units": r.active_units || 0,
          "Difference": r.difference || 0,
        }));
        break;
      }
      default:
        toast.error("Export not supported for this tab");
        return;
    }

    if (!rows || rows.length === 0) {
      toast.info(`No data to export in ${sheetName}`);
      return;
    }

    const safeName = sheetName.replace(/[^A-Za-z0-9_-]+/g, "_");
    const fname = `${cname}_${safeName}_${ts}.xlsx`;
    exportStyledXlsx([{ name: sheetName, rows }], fname);
    toast.success(`Exported ${rows.length.toLocaleString()} rows`, { description: fname });
  }, [
    selectedClient, selectedClientObj, allTransactions, filteredActiveUnits,
    soldFromAllTxn, selectedFYForSold, selectedFolioForSold, soldDateFrom, soldDateTo, dividendFromAllTxn,
    filteredJunkTransactions, segregatedFromAllTxn, folioDetailsFilteredByPattern,
    computedUnitMismatch, allTxnDateFrom, allTxnDateTo, allTxnDupFilter,
    selectedSubAccount, filteredFolioNos,
  ]);


  // Sorting logic
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc"
    }));
  };

  const SortIcon = ({ field }) => {
    if (sortConfig.key !== field) return <ArrowUpDown className="w-3 h-3 ml-1 text-gray-300" />;
    return sortConfig.direction === "asc" 
      ? <ArrowUp className="w-3 h-3 ml-1 text-teal-600" /> 
      : <ArrowDown className="w-3 h-3 ml-1 text-teal-600" />;
  };

  const SortableHeader = ({ field, children, className = "" }) => {
    // Extract width classes for TableHead
    const widthClasses = className.split(' ').filter(c => c.startsWith('w-') || c.startsWith('min-w-')).join(' ');
    const flexClasses = className.split(' ').filter(c => !c.startsWith('w-') && !c.startsWith('min-w-')).join(' ');
    
    return (
      <TableHead 
        className={`text-sm font-medium text-gray-500 cursor-pointer hover:bg-gray-50 select-none whitespace-nowrap ${widthClasses}`}
        onClick={() => handleSort(field)}
      >
        <div className={`flex items-center gap-1 ${flexClasses}`}>
          {children}
          <SortIcon field={field} />
        </div>
      </TableHead>
    );
  };

  // Active Units sort handler
  const handleActiveUnitsSort = (field) => {
    setActiveUnitsSortConfig(prev => ({
      key: field,
      direction: prev.key === field && prev.direction === "desc" ? "asc" : "desc"
    }));
  };

  // Active Units sort icon
  const ActiveUnitsSortIcon = ({ field }) => {
    if (activeUnitsSortConfig.key !== field) return <ArrowUpDown className="w-3 h-3 ml-1 text-gray-300" />;
    return activeUnitsSortConfig.direction === "asc" 
      ? <ArrowUp className="w-3 h-3 ml-1 text-teal-600" /> 
      : <ArrowDown className="w-3 h-3 ml-1 text-teal-600" />;
  };

  // Active Units sortable header component
  const ActiveUnitsSortableHeader = ({ field, children, className = "" }) => {
    const widthClasses = className.split(' ').filter(c => c.startsWith('w-') || c.startsWith('min-w-')).join(' ');
    const flexClasses = className.split(' ').filter(c => !c.startsWith('w-') && !c.startsWith('min-w-')).join(' ');
    
    return (
      <TableHead 
        className={`text-sm font-medium text-gray-500 cursor-pointer hover:bg-gray-50 select-none whitespace-nowrap ${widthClasses}`}
        onClick={() => handleActiveUnitsSort(field)}
      >
        <div className={`flex items-center gap-1 ${flexClasses}`}>
          {children}
          <ActiveUnitsSortIcon field={field} />
        </div>
      </TableHead>
    );
  };

  // Sold Units sort handler
  const handleSoldUnitsSort = (field) => {
    setSoldUnitsSortConfig(prev => ({
      key: field,
      direction: prev.key === field && prev.direction === "desc" ? "asc" : "desc"
    }));
  };

  // Sold Units sort icon
  const SoldUnitsSortIcon = ({ field }) => {
    if (soldUnitsSortConfig.key !== field) return <ArrowUpDown className="w-3 h-3 ml-1 text-gray-300" />;
    return soldUnitsSortConfig.direction === "asc" 
      ? <ArrowUp className="w-3 h-3 ml-1 text-orange-600" /> 
      : <ArrowDown className="w-3 h-3 ml-1 text-orange-600" />;
  };

  // Sold Units sortable header component
  const SoldUnitsSortableHeader = ({ field, children, className = "" }) => {
    return (
      <TableHead 
        className={`text-xs font-medium text-gray-600 cursor-pointer hover:bg-orange-100 select-none ${className}`}
        onClick={() => handleSoldUnitsSort(field)}
      >
        <div className="flex items-center gap-1">
          {children}
          <SoldUnitsSortIcon field={field} />
        </div>
      </TableHead>
    );
  };

  // Junk Units sort handler
  const handleJunkSort = (field) => {
    setJunkSortConfig(prev => ({
      key: field,
      direction: prev.key === field && prev.direction === "desc" ? "asc" : "desc"
    }));
  };

  // Junk Units sort icon
  const JunkSortIcon = ({ field }) => {
    if (junkSortConfig.key !== field) return <ArrowUpDown className="w-3 h-3 ml-1 text-gray-300" />;
    return junkSortConfig.direction === "asc" 
      ? <ArrowUp className="w-3 h-3 ml-1 text-gray-600" /> 
      : <ArrowDown className="w-3 h-3 ml-1 text-gray-600" />;
  };

  // Junk Units sortable header component
  const JunkSortableHeader = ({ field, children, className = "" }) => {
    return (
      <TableHead 
        className={`text-xs font-medium text-gray-600 cursor-pointer hover:bg-gray-100 select-none ${className}`}
        onClick={() => handleJunkSort(field)}
      >
        <div className="flex items-center gap-1">
          {children}
          <JunkSortIcon field={field} />
        </div>
      </TableHead>
    );
  };

  // Unit Mismatch sort handler
  const handleUnitMismatchSort = (field) => {
    setUnitMismatchSortConfig(prev => ({
      key: field,
      direction: prev.key === field && prev.direction === "desc" ? "asc" : "desc"
    }));
  };

  // Unit Mismatch sort icon
  const UnitMismatchSortIcon = ({ field }) => {
    if (unitMismatchSortConfig.key !== field) return <ArrowUpDown className="w-3 h-3 ml-1 text-gray-300" />;
    return unitMismatchSortConfig.direction === "asc" 
      ? <ArrowUp className="w-3 h-3 ml-1 text-amber-600" /> 
      : <ArrowDown className="w-3 h-3 ml-1 text-amber-600" />;
  };

  // Unit Mismatch sortable header component
  const UnitMismatchSortableHeader = ({ field, children, className = "" }) => {
    return (
      <TableHead 
        className={`text-xs font-medium text-gray-600 cursor-pointer hover:bg-amber-50 select-none ${className}`}
        onClick={() => handleUnitMismatchSort(field)}
      >
        <div className="flex items-center gap-1">
          {children}
          <UnitMismatchSortIcon field={field} />
        </div>
      </TableHead>
    );
  };

  // Get ownership type color based on holding pattern
  const getOwnershipColor = (holdingPattern) => {
    const colorMap = {
      "self_single": "bg-teal-600",
      "joint_holder": "bg-blue-500",
      "as_joint": "bg-cyan-500",
      "minor_guardian": "bg-purple-500",
      "self_minor": "bg-amber-500",
    };
    return colorMap[holdingPattern] || "bg-teal-600";
  };

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="portfolio-page">
      {/* Mobile App-style Hero (clients/everyone on phone) */}
      {isMobile && (
        <MobilePortfolioHero
          selectedClientObj={selectedClientObj}
          marketValue={totalMarketValue}
          costValue={(() => {
            const totalFromActiveUnits = Object.values(activeUnitsCostMap).reduce((sum, v) => sum + (v.cost || 0), 0);
            if (totalFromActiveUnits > 0) return totalFromActiveUnits;
            return holdingsFilteredByPattern.reduce((sum, h) => sum + (h.cost_value || 0), 0);
          })()}
          totalProfitLoss={(() => {
            const cost = Object.values(activeUnitsCostMap).reduce((sum, v) => sum + (v.cost || 0), 0)
              || holdingsFilteredByPattern.reduce((sum, h) => sum + (h.cost_value || 0), 0);
            return totalMarketValue - cost;
          })()}
          xirr={(() => {
            // Simple weighted XIRR fallback — average of holding xirr weighted by market value.
            const items = portfolioData.filter(h => (h.units || 0) > 0 && h.xirr != null);
            if (!items.length) return null;
            const totMv = items.reduce((s,h)=>s+(parseFloat(h.market_value)||0),0);
            if (totMv <= 0) return null;
            const w = items.reduce((s,h)=>s + ((parseFloat(h.market_value)||0) * (parseFloat(h.xirr)||0)), 0);
            return (w / totMv) / 100;
          })()}
          onClientClick={() => setClientDropdownOpen(true)}
          onRefresh={fetchPortfolioData}
          onExport={handleExportCurrentTab}
          exportDisabled={!selectedClient}
          loading={loading}
        />
      )}

      {/* Desktop Header — hidden on mobile (replaced by hero above) */}
      <div className={isMobile ? "hidden" : "flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4"}>
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-800">Portfolio</h1>
          <p className="text-teal-600 mt-1 text-xs sm:text-sm">Comprehensive portfolio analysis and reports</p>
        </div>
        
        <div className="flex items-center gap-2 sm:gap-3 w-full sm:w-auto">
          {(
            <Popover open={clientDropdownOpen} onOpenChange={setClientDropdownOpen}>
              <PopoverTrigger asChild>
                <Button 
                  variant="outline" 
                  className="w-full sm:min-w-[280px] sm:w-auto justify-between bg-white border-gray-200 text-gray-700 hover:bg-gray-50" 
                  data-testid="portfolio-client-select"
                >
                  {selectedClientObj ? (
                    <span className="truncate">{selectedClientObj.name}</span>
                  ) : (
                    <span>Select Client</span>
                  )}
                  <ChevronDown className="w-4 h-4 ml-2 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[calc(100vw-1.5rem)] sm:w-[420px] p-0" align="end">
                <div className="p-3 border-b">
                  <div className="relative">
                    <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <Input
                      placeholder="Search by name or PAN..."
                      value={clientSearchTerm}
                      onChange={(e) => setClientSearchTerm(e.target.value)}
                      className="pl-10 h-10"
                      data-testid="client-search-input"
                    />
                  </div>
                </div>
                <div className="max-h-72 overflow-y-auto">
                  {filteredClients.length === 0 ? (
                    <div className="p-4 text-center text-sm text-gray-500">No clients found</div>
                  ) : (
                    filteredClients.map((client) => (
                      <div
                        key={client.id}
                        className={`flex items-center justify-between px-3 py-2 cursor-pointer hover:bg-gray-100 ${
                          selectedClient === client.id ? "bg-teal-50" : ""
                        }`}
                        onClick={() => {
                          setSelectedClient(client.id);
                          setClientDropdownOpen(false);
                          setClientSearchTerm("");
                          setSelectedBankAccType("all"); // Reset NRE/NRO filter when changing clients
                        }}
                        data-testid={`client-option-${client.id}`}
                      >
                        <div className="flex flex-col min-w-0">
                          <span className={`truncate ${selectedClient === client.id ? "font-medium text-teal-700" : ""}`}>
                            {toTitleCase(client.name)}
                          </span>
                          {client.minor_names?.length > 0 && (
                            <span className="text-xs text-purple-500 truncate">
                              {client.minor_names.join(", ")}
                            </span>
                          )}
                        </div>
                        <span className={`text-sm shrink-0 ml-2 ${
                          selectedClient === client.id ? "text-teal-600 font-medium" : "text-gray-500"
                        }`}>
                          {formatCurrency(client.market_value || 0)}
                        </span>
                      </div>
                    ))
                  )}
                </div>
              </PopoverContent>
            </Popover>
          )}
          <Button variant="outline" onClick={fetchPortfolioData} disabled={loading} data-testid="refresh-portfolio" className="gap-2">
            <RefreshCcw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          <Button variant="outline" data-testid="export-portfolio" className="gap-2" onClick={handleExportCurrentTab} disabled={!selectedClient}>
            <Download className="w-4 h-4" />
            Export
          </Button>
        </div>
      </div>

      {/* Family Group banner — shown only when the logged-in client is a
          Group Head with at least one other family member mapped. */}
      {familyContext && familyContext.is_group_head && (familyContext.family_members || []).length > 1 && (
        <Card className="border-amber-200 bg-amber-50/40" data-testid="family-group-banner">
          <CardContent className="py-4">
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2">
                <Users className="h-5 w-5 text-amber-700" />
                <div>
                  <h3 className="font-semibold text-gray-800 text-sm">
                    Family Group — {familyContext.group_head_name || "Group Head"}
                    {familyAggregate && (
                      <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-amber-600 text-white" data-testid="family-aggregate-mode-tag">
                        Clubbed View
                      </span>
                    )}
                  </h3>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {familyAggregate
                      ? `Showing aggregated data for all ${(familyContext.family_members || []).length} members. Click a member to drill down.`
                      : drillDownPan
                        ? `Drilled into one member. Click All Family to return to the clubbed view.`
                        : `${(familyContext.family_members || []).length} members linked. Click a member to view their portfolio, or All Family to club.`}
                  </p>
                </div>
              </div>
              <div className="ml-auto flex flex-wrap items-center gap-1.5" data-testid="family-members-chips">
                {/* All Family chip — toggles aggregate (clubbed) view and
                    clears any single-member drill-down. The top-bar search
                    intentionally stays on the Head — chips are pure view
                    overlays, never replacing the search selection. */}
                <button
                  onClick={() => {
                    setDrillDownPan("");
                    setFamilyAggregate(true);
                  }}
                  className={`text-xs px-3 py-1.5 rounded-full border transition-all font-medium ${
                    familyAggregate
                      ? "bg-amber-600 text-white border-amber-600"
                      : "bg-white border-amber-400 text-amber-800 hover:bg-amber-100"
                  }`}
                  title="View aggregated family portfolio (clubbed)"
                  data-testid="family-all-chip"
                >
                  All Family
                </button>
                {(familyContext.family_members || []).map((m) => {
                  const memberPanUpper = (m.pan || "").toUpperCase();
                  // A chip is "highlighted" when its PAN matches the current
                  // drill-down. Clicking it sets drillDownPan and disables
                  // family-aggregate — but DOES NOT touch `selectedClient`
                  // (the top-bar dropdown), per spec.
                  const isCurrent = !familyAggregate && drillDownPan.toUpperCase() === memberPanUpper;
                  // "(you)" tag = the chip representing the LOGGED-IN user.
                  const myPan = ((user && user.pan_no) || "").toUpperCase();
                  const isMe = myPan
                    ? memberPanUpper === myPan
                    : memberPanUpper === (searchClientPan || "").toUpperCase();
                  return (
                    <button
                      key={m.pan}
                      onClick={() => {
                        setFamilyAggregate(false);
                        setDrillDownPan(memberPanUpper);
                      }}
                      className={`text-xs px-3 py-1.5 rounded-full border transition-all ${
                        isCurrent
                          ? "bg-amber-600 text-white border-amber-600"
                          : "bg-white border-amber-300 text-amber-800 hover:bg-amber-100"
                      }`}
                      title={`View ${m.name}'s portfolio`}
                      data-testid={`family-member-chip-${m.pan}`}
                    >
                      {/* Normalise chip labels to Title Case so a stray
                          ALL-CAPS aum_master holder name (e.g. an HUF row)
                          doesn't visually shout next to its title-cased
                          siblings. Acronyms in parens like "(HUF)" are
                          preserved. PAN fallback stays uppercase. */}
                      {(m.name ? m.name.replace(/\b([A-Z]+)\b/g, (w) => (
                        /^\([A-Z]+\)?$/.test(w) || w.length <= 3
                          ? w
                          : w.charAt(0) + w.slice(1).toLowerCase()
                      )) : m.pan)}
                      {isMe && <span className="ml-1 opacity-70">(you)</span>}
                    </button>
                  );
                })}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Sub-Accounts Card */}
      <Card className="border-gray-200">
        <CardContent className="py-5">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="font-semibold text-gray-800">Sub-Accounts</h3>
              <p className="text-sm text-gray-500 mt-0.5">Filter portfolio by ownership pattern</p>
              
              {/* Sub-account filters — unified for all client types */}
              <div className="flex items-center gap-2 mt-4 flex-wrap">
                {/* All button */}
                <button
                  onClick={() => {
                    setSelectedSubAccount("all");
                    setSelectedMinorKey(null);
                    setSelectedBankAccType("all");
                  }}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                    selectedSubAccount === "all" && !selectedMinorKey
                      ? "bg-teal-600 text-white" 
                      : "bg-white border border-gray-200 text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  <Building2 className="w-4 h-4" />
                  All
                  <span className={`ml-1 ${selectedSubAccount === "all" && !selectedMinorKey ? "text-white/80" : "text-gray-400"}`}>
                    {holdingPatternCounts.all || 0}
                  </span>
                </button>
                
                {/* Render each sub-account as a separate button (minors, firms, NRI, individuals) */}
                {holdingPatternCounts.minorAccounts?.map((acct) => {
                  const uniqueKey = acct.uniqueKey;
                  const isActive = selectedMinorKey === uniqueKey;
                  const formattedName = acct.holderName.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
                  const displayLabel = formattedName.length > 22 ? formattedName.substring(0, 22) + '...' : formattedName;
                  
                  let tooltipText, IconComp, activeBg;
                  if (acct.subType === 'minor') {
                    const gName = (acct.guardianName || '-').split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
                    tooltipText = `Minor: ${formattedName}\nGuardian: ${gName}\nGuardian PAN: ${acct.guardianPan || '-'}`;
                    IconComp = Baby;
                    activeBg = "bg-purple-500 text-white";
                  } else if (acct.subType === 'firm') {
                    tooltipText = `Firm: ${formattedName}`;
                    IconComp = Building2;
                    activeBg = "bg-amber-600 text-white";
                  } else if (acct.subType === 'nri') {
                    tooltipText = `NRI: ${acct.holderName}`;
                    IconComp = Globe;
                    activeBg = "bg-blue-600 text-white";
                  } else if (acct.subType === 'joint') {
                    tooltipText = `Joint: ${formattedName}`;
                    IconComp = Users;
                    activeBg = "bg-indigo-600 text-white";
                  } else {
                    tooltipText = formattedName;
                    IconComp = User;
                    activeBg = "bg-teal-600 text-white";
                  }
                  
                  const isZeroAum = acct.marketValue <= 0;
                  
                  return (
                    <button
                      key={uniqueKey}
                      onClick={() => {
                        setSelectedSubAccount("sub_account_filter");
                        setSelectedMinorKey(uniqueKey);
                        setSelectedBankAccType("all");
                      }}
                      title={tooltipText}
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                        isActive 
                          ? activeBg
                          : isZeroAum
                            ? "bg-red-50 border-2 border-red-400 text-red-700 hover:bg-red-100"
                            : "bg-white border border-gray-200 text-gray-700 hover:bg-gray-50"
                      }`}
                    >
                      <IconComp className="w-4 h-4" />
                      {displayLabel}
                      <span className={`ml-1 ${isActive ? "text-white/80" : isZeroAum ? "text-red-400" : "text-gray-400"}`}>{acct.count}</span>
                    </button>
                  );
                })}
              </div>

              {/* Ownership legend - only for non-NRI clients */}
              {!holdingPatternCounts.isNriClient && (
                <div className="flex items-center gap-4 mt-4 flex-wrap">
                  {ownershipLegend.map((item) => {
                    const count = holdingPatternCounts[item.key] || 0;
                    if (count === 0) return null;
                    return (
                      <div key={item.label} className="flex items-center gap-1.5">
                        <div className={`w-2.5 h-2.5 rounded-full ${item.color}`} />
                        <span className="text-sm text-gray-500">{item.label}</span>
                      </div>
                    );
                  })}
                </div>
              )}
              
              {/* NRE/NRO legend for NRI clients */}
              {holdingPatternCounts.isNriClient && (
                <div className="flex items-center gap-4 mt-4 flex-wrap">
                  {holdingPatternCounts.bankAccTypeCounts?.nre > 0 && (
                    <div className="flex items-center gap-1.5">
                      <div className="w-2.5 h-2.5 rounded-full bg-green-600" />
                      <span className="text-sm text-gray-500">NRE (Repatriable)</span>
                    </div>
                  )}
                  {holdingPatternCounts.bankAccTypeCounts?.nro > 0 && (
                    <div className="flex items-center gap-1.5">
                      <div className="w-2.5 h-2.5 rounded-full bg-orange-600" />
                      <span className="text-sm text-gray-500">NRO (Non-Repatriable)</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Total Folios */}
            <div className="text-right">
              <p className="text-sm text-gray-500">Total Folios</p>
              <p className="text-2xl font-bold text-teal-600">{allFoliosFilteredByPattern.length > 0 && selectedSubAccount !== "all" ? [...new Set(allFoliosFilteredByPattern.filter(h => (h.units || 0) > 0 || (h.txn_count || 0) > 0).map(h => h.folio_no))].length : totalFolios}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      {isMobile ? (
        <MobileTabDropdown
          activeKey={activeTab}
          onChange={(k) => { setActiveTab(k); setAllTxnPage(1); }}
          tabs={tabs.map((t) => ({
            key: t.key,
            label: t.label,
            icon: t.icon,
            count: tabCounts[t.key] != null ? tabCounts[t.key] : undefined,
          }))}
        />
      ) : (
      <div className="border-b border-gray-200">
        <div className="flex items-center gap-1 overflow-x-auto pb-px">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => { setActiveTab(tab.key); setAllTxnPage(1); }}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                  isActive 
                    ? "border-teal-600 text-teal-600" 
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
                {tabCounts[tab.key] != null && (
                  <span className={`ml-1 text-xs px-1.5 py-0.5 rounded ${isActive ? "bg-teal-100 text-teal-700" : "bg-gray-100 text-gray-500"}`}>{tabCounts[tab.key]}</span>
                )}
                {tab.key === "unit_mismatch" && computedUnitMismatch && (
                  computedUnitMismatch.mismatched_count > 0 ? (
                    <span className="ml-1 text-xs bg-amber-100 text-amber-600 px-1.5 py-0.5 rounded">{computedUnitMismatch.mismatched_count}</span>
                  ) : (
                    <span className="ml-1 text-xs bg-green-100 text-green-600 px-1.5 py-0.5 rounded">✓</span>
                  )
                )}
              </button>
            );
          })}
        </div>
      </div>
      )}

      {/* Per-tab Family Members banner — shown on every tab EXCEPT Summary
          when "All Family" (clubbed) view is active. Lists every family
          member whose data is being aggregated in the current tab and lets
          the user click a name to drill-down into a single member. */}
      {(familyAggregate || drillDownPan)
        && activeTab !== "summary"
        && familyContext?.is_group_head
        && (familyContext.family_members || []).length > 1 && (
        <div className="bg-amber-50/70 border border-amber-200 rounded-md px-3 py-2 flex flex-wrap items-center gap-2 text-xs" data-testid="per-tab-family-banner">
          <span className="font-semibold text-amber-900 mr-1">
            {familyAggregate
              ? `Aggregated across ${(familyContext.family_members || []).length} family members:`
              : `Viewing one member — switch:`}
          </span>
          <button
            type="button"
            onClick={() => { setDrillDownPan(""); setFamilyAggregate(true); }}
            className={`px-2.5 py-1 rounded-full border transition-colors ${
              familyAggregate
                ? "bg-amber-600 border-amber-700 text-white"
                : "bg-white border-amber-300 text-amber-900 hover:bg-amber-100"
            }`}
            data-testid="tab-banner-all-family"
          >
            All Family
          </button>
          {(familyContext.family_members || []).map((m) => {
            const titleCaseName = m.name
              ? m.name.replace(/\b([A-Z]+)\b/g, (w) => (
                  /^\([A-Z]+\)?$/.test(w) || w.length <= 3
                    ? w
                    : w.charAt(0) + w.slice(1).toLowerCase()
                ))
              : m.pan;
            const memberPanUpper = (m.pan || "").toUpperCase();
            const isCurrent = !familyAggregate && drillDownPan.toUpperCase() === memberPanUpper;
            return (
              <button
                key={`tab-banner-${m.pan}`}
                type="button"
                onClick={() => {
                  setFamilyAggregate(false);
                  setDrillDownPan(memberPanUpper);
                }}
                className={`px-2.5 py-1 rounded-full border transition-colors ${
                  isCurrent
                    ? "bg-amber-600 border-amber-700 text-white"
                    : "bg-white border-amber-300 text-amber-900 hover:bg-amber-100"
                }`}
                title={isCurrent ? `Currently viewing ${titleCaseName}` : `Drill into ${titleCaseName}`}
                data-testid={`tab-banner-member-${m.pan}`}
              >
                {titleCaseName}
              </button>
            );
          })}
        </div>
      )}

      {/* Filter by Goal & Search - shown in Scheme Wise Breakdown tab (includes Holdings table) */}
      {activeTab === "scheme_breakdown" && (
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 w-48 h-9"
              />
            </div>

            {/* Goal Filters */}
            <TooltipProvider>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500 flex items-center gap-1">
                  <Target className="w-4 h-4" />
                  Filter by Goal:
                </span>
                <Badge 
                  className={`cursor-pointer ${selectedGoalFilter === "all" ? "bg-teal-600 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
                  onClick={() => setSelectedGoalFilter("all")}
                >
                  All
                </Badge>
                
                {/* Dynamic Goal Capsules with Achievement % and Hover Details */}
                {goals.map((goal, goalIndex) => {
                  const progress = calculateGoalProgress(goal);
                  const color = goalColors[goalIndex % goalColors.length];
                  const schemesCount = (goal.schemes || []).length;
                  const isSelected = selectedGoalFilter === goal.id;
                
                return (
                  <Tooltip key={goal.id}>
                    <TooltipTrigger asChild>
                      <Badge 
                        variant="outline" 
                        className={`${color.text} ${color.border} ${color.bg} cursor-pointer hover:opacity-80 relative overflow-hidden px-3 py-1 ${isSelected ? 'ring-2 ring-offset-1 ring-gray-400' : ''}`}
                        onClick={() => setSelectedGoalFilter(isSelected ? "all" : goal.id)}
                      >
                        {/* Progress bar background */}
                        <div 
                          className={`absolute left-0 top-0 bottom-0 ${color.progress} opacity-20`}
                          style={{ width: `${progress.achievementPercent}%` }}
                        />
                        <span className="relative z-10 font-medium">
                          {goal.name}
                        </span>
                        <span className={`ml-2 ${color.accent} relative z-10 font-semibold`}>
                          {progress.achievementPercent}%
                        </span>
                        <span 
                          className={`ml-2 ${color.accent} hover:text-red-500 relative z-10`}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteGoal(goal.id);
                          }}
                        >×</span>
                      </Badge>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="p-3 max-w-xs">
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="font-semibold text-gray-800">{goal.name}</span>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0"
                            onClick={() => openEditGoalDialog(goal)}
                          >
                            <Edit2 className="w-3 h-3" />
                          </Button>
                        </div>
                        <div className="text-sm space-y-1">
                          <div className="flex justify-between">
                            <span className="text-gray-500">Target Date:</span>
                            <span className="font-medium">{goal.expected_date}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-500">Target Amount:</span>
                            <span className="font-medium">{progress.targetAmount > 0 ? formatCurrency(progress.targetAmount) : 'Not set'}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-500">Current Value:</span>
                            <span className="font-medium text-green-600">{formatCurrency(progress.currentValue)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-500">Schemes:</span>
                            <span className="font-medium">{schemesCount}</span>
                          </div>
                          {progress.targetAmount > 0 && (
                            <div className="mt-2 pt-2 border-t">
                              <div className="flex justify-between items-center">
                                <span className="text-gray-500">Achievement:</span>
                                <span className={`font-bold ${progress.achievementPercent >= 100 ? 'text-green-600' : progress.achievementPercent >= 50 ? 'text-amber-600' : 'text-rose-600'}`}>
                                  {progress.achievementPercent}%
                                </span>
                              </div>
                              {/* Progress bar */}
                              <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                                <div 
                                  className={`h-2 rounded-full ${progress.achievementPercent >= 100 ? 'bg-green-500' : progress.achievementPercent >= 50 ? 'bg-amber-500' : 'bg-rose-500'}`}
                                  style={{ width: `${Math.min(progress.achievementPercent, 100)}%` }}
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </TooltipContent>
                  </Tooltip>
                );
              })}
            </div>
          </TooltipProvider>
        </div>

        <Button 
          variant="outline" 
          className="gap-2 text-gray-600"
          onClick={() => setShowCreateGoalDialog(true)}
          data-testid="create-goal-btn"
        >
          <Plus className="w-4 h-4" />
          Create Goal
        </Button>
      </div>
      )}

      {/* Summary stack (KPIs + XIRR + AMC) — Summary tab only. */}
      {activeTab === "summary" && (
        /* Summary Tab - Portfolio Overview */
        <div className="space-y-6">
          {/* PORTFOLIO SUMMARY */}
          <Card className="border-gray-200">
            <CardContent className="p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-teal-600" />
                PORTFOLIO SUMMARY
              </h3>
              {(() => {
                // Space-constrained mobile grid needs compact ₹ formatting.
                // Desktop (md+) has room for full Indian numbering.
                const sumCost = Object.values(activeUnitsCostMap).reduce((sum, v) => sum + (v.cost || 0), 0);
                const sumMarket = Object.values(activeUnitsCostMap).reduce((sum, v) => sum + (v.market || 0), 0);
                const fbCost = sumCost > 0 ? sumCost : holdingsFilteredByPattern.reduce((sum, h) => sum + (h.cost_value || 0), 0);
                const withdrawn = soldFromAllTxn.reduce((sum, t) => sum + Math.abs(parseFloat(t.amount) || 0), 0);
                const dividend = dividendFromAllTxn.reduce((sum, t) => sum + Math.abs(parseFloat(t.amount) || 0), 0);
                const tds = filteredActiveUnits.reduce((sum, t) => sum + Math.abs(parseFloat(t.tds) || 0), 0);
                const gainLoss = sumMarket - sumCost;
                const isGain = gainLoss >= 0;
                const fmtFull = (n) => (Number(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 });
                const fmtShort = (n) => {
                  const v = Number(n) || 0;
                  const abs = Math.abs(v);
                  if (abs >= 1e7) return `${(v / 1e7).toFixed(2)} Cr`;
                  if (abs >= 1e5) return `${(v / 1e5).toFixed(2)} L`;
                  return v.toLocaleString('en-IN', { maximumFractionDigits: 0 });
                };
                const Value = ({ v, prefix = '₹' }) => (
                  <>
                    <span className="hidden md:inline">{prefix}{fmtFull(v)}</span>
                    <span className="md:hidden">{prefix}{fmtShort(v)}</span>
                  </>
                );
                return (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                    <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
                      <p className="text-xs text-blue-600 font-medium mb-1">Total Cost Value (Invested)</p>
                      <p className="text-lg font-bold text-blue-800" data-testid="summary-cost-value">
                        <Value v={fbCost} />
                      </p>
                    </div>
                    <div className="bg-orange-50 rounded-lg p-4 border border-orange-100">
                      <p className="text-xs text-orange-600 font-medium mb-1">Total Amount Withdrawn</p>
                      <p className="text-lg font-bold text-orange-800" data-testid="summary-withdrawn">
                        <Value v={withdrawn} />
                      </p>
                    </div>
                    <div className="bg-purple-50 rounded-lg p-4 border border-purple-100">
                      <p className="text-xs text-purple-600 font-medium mb-1">Total Dividend Payout</p>
                      <p className="text-lg font-bold text-purple-800" data-testid="summary-dividend">
                        <Value v={dividend} />
                      </p>
                    </div>
                    <div className="bg-red-50 rounded-lg p-4 border border-red-100">
                      <p className="text-xs text-red-600 font-medium mb-1">Total TDS Deducted</p>
                      <p className="text-lg font-bold text-red-800" data-testid="summary-tds">
                        <Value v={tds} />
                      </p>
                    </div>
                    <div className="bg-teal-50 rounded-lg p-4 border border-teal-100">
                      <p className="text-xs text-teal-600 font-medium mb-1">Current Market Value</p>
                      <p className="text-lg font-bold text-teal-800" data-testid="summary-market-value">
                        <Value v={sumMarket} />
                      </p>
                    </div>
                    <div className={`rounded-lg p-4 border ${isGain ? 'bg-green-50 border-green-100' : 'bg-red-50 border-red-100'}`}>
                      <p className={`text-xs font-medium mb-1 ${isGain ? 'text-green-600' : 'text-red-600'}`}>Absolute Gain/Loss</p>
                      <p className={`text-lg font-bold ${isGain ? 'text-green-800' : 'text-red-800'}`} data-testid="summary-gain-loss">
                        {isGain ? '+' : '-'}<Value v={Math.abs(gainLoss)} prefix="₹" />
                      </p>
                    </div>
                  </div>
                );
              })()}

              {/* Portfolio Mix — Equity / Debt / Hybrid (iteration 37, per-client) */}
              {portfolioMix && (portfolioMix.grand_market || 0) > 0 && (
                <div className="mt-6 pt-6 border-t border-gray-200" data-testid="portfolio-mix-summary">
                  {(() => {
                    const fmtFull = (n) => `₹${(Number(n) || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
                    const fmtShort = (n) => {
                      const v = Number(n) || 0;
                      return Math.abs(v) >= 1e7
                        ? `₹${(v / 1e7).toFixed(2)} Cr`
                        : `₹${(v / 1e5).toFixed(2)} L`;
                    };
                    return (
                      <>
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                            Portfolio Mix — Equity / Debt / Hybrid
                          </h4>
                          <span className="text-[11px] text-gray-400 font-mono" data-testid="portfolio-mix-total">
                            <span className="hidden md:inline">{fmtFull(portfolioMix.grand_market || 0)} total</span>
                            <span className="md:hidden">{fmtShort(portfolioMix.grand_market || 0)} total</span>
                          </span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                          {['Equity', 'Debt', 'Hybrid'].map((cat) => {
                            const b = portfolioMix.overall?.[cat] || {};
                            const styles = {
                              Equity: { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', bar: 'bg-emerald-500' },
                              Debt:   { bg: 'bg-amber-50',   border: 'border-amber-200',   text: 'text-amber-700',   bar: 'bg-amber-500' },
                              Hybrid: { bg: 'bg-indigo-50',  border: 'border-indigo-200',  text: 'text-indigo-700',  bar: 'bg-indigo-500' },
                            }[cat];
                            const mkt = b.market_value ?? 0;
                            const pl = b.profit_loss ?? 0;
                            return (
                              <div key={cat} className={`${styles.bg} ${styles.border} border rounded-lg p-3`} data-testid={`portfolio-mix-client-${cat.toLowerCase()}`}>
                                <div className="flex items-center justify-between mb-2">
                                  <span className={`text-xs font-semibold ${styles.text}`}>{cat}</span>
                                  <span className={`text-[10px] font-mono ${styles.text}`}>{(b.share_pct ?? 0).toFixed(1)}%</span>
                                </div>
                                <div className="text-sm font-bold text-gray-800 mb-1" data-testid={`portfolio-mix-market-${cat.toLowerCase()}`}>
                                  <span className="hidden md:inline">{fmtFull(mkt)}</span>
                                  <span className="md:hidden">{fmtShort(mkt)}</span>
                                </div>
                                <div className={`text-[11px] font-mono ${pl >= 0 ? 'text-emerald-700' : 'text-red-700'}`} data-testid={`portfolio-mix-gain-${cat.toLowerCase()}`}>
                                  {pl >= 0 ? '+' : '-'}
                                  <span className="hidden md:inline">{fmtFull(Math.abs(pl))}</span>
                                  <span className="md:hidden">{fmtShort(Math.abs(pl))}</span>
                                  {' '}({(b.gain_pct ?? 0).toFixed(1)}%)
                                </div>
                                <div className="text-[10px] text-gray-500 mt-1">{b.holdings_count || 0} holdings</div>
                                <div className="mt-2 h-1 bg-white rounded-full overflow-hidden">
                                  <div className={`${styles.bar} h-full`} style={{ width: `${b.share_pct ?? 0}%` }} />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </>
                    );
                  })()}
                </div>
              )}
            </CardContent>
          </Card>

          {/* XIRR BASIS HOLDING PERIOD */}
          <Card className="border-gray-200">
            <CardContent className="p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5 text-teal-600" />
                XIRR BASIS HOLDING PERIOD
              </h3>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="font-semibold">Holding Period</TableHead>
                      <TableHead className="font-semibold text-right">No. of Schemes</TableHead>
                      <TableHead className="font-semibold text-right">Transactions</TableHead>
                      <TableHead className="font-semibold text-right">Cost Value (INR)</TableHead>
                      <TableHead className="font-semibold text-right">Current Market Value (INR)</TableHead>
                      <TableHead className="font-semibold text-right">Gain/Loss (INR)</TableHead>
                      <TableHead className="font-semibold text-right">Gain/Loss %</TableHead>
                      <TableHead className="font-semibold text-right">Avg XIRR %</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(() => {
                      // Group Active Units transactions by holding period — bucket each
                      // TRANSACTION by its own purchase date (not the scheme's earliest).
                      // This matches the dashboard XIRR-by-holding logic and ensures a
                      // SIP that started 3 yrs ago still attributes its recent
                      // installments to "1-2 Years" / "<1 Year".
                      const now = new Date();
                      const periods = {
                        '5+ Years': { minMonths: 60, schemeSet: new Set(), txnCount: 0, cost: 0, market: 0, xirrs: [] },
                        '3-5 Years': { minMonths: 36, maxMonths: 60, schemeSet: new Set(), txnCount: 0, cost: 0, market: 0, xirrs: [] },
                        '2-3 Years': { minMonths: 24, maxMonths: 36, schemeSet: new Set(), txnCount: 0, cost: 0, market: 0, xirrs: [] },
                        '1-2 Years': { minMonths: 12, maxMonths: 24, schemeSet: new Set(), txnCount: 0, cost: 0, market: 0, xirrs: [] },
                        'Less than 1 Year': { maxMonths: 12, schemeSet: new Set(), txnCount: 0, cost: 0, market: 0, xirrs: [] },
                      };
                      
                      const parseDDMMYYYY = (ds) => {
                        if (!ds) return null;
                        const parts = ds.split('-');
                        if (parts.length === 3 && parts[0].length === 2) return new Date(parts[2], parts[1] - 1, parts[0]);
                        return new Date(ds);
                      };

                      const pickPeriod = (months) => {
                        for (const [period, config] of Object.entries(periods)) {
                          const minOk = !config.minMonths || months >= config.minMonths;
                          const maxOk = !config.maxMonths || months < config.maxMonths;
                          if (minOk && maxOk) return config;
                        }
                        return null;
                      };

                      // Iterate per TRANSACTION so each SIP installment lands in its
                      // own holding-period bucket — and also push that txn's cashflow
                      // into the bucket so the per-bucket Avg XIRR can be computed
                      // from cashflows that actually fall within the bucket (rather
                      // than attributing the whole scheme's XIRR by earliest date,
                      // which left 1-2yr & <1yr buckets perpetually empty for any
                      // scheme older than 2 years).
                      filteredActiveUnits.forEach(txn => {
                        const d = parseDDMMYYYY(txn.transaction_date || txn.trade_date);
                        let bucket;
                        if (!d || isNaN(d.getTime())) {
                          // Txns with unparseable trade_date used to be dropped
                          // here — which silently shaved ~₹453 off Jayashree's
                          // XIRR grand total on the Summary tab and made it
                          // disagree with Scheme Wise. Fall back to the safest
                          // bucket (`Less than 1 Year`) so the row stays in the
                          // grand total and Summary matches Scheme Wise.
                          bucket = periods['Less than 1 Year'];
                        } else {
                          const months = (now.getFullYear() - d.getFullYear()) * 12 + (now.getMonth() - d.getMonth());
                          bucket = pickPeriod(months);
                        }
                        if (!bucket) return;

                        const originalUnits = Math.abs(parseFloat(txn.units || txn.original_units || 0));
                        let remainingUnits;
                        if (txn.remaining_units != null && txn.remaining_units !== '') {
                          remainingUnits = Math.max(0, parseFloat(txn.remaining_units) || 0);
                        } else {
                          const sold = Math.abs(parseFloat(txn.units_sold || txn.sold_units || 0));
                          remainingUnits = Math.max(0, originalUnits - sold);
                        }
                        const stampDuty = parseFloat(txn.stamp_duty || 0);
                        const propStamp = originalUnits > 0 && stampDuty > 0
                          ? (remainingUnits / originalUnits) * stampDuty
                          : 0;
                        const costValue = remainingUnits * (parseFloat(txn.nav) || 0) + propStamp;
                        const currentNavObj = txn.current_nav;
                        const currentNav = typeof currentNavObj === 'object' ? (currentNavObj?.nav || 0) : (currentNavObj || txn.nav || 0);
                        const marketValue = remainingUnits * (parseFloat(currentNav) || 0);

                        bucket.schemeSet.add(`${txn.folio_no}:${txn.isin || txn.scheme_code}`);
                        bucket.txnCount += 1;
                        bucket.cost += costValue;
                        bucket.market += marketValue;

                        // Per-bucket cashflow: outflow at txn date, inflow accrues
                        // at the bucket level (added once after the loop).
                        if (costValue > 0) {
                          if (!bucket._cashflows) bucket._cashflows = [];
                          bucket._cashflows.push({ date: d, amount: -costValue });
                        }
                      });

                      // Compute per-bucket XIRR from the bucket-scoped cashflows
                      // using the same weighted-days approximation as
                      // `activeUnitsCostMap` (keeps the math identical).
                      Object.values(periods).forEach(b => {
                        const cfs = b._cashflows || [];
                        if (b.cost > 0 && b.market > 0 && cfs.length > 0) {
                          let weightedDays = 0, totalWeight = 0;
                          cfs.forEach(cf => {
                            if (cf.amount < 0) {
                              const days = (now - cf.date) / (1000 * 60 * 60 * 24);
                              weightedDays += days * Math.abs(cf.amount);
                              totalWeight += Math.abs(cf.amount);
                            }
                          });
                          const avgDays = totalWeight > 0 ? weightedDays / totalWeight : 0;
                          const years = avgDays / 365.25;
                          if (years > 0) {
                            const x = (Math.pow(b.market / b.cost, 1 / years) - 1) * 100;
                            if (!isNaN(x) && isFinite(x)) b.xirrs.push(x);
                          }
                        }
                      });

                      let grandTotalCost = 0, grandTotalMarket = 0, grandTotalHoldings = 0, grandTotalTxns = 0;
                      
                      return (
                        <>
                          {Object.entries(periods).map(([period, data]) => {
                            const schemeCount = data.schemeSet.size;
                            grandTotalCost += data.cost;
                            grandTotalMarket += data.market;
                            grandTotalHoldings += schemeCount;
                            grandTotalTxns += data.txnCount;
                            const gainLoss = data.market - data.cost;
                            const gainLossPct = data.cost > 0 ? (gainLoss / data.cost) * 100 : 0;
                            const avgXirr = data.xirrs.length > 0 ? data.xirrs.reduce((a, b) => a + b, 0) / data.xirrs.length : 0;
                            
                            return (
                              <TableRow key={period} className="hover:bg-gray-50">
                                <TableCell className="font-medium">{period}</TableCell>
                                <TableCell className="text-right font-mono">{schemeCount}</TableCell>
                                <TableCell className="text-right font-mono">{data.txnCount}</TableCell>
                                <TableCell className="text-right font-mono">₹{data.cost.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</TableCell>
                                <TableCell className="text-right font-mono">₹{data.market.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</TableCell>
                                <TableCell className={`text-right font-mono ${gainLoss >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                  {gainLoss >= 0 ? '+' : ''}₹{Math.abs(gainLoss).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                </TableCell>
                                <TableCell className={`text-right font-mono ${gainLossPct >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                  {gainLossPct >= 0 ? '+' : ''}{gainLossPct.toFixed(2)}%
                                </TableCell>
                                <TableCell className={`text-right font-mono ${avgXirr >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                  {avgXirr >= 0 ? '+' : ''}{avgXirr.toFixed(2)}%
                                </TableCell>
                              </TableRow>
                            );
                          })}
                          <TableRow className="bg-gray-100 font-semibold border-t-2">
                            <TableCell className="font-bold">GRAND TOTAL</TableCell>
                            <TableCell className="text-right font-mono font-bold">{grandTotalHoldings}</TableCell>
                            <TableCell className="text-right font-mono font-bold">{grandTotalTxns}</TableCell>
                            <TableCell className="text-right font-mono font-bold">₹{grandTotalCost.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</TableCell>
                            <TableCell className="text-right font-mono font-bold">₹{grandTotalMarket.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</TableCell>
                            <TableCell className={`text-right font-mono font-bold ${(grandTotalMarket - grandTotalCost) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {(grandTotalMarket - grandTotalCost) >= 0 ? '+' : ''}₹{Math.abs(grandTotalMarket - grandTotalCost).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                            </TableCell>
                            <TableCell className={`text-right font-mono font-bold ${grandTotalCost > 0 && ((grandTotalMarket - grandTotalCost) / grandTotalCost * 100) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {grandTotalCost > 0 ? `${((grandTotalMarket - grandTotalCost) / grandTotalCost * 100) >= 0 ? '+' : ''}${((grandTotalMarket - grandTotalCost) / grandTotalCost * 100).toFixed(2)}%` : '0.00%'}
                            </TableCell>
                            <TableCell className="text-right font-mono font-bold">-</TableCell>
                          </TableRow>
                        </>
                      );
                    })()}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          {/* AMC-WISE BREAKDOWN */}
          <Card className="border-gray-200">
            <CardContent className="p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <Building2 className="w-5 h-5 text-teal-600" />
                AMC-WISE BREAKDOWN
              </h3>
              <div className="overflow-x-auto">
                <TooltipProvider>
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="font-semibold" rowSpan={2}>Mutual Fund (AMC)</TableHead>
                      <TableHead className="font-semibold text-right" rowSpan={2}>Cost Value (INR)</TableHead>
                      <TableHead className="font-semibold text-right" rowSpan={2}>Market Value (INR)</TableHead>
                      <TableHead className="font-semibold text-center border-l border-gray-300" colSpan={5}>Holding Period (No. of Schemes)</TableHead>
                      <TableHead className="font-semibold text-right border-l border-gray-300" rowSpan={2}>Profit/Loss (INR)</TableHead>
                    </TableRow>
                    <TableRow className="bg-gray-100">
                      <TableHead className="font-medium text-center text-xs border-l border-gray-300">5+ Yrs</TableHead>
                      <TableHead className="font-medium text-center text-xs">3-5 Yrs</TableHead>
                      <TableHead className="font-medium text-center text-xs">2-3 Yrs</TableHead>
                      <TableHead className="font-medium text-center text-xs">1-2 Yrs</TableHead>
                      <TableHead className="font-medium text-center text-xs">&lt;1 Yr</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(() => {
                      const now = new Date();
                      
                      const parseDDMMYYYY = (ds) => {
                        if (!ds) return null;
                        const parts = ds.split('-');
                        if (parts.length === 3 && parts[0].length === 2) return new Date(parts[2], parts[1] - 1, parts[0]);
                        return new Date(ds);
                      };
                      
                      // Helper function to get holding period bucket from earliest transaction date
                      const getHoldingBucket = (txns) => {
                        let earliest = null;
                        txns.forEach(t => {
                          const d = parseDDMMYYYY(t.transaction_date || t.trade_date);
                          if (d && !isNaN(d.getTime()) && (!earliest || d < earliest)) earliest = d;
                        });
                        if (!earliest) return null;
                        const months = (now.getFullYear() - earliest.getFullYear()) * 12 + (now.getMonth() - earliest.getMonth());
                        if (months >= 60) return '5+';
                        if (months >= 36) return '3-5';
                        if (months >= 24) return '2-3';
                        if (months >= 12) return '1-2';
                        return '<1';
                      };
                      
                      // Resolve AMC name: prefer the authoritative AMC name as
                      // stored in scheme_master (e.g. "ICICI Prudential Asset
                      // Management Company"). We only strip legal-entity tails
                      // (Limited / Ltd / Pvt / LLP / Co. / Co), parenthetical
                      // location qualifiers, and expand "Co" → "Company". Common
                      // all-caps abbreviations (ICICI, DSP, ITI, SBI, HDFC, UTI,
                      // LIC, IIFL, JM, IDBI, AMC, NJ, PGIM, BNP) keep their
                      // casing — we don't title-case them.
                      const ALL_CAPS_TOKENS = new Set([
                        'ICICI','DSP','SBI','HDFC','UTI','ITI','LIC','IIFL','JM',
                        'IDBI','AMC','NJ','PGIM','BNP','HSBC','PPF','LIC',
                        '360','ASK','WOC','WHITE','OAK','TRUST','PNB','AMC','MF'
                      ]);
                      const smartCase = (raw) => {
                        return raw.split(/\s+/).map(tok => {
                          const up = tok.toUpperCase().replace(/[.,]/g,'');
                          if (ALL_CAPS_TOKENS.has(up)) return up;
                          // Preserve already-all-caps tokens >=3 chars (e.g. "BNP")
                          if (tok.length >= 2 && tok === tok.toUpperCase() && /^[A-Z0-9.&]+$/.test(tok)) return tok;
                          if (/^(of|and|the|de|la|le)$/i.test(tok)) return tok.toLowerCase();
                          return tok.charAt(0).toUpperCase() + tok.slice(1).toLowerCase();
                        }).join(' ');
                      };
                      const cleanAmcName = (raw) => {
                        if (!raw) return null;
                        let s = String(raw).trim();
                        // Drop parenthetical qualifiers e.g. "(India)" "(MF)"
                        s = s.replace(/\s*\([^)]*\)/g, ' ');
                        // Drop trailing legal-entity suffixes (multi-pass so chains
                        // like "Pvt. Ltd" → "Pvt." → "" both strip)
                        for (let i = 0; i < 4; i++) {
                          const before = s;
                          s = s.replace(/[\s.,]+(limited|ltd|pvt|private|llp)\.?$/i, '');
                          if (s === before) break;
                        }
                        // Expand the common short form "Co." or "Co" at end → "Company"
                        s = s.replace(/[\s.,]+co\.?$/i, ' Company');
                        s = s.replace(/\s+/g, ' ').trim();
                        if (!s) return null;
                        return smartCase(s);
                      };
                      const getAMC = (txn) => {
                        if (!txn) return 'Unknown AMC';
                        // 1) ISIN
                        const isin = (txn.isin || '').toString().toUpperCase().trim();
                        if (isin && amcLookup.by_isin?.[isin]) {
                          const c = cleanAmcName(amcLookup.by_isin[isin]);
                          if (c) return c;
                        }
                        // 2) AMFI code
                        const amfi = (txn.amfi_code || '').toString().trim();
                        if (amfi && amcLookup.by_amfi_code?.[amfi]) {
                          const c = cleanAmcName(amcLookup.by_amfi_code[amfi]);
                          if (c) return c;
                        }
                        // 3) Product code (CAMS scheme_code / Karvy channel_partner_code)
                        const pc = (txn.product_code || txn.scheme_code || '').toString().trim();
                        if (pc && amcLookup.by_product_code?.[pc]) {
                          const c = cleanAmcName(amcLookup.by_product_code[pc]);
                          if (c) return c;
                        }
                        // 4) Scheme name → AMC (normalised lookup; preserves
                        //    canonical AMC for txns missing all codes)
                        const schemeKey = (txn.scheme_name || '')
                          .toString().toUpperCase().trim().replace(/\s+/g, ' ');
                        if (schemeKey && amcLookup.by_scheme_name?.[schemeKey]) {
                          const c = cleanAmcName(amcLookup.by_scheme_name[schemeKey]);
                          if (c) return c;
                        }
                        // 5) Fallback — match the scheme name against a
                        //    canonical AMC keyword map. The previous version
                        //    sliced the first 2 tokens of the scheme name and
                        //    surfaced gibberish AMC names like "Tata Arbitrage",
                        //    "Axis Liquid", "Bank of", "ICICI Prudential"
                        //    (which were really scheme-name prefixes, not AMCs).
                        //    Mirror the backend `_AMC_CANONICAL` map so the
                        //    Dashboard and Portfolio breakdowns tie out.
                        const schemeName = (txn.scheme_name || '').toString();
                        if (!schemeName) return 'Unknown AMC';
                        const lc = schemeName.toLowerCase();
                        const AMC_KEYS = [
                          // brand prefix (lower-cased) → canonical display
                          ['aditya birla sun life', 'Aditya Birla Sun Life Mutual Fund'],
                          ['aditya birla', 'Aditya Birla Sun Life Mutual Fund'],
                          ['icici prudential', 'ICICI Prudential Mutual Fund'],
                          ['icici', 'ICICI Prudential Mutual Fund'],
                          ['hdfc', 'HDFC Mutual Fund'],
                          ['sbi', 'SBI Mutual Fund'],
                          ['uti', 'UTI Mutual Fund'],
                          ['kotak mahindra', 'Kotak Mahindra Mutual Fund'],
                          ['kotak', 'Kotak Mahindra Mutual Fund'],
                          ['axis', 'Axis Mutual Fund'],
                          ['nippon india', 'Nippon India Mutual Fund'],
                          ['nippon', 'Nippon India Mutual Fund'],
                          ['dsp', 'DSP Mutual Fund'],
                          ['mirae asset', 'Mirae Asset Mutual Fund'],
                          ['mirae', 'Mirae Asset Mutual Fund'],
                          ['tata', 'Tata Mutual Fund'],
                          ['franklin templeton', 'Franklin Templeton Mutual Fund'],
                          ['franklin', 'Franklin Templeton Mutual Fund'],
                          ['invesco', 'Invesco Mutual Fund'],
                          ['edelweiss', 'Edelweiss Mutual Fund'],
                          ['parag parikh', 'PPFAS Mutual Fund'],
                          ['ppfas', 'PPFAS Mutual Fund'],
                          ['samco', 'Samco Mutual Fund'],
                          ['hsbc', 'HSBC Mutual Fund'],
                          ['sundaram', 'Sundaram Mutual Fund'],
                          ['baroda bnp paribas', 'Baroda BNP Paribas Mutual Fund'],
                          ['baroda', 'Baroda BNP Paribas Mutual Fund'],
                          ['motilal oswal', 'Motilal Oswal Mutual Fund'],
                          ['quant ', 'quant Mutual Fund'],
                          ['quantum', 'Quantum Mutual Fund'],
                          ['pgim india', 'PGIM India Mutual Fund'],
                          ['pgim', 'PGIM India Mutual Fund'],
                          ['union', 'Union Mutual Fund'],
                          ['jm financial', 'JM Financial Mutual Fund'],
                          ['whiteoak capital', 'WhiteOak Capital Mutual Fund'],
                          ['whiteoak', 'WhiteOak Capital Mutual Fund'],
                          ['bajaj finserv', 'Bajaj Finserv Mutual Fund'],
                          ['bandhan', 'Bandhan Mutual Fund'],
                          ['idfc', 'Bandhan Mutual Fund'],
                          ['groww', 'Groww Mutual Fund'],
                          ['navi', 'Navi Mutual Fund'],
                          ['trust ', 'TRUST Mutual Fund'],
                          ['shriram', 'Shriram Mutual Fund'],
                          ['canara robeco', 'Canara Robeco Mutual Fund'],
                          ['canara', 'Canara Robeco Mutual Fund'],
                          ['lic ', 'LIC Mutual Fund'],
                          ['l&t ', 'L&T Mutual Fund'],
                          ['l & t ', 'L&T Mutual Fund'],
                          ['mahindra manulife', 'Mahindra Manulife Mutual Fund'],
                          ['mahindra', 'Mahindra Manulife Mutual Fund'],
                          ['iti ', 'ITI Mutual Fund'],
                          ['360 one', '360 ONE Mutual Fund'],
                          ['iifl', 'IIFL Mutual Fund'],
                          ['bank of india', 'Bank of India Mutual Fund'],
                          ['boi axa', 'Bank of India Mutual Fund'],
                          ['taurus', 'Taurus Mutual Fund'],
                          ['indiabulls', 'Indiabulls Mutual Fund'],
                          ['mahindra manulife', 'Mahindra Manulife Mutual Fund'],
                          ['principal', 'Principal Mutual Fund'],
                          ['old bridge', 'Old Bridge Mutual Fund'],
                          ['helios', 'Helios Mutual Fund'],
                          ['zerodha', 'Zerodha Mutual Fund'],
                          ['ng asset', 'NG Asset Management Mutual Fund'],
                          ['ng ', 'NG Asset Management Mutual Fund'],
                        ];
                        for (const [key, label] of AMC_KEYS) {
                          if (lc.startsWith(key) || lc.includes(' ' + key)) return label;
                        }
                        return 'Unknown AMC';
                      };
                      
                      // Group by AMC using Active Units data
                      const amcData = {};
                      Object.entries(activeUnitsCostMap).forEach(([key, entry]) => {
                        if (entry.market <= 0) return;
                        const firstTxn = entry.transactions[0];
                        const amc = getAMC(firstTxn);
                        if (!amcData[amc]) {
                          amcData[amc] = { 
                            cost: 0, market: 0, 
                            periods: { 
                              '5+': { count: 0, schemes: [] }, 
                              '3-5': { count: 0, schemes: [] }, 
                              '2-3': { count: 0, schemes: [] }, 
                              '1-2': { count: 0, schemes: [] }, 
                              '<1': { count: 0, schemes: [] } 
                            }
                          };
                        }
                        amcData[amc].cost += entry.cost || 0;
                        amcData[amc].market += entry.market || 0;
                        
                        const bucket = getHoldingBucket(entry.transactions);
                        if (bucket) {
                          amcData[amc].periods[bucket].count++;
                          amcData[amc].periods[bucket].schemes.push(firstTxn?.scheme_name || 'Unknown Scheme');
                        }
                      });
                      
                      const sortedAMCs = Object.entries(amcData).sort((a, b) => b[1].market - a[1].market);
                      let totalCost = 0, totalMarket = 0;
                      let totalPeriods = { '5+': 0, '3-5': 0, '2-3': 0, '1-2': 0, '<1': 0 };
                      
                      return (
                        <>
                          {sortedAMCs.map(([amc, data]) => {
                            totalCost += data.cost;
                            totalMarket += data.market;
                            Object.keys(data.periods).forEach(p => {
                              totalPeriods[p] += data.periods[p].count;
                            });
                            const profitLoss = data.market - data.cost;
                            
                            return (
                              <TableRow key={amc} className="hover:bg-gray-50">
                                <TableCell className="font-medium">{amc}</TableCell>
                                <TableCell className="text-right font-mono">₹{data.cost.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</TableCell>
                                <TableCell className="text-right font-mono">₹{data.market.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</TableCell>
                                <TableCell className="text-center font-mono border-l border-gray-200">
                                  {data.periods['5+'].count > 0 ? (
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <span className="cursor-help underline decoration-dotted">{data.periods['5+'].count}</span>
                                      </TooltipTrigger>
                                      <TooltipContent side="bottom" className="bg-gray-900 text-white p-3 max-w-sm">
                                        <p className="font-semibold mb-1 text-teal-300">5+ Years Schemes:</p>
                                        <ul className="text-xs space-y-1">
                                          {data.periods['5+'].schemes.map((s, i) => <li key={i}>• {s}</li>)}
                                        </ul>
                                      </TooltipContent>
                                    </Tooltip>
                                  ) : '-'}
                                </TableCell>
                                <TableCell className="text-center font-mono">
                                  {data.periods['3-5'].count > 0 ? (
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <span className="cursor-help underline decoration-dotted">{data.periods['3-5'].count}</span>
                                      </TooltipTrigger>
                                      <TooltipContent side="bottom" className="bg-gray-900 text-white p-3 max-w-sm">
                                        <p className="font-semibold mb-1 text-teal-300">3-5 Years Schemes:</p>
                                        <ul className="text-xs space-y-1">
                                          {data.periods['3-5'].schemes.map((s, i) => <li key={i}>• {s}</li>)}
                                        </ul>
                                      </TooltipContent>
                                    </Tooltip>
                                  ) : '-'}
                                </TableCell>
                                <TableCell className="text-center font-mono">
                                  {data.periods['2-3'].count > 0 ? (
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <span className="cursor-help underline decoration-dotted">{data.periods['2-3'].count}</span>
                                      </TooltipTrigger>
                                      <TooltipContent side="bottom" className="bg-gray-900 text-white p-3 max-w-sm">
                                        <p className="font-semibold mb-1 text-teal-300">2-3 Years Schemes:</p>
                                        <ul className="text-xs space-y-1">
                                          {data.periods['2-3'].schemes.map((s, i) => <li key={i}>• {s}</li>)}
                                        </ul>
                                      </TooltipContent>
                                    </Tooltip>
                                  ) : '-'}
                                </TableCell>
                                <TableCell className="text-center font-mono">
                                  {data.periods['1-2'].count > 0 ? (
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <span className="cursor-help underline decoration-dotted">{data.periods['1-2'].count}</span>
                                      </TooltipTrigger>
                                      <TooltipContent side="bottom" className="bg-gray-900 text-white p-3 max-w-sm">
                                        <p className="font-semibold mb-1 text-teal-300">1-2 Years Schemes:</p>
                                        <ul className="text-xs space-y-1">
                                          {data.periods['1-2'].schemes.map((s, i) => <li key={i}>• {s}</li>)}
                                        </ul>
                                      </TooltipContent>
                                    </Tooltip>
                                  ) : '-'}
                                </TableCell>
                                <TableCell className="text-center font-mono">
                                  {data.periods['<1'].count > 0 ? (
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <span className="cursor-help underline decoration-dotted">{data.periods['<1'].count}</span>
                                      </TooltipTrigger>
                                      <TooltipContent side="bottom" className="bg-gray-900 text-white p-3 max-w-sm">
                                        <p className="font-semibold mb-1 text-teal-300">&lt;1 Year Schemes:</p>
                                        <ul className="text-xs space-y-1">
                                          {data.periods['<1'].schemes.map((s, i) => <li key={i}>• {s}</li>)}
                                        </ul>
                                      </TooltipContent>
                                    </Tooltip>
                                  ) : '-'}
                                </TableCell>
                                <TableCell className={`text-right font-mono font-medium border-l border-gray-200 ${profitLoss >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                  {profitLoss >= 0 ? '+' : ''}₹{Math.abs(profitLoss).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                          <TableRow className="bg-gray-100 font-semibold border-t-2">
                            <TableCell className="font-bold">Total</TableCell>
                            <TableCell className="text-right font-mono font-bold">₹{totalCost.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</TableCell>
                            <TableCell className="text-right font-mono font-bold">₹{totalMarket.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</TableCell>
                            <TableCell className="text-center font-mono font-bold border-l border-gray-200">{totalPeriods['5+']}</TableCell>
                            <TableCell className="text-center font-mono font-bold">{totalPeriods['3-5']}</TableCell>
                            <TableCell className="text-center font-mono font-bold">{totalPeriods['2-3']}</TableCell>
                            <TableCell className="text-center font-mono font-bold">{totalPeriods['1-2']}</TableCell>
                            <TableCell className="text-center font-mono font-bold">{totalPeriods['<1']}</TableCell>
                            <TableCell className={`text-right font-mono font-bold border-l border-gray-200 ${(totalMarket - totalCost) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {(totalMarket - totalCost) >= 0 ? '+' : ''}₹{Math.abs(totalMarket - totalCost).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                            </TableCell>
                          </TableRow>
                        </>
                      );
                    })()}
                  </TableBody>
                </Table>
                </TooltipProvider>
              </div>
            </CardContent>
          </Card>

          {/* Sections C, F, D, E — extracted to /pages/Portfolio/summary/*.jsx */}
          <CashflowStatusCard
            allTransactions={allTransactions}
            filteredActiveUnits={filteredActiveUnits}
            activeUnitsCostMap={activeUnitsCostMap}
            holdingsFilteredByPattern={holdingsFilteredByPattern}
          />
          <SubCategoryAllocationCard filteredActiveUnits={filteredActiveUnits} />
          <FamilySipOverviewCard
            allTransactions={allTransactions}
            familyContext={familyContext}
            familyAggregate={familyAggregate}
            panToClientName={panToClientName}
          />
          <FamilyAllocationCard
            filteredActiveUnits={filteredActiveUnits}
            familyContext={familyContext}
            familyAggregate={familyAggregate}
            panToClientName={panToClientName}
          />
        </div>
      )}
      {/* Tab Content (Holdings Table renders for summary + scheme_breakdown via default else branch) */}
      {activeTab === "transaction_details" ? (
        /* Transaction Details Tab - ALL transactions from MFSD201 */
        <Card className="border-gray-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                  All Transactions
                  {unitReconciliation && (
                    unitReconciliation.is_reconciled ? (
                      <Badge className="bg-green-100 text-green-700 border-green-300">
                        Units Reconciled ({unitReconciliation.matched_count} schemes)
                      </Badge>
                    ) : (
                      <Badge className="bg-amber-100 text-amber-700 border-amber-300">
                        {unitReconciliation.mismatched_count} Mismatches
                      </Badge>
                    )
                  )}
                </h3>
                <p className="text-sm text-gray-500">Complete transaction history from MFSD201 data</p>
              </div>
              <div className="flex items-center gap-2">
                <Select value={selectedFolioForTxn || "all"} onValueChange={(v) => { setSelectedFolioForTxn(v === "all" ? "" : v); fetchTransactions(1, v === "all" ? "" : v); }}>
                  <SelectTrigger className="w-[350px]">
                    <SelectValue placeholder="All Folios" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    <SelectItem value="all">All Folios</SelectItem>
                    {folioOptions.map(folio => (
                      <SelectItem key={folio.folio_no} value={folio.folio_no}>
                        <div className="flex flex-col">
                          <span className="font-mono font-medium">{folio.folio_no}</span>
                          <span className="text-sm text-gray-500 truncate max-w-[300px]">
                            {folio.schemes.slice(0, 2).join(', ')}{folio.schemes.length > 2 ? ` +${folio.schemes.length - 2} more` : ''}
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Badge variant="outline" className="text-teal-600 border-teal-300 bg-teal-50">
                  {transactionTotal.toLocaleString()} transactions
                </Badge>
              </div>
            </div>
            
            {transactionsLoading ? (
              <div className="flex items-center justify-center py-16">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600" />
              </div>
            ) : transactions.length === 0 ? (
              <div className="text-center py-16 text-gray-500">
                <Receipt className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p className="font-medium text-gray-600">No transactions found</p>
                <p className="text-sm mt-1">Transaction data from MFSD201 will appear here once imported.</p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50/50">
                        <TableHead className="text-sm font-medium text-gray-500">Date</TableHead>
                        <TableHead className="text-sm font-medium text-gray-500">Folio No</TableHead>
                        <TableHead className="text-sm font-medium text-gray-500">Scheme</TableHead>
                        <TableHead className="text-sm font-medium text-gray-500">Type</TableHead>
                        <TableHead className="text-sm font-medium text-gray-500 text-right">Units</TableHead>
                        <TableHead className="text-sm font-medium text-gray-500 text-right">NAV</TableHead>
                        <TableHead className="text-sm font-medium text-gray-500 text-right">Amount</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {transactions.map((txn, index) => (
                        <TableRow key={index} className="hover:bg-gray-50/50">
                          <TableCell className="py-2 text-sm">
                            {txn.transaction_date ? (() => {
                              // Handle various date formats
                              const dateStr = txn.transaction_date;
                              // Format: YYYYMMDD (e.g., 20260305)
                              if (/^\d{8}$/.test(dateStr)) {
                                const year = dateStr.substring(0, 4);
                                const month = dateStr.substring(4, 6);
                                const day = dateStr.substring(6, 8);
                                return `${day}-${month}-${year}`;
                              }
                              // Format: ISO date with T or -
                              if (dateStr.includes('T') || dateStr.includes('-')) {
                                const date = new Date(dateStr);
                                const day = String(date.getDate()).padStart(2, '0');
                                const month = String(date.getMonth() + 1).padStart(2, '0');
                                const year = date.getFullYear();
                                return `${day}-${month}-${year}`;
                              }
                              return dateStr;
                            })() : '-'}
                          </TableCell>
                          <TableCell className="py-2 text-sm font-mono">{txn.folio_no || '-'}</TableCell>
                          <TableCell className="py-2 text-sm max-w-[250px] truncate" title={toTitleCase(txn.scheme_name) || txn.scheme_name}>
                            {toTitleCase(txn.scheme_name) || '-'}
                          </TableCell>
                          <TableCell className="py-2">
                            <Badge variant="outline" className={`text-sm ${
                              txn.purred === 'P' || txn.transaction_desc?.toLowerCase().includes('purchase') || txn.transaction_desc?.toLowerCase().includes('sip') 
                                ? 'bg-green-50 text-green-700 border-green-200' 
                              : txn.purred === 'R' || txn.transaction_desc?.toLowerCase().includes('redemption') || txn.transaction_desc?.toLowerCase().includes('switch out')
                                ? 'bg-red-50 text-red-700 border-red-200'
                              : txn.transaction_desc?.toLowerCase().includes('switch in') 
                                ? 'bg-blue-50 text-blue-700 border-blue-200'
                              : 'bg-gray-50 text-gray-700'
                            }`}>
                              {txn.transaction_desc || txn.transaction_type || '-'}
                            </Badge>
                          </TableCell>
                          <TableCell className={`py-2 text-sm text-right font-mono ${
                            txn.purred === 'P' ? 'text-green-600' : txn.purred === 'R' ? 'text-red-600' : ''
                          }`}>
                            {txn.purred === 'R' ? '-' : ''}{txn.units?.toLocaleString('en-IN', { minimumFractionDigits: 3, maximumFractionDigits: 3 }) || '-'}
                          </TableCell>
                          <TableCell className="py-2 text-sm text-right font-mono">
                            {txn.nav?.toLocaleString('en-IN', { minimumFractionDigits: 4, maximumFractionDigits: 4 }) || '-'}
                          </TableCell>
                          <TableCell className={`py-2 text-sm text-right font-mono font-medium ${
                            txn.purred === 'P' ? 'text-green-600' : txn.purred === 'R' ? 'text-red-600' : ''
                          }`}>
                            {txn.purred === 'R' ? '-' : ''}₹{txn.amount?.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                
                {/* Pagination */}
                {transactionTotalPages > 1 && (
                  <div className="flex items-center justify-between mt-4 px-2">
                    <p className="text-sm text-gray-500">
                      Page {transactionPage} of {transactionTotalPages}
                    </p>
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        disabled={transactionPage === 1}
                        onClick={() => fetchTransactions(transactionPage - 1, selectedFolioForTxn)}
                      >
                        Previous
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        disabled={transactionPage === transactionTotalPages}
                        onClick={() => fetchTransactions(transactionPage + 1, selectedFolioForTxn)}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      ) : activeTab === "unit_mismatch" ? (
        /* Unit Mismatch Tab - Compares Active Units (transactions) with Scheme Wise Breakdown (holdings) */
        <Card className="border-gray-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-800">Unit Reconciliation</h3>
                <p className="text-sm text-gray-500">Comparing Active Units (from Transactions) with Scheme Wise Breakdown (from Holdings)</p>
              </div>
              <div className="flex items-center gap-2">
                {computedUnitMismatch && (
                  <>
                    <Badge className="bg-green-100 text-green-700">
                      {computedUnitMismatch.matched_count} Matched
                    </Badge>
                    {computedUnitMismatch.mismatched_count > 0 && (
                      <Badge className="bg-amber-100 text-amber-700">
                        {computedUnitMismatch.mismatched_count} Mismatched
                      </Badge>
                    )}
                  </>
                )}
                <Button variant="outline" size="sm" className="h-8 gap-1.5"
                  onClick={() => handleExportTabExcel("unit_mismatch")}
                  data-testid="download-unit-mismatch-xlsx">
                  <Download className="w-3.5 h-3.5" />
                  Download
                </Button>
              </div>
            </div>
            
            {!computedUnitMismatch ? (
              <div className="flex items-center justify-center py-16">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600" />
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <UnitMismatchSortableHeader field="folio_no">Folio No</UnitMismatchSortableHeader>
                      {showFamilyClientCol && (
                        <TableHead className="text-xs">Client</TableHead>
                      )}
                      <UnitMismatchSortableHeader field="scheme_name">Scheme Name</UnitMismatchSortableHeader>
                      <UnitMismatchSortableHeader field="source">Source</UnitMismatchSortableHeader>
                      <UnitMismatchSortableHeader field="holding_units" className="text-right bg-blue-50">Holdings (Scheme Wise)</UnitMismatchSortableHeader>
                      <UnitMismatchSortableHeader field="active_units" className="text-right bg-purple-50">Active Units (Txn)</UnitMismatchSortableHeader>
                      <UnitMismatchSortableHeader field="difference" className="text-center">Difference</UnitMismatchSortableHeader>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {[...computedUnitMismatch.matched, ...computedUnitMismatch.mismatched]
                      .sort((a, b) => {
                        if (!unitMismatchSortConfig.key) return 0;
                        const key = unitMismatchSortConfig.key;
                        let aVal = a[key];
                        let bVal = b[key];
                        
                        // Numeric fields
                        if (["holding_units", "active_units", "difference"].includes(key)) {
                          aVal = parseFloat(aVal) || 0;
                          bVal = parseFloat(bVal) || 0;
                        } else {
                          aVal = (aVal || "").toString().toLowerCase();
                          bVal = (bVal || "").toString().toLowerCase();
                        }
                        
                        if (aVal < bVal) return unitMismatchSortConfig.direction === "asc" ? -1 : 1;
                        if (aVal > bVal) return unitMismatchSortConfig.direction === "asc" ? 1 : -1;
                        return 0;
                      })
                      .map((item, index) => {
                      const isMatched = Math.abs(item.difference) < 0.01;
                      return (
                        <TableRow key={`${item.folio_no}-${item.isin}-${index}`} className={isMatched ? "hover:bg-green-50/50" : "hover:bg-amber-50/50"}>
                          <TableCell className="py-2 text-sm font-mono">{item.folio_no}</TableCell>
                          {showFamilyClientCol && (
                            <TableCell className="py-2 text-xs text-gray-700 whitespace-nowrap">
                              {resolveRowClientName(item)}
                            </TableCell>
                          )}
                          <TableCell className="py-2 text-sm max-w-xs truncate" title={item.scheme_name}>
                            {toTitleCase(item.scheme_name) || item.scheme_code || item.isin}
                          </TableCell>
                          <TableCell className="py-2 text-xs">
                            <span className={`px-2 py-0.5 rounded ${item.source === 'KARVY' ? 'bg-orange-100 text-orange-700' : item.source === 'CAMS' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>
                              {item.source || '-'}
                            </span>
                          </TableCell>
                          <TableCell className="py-2 text-sm text-right font-mono text-blue-600 bg-blue-50/30">
                            {item.holding_units?.toLocaleString('en-IN', { minimumFractionDigits: 4 })}
                          </TableCell>
                          <TableCell className="py-2 text-sm text-right font-mono text-purple-600 bg-purple-50/30">
                            {item.active_units?.toLocaleString('en-IN', { minimumFractionDigits: 4 })}
                          </TableCell>
                          <TableCell className="py-2 text-sm text-center">
                            {isMatched ? (
                              <span className="inline-flex items-center justify-center w-6 h-6 bg-green-100 rounded-full">
                                <Check className="w-4 h-4 text-green-600" />
                              </span>
                            ) : (
                              <span className={`font-mono font-bold ${item.difference >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {item.difference >= 0 ? '+' : ''}{item.difference?.toLocaleString('en-IN', { minimumFractionDigits: 4 })}
                              </span>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      ) : activeTab === "junk_units" ? (
        /* Non Financial Tab - Shows all non-financial transactions from transaction_master */
        <Card className="border-gray-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-base font-semibold text-gray-800">Non Financial Transactions</h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  Transactions classified as non-financial (pledging, reversals, dividends, etc.)
                </p>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  placeholder="Search folio, scheme, flag..."
                  className="h-8 w-56 rounded-md border border-gray-300 px-3 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400"
                  value={allTransactionsSearch}
                  onChange={(e) => { setAllTransactionsSearch(e.target.value); setAllTxnPage(1); }}
                  data-testid="nf-txn-search"
                />
                <Button variant="outline" size="sm" className="h-8 gap-1.5"
                  onClick={() => handleExportTabExcel("junk_units")}
                  data-testid="download-junk-units-xlsx">
                  <Download className="w-3.5 h-3.5" />
                  Download
                </Button>
              </div>
            </div>
            {allTransactionsLoading ? (
              <div className="flex items-center justify-center py-10 text-gray-500 text-sm">Loading transactions...</div>
            ) : (() => {
              let nfTxns = allTransactions.filter(t => t.reflecting_in === "Non Financial");
              if (selectedSubAccount !== "all" && filteredFolioNos.length > 0) {
                nfTxns = nfTxns.filter(t => filteredFolioNos.includes(t.folio_no));
              }
              if (allTransactionsSearch.trim()) {
                const q = allTransactionsSearch.toLowerCase();
                nfTxns = nfTxns.filter(t =>
                  (t.folio_no || "").toLowerCase().includes(q) ||
                  (t.scheme_name || "").toLowerCase().includes(q) ||
                  (t.flag || "").toLowerCase().includes(q) ||
                  (t.txn_type || "").toLowerCase().includes(q) ||
                  (t.txn_no || "").toLowerCase().includes(q)
                );
              }
              const sorted = [...nfTxns].sort((a, b) => {
                try {
                  const da = parseDateStr(a.trade_date);
                  const db = parseDateStr(b.trade_date);
                  if (da !== db) return db - da;
                  return (a.scheme_name || "").localeCompare(b.scheme_name || "");
                } catch { return 0; }
              });

              if (sorted.length === 0) {
                return (
                  <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                    <Trash2 className="w-12 h-12 mb-3 text-gray-300" />
                    <p className="text-sm font-medium text-gray-500">No non-financial transactions found</p>
                  </div>
                );
              }

              const nfTotalPages = Math.ceil(sorted.length / ALL_TXN_PAGE_SIZE);
              const nfPage = Math.min(allTxnPage, nfTotalPages);
              const nfRows = sorted.slice((nfPage - 1) * ALL_TXN_PAGE_SIZE, nfPage * ALL_TXN_PAGE_SIZE);

              return (
                <>
                <p className="text-xs text-gray-500 font-medium mb-2">{sorted.length} non-financial transactions</p>
                <div className="max-h-[600px] overflow-auto relative border rounded-md">
                  <table className="w-full caption-bottom text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-xs font-semibold bg-gray-50 sticky top-0 z-10 px-3 py-2 text-left whitespace-nowrap">Sl No</th>
                        <th className="text-xs font-semibold bg-gray-50 sticky top-0 z-10 px-3 py-2 text-left">Folio No</th>
                        {showFamilyClientCol && (
                          <th className="text-xs font-semibold bg-gray-50 sticky top-0 z-10 px-3 py-2 text-left whitespace-nowrap">Client</th>
                        )}
                        <th className="text-xs font-semibold bg-gray-50 sticky top-0 z-10 px-3 py-2 text-left">Trade Date</th>
                        <th className="text-xs font-semibold bg-gray-50 sticky top-0 z-10 px-3 py-2 text-left">Description</th>
                        <th className="text-xs font-semibold bg-gray-50 sticky top-0 z-10 px-3 py-2 text-left">Scheme Name</th>
                        <th className="text-xs font-semibold bg-gray-50 sticky top-0 z-10 px-3 py-2 text-right">Units</th>
                        <th className="text-xs font-semibold bg-gray-50 sticky top-0 z-10 px-3 py-2 text-right">NAV</th>
                        <th className="text-xs font-semibold bg-gray-50 sticky top-0 z-10 px-3 py-2 text-right">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {nfRows.map((txn, idx) => {
                        const units = parseFloat(txn.units) || 0;
                        const tooltipParts = [txn.scheme_name || ""];
                        if (txn.source) tooltipParts.push(`Source: ${txn.source}`);
                        if (txn.product_code) tooltipParts.push(`Product Code: ${txn.product_code}`);
                        if (txn.isin) tooltipParts.push(`ISIN: ${txn.isin}`);
                        if (txn.amfi_code) tooltipParts.push(`AMFI Code: ${txn.amfi_code}`);
                        if (txn.remarks) tooltipParts.push(`Remarks: ${txn.remarks}`);
                        const dateTooltip = [txn.trade_date || "", txn.txn_no ? `Txn No: ${txn.txn_no}` : "", txn.application_no ? `Application No: ${txn.application_no}` : ""].filter(Boolean).join("\n");
                        const amt = parseFloat(txn.amount) || 0;
                        const tds = parseFloat(txn.tds) || 0;
                        const sd = parseFloat(txn.stamp_duty) || 0;
                        const stt = parseFloat(txn.stt) || 0;
                        const total = amt + tds + sd + stt;
                        const amtParts = [`Amount: ${amt.toLocaleString("en-IN", {minimumFractionDigits: 2, maximumFractionDigits: 2})}`];
                        if (tds) amtParts.push(`TDS: ${tds.toFixed(2)}`);
                        if (sd) amtParts.push(`Stamp Duty: ${sd.toFixed(2)}`);
                        if (stt) amtParts.push(`STT: ${stt.toFixed(2)}`);
                        if (tds || sd || stt) amtParts.push(`Total: ${total.toLocaleString("en-IN", {minimumFractionDigits: 2, maximumFractionDigits: 2})}`);
                        return (
                          <tr key={`nf-${idx}`} className="hover:bg-gray-50 border-b">
                            <td className="py-1.5 px-3 text-xs text-gray-400">{(nfPage - 1) * ALL_TXN_PAGE_SIZE + idx + 1}</td>
                            <td className="py-1.5 px-3 text-xs font-mono">{txn.folio_no}</td>
                            {showFamilyClientCol && (
                              <td className="py-1.5 px-3 text-xs text-gray-700 whitespace-nowrap">{resolveRowClientName(txn)}</td>
                            )}
                            <td className="py-1.5 px-3 text-xs font-mono whitespace-nowrap cursor-help" title={dateTooltip}>{txn.trade_date || "-"}</td>
                            <td className="py-1.5 px-3 cursor-help" title={`Description: ${txn.description || txn.flag || "-"}\nTreatment: ${txn.treatment || "-"}\nFlag: ${txn.flag || "-"}\nTxn Type: ${txn.txn_type || "-"}`}>
                              <span className={`text-[10px] px-1.5 py-0.5 rounded ${(txn.treatment || "").toLowerCase().includes("add units") ? "bg-green-100 text-green-700" : (txn.treatment || "").toLowerCase().includes("minus units") ? "bg-red-100 text-red-700" : "bg-gray-100 text-gray-600"}`}>{txn.description || txn.flag || "-"}</span>
                            </td>
                            <td className="py-1.5 px-3 text-xs max-w-[250px] truncate cursor-help" title={tooltipParts.join("\n")}>{toTitleCase(txn.scheme_name) || "-"}</td>
                            <td className="py-1.5 px-3 text-xs text-right font-mono">{units !== 0 ? units.toFixed(3) : "-"}</td>
                            <td className="py-1.5 px-3 text-xs text-right font-mono">{parseFloat(txn.nav) ? parseFloat(txn.nav).toFixed(4) : "-"}</td>
                            <td className={`py-1.5 px-3 text-xs text-right font-mono font-medium cursor-help ${total >= 0 ? "text-green-700" : "text-red-600"}`} title={amtParts.join("\n")}>{total !== 0 ? total.toLocaleString("en-IN", {minimumFractionDigits: 2, maximumFractionDigits: 2}) : "-"}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {nfTotalPages > 1 && (
                  <div className="flex items-center justify-between mt-3 px-1">
                    <span className="text-xs text-gray-500">Showing {(nfPage - 1) * ALL_TXN_PAGE_SIZE + 1}-{Math.min(nfPage * ALL_TXN_PAGE_SIZE, sorted.length)} of {sorted.length}</span>
                    <div className="flex items-center gap-1">
                      <button className="px-2 py-1 text-xs rounded border border-gray-300 hover:bg-gray-100 disabled:opacity-40" disabled={nfPage <= 1} onClick={() => setAllTxnPage(1)}>First</button>
                      <button className="px-2 py-1 text-xs rounded border border-gray-300 hover:bg-gray-100 disabled:opacity-40" disabled={nfPage <= 1} onClick={() => setAllTxnPage(p => p - 1)}>Prev</button>
                      <span className="text-xs px-2">Page {nfPage} / {nfTotalPages}</span>
                      <button className="px-2 py-1 text-xs rounded border border-gray-300 hover:bg-gray-100 disabled:opacity-40" disabled={nfPage >= nfTotalPages} onClick={() => setAllTxnPage(p => p + 1)}>Next</button>
                      <button className="px-2 py-1 text-xs rounded border border-gray-300 hover:bg-gray-100 disabled:opacity-40" disabled={nfPage >= nfTotalPages} onClick={() => setAllTxnPage(nfTotalPages)}>Last</button>
                    </div>
                  </div>
                )}
                </>
              );
            })()}
          </CardContent>
        </Card>
      ) : activeTab === "segregated_portfolios" ? (
        /* Segregated Portfolios Tab - Non-Financial/Junk Entries from Unit Reconciliation */
        <Card className="border-gray-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-800">Segregated Portfolios</h3>
                <p className="text-sm text-gray-500">Non-financial entries excluded from unit reconciliation</p>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="secondary" className="bg-amber-100 text-amber-700">
                  {segregatedPortfolios.length} entries
                </Badge>
              </div>
            </div>

            {segregatedLoading ? (
              <div className="flex items-center justify-center py-16">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600" />
              </div>
            ) : segregatedPortfolios.length === 0 ? (
              <div className="text-center py-16 text-gray-500">
                <AlertTriangle className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p>No segregated portfolio entries found for this client</p>
                <p className="text-sm mt-2">Segregated portfolios are treated as non-financial entries</p>
              </div>
            ) : (
              <>
                <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-amber-800">Segregated Portfolio Information</p>
                      <p className="text-sm text-amber-700 mt-1">
                        These entries are from segregated portfolios created during credit events. 
                        They are excluded from main unit reconciliation as they represent non-standard corporate actions.
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-amber-50/50">
                        <TableHead className="text-sm font-medium text-gray-500">Folio No</TableHead>
                        <TableHead className="text-sm font-medium text-gray-500">Scheme Name</TableHead>
                        <TableHead className="text-sm font-medium text-gray-500 text-right">Units Bought</TableHead>
                        <TableHead className="text-sm font-medium text-gray-500 text-right">Units Sold</TableHead>
                        <TableHead className="text-sm font-medium text-gray-500 text-right">Net (Txn)</TableHead>
                        <TableHead className="text-sm font-medium text-gray-500 text-right">Holdings</TableHead>
                        <TableHead className="text-sm font-medium text-gray-500 text-right">Difference</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {segregatedPortfolios.map((entry, index) => (
                        <TableRow key={`${entry.folio_no}-${index}`} className="hover:bg-amber-50/30">
                          <TableCell className="py-2 text-sm font-mono">{entry.folio_no}</TableCell>
                          <TableCell className="py-2 text-sm max-w-xs truncate" title={toTitleCase(entry.scheme_name) || entry.scheme_name}>
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-xs">
                                Segregated
                              </Badge>
                              <span className="truncate">{toTitleCase(entry.scheme_name) || entry.scheme_name}</span>
                            </div>
                          </TableCell>
                          <TableCell className="py-2 text-sm text-right font-mono text-green-600">
                            {entry.units_bought?.toLocaleString('en-IN', { minimumFractionDigits: 4 })}
                          </TableCell>
                          <TableCell className="py-2 text-sm text-right font-mono text-red-600">
                            {entry.units_sold?.toLocaleString('en-IN', { minimumFractionDigits: 4 })}
                          </TableCell>
                          <TableCell className="py-2 text-sm text-right font-mono">
                            {entry.net_from_transactions?.toLocaleString('en-IN', { minimumFractionDigits: 4 })}
                          </TableCell>
                          <TableCell className="py-2 text-sm text-right font-mono font-medium text-blue-600">
                            {entry.balance_from_holdings?.toLocaleString('en-IN', { minimumFractionDigits: 4 })}
                          </TableCell>
                          <TableCell className={`py-2 text-sm text-right font-mono font-bold ${
                            entry.difference >= 0 ? 'text-green-600' : 'text-red-600'
                          }`}>
                            {entry.difference >= 0 ? '+' : ''}{entry.difference?.toLocaleString('en-IN', { minimumFractionDigits: 4 })}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      ) : activeTab === "all_transactions" ? (
        /* All Transactions Tab - from transaction_master */
        <Card className="border-gray-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-base font-semibold text-gray-800">All Transactions</h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  {allTransactions.length} transactions from Transaction Master
                  {selectedSubAccount !== "all" && " (filtered by sub-account)"}
                </p>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {/* FY quick-pick + custom date range — derived from transaction set */}
                {(() => {
                  // Build the FY options once, derived from all loaded txns.
                  const fySet = new Set();
                  for (const t of allTransactions) {
                    const ms = parseDateStr(t.trade_date);
                    if (!ms) continue;
                    const d = new Date(ms);
                    const y = d.getFullYear();
                    const m = d.getMonth(); // 0-indexed; Apr = 3
                    const startYr = m >= 3 ? y : y - 1;
                    fySet.add(`${startYr}-${String((startYr + 1) % 100).padStart(2, "0")}`);
                  }
                  const fyOpts = Array.from(fySet).sort((a, b) => b.localeCompare(a));
                  return (
                    <select
                      className="h-8 rounded-md border border-gray-300 px-2 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400 bg-white"
                      value={allTxnFY}
                      onChange={(e) => {
                        const v = e.target.value;
                        setAllTxnFY(v);
                        // Sync the date inputs so the user sees what's applied.
                        if (v) {
                          const [from, to] = fyToRangeMs(v);
                          if (from && to) {
                            const f = new Date(from);
                            const t = new Date(to);
                            const pad = (n) => String(n).padStart(2, "0");
                            setAllTxnDateFrom(`${f.getFullYear()}-${pad(f.getMonth() + 1)}-${pad(f.getDate())}`);
                            setAllTxnDateTo(`${t.getFullYear()}-${pad(t.getMonth() + 1)}-${pad(t.getDate())}`);
                          }
                        } else {
                          setAllTxnDateFrom("");
                          setAllTxnDateTo("");
                        }
                        setAllTxnPage(1);
                      }}
                      data-testid="all-txn-fy-filter"
                      title="Financial Year"
                    >
                      <option value="">All FYs</option>
                      {fyOpts.map((fy) => (
                        <option key={fy} value={fy}>FY {fy}</option>
                      ))}
                    </select>
                  );
                })()}
                <input
                  type="date"
                  className="h-8 rounded-md border border-gray-300 px-2 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400 bg-white"
                  value={allTxnDateFrom}
                  onChange={(e) => { setAllTxnDateFrom(e.target.value); setAllTxnFY(""); setAllTxnPage(1); }}
                  data-testid="all-txn-date-from"
                  title="From date"
                />
                <span className="text-xs text-gray-400">→</span>
                <input
                  type="date"
                  className="h-8 rounded-md border border-gray-300 px-2 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400 bg-white"
                  value={allTxnDateTo}
                  onChange={(e) => { setAllTxnDateTo(e.target.value); setAllTxnFY(""); setAllTxnPage(1); }}
                  data-testid="all-txn-date-to"
                  title="To date"
                />
                {(allTxnDateFrom || allTxnDateTo || allTxnFY) && (
                  <button
                    type="button"
                    className="h-8 px-2 rounded-md border border-gray-300 text-xs text-gray-600 hover:bg-gray-50"
                    onClick={() => { setAllTxnDateFrom(""); setAllTxnDateTo(""); setAllTxnFY(""); setAllTxnPage(1); }}
                    data-testid="all-txn-date-clear"
                  >
                    Clear dates
                  </button>
                )}
                <select
                  className="h-8 rounded-md border border-gray-300 px-2 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400 bg-white"
                  value={allTxnDupFilter}
                  onChange={(e) => { setAllTxnDupFilter(e.target.value); setAllTxnPage(1); }}
                  data-testid="all-txn-dup-filter"
                >
                  <option value="all">All Transactions</option>
                  <option value="active">Active Units</option>
                </select>
                <input
                  type="text"
                  placeholder="Search folio, scheme, ISIN, type..."
                  className="h-8 w-56 rounded-md border border-gray-300 px-3 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400"
                  value={allTransactionsSearch}
                  onChange={(e) => { setAllTransactionsSearch(e.target.value); setAllTxnPage(1); }}
                  data-testid="all-txn-search"
                />
                <Button variant="outline" size="sm" className="h-8 gap-1.5"
                  onClick={() => handleExportTabExcel("all_transactions")}
                  data-testid="download-all-txn-xlsx">
                  <Download className="w-3.5 h-3.5" />
                  Download
                </Button>
              </div>
            </div>
            {allTransactionsLoading ? (
              <div className="flex items-center justify-center py-10 text-gray-500 text-sm">Loading transactions...</div>
            ) : (() => {
              // Filter by sub-account (holding pattern) using filteredFolioNos
              let filtered = allTransactions;
              if (selectedSubAccount !== "all" && filteredFolioNos.length > 0) {
                filtered = filtered.filter(t => filteredFolioNos.includes(t.folio_no));
              }
              // Search filter
              if (allTransactionsSearch.trim()) {
                const q = allTransactionsSearch.toLowerCase();
                filtered = filtered.filter(t =>
                  (t.folio_no || "").toLowerCase().includes(q) ||
                  (t.scheme_name || "").toLowerCase().includes(q) ||
                  (t.txn_type || "").toLowerCase().includes(q) ||
                  (t.flag || "").toLowerCase().includes(q) ||
                  (t.description || "").toLowerCase().includes(q) ||
                  (t.treatment || "").toLowerCase().includes(q) ||
                  (t.isin || "").toLowerCase().includes(q) ||
                  (t.amfi_code || "").toLowerCase().includes(q) ||
                  (t.product_code || "").toLowerCase().includes(q) ||
                  (t.txn_no || "").toLowerCase().includes(q)
                );
              }

              // Apply duplicate/category filter based on dropdown
              if (allTxnDupFilter === "active") {
                filtered = filtered.filter(t => t.reflecting_in === "Active Units");
              }
              // "all" shows everything — no further filtering

              // Apply date range filter (FY-driven values flow into these two)
              {
                const fromMs = ymdToMs(allTxnDateFrom);
                const toMs = ymdToMs(allTxnDateTo);
                if (fromMs || toMs) {
                  const endOfDayMs = toMs ? toMs + 24 * 60 * 60 * 1000 - 1 : null;
                  filtered = filtered.filter(t => {
                    const ms = parseDateStr(t.trade_date);
                    if (!ms) return false;
                    if (fromMs && ms < fromMs) return false;
                    if (endOfDayMs && ms > endOfDayMs) return false;
                    return true;
                  });
                }
              }

              // Sort — when sorting by trade_date, use secondary sort by scheme_name to group same schemes together
              const sorted = [...filtered].sort((a, b) => {
                const dir = allTransactionsSort.direction === "asc" ? 1 : -1;
                const key = allTransactionsSort.key;
                let va = a[key] || "";
                let vb = b[key] || "";
                if (["units", "nav", "amount", "stt", "stamp_duty", "tds", "sl_no"].includes(key)) {
                  va = parseFloat(va) || 0;
                  vb = parseFloat(vb) || 0;
                  return (va - vb) * dir;
                }
                if (key === "trade_date") {
                  try {
                    const da = parseDateStr(va);
                    const db = parseDateStr(vb);
                    if (da !== db) return (da - db) * dir;
                    // Secondary: group by scheme_name
                    const sa = (a.scheme_name || "").toLowerCase();
                    const sb = (b.scheme_name || "").toLowerCase();
                    if (sa !== sb) return sa.localeCompare(sb);
                    // Tertiary: positive units first
                    return (parseFloat(b.units) || 0) - (parseFloat(a.units) || 0);
                  } catch { return 0; }
                }
                return String(va).localeCompare(String(vb)) * dir;
              });

              if (sorted.length === 0) {
                return <div className="text-center text-gray-500 py-10 text-sm">No transactions found</div>;
              }

              // Pagination
              const totalPages = Math.ceil(sorted.length / ALL_TXN_PAGE_SIZE);
              const currentPage = Math.min(allTxnPage, totalPages);
              const paginatedRows = sorted.slice((currentPage - 1) * ALL_TXN_PAGE_SIZE, currentPage * ALL_TXN_PAGE_SIZE);

              const SortHeader = ({ field, children, align }) => (
                <th
                  className={`text-xs font-semibold cursor-pointer hover:bg-gray-200 bg-gray-50 sticky top-0 z-10 px-3 py-2 whitespace-nowrap ${align === "right" ? "text-right" : "text-left"}`}
                  onClick={() => setAllTransactionsSort(prev => ({
                    key: field,
                    direction: prev.key === field && prev.direction === "asc" ? "desc" : "asc"
                  }))}
                >
                  <div className={`flex items-center gap-1 ${align === "right" ? "justify-end" : ""}`}>
                    {children}
                    {allTransactionsSort.key === field ? (allTransactionsSort.direction === "asc" ? "↑" : "↓") : ""}
                  </div>
                </th>
              );

              return (
                <>
                {/* Mobile card list — vertical scroll, no horizontal */}
                <div className="md:hidden">
                  <MobileTransactionsCards rows={paginatedRows} emptyMessage="No transactions found" />
                </div>
                {/* Desktop table */}
                <div className="max-h-[600px] overflow-auto relative border rounded-md hidden md:block">
                  <table className="w-full caption-bottom text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-xs font-semibold bg-gray-50 sticky top-0 z-10 px-3 py-2 text-left whitespace-nowrap">Sl No</th>
                        <SortHeader field="folio_no">Folio No</SortHeader>
                        {showFamilyClientCol && (
                          <th className="text-xs font-semibold bg-gray-50 sticky top-0 z-10 px-3 py-2 text-left whitespace-nowrap">Client</th>
                        )}
                        <SortHeader field="trade_date">Trade Date</SortHeader>
                        <SortHeader field="description">Description</SortHeader>
                        <SortHeader field="scheme_name">Scheme Name</SortHeader>
                        <SortHeader field="units" align="right">Units</SortHeader>
                        <SortHeader field="nav" align="right">NAV</SortHeader>
                        <SortHeader field="amount" align="right">Amount</SortHeader>
                        <SortHeader field="reflecting_in">Reflecting In</SortHeader>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        return paginatedRows.map((txn, idx) => {
                        const units = parseFloat(txn.units) || 0;
                        const isCredit = units > 0;
                        const tooltipParts = [txn.scheme_name || ""];
                        if (txn.source) tooltipParts.push(`Source: ${txn.source}`);
                        if (txn.product_code) tooltipParts.push(`Product Code: ${txn.product_code}`);
                        if (txn.isin) tooltipParts.push(`ISIN: ${txn.isin}`);
                        if (txn.amfi_code) tooltipParts.push(`AMFI Code: ${txn.amfi_code}`);
                        if (txn.transfer_details) tooltipParts.push(`Transfer Details: ${txn.transfer_details}`);
                        if (txn.remarks) tooltipParts.push(`Remarks: ${txn.remarks}`);
                        const schemeTooltip = tooltipParts.join("\n");
                        const isDup = txn.is_failed_dup === true;
                        const isRedemption = (txn.flag || "").toLowerCase().includes("redemption");
                        const isPartialSold = txn.fifo_consumed_type === "partial";
                        const rowBg = isDup ? "bg-amber-50" : isPartialSold ? "bg-orange-50/40" : "";
                        return (
                          <tr key={`${txn.sl_no}-${idx}`} className={`hover:bg-gray-100 border-b ${rowBg}`}>
                            <td className="py-1.5 px-3 text-xs text-gray-400">{(currentPage - 1) * ALL_TXN_PAGE_SIZE + idx + 1}</td>
                            <td className="py-1.5 px-3 text-xs font-mono">{txn.folio_no}</td>
                            {showFamilyClientCol && (
                              <td className="py-1.5 px-3 text-xs text-gray-700 whitespace-nowrap">{resolveRowClientName(txn)}</td>
                            )}
                            <td className="py-1.5 px-3 text-xs font-mono whitespace-nowrap cursor-help" title={[txn.trade_date || "", txn.txn_no ? `Txn No: ${txn.txn_no}` : "", txn.application_no ? `Application No: ${txn.application_no}` : ""].filter(Boolean).join("\n")}><span className={isDup ? "text-amber-700 font-semibold" : ""}>{txn.trade_date || "-"}</span></td>
                            <td className="py-1.5 px-3 cursor-help" title={`Description: ${txn.description || txn.flag || "-"}\nTreatment: ${txn.treatment || "-"}\nFlag: ${txn.flag || "-"}\nTxn Type: ${txn.txn_type || "-"}`}>
                              <span className={`text-[10px] px-1.5 py-0.5 rounded ${(txn.treatment || "").toLowerCase().includes("add units") ? "bg-green-100 text-green-700" : (txn.treatment || "").toLowerCase().includes("minus units") ? "bg-red-100 text-red-700" : "bg-gray-100 text-gray-600"}`}>
                                {txn.description || txn.flag || "-"}
                              </span>
                            </td>
                            <td className="py-1.5 px-3 text-xs max-w-[250px] truncate cursor-help" title={schemeTooltip}>{toTitleCase(txn.scheme_name) || "-"}</td>
                            <td className={`py-1.5 px-3 text-xs text-right font-mono ${isCredit ? "text-green-700" : "text-red-600"}`}>
                              {isPartialSold ? (
                                <div className="space-y-0.5" title={`Original: ${txn.original_units?.toFixed(3) || units.toFixed(3)}\nSold: ${txn.sold_units?.toFixed(3) || "0"}\nRemaining: ${txn.remaining_units?.toFixed(3) || units.toFixed(3)}`}>
                                  <div className="text-orange-600 font-semibold">{txn.remaining_units?.toFixed(3) || units.toFixed(3)}</div>
                                  <div className="text-[9px] text-orange-500">({txn.sold_units?.toFixed(3)} sold)</div>
                                </div>
                              ) : (
                                units !== 0 ? units.toFixed(3) : "-"
                              )}
                            </td>
                            <td className="py-1.5 px-3 text-xs text-right font-mono">{parseFloat(txn.nav) ? parseFloat(txn.nav).toFixed(4) : "-"}</td>
                            {(() => {
                              const amt = parseFloat(txn.amount) || 0;
                              const tds = parseFloat(txn.tds) || 0;
                              const sd = parseFloat(txn.stamp_duty) || 0;
                              const stt = parseFloat(txn.stt) || 0;
                              const total = amt + tds + sd + stt;
                              const parts = [`Amount: ${amt.toLocaleString("en-IN", {minimumFractionDigits: 2, maximumFractionDigits: 2})}`];
                              if (tds) parts.push(`TDS: ${tds.toFixed(2)}`);
                              if (sd) parts.push(`Stamp Duty: ${sd.toFixed(2)}`);
                              if (stt) parts.push(`STT: ${stt.toFixed(2)}`);
                              if (tds || sd || stt) parts.push(`Total: ${total.toLocaleString("en-IN", {minimumFractionDigits: 2, maximumFractionDigits: 2})}`);
                              return (
                                <td className={`py-1.5 px-3 text-xs text-right font-mono font-medium cursor-help ${total >= 0 ? "text-green-700" : "text-red-600"}`} title={parts.join("\n")}>
                                  {total !== 0 ? total.toLocaleString("en-IN", {minimumFractionDigits: 2, maximumFractionDigits: 2}) : "-"}
                                </td>
                              );
                            })()}
                            <td className="py-1.5 px-3">
                              <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded whitespace-nowrap ${txn.reflecting_in === "Active Units" ? "bg-green-100 text-green-700" : txn.reflecting_in === "Sold Units" ? "bg-red-100 text-red-700" : txn.reflecting_in === "Failed Transactions" ? "bg-amber-100 text-amber-700" : "bg-gray-100 text-gray-600"}`}>
                                {txn.reflecting_in || "-"}
                              </span>
                            </td>
                          </tr>
                        );
                      });
                      })()}
                    </tbody>
                  </table>
                </div>
                {totalPages > 1 && (
                  <div className="flex items-center justify-between mt-3 px-1">
                    <span className="text-xs text-gray-500">
                      Showing {(currentPage - 1) * ALL_TXN_PAGE_SIZE + 1}-{Math.min(currentPage * ALL_TXN_PAGE_SIZE, sorted.length)} of {sorted.length}
                    </span>
                    <div className="flex items-center gap-1">
                      <button className="px-2 py-1 text-xs rounded border border-gray-300 hover:bg-gray-100 disabled:opacity-40" disabled={currentPage <= 1} onClick={() => setAllTxnPage(1)}>First</button>
                      <button className="px-2 py-1 text-xs rounded border border-gray-300 hover:bg-gray-100 disabled:opacity-40" disabled={currentPage <= 1} onClick={() => setAllTxnPage(p => p - 1)}>Prev</button>
                      <span className="text-xs px-2">Page {currentPage} / {totalPages}</span>
                      <button className="px-2 py-1 text-xs rounded border border-gray-300 hover:bg-gray-100 disabled:opacity-40" disabled={currentPage >= totalPages} onClick={() => setAllTxnPage(p => p + 1)}>Next</button>
                      <button className="px-2 py-1 text-xs rounded border border-gray-300 hover:bg-gray-100 disabled:opacity-40" disabled={currentPage >= totalPages} onClick={() => setAllTxnPage(totalPages)}>Last</button>
                    </div>
                  </div>
                )}
                </>
              );
            })()}
          </CardContent>
        </Card>
      ) : activeTab === "failed_transactions" ? (
        /* Failed Transactions Tab - Shows all failed/rejected transactions from transaction_master */
        <Card className="border-gray-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-base font-semibold text-gray-800">Failed Transactions</h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  Transactions that failed processing, were rejected, or are duplicates
                </p>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  placeholder="Search folio, scheme, flag..."
                  className="h-8 w-56 rounded-md border border-gray-300 px-3 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400"
                  value={allTransactionsSearch}
                  onChange={(e) => { setAllTransactionsSearch(e.target.value); setAllTxnPage(1); }}
                  data-testid="failed-txn-search"
                />
                <Button variant="outline" size="sm" className="h-8 gap-1.5"
                  onClick={() => handleExportTabExcel("failed_transactions")}
                  data-testid="download-failed-txn-xlsx">
                  <Download className="w-3.5 h-3.5" />
                  Download
                </Button>
              </div>
            </div>
            {allTransactionsLoading ? (
              <div className="flex items-center justify-center py-10 text-gray-500 text-sm">Loading transactions...</div>
            ) : (() => {
              let failed = allTransactions.filter(t => t.reflecting_in === "Failed Transactions");
              if (selectedSubAccount !== "all" && filteredFolioNos.length > 0) {
                failed = failed.filter(t => filteredFolioNos.includes(t.folio_no));
              }
              if (allTransactionsSearch.trim()) {
                const q = allTransactionsSearch.toLowerCase();
                failed = failed.filter(t =>
                  (t.folio_no || "").toLowerCase().includes(q) ||
                  (t.scheme_name || "").toLowerCase().includes(q) ||
                  (t.flag || "").toLowerCase().includes(q) ||
                  (t.txn_type || "").toLowerCase().includes(q) ||
                  (t.description || "").toLowerCase().includes(q) ||
                  (t.treatment || "").toLowerCase().includes(q) ||
                  (t.txn_no || "").toLowerCase().includes(q)
                );
              }
              // Sort by trade_date desc, then scheme_name
              const sorted = [...failed].sort((a, b) => {
                try {
                  const da = parseDateStr(a.trade_date);
                  const db = parseDateStr(b.trade_date);
                  if (da !== db) return db - da;
                  const sa = (a.scheme_name || "").toLowerCase();
                  const sb = (b.scheme_name || "").toLowerCase();
                  if (sa !== sb) return sa.localeCompare(sb);
                  return (parseFloat(b.units) || 0) - (parseFloat(a.units) || 0);
                } catch { return 0; }
              });

              if (sorted.length === 0) {
                return (
                  <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                    <XCircle className="w-12 h-12 mb-3 text-gray-300" />
                    <p className="text-sm font-medium text-gray-500">No failed transactions found</p>
                  </div>
                );
              }

              const ftTotalPages = Math.ceil(sorted.length / ALL_TXN_PAGE_SIZE);
              const ftPage = Math.min(allTxnPage, ftTotalPages);
              const ftRows = sorted.slice((ftPage - 1) * ALL_TXN_PAGE_SIZE, ftPage * ALL_TXN_PAGE_SIZE);

              return (
                <>
                <p className="text-xs text-amber-600 font-medium mb-2">{sorted.length} failed transactions found</p>
                <div className="max-h-[600px] overflow-auto relative border rounded-md">
                  <table className="w-full caption-bottom text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-xs font-semibold bg-gray-50 sticky top-0 z-10 px-3 py-2 text-left whitespace-nowrap">Sl No</th>
                        <th className="text-xs font-semibold bg-gray-50 sticky top-0 z-10 px-3 py-2 text-left">Folio No</th>
                        {showFamilyClientCol && (
                          <th className="text-xs font-semibold bg-gray-50 sticky top-0 z-10 px-3 py-2 text-left whitespace-nowrap">Client</th>
                        )}
                        <th className="text-xs font-semibold bg-gray-50 sticky top-0 z-10 px-3 py-2 text-left">Trade Date</th>
                        <th className="text-xs font-semibold bg-gray-50 sticky top-0 z-10 px-3 py-2 text-left">Description</th>
                        <th className="text-xs font-semibold bg-gray-50 sticky top-0 z-10 px-3 py-2 text-left">Scheme Name</th>
                        <th className="text-xs font-semibold bg-gray-50 sticky top-0 z-10 px-3 py-2 text-right">Units</th>
                        <th className="text-xs font-semibold bg-gray-50 sticky top-0 z-10 px-3 py-2 text-right">NAV</th>
                        <th className="text-xs font-semibold bg-gray-50 sticky top-0 z-10 px-3 py-2 text-right">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ftRows.map((txn, idx) => {
                        const units = parseFloat(txn.units) || 0;
                        const isCredit = units > 0;
                        const tooltipParts = [txn.scheme_name || ""];
                        if (txn.source) tooltipParts.push(`Source: ${txn.source}`);
                        if (txn.product_code) tooltipParts.push(`Product Code: ${txn.product_code}`);
                        if (txn.isin) tooltipParts.push(`ISIN: ${txn.isin}`);
                        if (txn.amfi_code) tooltipParts.push(`AMFI Code: ${txn.amfi_code}`);
                        if (txn.transfer_details) tooltipParts.push(`Transfer Details: ${txn.transfer_details}`);
                        if (txn.remarks) tooltipParts.push(`Remarks: ${txn.remarks}`);
                        const dateTooltip = [txn.trade_date || "", txn.txn_no ? `Txn No: ${txn.txn_no}` : "", txn.application_no ? `Application No: ${txn.application_no}` : ""].filter(Boolean).join("\n");
                        const amt = parseFloat(txn.amount) || 0;
                        const tds = parseFloat(txn.tds) || 0;
                        const sd = parseFloat(txn.stamp_duty) || 0;
                        const stt = parseFloat(txn.stt) || 0;
                        const total = amt + tds + sd + stt;
                        const amtParts = [`Amount: ${amt.toLocaleString("en-IN", {minimumFractionDigits: 2, maximumFractionDigits: 2})}`];
                        if (tds) amtParts.push(`TDS: ${tds.toFixed(2)}`);
                        if (sd) amtParts.push(`Stamp Duty: ${sd.toFixed(2)}`);
                        if (stt) amtParts.push(`STT: ${stt.toFixed(2)}`);
                        if (tds || sd || stt) amtParts.push(`Total: ${total.toLocaleString("en-IN", {minimumFractionDigits: 2, maximumFractionDigits: 2})}`);
                        return (
                          <tr key={`ft-${idx}`} className="hover:bg-amber-50 border-b bg-amber-50/50">
                            <td className="py-1.5 px-3 text-xs text-gray-400">{(ftPage - 1) * ALL_TXN_PAGE_SIZE + idx + 1}</td>
                            <td className="py-1.5 px-3 text-xs font-mono">{txn.folio_no}</td>
                            {showFamilyClientCol && (
                              <td className="py-1.5 px-3 text-xs text-gray-700 whitespace-nowrap">{resolveRowClientName(txn)}</td>
                            )}
                            <td className="py-1.5 px-3 text-xs font-mono whitespace-nowrap cursor-help" title={dateTooltip}>{txn.trade_date || "-"}</td>
                            <td className="py-1.5 px-3 cursor-help" title={`Description: ${txn.description || txn.flag || "-"}\nTreatment: ${txn.treatment || "-"}\nFlag: ${txn.flag || "-"}\nTxn Type: ${txn.txn_type || "-"}`}>
                              <span className={`text-[10px] px-1.5 py-0.5 rounded ${(txn.treatment || "").toLowerCase().includes("add units") ? "bg-green-100 text-green-700" : (txn.treatment || "").toLowerCase().includes("minus units") ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>{txn.description || txn.flag || "-"}</span>
                            </td>
                            <td className="py-1.5 px-3 text-xs max-w-[250px] truncate cursor-help" title={tooltipParts.join("\n")}>{toTitleCase(txn.scheme_name) || "-"}</td>
                            <td className="py-1.5 px-3 text-xs text-right font-mono">{units !== 0 ? units.toFixed(3) : "-"}</td>
                            <td className="py-1.5 px-3 text-xs text-right font-mono">{parseFloat(txn.nav) ? parseFloat(txn.nav).toFixed(4) : "-"}</td>
                            <td className={`py-1.5 px-3 text-xs text-right font-mono font-medium cursor-help ${total >= 0 ? "text-green-700" : "text-red-600"}`} title={amtParts.join("\n")}>{total !== 0 ? total.toLocaleString("en-IN", {minimumFractionDigits: 2, maximumFractionDigits: 2}) : "-"}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {ftTotalPages > 1 && (
                  <div className="flex items-center justify-between mt-3 px-1">
                    <span className="text-xs text-gray-500">Showing {(ftPage - 1) * ALL_TXN_PAGE_SIZE + 1}-{Math.min(ftPage * ALL_TXN_PAGE_SIZE, sorted.length)} of {sorted.length}</span>
                    <div className="flex items-center gap-1">
                      <button className="px-2 py-1 text-xs rounded border border-gray-300 hover:bg-gray-100 disabled:opacity-40" disabled={ftPage <= 1} onClick={() => setAllTxnPage(1)}>First</button>
                      <button className="px-2 py-1 text-xs rounded border border-gray-300 hover:bg-gray-100 disabled:opacity-40" disabled={ftPage <= 1} onClick={() => setAllTxnPage(p => p - 1)}>Prev</button>
                      <span className="text-xs px-2">Page {ftPage} / {ftTotalPages}</span>
                      <button className="px-2 py-1 text-xs rounded border border-gray-300 hover:bg-gray-100 disabled:opacity-40" disabled={ftPage >= ftTotalPages} onClick={() => setAllTxnPage(p => p + 1)}>Next</button>
                      <button className="px-2 py-1 text-xs rounded border border-gray-300 hover:bg-gray-100 disabled:opacity-40" disabled={ftPage >= ftTotalPages} onClick={() => setAllTxnPage(ftTotalPages)}>Last</button>
                    </div>
                  </div>
                )}
                </>
              );
            })()}
          </CardContent>
        </Card>
      ) : activeTab === "active_units" ? (
        /* Active Units Tab - Current Holdings at Transaction Level */
        <Card className="border-gray-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Active Units</h3>
                <p className="text-sm text-gray-500">Current holdings at transaction level after FIFO matching with sales</p>
                {activeUnitsRebuildStatus && (
                  <div className="mt-2 flex items-center gap-2 text-xs flex-wrap" data-testid="active-units-status-pill">
                    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full font-medium ${
                      activeUnitsRebuildStatus.status === "running" ? "bg-indigo-100 text-indigo-700" :
                      activeUnitsRebuildStatus.status === "completed" ? "bg-emerald-100 text-emerald-700" :
                      "bg-red-100 text-red-700"
                    }`}>
                      <Activity className="w-3 h-3" />
                      Rebuild: {activeUnitsRebuildStatus.status}
                    </span>
                    {activeUnitsRebuildStatus.status === "running" && (
                      <span className="text-indigo-700 font-medium">
                        {(activeUnitsRebuildStatus.progress_done || 0).toLocaleString()} / {(activeUnitsRebuildStatus.progress_total || 0).toLocaleString()} clients
                      </span>
                    )}
                    {typeof activeUnitsRebuildStatus.rows_in_collection === "number" && (
                      <span className="text-gray-600">{activeUnitsRebuildStatus.rows_in_collection.toLocaleString()} rows</span>
                    )}
                    {activeUnitsRebuildStatus.completed_at && (
                      <span className="text-gray-400">last {new Date(activeUnitsRebuildStatus.completed_at).toLocaleString()}</span>
                    )}
                  </div>
                )}
                {activeUnitsRebuildStatus?.status === "running" && (
                  (() => {
                    const done = activeUnitsRebuildStatus.progress_done || 0;
                    const total = activeUnitsRebuildStatus.progress_total || 0;
                    const pct = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 0;
                    let etaText = "estimating…";
                    try {
                      const started = activeUnitsRebuildStatus.started_at;
                      if (started && done > 0 && total > done) {
                        const startedMs = new Date(started.replace("Z", "+00:00")).getTime();
                        const elapsed = (Date.now() - startedMs) / 1000;
                        if (elapsed > 0) {
                          const rate = done / elapsed; // clients per sec
                          const remaining = (total - done) / rate;
                          const mins = Math.floor(remaining / 60);
                          const secs = Math.round(remaining % 60);
                          etaText = mins > 0 ? `~${mins}m ${secs}s remaining` : `~${secs}s remaining`;
                        }
                      } else if (done >= total && total > 0) {
                        etaText = "finalising…";
                      }
                    } catch (_e) { /* ignore */ }
                    return (
                      <div className="mt-2 max-w-xl" data-testid="active-units-progress-bar">
                        <div className="flex items-center justify-between text-xs mb-1">
                          <span className="text-indigo-700 font-semibold">{pct}%</span>
                          <span className="text-gray-500">{etaText}</span>
                        </div>
                        <div className="h-2 w-full bg-indigo-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-indigo-600 transition-all duration-500 ease-out"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      </div>
                    );
                  })()
                )}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <Button
                  onClick={handleRebuildActiveUnits}
                  disabled={rebuildingActiveUnits}
                  variant="outline"
                  size="sm"
                  className="border-indigo-200 text-indigo-700 hover:bg-indigo-50"
                  data-testid="rebuild-active-units-button"
                  title="Rebuild FIFO snapshot of holdings across all clients. ~60–120s."
                >
                  {rebuildingActiveUnits
                    ? <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                    : <Activity className="h-4 w-4 mr-1" />}
                  Rebuild Active Units
                </Button>
                <Button
                  onClick={handleRebuildActiveUnitsForPan}
                  disabled={rebuildingPan}
                  variant="outline"
                  size="sm"
                  className="border-purple-200 text-purple-700 hover:bg-purple-50"
                  data-testid="rebuild-active-units-pan-button"
                  title="Rebuild Active Units for a SINGLE PAN. Light/fast (~5-10s). Does not touch dashboard cache."
                >
                  {rebuildingPan
                    ? <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                    : <Activity className="h-4 w-4 mr-1" />}
                  Rebuild (One PAN)
                </Button>
                <Button
                  onClick={handleForceResetActiveUnitsLock}
                  disabled={forceResettingActiveUnitsLock}
                  variant="outline"
                  size="sm"
                  className="border-amber-200 text-amber-700 hover:bg-amber-50"
                  data-testid="force-reset-active-units-lock-button"
                  title="Force-clear a stuck rebuild lock. No data is touched."
                >
                  {forceResettingActiveUnitsLock
                    ? <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                    : <Unlock className="h-4 w-4 mr-1" />}
                  Force Reset Lock
                </Button>
                <Button variant="outline" size="sm" className="h-8 gap-1.5"
                  onClick={() => handleExportTabExcel("active_units")}
                  data-testid="download-active-units-xlsx">
                  <Download className="w-3.5 h-3.5" />
                  Download
                </Button>
              </div>
            </div>
            {/* Category capsule filter — driven by /portfolio-strategy/holdings-categorized
                Pre-2018-07-11 lots are bucketed as Short Term. Click a capsule to
                filter the Active Units table to lots in that category. */}
            {categoryHoldings && (categoryHoldings.categories || []).length > 0 && (
              <div className="mb-4 flex flex-wrap items-center gap-2" data-testid="category-capsules">
                <span className="text-xs text-gray-500 mr-1">Category:</span>
                <button
                  type="button"
                  onClick={() => setCategoryFilter("All")}
                  className={`px-3 py-1 rounded-full text-xs border transition-colors ${
                    categoryFilter === "All"
                      ? "bg-emerald-700 border-emerald-800 text-white"
                      : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
                  }`}
                  data-testid="capsule-All"
                >
                  All · ₹{(categoryHoldings.grand_total / 1e7).toFixed(2)} Cr
                </button>
                {categoryHoldings.categories.map((c) => (
                  <button
                    key={c.category}
                    type="button"
                    onClick={() => setCategoryFilter(c.category)}
                    className={`px-3 py-1 rounded-full text-xs border transition-colors ${
                      categoryFilter === c.category
                        ? "bg-emerald-700 border-emerald-800 text-white"
                        : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
                    }`}
                    data-testid={`capsule-${c.category.replace(/\s+/g, "-")}`}
                  >
                    {c.category} · ₹{(c.market_value / 1e7).toFixed(2)} Cr ({c.scheme_count})
                  </button>
                ))}
                <span className="text-[10px] text-gray-400 ml-2">
                  Epoch: {categoryHoldings.epoch} · pre-epoch lots → Short Term
                </span>
              </div>
            )}
            <div className="flex items-center justify-end mb-4 gap-4 flex-wrap">
                {/* Holding Period Filter */}
                <Select 
                  value={selectedHoldingPeriodForActiveUnits} 
                  onValueChange={setSelectedHoldingPeriodForActiveUnits}
                >
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="All Holding Periods" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Holdings</SelectItem>
                    <SelectItem value="5+">5+ Years</SelectItem>
                    <SelectItem value="3-5">3-5 Years</SelectItem>
                    <SelectItem value="2-3">2-3 Years</SelectItem>
                    <SelectItem value="1-2">1-2 Years</SelectItem>
                    <SelectItem value="<1">Less than 1 Year</SelectItem>
                  </SelectContent>
                </Select>
                {/* Folio Multi-Select with Search (parity with All Transactions UX) */}
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-[350px] justify-between font-normal"
                      data-testid="active-units-folio-multi"
                    >
                      <span className="truncate text-sm text-gray-700">
                        {selectedFoliosForActiveUnits.length === 0
                          ? "All Folios"
                          : selectedFoliosForActiveUnits.length === 1
                          ? selectedFoliosForActiveUnits[0]
                          : `${selectedFoliosForActiveUnits.length} folios selected`}
                      </span>
                      <ChevronsUpDown className="w-4 h-4 ml-2 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[420px] p-0" align="end" sideOffset={4}>
                    <div className="px-2 py-2 sticky top-0 bg-white z-10 border-b">
                      <Input
                        placeholder="Search folio or scheme..."
                        value={folioSearchTerm}
                        onChange={(e) => setFolioSearchTerm(e.target.value)}
                        className="h-8"
                      />
                    </div>
                    <div className="flex items-center justify-between px-3 py-2 border-b bg-gray-50">
                      <span className="text-xs text-gray-600">
                        {selectedFoliosForActiveUnits.length} selected
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 text-xs text-gray-500 hover:text-gray-800"
                        onClick={() => { setSelectedFoliosForActiveUnits([]); setSelectedFolioForActiveUnits(""); }}
                        data-testid="active-units-folio-clear-all"
                      >
                        Clear
                      </Button>
                    </div>
                    <div className="max-h-[320px] overflow-y-auto py-1">
                      {(() => {
                        // Derive unique folios from the FULL active-units set
                        // (ignoring the current free-text/multi-folio filter)
                        // so users can re-add folios they previously removed.
                        const baseTxns = allTransactions.filter(t => t.reflecting_in === "Active Units");
                        const folioMap = {};
                        baseTxns.forEach(t => {
                          const fn = t.folio_no;
                          if (fn && !folioMap[fn]) {
                            folioMap[fn] = { folio_no: fn, scheme_name: t.scheme_name || '', scheme_code: t.scheme_code || '' };
                          }
                        });
                        const filtered = Object.values(folioMap).filter(folioObj => {
                          if (!folioSearchTerm) return true;
                          const q = folioSearchTerm.toLowerCase();
                          return folioObj.folio_no?.toLowerCase().includes(q) ||
                                 folioObj.scheme_name?.toLowerCase().includes(q);
                        });
                        if (filtered.length === 0) {
                          return <div className="px-3 py-3 text-xs text-gray-400">No matches</div>;
                        }
                        return filtered.map(folioObj => {
                          const isSelected = selectedFoliosForActiveUnits.includes(folioObj.folio_no);
                          return (
                            <label
                              key={`${folioObj.folio_no}|${folioObj.scheme_code}`}
                              className="flex items-start gap-2 px-3 py-2 cursor-pointer hover:bg-gray-50"
                            >
                              <input
                                type="checkbox"
                                className="mt-0.5"
                                checked={isSelected}
                                onChange={() => {
                                  setSelectedFoliosForActiveUnits(prev => {
                                    const next = isSelected
                                      ? prev.filter(f => f !== folioObj.folio_no)
                                      : [...prev, folioObj.folio_no];
                                    // Keep the legacy single-folio state in sync
                                    // for the pre-warming fetch path.
                                    setSelectedFolioForActiveUnits(next.length === 1 ? next[0] : "");
                                    return next;
                                  });
                                }}
                              />
                              <div className="flex flex-col min-w-0">
                                <span className="font-mono text-xs text-gray-900">{folioObj.folio_no}</span>
                                <span className="text-xs text-gray-600 truncate max-w-[330px]">
                                  {folioObj.scheme_name}
                                </span>
                              </div>
                            </label>
                          );
                        });
                      })()}
                    </div>
                  </PopoverContent>
                </Popover>
                {/* Free-text search — parity with All Transactions */}
                <input
                  type="text"
                  placeholder="Search folio, scheme, txn..."
                  className="h-9 w-56 rounded-md border border-gray-300 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-emerald-400"
                  value={activeUnitsSearch}
                  onChange={(e) => setActiveUnitsSearch(e.target.value)}
                  data-testid="active-units-search"
                />
                <Badge variant="secondary" className="bg-emerald-100 text-emerald-700">
                  {filteredActiveUnits.length} active entries
                </Badge>
              </div>

            {allTransactionsLoading ? (
              <div className="flex items-center justify-center py-16">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" />
              </div>
            ) : filteredActiveUnits.length === 0 ? (
              <div className="text-center py-16 text-gray-500">
                No active units found
              </div>
            ) : (
              <>
                {(() => {
                  // Helper function to calculate holding period in months
                  const getHoldingMonths = (dateStr) => {
                    if (!dateStr) return null;
                    let purchaseDate = null;
                    if (dateStr.includes('-')) {
                      const parts = dateStr.split('-');
                      if (parts.length === 3 && parts[0].length === 2) {
                        // DD-MM-YYYY format
                        purchaseDate = new Date(parts[2], parts[1] - 1, parts[0]);
                      } else {
                        purchaseDate = new Date(dateStr);
                      }
                    } else if (dateStr.length === 8) {
                      purchaseDate = new Date(dateStr.substring(0,4), dateStr.substring(4,6)-1, dateStr.substring(6,8));
                    }
                    if (!purchaseDate || isNaN(purchaseDate.getTime())) return null;
                    const now = new Date();
                    return (now.getFullYear() - purchaseDate.getFullYear()) * 12 + (now.getMonth() - purchaseDate.getMonth());
                  };

                  // Helper to get holding period bucket
                  const getHoldingPeriodBucket = (months) => {
                    if (months === null) return null;
                    if (months >= 60) return '5+';
                    if (months >= 36) return '3-5';
                    if (months >= 24) return '2-3';
                    if (months >= 12) return '1-2';
                    return '<1';
                  };

                  // Filter transactions by holding period
                  const _holdingFiltered = selectedHoldingPeriodForActiveUnits === 'all' 
                    ? filteredActiveUnits
                    : filteredActiveUnits.filter(txn => {
                        const months = getHoldingMonths(txn.transaction_date);
                        const bucket = getHoldingPeriodBucket(months);
                        return bucket === selectedHoldingPeriodForActiveUnits;
                      });

                  // Category-capsule filter — uses the per-(scheme,category)
                  // map from /portfolio-strategy/holdings-categorized. A
                  // scheme straddling the 2018-07-11 epoch may appear in BOTH
                  // Short Term and Wealth — applying the category filter
                  // intersects scheme_code AND transaction_date bucket.
                  const filteredTransactions = (() => {
                    if (categoryFilter === "All" || !categoryHoldings?.rows) return _holdingFiltered;
                    // Build scheme_code → set of categories that the
                    // categorizer says this scheme belongs to (could be 1 or 2).
                    const codeToCats = new Map();
                    for (const r of (categoryHoldings.rows || [])) {
                      if (!codeToCats.has(r.scheme_code)) codeToCats.set(r.scheme_code, new Set());
                      codeToCats.get(r.scheme_code).add(r.category);
                    }
                    return _holdingFiltered.filter(t => {
                      const cats = codeToCats.get(t.scheme_code);
                      return cats && cats.has(categoryFilter);
                    });
                  })();

                  // Sort filtered transactions
                  const sortedFilteredTransactions = [...filteredTransactions].sort((a, b) => {
                    if (!activeUnitsSortConfig.key) return 0;
                    
                    let aVal, bVal;
                    const key = activeUnitsSortConfig.key;
                    
                    // Helper function to calculate proportionate stamp duty
                    const getProportionateStampDuty = (txn) => {
                      const stampDuty = parseFloat(txn.stamp_duty) || 0;
                      const originalUnits = txn.original_units || txn.remaining_units || 0;
                      const remainingUnits = txn.remaining_units || 0;
                      return originalUnits > 0 ? (stampDuty * remainingUnits / originalUnits) : 0;
                    };
                    
                    // Handle computed fields
                    if (key === 'cost_value') {
                      // Cost Value includes proportionate stamp duty
                      aVal = ((a.remaining_units || 0) * (a.nav || 0)) + getProportionateStampDuty(a);
                      bVal = ((b.remaining_units || 0) * (b.nav || 0)) + getProportionateStampDuty(b);
                    } else if (key === 'current_value') {
                      const aNav = typeof a.current_nav === 'object' ? (a.current_nav?.nav || 0) : (a.current_nav || a.nav || 0);
                      const bNav = typeof b.current_nav === 'object' ? (b.current_nav?.nav || 0) : (b.current_nav || b.nav || 0);
                      aVal = (a.remaining_units || 0) * aNav;
                      bVal = (b.remaining_units || 0) * bNav;
                    } else if (key === 'profit_loss') {
                      const aNav = typeof a.current_nav === 'object' ? (a.current_nav?.nav || 0) : (a.current_nav || a.nav || 0);
                      const bNav = typeof b.current_nav === 'object' ? (b.current_nav?.nav || 0) : (b.current_nav || b.nav || 0);
                      // Cost includes proportionate stamp duty
                      const aCost = ((a.remaining_units || 0) * (a.nav || 0)) + getProportionateStampDuty(a);
                      const bCost = ((b.remaining_units || 0) * (b.nav || 0)) + getProportionateStampDuty(b);
                      aVal = ((a.remaining_units || 0) * aNav) - aCost;
                      bVal = ((b.remaining_units || 0) * bNav) - bCost;
                    } else if (key === 'current_nav') {
                      aVal = typeof a.current_nav === 'object' ? (a.current_nav?.nav || 0) : (a.current_nav || 0);
                      bVal = typeof b.current_nav === 'object' ? (b.current_nav?.nav || 0) : (b.current_nav || 0);
                    } else {
                      aVal = a[key];
                      bVal = b[key];
                    }
                    
                    // Handle numeric fields
                    if (['original_units', 'units_sold', 'remaining_units', 'nav', 'cost_value', 'current_value', 'current_nav', 'profit_loss', 'xirr'].includes(key)) {
                      aVal = parseFloat(aVal) || 0;
                      bVal = parseFloat(bVal) || 0;
                    }
                    
                    // Handle date field
                    if (key === 'transaction_date') {
                      aVal = parseDateStr(aVal);
                      bVal = parseDateStr(bVal);
                    }
                    
                    if (aVal < bVal) return activeUnitsSortConfig.direction === "asc" ? -1 : 1;
                    if (aVal > bVal) return activeUnitsSortConfig.direction === "asc" ? 1 : -1;
                    return 0;
                  });

                  // Calculate totals and XIRR for filtered transactions
                  const totals = sortedFilteredTransactions.reduce((acc, txn) => {
                    acc.originalUnits += txn.original_units || 0;
                    acc.unitsSold += txn.units_sold || 0;
                    acc.remainingUnits += txn.remaining_units || 0;
                    
                    // Calculate proportionate stamp duty for totals
                    const stampDuty = parseFloat(txn.stamp_duty) || 0;
                    const originalUnits = txn.original_units || txn.remaining_units || 0;
                    const remainingUnits = txn.remaining_units || 0;
                    const proportionateStampDuty = originalUnits > 0 
                      ? (stampDuty * remainingUnits / originalUnits) 
                      : 0;
                    
                    // Cost Value = (Remaining Units × NAV) + Proportionate Stamp Duty
                    const costValue = (remainingUnits * (txn.nav || 0)) + proportionateStampDuty;
                    const currentNavObj = txn.current_nav;
                    const currentNav = typeof currentNavObj === 'object' ? (currentNavObj?.nav || 0) : (currentNavObj || txn.nav || 0);
                    const currentValue = remainingUnits * currentNav;
                    acc.costValue += costValue;
                    acc.currentValue += currentValue;
                    acc.totalStampDuty += proportionateStampDuty;
                    
                    // Collect XIRR data for averaging
                    const months = getHoldingMonths(txn.transaction_date);
                    if (months !== null && costValue > 0 && currentValue > 0) {
                      // Calculate simple annualized return as proxy for XIRR
                      const years = months / 12;
                      if (years > 0) {
                        const annualizedReturn = (Math.pow(currentValue / costValue, 1 / years) - 1) * 100;
                        if (!isNaN(annualizedReturn) && isFinite(annualizedReturn)) {
                          acc.xirrs.push(annualizedReturn);
                        }
                      }
                    }
                    return acc;
                  }, { originalUnits: 0, unitsSold: 0, remainingUnits: 0, costValue: 0, currentValue: 0, totalStampDuty: 0, xirrs: [] });

                  const totalProfitLoss = totals.currentValue - totals.costValue;
                  const avgXirr = totals.xirrs.length > 0 ? totals.xirrs.reduce((a, b) => a + b, 0) / totals.xirrs.length : 0;

                  return (
                    <>
                      {/* Mobile card list — vertical scroll */}
                      <div className="md:hidden">
                        <MobileTransactionsCards rows={filteredActiveUnits} emptyMessage="No active units" />
                      </div>
                      <div className="overflow-x-auto hidden md:block">
                        <Table>
                          <TableHeader>
                            <TableRow className="bg-gray-50/50">
                              <ActiveUnitsSortableHeader field="transaction_date" className="w-[100px]">Purchase Date</ActiveUnitsSortableHeader>
                              <ActiveUnitsSortableHeader field="folio_no" className="w-[110px]">Folio No</ActiveUnitsSortableHeader>
                              {showFamilyClientCol && (
                                <TableHead className="text-xs w-[140px]">Client</TableHead>
                              )}
                              <ActiveUnitsSortableHeader field="scheme_name" className="w-[200px]">Scheme Name</ActiveUnitsSortableHeader>
                              <ActiveUnitsSortableHeader field="original_units" className="justify-end text-right w-[100px]">Original Units</ActiveUnitsSortableHeader>
                              <ActiveUnitsSortableHeader field="units_sold" className="justify-end text-right w-[90px]">Units Sold</ActiveUnitsSortableHeader>
                              <ActiveUnitsSortableHeader field="remaining_units" className="justify-end text-right w-[100px]">Balance Units</ActiveUnitsSortableHeader>
                              <ActiveUnitsSortableHeader field="nav" className="justify-end text-right w-[90px]">Purchase NAV</ActiveUnitsSortableHeader>
                              <ActiveUnitsSortableHeader field="cost_value" className="justify-end text-right w-[120px]">Cost Value (INR)</ActiveUnitsSortableHeader>
                              <ActiveUnitsSortableHeader field="current_nav" className="justify-end text-right w-[90px]">Current NAV</ActiveUnitsSortableHeader>
                              <ActiveUnitsSortableHeader field="current_value" className="justify-end text-right w-[120px]">Current Value (INR)</ActiveUnitsSortableHeader>
                              <ActiveUnitsSortableHeader field="profit_loss" className="justify-end text-right w-[100px]">Profit/Loss</ActiveUnitsSortableHeader>
                              <ActiveUnitsSortableHeader field="xirr" className="justify-end text-right w-[80px]">XIRR %</ActiveUnitsSortableHeader>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {sortedFilteredTransactions.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={showFamilyClientCol ? 13 : 12} className="text-center py-8 text-gray-500">
                                  No transactions found for selected holding period
                                </TableCell>
                              </TableRow>
                            ) : (
                              <>
                                {sortedFilteredTransactions.map((txn, index) => {
                                  // Calculate proportionate stamp duty based on remaining units
                                  const stampDuty = parseFloat(txn.stamp_duty) || 0;
                                  const originalUnits = txn.original_units || txn.remaining_units || 0;
                                  const remainingUnits = txn.remaining_units || 0;
                                  const proportionateStampDuty = originalUnits > 0 
                                    ? (stampDuty * remainingUnits / originalUnits) 
                                    : 0;
                                  
                                  // Cost Value = (Remaining Units × NAV) + Proportionate Stamp Duty
                                  const costValue = (remainingUnits * (txn.nav || 0)) + proportionateStampDuty;
                                  const currentNavObj = txn.current_nav;
                                  const currentNav = typeof currentNavObj === 'object' ? (currentNavObj?.nav || 0) : (currentNavObj || txn.nav || 0);
                                  const currentValue = remainingUnits * currentNav;
                                  const profitLoss = currentValue - costValue;
                                  
                                  // Use backend-calculated XIRR if available, otherwise calculate locally
                                  let txnXirr = txn.xirr;
                                  if (txnXirr === null || txnXirr === undefined) {
                                    // Parse the purchase date robustly. Source stores
                                    // it as DD-MM-YYYY which `new Date()` mis-parses
                                    // to Invalid Date in Chromium → fallback used to
                                    // bail for EVERY row, not just <30-day ones.
                                    let _purchaseDate = null;
                                    const raw = String(txn.transaction_date || "").trim();
                                    if (raw) {
                                      // DD-MM-YYYY or DD/MM/YYYY
                                      const ddmmyyyy = raw.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
                                      // YYYY-MM-DD (ISO)
                                      const ymd = raw.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
                                      // YYYYMMDD compact
                                      const compact = raw.match(/^(\d{4})(\d{2})(\d{2})$/);
                                      if (ddmmyyyy) {
                                        _purchaseDate = new Date(
                                          Number(ddmmyyyy[3]),
                                          Number(ddmmyyyy[2]) - 1,
                                          Number(ddmmyyyy[1])
                                        );
                                      } else if (ymd) {
                                        _purchaseDate = new Date(
                                          Number(ymd[1]),
                                          Number(ymd[2]) - 1,
                                          Number(ymd[3])
                                        );
                                      } else if (compact) {
                                        _purchaseDate = new Date(
                                          Number(compact[1]),
                                          Number(compact[2]) - 1,
                                          Number(compact[3])
                                        );
                                      }
                                    }
                                    if (_purchaseDate && !isNaN(_purchaseDate.getTime()) && costValue > 0 && currentValue > 0) {
                                      const daysLocal = Math.floor((Date.now() - _purchaseDate.getTime()) / 86400000);
                                      if (daysLocal > 0) {
                                        const years = daysLocal / 365;
                                        txnXirr = (Math.pow(currentValue / costValue, 1 / years) - 1) * 100;
                                        if (isNaN(txnXirr) || !isFinite(txnXirr)) txnXirr = null;
                                      }
                                    }
                                  }
                                  
                                  // Calculate days from purchase date.
                                  // CAMS / Karvy ship DD-MM-YYYY; relying on
                                  // `new Date(str)` parses that as MM-DD-YYYY
                                  // (US format) and silently inflates the
                                  // count — e.g. "02-08-2023" became Feb-08
                                  // instead of Aug-02 → 1198 days vs 1024.
                                  const getDaysFromToday = (dateStr) => {
                                    if (!dateStr) return null;
                                    const s = String(dateStr).trim();
                                    let purchaseDate = null;
                                    const ddmmyyyy = s.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})/);
                                    if (ddmmyyyy) {
                                      purchaseDate = new Date(
                                        Number(ddmmyyyy[3]),
                                        Number(ddmmyyyy[2]) - 1,
                                        Number(ddmmyyyy[1])
                                      );
                                    } else if (/^\d{8}$/.test(s)) {
                                      // YYYYMMDD compact
                                      purchaseDate = new Date(
                                        Number(s.substring(0, 4)),
                                        Number(s.substring(4, 6)) - 1,
                                        Number(s.substring(6, 8))
                                      );
                                    } else if (/^\d{4}-\d{1,2}-\d{1,2}/.test(s)) {
                                      // YYYY-MM-DD ISO
                                      purchaseDate = new Date(s);
                                    }
                                    if (!purchaseDate || isNaN(purchaseDate.getTime())) return null;
                                    const today = new Date();
                                    return Math.floor((today - purchaseDate) / (1000 * 60 * 60 * 24));
                                  };
                                  const daysHeld = getDaysFromToday(txn.transaction_date);
                                  
                                  return (
                                    <TableRow key={`${txn.folio_no}-${txn.transaction_date}-${index}`} className="hover:bg-gray-50/50">
                                      <TableCell 
                                        className="py-2 text-sm cursor-help" 
                                        title={daysHeld !== null ? `${daysHeld.toLocaleString('en-IN')} days` : ''}
                                      >
                                        {formatDate(txn.transaction_date)}
                                      </TableCell>
                                      <TableCell className="py-2 text-sm font-mono">{txn.folio_no}</TableCell>
                                      {showFamilyClientCol && (
                                        <TableCell className="py-2 text-xs text-gray-700 whitespace-nowrap">
                                          {resolveRowClientName(txn)}
                                        </TableCell>
                                      )}
                                      <TableCell 
                                        className="py-2 text-sm max-w-xs truncate cursor-help" 
                                        title={`${toTitleCase(txn.scheme_name) || 'Unknown Scheme'}\nISIN: ${txn.isin || '-'}\nCode: ${txn.scheme_code || '-'}`}
                                      >
                                        {toTitleCase(txn.scheme_name) || txn.scheme_name}
                                      </TableCell>
                                      <TableCell 
                                        className="py-2 text-sm text-right font-mono cursor-help"
                                        title={`Type: ${txn.transaction_desc || txn.transaction_type || 'N/A'}`}
                                      >
                                        {txn.original_units?.toLocaleString('en-IN', { minimumFractionDigits: 4 })}
                                      </TableCell>
                                      <TableCell className="py-2 text-sm text-right font-mono text-orange-600">
                                        {txn.units_sold > 0 ? txn.units_sold?.toLocaleString('en-IN', { minimumFractionDigits: 4 }) : '-'}
                                      </TableCell>
                                      <TableCell className="py-2 text-sm text-right font-mono font-semibold text-emerald-600">
                                        {txn.remaining_units?.toLocaleString('en-IN', { minimumFractionDigits: 4 })}
                                      </TableCell>
                                      <TableCell className="py-2 text-sm text-right font-mono">
                                        {txn.nav?.toLocaleString('en-IN', { minimumFractionDigits: 4 })}
                                      </TableCell>
                                      <TableCell 
                                        className="py-2 text-sm text-right font-mono text-blue-600 cursor-help"
                                        title={proportionateStampDuty > 0 
                                          ? `Units × NAV: ₹${(remainingUnits * (txn.nav || 0)).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}\nStamp Duty: ₹${proportionateStampDuty.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${txn.units_sold > 0 ? ` (proportionate from ₹${stampDuty.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })})` : ''}\nTotal Cost: ₹${costValue.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                          : `Units × NAV: ₹${costValue.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                                      >
                                        {costValue > 0 ? `₹${costValue.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'}
                                      </TableCell>
                                      <TableCell className="py-2 text-sm text-right font-mono text-gray-600">
                                        {currentNav > 0 ? currentNav.toLocaleString('en-IN', { minimumFractionDigits: 4 }) : '-'}
                                      </TableCell>
                                      <TableCell className="py-2 text-sm text-right font-mono font-medium">
                                        {currentValue > 0 ? `₹${currentValue.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'}
                                      </TableCell>
                                      <TableCell className={`py-2 text-sm text-right font-mono font-medium ${profitLoss >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                        {costValue > 0 ? `${profitLoss >= 0 ? '+' : ''}₹${Math.abs(profitLoss).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'}
                                      </TableCell>
                                      <TableCell className={`py-2 text-sm text-right font-mono font-medium ${txnXirr != null && txnXirr >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                        {txnXirr != null ? `${txnXirr >= 0 ? '+' : ''}${Number(txnXirr).toFixed(2)}%` : '-'}
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}
                                {/* Summary Row with XIRR */}
                                <TableRow className="bg-emerald-50 border-t-2 border-emerald-200 font-semibold">
                                  <TableCell colSpan={showFamilyClientCol ? 8 : 7} className="py-3 text-sm text-emerald-800">
                                    <div className="flex items-center gap-4">
                                      <span>Total ({sortedFilteredTransactions.length} transactions)</span>
                                      {selectedHoldingPeriodForActiveUnits !== 'all' && (
                                        <Badge variant="outline" className="bg-teal-50 text-teal-700 border-teal-200">
                                          {selectedHoldingPeriodForActiveUnits === '5+' ? '5+ Years' :
                                           selectedHoldingPeriodForActiveUnits === '3-5' ? '3-5 Years' :
                                           selectedHoldingPeriodForActiveUnits === '2-3' ? '2-3 Years' :
                                           selectedHoldingPeriodForActiveUnits === '1-2' ? '1-2 Years' : '<1 Year'}
                                        </Badge>
                                      )}
                                    </div>
                                  </TableCell>
                                  <TableCell className="py-3 text-sm text-right font-mono font-bold text-blue-700">
                                    ₹{totals.costValue.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </TableCell>
                                  <TableCell className="py-3"></TableCell>
                                  <TableCell className="py-3 text-sm text-right font-mono font-bold">
                                    ₹{totals.currentValue.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </TableCell>
                                  <TableCell className={`py-3 text-sm text-right font-mono font-bold ${totalProfitLoss >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                    {totalProfitLoss >= 0 ? '+' : ''}₹{Math.abs(totalProfitLoss).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </TableCell>
                                  <TableCell className={`py-3 text-sm text-right font-mono font-bold ${avgXirr >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                    {avgXirr >= 0 ? '+' : ''}{avgXirr.toFixed(2)}%
                                  </TableCell>
                                </TableRow>
                              </>
                            )}
                          </TableBody>
                        </Table>
                      </div>
                    </>
                  );
                })()}
              </>
            )}
          </CardContent>
        </Card>
      ) : activeTab === "sold_units" ? (
        /* Sold Units Tab - Redemption Transactions with FIFO Cost Basis */
        <Card className="border-gray-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-800">Sold Units</h3>
                <p className="text-sm text-gray-500">Redemptions with FIFO cost basis and matched purchase entries</p>
              </div>
              <div className="flex items-center gap-2">
                {/* Financial Year Filter */}
                <Select value={selectedFYForSold} onValueChange={setSelectedFYForSold}>
                  <SelectTrigger className="w-[140px]">
                    <SelectValue placeholder="All FYs" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All FYs</SelectItem>
                    {(() => {
                      const fys = new Set();
                      soldFromAllTxn.forEach(txn => {
                        const fy = getFinancialYear(txn.trade_date || txn.transaction_date);
                        if (fy) fys.add(fy);
                      });
                      return Array.from(fys).sort().reverse().map(fy => (
                        <SelectItem key={fy} value={fy}>{fy}</SelectItem>
                      ));
                    })()}
                  </SelectContent>
                </Select>
                {/* Custom Date Range (NRI users - calendar year tax cycle) */}
                <div className="flex items-center gap-1" data-testid="sold-date-range-filter">
                  <Input
                    type="date"
                    value={soldDateFrom}
                    onChange={(e) => setSoldDateFrom(e.target.value)}
                    className="h-9 w-[140px] text-xs"
                    placeholder="From"
                    title="Sale date from (calendar year)"
                    data-testid="sold-date-from"
                  />
                  <span className="text-gray-400 text-xs">–</span>
                  <Input
                    type="date"
                    value={soldDateTo}
                    onChange={(e) => setSoldDateTo(e.target.value)}
                    className="h-9 w-[140px] text-xs"
                    placeholder="To"
                    title="Sale date to (calendar year)"
                    data-testid="sold-date-to"
                  />
                  {(soldDateFrom || soldDateTo) && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 px-2 text-xs text-gray-500"
                      onClick={() => { setSoldDateFrom(""); setSoldDateTo(""); }}
                      title="Clear date range"
                      data-testid="sold-date-clear"
                    >
                      Clear
                    </Button>
                  )}
                </div>
                {/* Folio Filter - in-memory */}
                <Select value={selectedFolioForSold || "all"} onValueChange={(v) => { setSelectedFolioForSold(v === "all" ? "" : v); setFolioSearchTerm(""); }}>
                  <SelectTrigger className="w-[350px]">
                    <SelectValue placeholder="All Folios" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    <div className="px-2 py-2 sticky top-0 bg-white z-10 border-b">
                      <Input
                        placeholder="Search folio or scheme..."
                        value={folioSearchTerm}
                        onChange={(e) => setFolioSearchTerm(e.target.value)}
                        className="h-8"
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                      />
                    </div>
                    <SelectItem value="all">All Folios</SelectItem>
                    {[...new Map(soldFromAllTxn.map(t => [t.folio_no, t])).values()]
                      .filter(t => {
                        if (!folioSearchTerm) return true;
                        const s = folioSearchTerm.toLowerCase();
                        return t.folio_no?.toLowerCase().includes(s) || t.scheme_name?.toLowerCase().includes(s);
                      })
                      .map(t => (
                        <SelectItem key={t.folio_no} value={t.folio_no}>
                          <div className="flex flex-col">
                            <span className="font-mono text-xs">{t.folio_no}</span>
                            <span className="text-sm text-gray-600 truncate max-w-[300px]">{t.scheme_name}</span>
                          </div>
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                <Badge variant="outline" className="text-orange-600 border-orange-200 bg-orange-50">
                  {soldFromAllTxn.length.toLocaleString()} sold
                </Badge>
                <Button variant="outline" size="sm" className="h-8 gap-1.5"
                  onClick={() => handleExportTabExcel("sold_units")}
                  data-testid="download-sold-units-xlsx">
                  <Download className="w-3.5 h-3.5" />
                  Download
                </Button>
              </div>
            </div>
            
            {allTransactionsLoading ? (
              <div className="flex items-center justify-center py-16">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600" />
              </div>
            ) : soldFromAllTxn.length === 0 ? (
              <div className="text-center py-16 text-gray-500">
                <TrendingDown className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p className="font-medium">No sold transactions found</p>
                <p className="text-sm mt-1">No redemptions or switch-outs for this client</p>
              </div>
            ) : (
              <>
                {/* Sold Entries with FIFO */}
                <div className="mb-6">
                    <div className="overflow-x-auto border rounded-lg">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-gray-100 border-b border-gray-300">
                            <th colSpan={2} className="text-center text-xs font-semibold text-gray-700 border-r-2 border-gray-400 py-2">Fund Details</th>
                            <th colSpan={4} className="text-center text-xs font-semibold text-orange-700 border-r-2 border-gray-400 py-2">Sale Details</th>
                            <th colSpan={3} className="text-center text-xs font-semibold text-blue-700 border-r-2 border-gray-400 py-2">FIFO Purchase Match</th>
                            <th colSpan={1} className="text-center text-xs font-semibold text-purple-700 border-r-2 border-gray-400 py-2">Grandfathering</th>
                            <th colSpan={2} className="text-center text-xs font-semibold text-green-700 border-r-2 border-gray-400 py-2">Actual P/L</th>
                            <th colSpan={2} className="text-center text-xs font-semibold text-purple-700 py-2">GF P/L</th>
                          </tr>
                          <tr className="bg-orange-50 border-b-2 border-gray-400">
                            <th className="text-xs font-medium text-gray-600 px-3 py-2 text-left">Folio</th>
                            <th className="text-xs font-medium text-gray-600 px-3 py-2 text-left border-r-2 border-gray-400" style={{minWidth: "180px"}}>Scheme</th>
                            <th 
                              className="text-xs font-medium text-gray-600 px-3 py-2 text-left cursor-pointer hover:bg-orange-100 select-none"
                              onClick={() => handleSoldUnitsSort("trade_date")}
                            >
                              <div className="flex items-center gap-1">
                                Sell Date
                                {soldUnitsSortConfig.key !== "trade_date" 
                                  ? <ArrowUpDown className="w-3 h-3 text-gray-300" />
                                  : soldUnitsSortConfig.direction === "asc"
                                    ? <ArrowUp className="w-3 h-3 text-orange-600" />
                                    : <ArrowDown className="w-3 h-3 text-orange-600" />}
                              </div>
                            </th>
                            <th className="text-xs font-medium text-gray-600 px-3 py-2 text-left">Description</th>
                            <th className="text-xs font-medium text-gray-600 px-3 py-2 text-right">Units</th>
                            <th className="text-xs font-medium text-gray-600 px-3 py-2 text-right border-r-2 border-gray-400">Sell Value</th>
                            <th className="text-xs font-medium text-gray-600 px-3 py-2 text-left">Buy Date(s)</th>
                            <th className="text-xs font-medium text-gray-600 px-3 py-2 text-right">Units</th>
                            <th className="text-xs font-medium text-gray-600 px-3 py-2 text-right border-r-2 border-gray-400">Cost Value</th>
                            <th className="text-xs font-medium text-purple-600 px-3 py-2 text-right border-r-2 border-gray-400" title="For equity funds with purchases before 31-Jan-2018: max(Buy NAV, NAV on 31-Jan-2018) x Units">GF Cost</th>
                            <th className="text-xs font-medium text-gray-600 px-3 py-2 text-right">P/L</th>
                            <th className="text-xs font-medium text-gray-600 px-3 py-2 text-right border-r-2 border-gray-400">P/L %</th>
                            <th className="text-xs font-medium text-purple-600 px-3 py-2 text-right">GF P/L</th>
                            <th className="text-xs font-medium text-purple-600 px-3 py-2 text-right">GF P/L %</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(() => {
                            let displayTxns = soldFromAllTxn;
                            // Apply FY filter
                            if (selectedFYForSold !== "all") {
                              displayTxns = displayTxns.filter(t => getFinancialYear(t.trade_date || t.transaction_date) === selectedFYForSold);
                            }
                            // Apply folio filter
                            if (selectedFolioForSold) {
                              displayTxns = displayTxns.filter(t => t.folio_no === selectedFolioForSold);
                            }
                            // Apply custom date range filter (NRI calendar-year support)
                            if (soldDateFrom || soldDateTo) {
                              displayTxns = displayTxns.filter(t => isWithinDateRange(t.trade_date || t.transaction_date, soldDateFrom, soldDateTo));
                            }
                            // Apply sort
                            if (soldUnitsSortConfig.key === "trade_date") {
                              const parseDate = (ds) => {
                                if (!ds) return 0;
                                const parts = ds.split("-");
                                if (parts.length === 3) return new Date(parts[2], parts[1] - 1, parts[0]).getTime();
                                return 0;
                              };
                              displayTxns = [...displayTxns].sort((a, b) => {
                                const da = parseDate(a.trade_date || a.transaction_date);
                                const db = parseDate(b.trade_date || b.transaction_date);
                                return soldUnitsSortConfig.direction === "asc" ? da - db : db - da;
                              });
                            }
                            // Compute footer totals across the filtered+sorted set so the
                            // user always sees a sum that matches the rows currently on screen.
                            const totals = {
                              count: displayTxns.length,
                              sellUnits: 0, sellValue: 0,
                              fifoUnits: 0, costValue: 0,
                              gfCost: 0, hasGfCost: false,
                              actualPL: 0, gfPL: 0, hasGfPL: false,
                            };
                            displayTxns.forEach(txn => {
                              const sellValue = Math.abs(parseFloat(txn.amount) || 0);
                              const costValue = txn.fifo_cost_value || 0;
                              const gfCostValue = txn.grandfathered_cost_value;
                              totals.sellUnits += Math.abs(parseFloat(txn.units) || 0);
                              totals.sellValue += sellValue;
                              (txn.fifo_details || []).forEach(f => {
                                totals.fifoUnits += (f.units_matched || 0);
                              });
                              totals.costValue += costValue;
                              if (costValue > 0) totals.actualPL += (sellValue - costValue);
                              if (gfCostValue != null) {
                                totals.hasGfCost = true;
                                totals.gfCost += gfCostValue;
                                totals.hasGfPL = true;
                                totals.gfPL += (sellValue - gfCostValue);
                              }
                            });
                            const totalActualPLPct = totals.costValue > 0
                              ? (totals.actualPL / totals.costValue) * 100 : 0;
                            const totalGfPLPct = totals.gfCost > 0
                              ? (totals.gfPL / totals.gfCost) * 100 : 0;
                            const rows = displayTxns.map((txn, idx) => {
                            const sellValue = Math.abs(parseFloat(txn.amount) || 0);
                            const costValue = txn.fifo_cost_value || 0;
                            const gfCostValue = txn.grandfathered_cost_value;
                            const actualPL = sellValue - costValue;
                            const actualPLPct = costValue > 0 ? ((actualPL / costValue) * 100) : 0;
                            const fifo = txn.fifo_details || [];
                            const hasGrandfathering = fifo.some(f => f.is_grandfathered);
                            const gfPL = gfCostValue != null ? sellValue - gfCostValue : null;
                            const gfPLPct = gfCostValue > 0 ? ((gfPL / gfCostValue) * 100) : null;
                            return (
                              <tr key={`sold-txm-${idx}`} className="hover:bg-orange-50/50 border-b">
                                <td className="px-3 py-1.5 text-xs font-mono">{txn.folio_no}</td>
                                <td className="px-3 py-1.5 text-xs border-r-2 border-gray-300" style={{minWidth: "180px"}}>{toTitleCase(txn.scheme_name) || "-"}</td>
                                <td className="px-3 py-1.5 text-xs font-mono whitespace-nowrap">{txn.trade_date}</td>
                                <td className="px-3 py-1.5">
                                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-100 text-red-700">{txn.description || txn.flag || "-"}</span>
                                </td>
                                <td className="px-3 py-1.5 text-xs text-right font-mono text-red-600">{Math.abs(parseFloat(txn.units) || 0).toFixed(3)}</td>
                                <td className="px-3 py-1.5 text-xs text-right font-mono border-r-2 border-gray-300">{sellValue.toLocaleString("en-IN", {minimumFractionDigits: 2, maximumFractionDigits: 2})}</td>
                                <td className="px-3 py-1.5 text-xs" title={fifo.map(f => `${f.buy_date}: ${f.units_matched} units @ NAV ${f.buy_nav}${f.is_grandfathered ? ` (GF NAV: ${f.grandfathered_nav})` : ""}`).join("\n")}>
                                  {fifo.length > 0 ? (
                                    <div className="space-y-0.5">
                                      {fifo.map((f, fi) => (
                                        <div key={fi} className={`text-[10px] font-mono ${f.is_grandfathered ? "text-purple-700 font-semibold" : "text-blue-700"}`}>
                                          {f.buy_date}{f.is_grandfathered ? " *" : ""}
                                        </div>
                                      ))}
                                    </div>
                                  ) : <span className="text-gray-400">-</span>}
                                </td>
                                <td className="px-3 py-1.5 text-xs text-right">
                                  {fifo.length > 0 ? (
                                    <div className="space-y-0.5">
                                      {fifo.map((f, fi) => (
                                        <div key={fi} className="text-[10px] font-mono text-blue-700">{f.units_matched.toFixed(3)}</div>
                                      ))}
                                    </div>
                                  ) : "-"}
                                </td>
                                <td className="px-3 py-1.5 text-xs text-right font-mono border-r-2 border-gray-300">{costValue > 0 ? costValue.toLocaleString("en-IN", {minimumFractionDigits: 2, maximumFractionDigits: 2}) : "-"}</td>
                                <td className={`px-3 py-1.5 text-xs text-right font-mono border-r-2 border-gray-300 ${hasGrandfathering ? "text-purple-700 font-semibold bg-purple-50/50" : "text-gray-400"}`}
                                    title={hasGrandfathering ? fifo.filter(f => f.is_grandfathered).map(f => `${f.buy_date}: NAV 31-Jan-2018 = ${f.nav_31jan2018}, GF NAV = ${f.grandfathered_nav}, GF Cost = ${f.grandfathered_cost}`).join("\n") : ""}>
                                  {hasGrandfathering && gfCostValue != null ? gfCostValue.toLocaleString("en-IN", {minimumFractionDigits: 2, maximumFractionDigits: 2}) : "-"}
                                </td>
                                <td className={`px-3 py-1.5 text-xs text-right font-mono font-semibold ${actualPL >= 0 ? "text-green-700" : "text-red-600"}`}>
                                  {costValue > 0 ? `${actualPL >= 0 ? "+" : ""}${actualPL.toLocaleString("en-IN", {minimumFractionDigits: 2, maximumFractionDigits: 2})}` : "-"}
                                </td>
                                <td className={`px-3 py-1.5 text-xs text-right font-mono border-r-2 border-gray-300 ${actualPL >= 0 ? "text-green-700" : "text-red-600"}`}>
                                  {costValue > 0 ? `${actualPLPct >= 0 ? "+" : ""}${actualPLPct.toFixed(2)}%` : "-"}
                                </td>
                                <td className={`px-3 py-1.5 text-xs text-right font-mono font-semibold ${hasGrandfathering && gfPL != null ? (gfPL >= 0 ? "text-purple-700" : "text-red-600") : "text-gray-400"}`}>
                                  {hasGrandfathering && gfPL != null ? `${gfPL >= 0 ? "+" : ""}${gfPL.toLocaleString("en-IN", {minimumFractionDigits: 2, maximumFractionDigits: 2})}` : "-"}
                                </td>
                                <td className={`px-3 py-1.5 text-xs text-right font-mono ${hasGrandfathering && gfPLPct != null ? (gfPL >= 0 ? "text-purple-700" : "text-red-600") : "text-gray-400"}`}>
                                  {hasGrandfathering && gfPLPct != null ? `${gfPLPct >= 0 ? "+" : ""}${gfPLPct.toFixed(2)}%` : "-"}
                                </td>
                              </tr>
                            );
                          });
                          return (
                            <>
                              {rows}
                              {/* TOTALS — reflect the current FY + Folio filter */}
                              <tr className="bg-orange-50 border-t-2 border-orange-300 font-semibold sticky bottom-0" data-testid="sold-units-totals-row">
                                <td colSpan={2} className="px-3 py-2 text-xs border-r-2 border-gray-300 text-gray-800">
                                  Total ({totals.count.toLocaleString()} txns)
                                </td>
                                <td className="px-3 py-2 text-xs"></td>
                                <td className="px-3 py-2 text-xs"></td>
                                <td className="px-3 py-2 text-xs text-right font-mono text-red-700">
                                  {totals.sellUnits.toFixed(3)}
                                </td>
                                <td className="px-3 py-2 text-xs text-right font-mono border-r-2 border-gray-300 text-gray-800">
                                  {totals.sellValue.toLocaleString("en-IN", {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                                </td>
                                <td className="px-3 py-2 text-xs"></td>
                                <td className="px-3 py-2 text-xs text-right font-mono text-blue-700">
                                  {totals.fifoUnits.toFixed(3)}
                                </td>
                                <td className="px-3 py-2 text-xs text-right font-mono border-r-2 border-gray-300 text-gray-800">
                                  {totals.costValue > 0 ? totals.costValue.toLocaleString("en-IN", {minimumFractionDigits: 2, maximumFractionDigits: 2}) : "-"}
                                </td>
                                <td className="px-3 py-2 text-xs text-right font-mono border-r-2 border-gray-300 text-purple-700">
                                  {totals.hasGfCost ? totals.gfCost.toLocaleString("en-IN", {minimumFractionDigits: 2, maximumFractionDigits: 2}) : "-"}
                                </td>
                                <td className={`px-3 py-2 text-xs text-right font-mono ${totals.actualPL >= 0 ? "text-green-700" : "text-red-600"}`}>
                                  {totals.costValue > 0 ? `${totals.actualPL >= 0 ? "+" : ""}${totals.actualPL.toLocaleString("en-IN", {minimumFractionDigits: 2, maximumFractionDigits: 2})}` : "-"}
                                </td>
                                <td className={`px-3 py-2 text-xs text-right font-mono border-r-2 border-gray-300 ${totals.actualPL >= 0 ? "text-green-700" : "text-red-600"}`}>
                                  {totals.costValue > 0 ? `${totalActualPLPct >= 0 ? "+" : ""}${totalActualPLPct.toFixed(2)}%` : "-"}
                                </td>
                                <td className={`px-3 py-2 text-xs text-right font-mono ${totals.hasGfPL ? (totals.gfPL >= 0 ? "text-purple-700" : "text-red-600") : "text-gray-400"}`}>
                                  {totals.hasGfPL ? `${totals.gfPL >= 0 ? "+" : ""}${totals.gfPL.toLocaleString("en-IN", {minimumFractionDigits: 2, maximumFractionDigits: 2})}` : "-"}
                                </td>
                                <td className={`px-3 py-2 text-xs text-right font-mono ${totals.hasGfPL ? (totals.gfPL >= 0 ? "text-purple-700" : "text-red-600") : "text-gray-400"}`}>
                                  {totals.hasGfPL && totals.gfCost > 0 ? `${totalGfPLPct >= 0 ? "+" : ""}${totalGfPLPct.toFixed(2)}%` : "-"}
                                </td>
                              </tr>
                            </>
                          );
                          })()}
                        </tbody>
                      </table>
                    </div>
                  </div>

                {/* FY-wise Capital Gains Summary - Equity/Debt × LTCG/STCG */}
                {(() => {
                  // Helper to parse DD-MM-YYYY to Date
                  const parseDate = (ds) => {
                    if (!ds) return null;
                    const parts = ds.split('-');
                    if (parts.length === 3 && parts[2].length === 4) {
                      return new Date(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]));
                    }
                    return null;
                  };

                  // Helper to calculate months between two dates
                  const monthsDiff = (d1, d2) => {
                    if (!d1 || !d2) return 0;
                    return (d2.getFullYear() - d1.getFullYear()) * 12 + (d2.getMonth() - d1.getMonth());
                  };

                  const fyData = {};
                  let txnsForSummary = soldFromAllTxn;
                  if (selectedFolioForSold) {
                    txnsForSummary = txnsForSummary.filter(t => t.folio_no === selectedFolioForSold);
                  }

                  txnsForSummary.forEach(txn => {
                    const fy = getFinancialYear(txn.trade_date || txn.transaction_date) || 'Unknown';
                    if (!fyData[fy]) {
                      fyData[fy] = {
                        equityLT: 0, equityST: 0,
                        debtLT: 0, debtST: 0,
                        equityLT_gf: 0, equityST_gf: 0,
                        hasGF: false, count: 0
                      };
                    }

                    const isEquity = txn.is_equity === true;
                    const sellDate = parseDate(txn.trade_date || txn.transaction_date);
                    const sellValue = Math.abs(parseFloat(txn.amount) || 0);
                    const fifo = txn.fifo_details || [];

                    if (fifo.length > 0) {
                      // Split P/L per FIFO entry based on holding period
                      fifo.forEach(fd => {
                        const buyDate = parseDate(fd.buy_date);
                        const months = monthsDiff(buyDate, sellDate);
                        // Equity: >12m = LTCG; Debt: >36m = LTCG
                        const isLT = isEquity ? months > 12 : months > 36;
                        // Proportional sell value for this FIFO slice
                        const totalFIFOUnits = fifo.reduce((s, f) => s + (f.units_matched || 0), 0);
                        const proportion = totalFIFOUnits > 0 ? (fd.units_matched || 0) / totalFIFOUnits : 0;
                        const sliceSell = sellValue * proportion;
                        const sliceCost = fd.buy_amount || 0;
                        const slicePL = sliceSell - sliceCost;

                        if (isEquity) {
                          if (isLT) { fyData[fy].equityLT += slicePL; }
                          else { fyData[fy].equityST += slicePL; }
                        } else {
                          if (isLT) { fyData[fy].debtLT += slicePL; }
                          else { fyData[fy].debtST += slicePL; }
                        }

                        // GF P/L for equity with grandfathering
                        if (isEquity && fd.is_grandfathered && fd.grandfathered_cost != null) {
                          const gfSlicePL = sliceSell - fd.grandfathered_cost;
                          fyData[fy].hasGF = true;
                          if (isLT) { fyData[fy].equityLT_gf += gfSlicePL; }
                          else { fyData[fy].equityST_gf += gfSlicePL; }
                        } else if (isEquity) {
                          // Non-grandfathered equity: GF P/L = Actual P/L
                          if (isLT) { fyData[fy].equityLT_gf += slicePL; }
                          else { fyData[fy].equityST_gf += slicePL; }
                        }
                      });
                    } else {
                      // No FIFO: treat as short term
                      const costValue = txn.fifo_cost_value || 0;
                      const pl = sellValue - costValue;
                      if (isEquity) { fyData[fy].equityST += pl; }
                      else { fyData[fy].debtST += pl; }
                    }

                    fyData[fy].count += 1;
                  });

                  const displayFYs = selectedFYForSold === "all"
                    ? Object.keys(fyData).filter(fy => fy !== 'Unknown').sort().reverse()
                    : [selectedFYForSold].filter(fy => fyData[fy]);

                  if (displayFYs.length === 0) return null;

                  const fmtAmt = (v) => {
                    const prefix = v >= 0 ? '+' : '';
                    return `${prefix}\u20B9${Math.abs(v).toLocaleString('en-IN', {maximumFractionDigits: 0})}`;
                  };
                  const plColor = (v) => v >= 0 ? 'text-green-700' : 'text-red-600';

                  return (
                    <div className="mt-6 pt-4 border-t-2 border-gray-200">
                      <h4 className="text-md font-semibold text-gray-800 mb-3">Capital Gains Summary by Financial Year</h4>
                      <div className={`grid gap-4 ${displayFYs.length === 1 ? 'grid-cols-1 max-w-md' : displayFYs.length <= 3 ? 'grid-cols-1 sm:grid-cols-3' : 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4'}`}>
                        {displayFYs.map(fy => {
                          const d = fyData[fy];
                          if (!d) return null;
                          return (
                            <Card key={fy} className="border-gray-200">
                              <CardContent className="p-3">
                                <div className="flex items-center justify-between mb-2">
                                  <span className="font-semibold text-gray-800 text-sm">{fy}</span>
                                  <Badge variant="outline" className="text-gray-500 text-xs">{d.count} txns</Badge>
                                </div>
                                {/* Actual P/L Table */}
                                <p className="text-[10px] text-gray-500 mb-1 font-medium">Actual Capital Gains</p>
                                <table className="w-full text-xs border border-gray-200 mb-2">
                                  <thead>
                                    <tr className="bg-gray-50">
                                      <th className="border border-gray-200 px-2 py-1 text-left font-medium text-gray-600"></th>
                                      <th className="border border-gray-200 px-2 py-1 text-right font-medium text-gray-600">Long Term</th>
                                      <th className="border border-gray-200 px-2 py-1 text-right font-medium text-gray-600">Short Term</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    <tr>
                                      <td className="border border-gray-200 px-2 py-1 font-medium text-gray-700">Equity</td>
                                      <td className={`border border-gray-200 px-2 py-1 text-right font-mono ${plColor(d.equityLT)}`}>{fmtAmt(d.equityLT)}</td>
                                      <td className={`border border-gray-200 px-2 py-1 text-right font-mono ${plColor(d.equityST)}`}>{fmtAmt(d.equityST)}</td>
                                    </tr>
                                    <tr>
                                      <td className="border border-gray-200 px-2 py-1 font-medium text-gray-700">Debt</td>
                                      <td className={`border border-gray-200 px-2 py-1 text-right font-mono ${plColor(d.debtLT)}`}>{fmtAmt(d.debtLT)}</td>
                                      <td className={`border border-gray-200 px-2 py-1 text-right font-mono ${plColor(d.debtST)}`}>{fmtAmt(d.debtST)}</td>
                                    </tr>
                                  </tbody>
                                </table>
                                {/* GF P/L Table (only for equity when grandfathering applies) */}
                                {d.hasGF && (
                                  <>
                                    <p className="text-[10px] text-purple-500 mb-1 font-medium">Grandfathered Capital Gains</p>
                                    <table className="w-full text-xs border border-purple-200">
                                      <thead>
                                        <tr className="bg-purple-50">
                                          <th className="border border-purple-200 px-2 py-1 text-left font-medium text-purple-600"></th>
                                          <th className="border border-purple-200 px-2 py-1 text-right font-medium text-purple-600">Long Term</th>
                                          <th className="border border-purple-200 px-2 py-1 text-right font-medium text-purple-600">Short Term</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        <tr>
                                          <td className="border border-purple-200 px-2 py-1 font-medium text-purple-700">Equity</td>
                                          <td className={`border border-purple-200 px-2 py-1 text-right font-mono ${d.equityLT_gf >= 0 ? 'text-purple-700' : 'text-red-600'}`}>{fmtAmt(d.equityLT_gf)}</td>
                                          <td className={`border border-purple-200 px-2 py-1 text-right font-mono ${d.equityST_gf >= 0 ? 'text-purple-700' : 'text-red-600'}`}>{fmtAmt(d.equityST_gf)}</td>
                                        </tr>
                                      </tbody>
                                    </table>
                                  </>
                                )}
                              </CardContent>
                            </Card>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}

              </>
            )}
          </CardContent>
        </Card>
      ) : activeTab === "idcw_payouts" ? (
        /* Dividend Payout Tab - sourced from allTransactions */
        <Card className="border-gray-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-800">Dividend Payouts</h3>
                <p className="text-sm text-gray-500">Dividend payout entries from Transaction Master</p>
              </div>
              <div className="flex items-center gap-2">
                {/* Financial Year Filter */}
                <Select value={selectedFYForIdcw} onValueChange={setSelectedFYForIdcw}>
                  <SelectTrigger className="w-[140px]">
                    <SelectValue placeholder="All FYs" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All FYs</SelectItem>
                    {(() => {
                      const fys = new Set();
                      dividendFromAllTxn.forEach(txn => {
                        const fy = getFinancialYear(txn.trade_date || txn.transaction_date);
                        if (fy) fys.add(fy);
                      });
                      return Array.from(fys).sort().reverse().map(fy => (
                        <SelectItem key={fy} value={fy}>{fy}</SelectItem>
                      ));
                    })()}
                  </SelectContent>
                </Select>
                <Badge variant="outline" className="text-green-600 border-green-200 bg-green-50">
                  {dividendFromAllTxn.length.toLocaleString()} records
                </Badge>
                <Button variant="outline" size="sm" className="h-8 gap-1.5"
                  onClick={() => handleExportTabExcel("idcw_payouts")}
                  data-testid="download-idcw-xlsx">
                  <Download className="w-3.5 h-3.5" />
                  Download
                </Button>
              </div>
            </div>
            
            {allTransactionsLoading ? (
              <div className="flex items-center justify-center py-16">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600" />
              </div>
            ) : dividendFromAllTxn.length === 0 ? (
              <div className="text-center py-16 text-gray-500">
                <DollarSign className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p className="font-medium">No dividend payout transactions found</p>
                <p className="text-sm mt-1">No dividend payouts for this client</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-green-50 border-b-2 border-gray-400">
                      <th className="text-xs font-medium text-gray-600 px-3 py-2 text-left">Date</th>
                      <th className="text-xs font-medium text-gray-600 px-3 py-2 text-left">Folio No</th>
                      {showFamilyClientCol && (
                        <th className="text-xs font-medium text-gray-600 px-3 py-2 text-left whitespace-nowrap">Client</th>
                      )}
                      <th className="text-xs font-medium text-gray-600 px-3 py-2 text-left">Scheme</th>
                      <th className="text-xs font-medium text-gray-600 px-3 py-2 text-left">Description</th>
                      <th className="text-xs font-medium text-gray-600 px-3 py-2 text-right">Units</th>
                      <th className="text-xs font-medium text-gray-600 px-3 py-2 text-right">NAV</th>
                      <th className="text-xs font-medium text-gray-600 px-3 py-2 text-right">Amount</th>
                      <th className="text-xs font-medium text-gray-600 px-3 py-2 text-left">Source</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dividendFromAllTxn
                      .filter(txn => selectedFYForIdcw === "all" || getFinancialYear(txn.trade_date || txn.transaction_date) === selectedFYForIdcw)
                      .map((txn, idx) => (
                      <tr key={`idcw-${idx}`} className="hover:bg-green-50/50 border-b">
                        <td className="px-3 py-1.5 text-xs font-mono">{formatDate(txn.trade_date || txn.transaction_date)}</td>
                        <td className="px-3 py-1.5 text-xs font-mono">{txn.folio_no}</td>
                        {showFamilyClientCol && (
                          <td className="px-3 py-1.5 text-xs text-gray-700 whitespace-nowrap">{resolveRowClientName(txn)}</td>
                        )}
                        <td className="px-3 py-1.5 text-xs max-w-[200px] truncate" title={toTitleCase(txn.scheme_name) || txn.scheme_name}>{toTitleCase(txn.scheme_name)}</td>
                        <td className="px-3 py-1.5">
                          <span className={`text-[10px] px-1.5 py-0.5 rounded ${(txn.treatment || "").toLowerCase().includes("add units") ? "bg-green-100 text-green-700" : (txn.treatment || "").toLowerCase().includes("minus units") ? "bg-red-100 text-red-700" : "bg-gray-100 text-gray-600"}`}>{txn.description || txn.flag || "-"}</span>
                        </td>
                        <td className="px-3 py-1.5 text-xs text-right font-mono">
                          {parseFloat(txn.units || 0) !== 0 ? parseFloat(txn.units).toLocaleString('en-IN', { minimumFractionDigits: 3 }) : "-"}
                        </td>
                        <td className="px-3 py-1.5 text-xs text-right font-mono">
                          {parseFloat(txn.nav || 0) > 0 ? parseFloat(txn.nav).toLocaleString('en-IN', { minimumFractionDigits: 4 }) : "-"}
                        </td>
                        <td className={`px-3 py-1.5 text-xs text-right font-mono ${parseFloat(txn.amount || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {parseFloat(txn.amount || 0) !== 0 ? `₹${Math.abs(parseFloat(txn.amount)).toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : "-"}
                        </td>
                        <td className="px-3 py-1.5 text-xs text-gray-500">{txn.source || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      ) : activeTab === "segregated" ? (
        /* Segregated Funds Tab - sourced from allTransactions */
        <Card className="border-gray-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-800">Segregated Funds</h3>
                <p className="text-sm text-gray-500">Transactions in segregated portfolio schemes</p>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-amber-600 border-amber-200 bg-amber-50">
                  {segregatedFromAllTxn.length.toLocaleString()} entries
                </Badge>
                <Button variant="outline" size="sm" className="h-8 gap-1.5"
                  onClick={() => handleExportTabExcel("segregated")}
                  data-testid="download-segregated-xlsx">
                  <Download className="w-3.5 h-3.5" />
                  Download
                </Button>
              </div>
            </div>

            {allTransactionsLoading ? (
              <div className="flex items-center justify-center py-16">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600" />
              </div>
            ) : segregatedFromAllTxn.length === 0 ? (
              <div className="text-center py-16 text-gray-500">
                <AlertTriangle className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p className="font-medium">No segregated fund transactions found</p>
                <p className="text-sm mt-1">No segregated portfolio entries for this client</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-amber-50 border-b-2 border-gray-400">
                      <th className="text-xs font-medium text-gray-600 px-3 py-2 text-left">Date</th>
                      <th className="text-xs font-medium text-gray-600 px-3 py-2 text-left">Folio No</th>
                      <th className="text-xs font-medium text-gray-600 px-3 py-2 text-left" style={{minWidth: "200px"}}>Scheme Name</th>
                      <th className="text-xs font-medium text-gray-600 px-3 py-2 text-left">Description</th>
                      <th className="text-xs font-medium text-gray-600 px-3 py-2 text-right">Units</th>
                      <th className="text-xs font-medium text-gray-600 px-3 py-2 text-right">NAV</th>
                      <th className="text-xs font-medium text-gray-600 px-3 py-2 text-right">Amount</th>
                      <th className="text-xs font-medium text-gray-600 px-3 py-2 text-left">Source</th>
                    </tr>
                  </thead>
                  <tbody>
                    {segregatedFromAllTxn.map((txn, idx) => (
                      <tr key={`seg-${idx}`} className="hover:bg-amber-50/50 border-b">
                        <td className="px-3 py-1.5 text-xs font-mono whitespace-nowrap">{formatDate(txn.trade_date || txn.transaction_date)}</td>
                        <td className="px-3 py-1.5 text-xs font-mono">{txn.folio_no}</td>
                        <td className="px-3 py-1.5 text-xs" style={{minWidth: "200px"}}>{txn.scheme_name}</td>
                        <td className="px-3 py-1.5">
                          <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                            (txn.treatment || "").toLowerCase().includes("add units") ? "bg-green-100 text-green-700" :
                            (txn.treatment || "").toLowerCase().includes("minus units") ? "bg-red-100 text-red-700" :
                            "bg-amber-100 text-amber-700"
                          }`}>{txn.description || txn.flag || "-"}</span>
                        </td>
                        <td className="px-3 py-1.5 text-xs text-right font-mono">
                          {parseFloat(txn.units || 0) !== 0 ? parseFloat(txn.units).toLocaleString('en-IN', { minimumFractionDigits: 3 }) : "-"}
                        </td>
                        <td className="px-3 py-1.5 text-xs text-right font-mono">
                          {parseFloat(txn.nav || 0) > 0 ? parseFloat(txn.nav).toLocaleString('en-IN', { minimumFractionDigits: 4 }) : "-"}
                        </td>
                        <td className={`px-3 py-1.5 text-xs text-right font-mono ${parseFloat(txn.amount || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {parseFloat(txn.amount || 0) !== 0 ? `\u20B9${Math.abs(parseFloat(txn.amount)).toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : "-"}
                        </td>
                        <td className="px-3 py-1.5 text-xs text-gray-500">{txn.source || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      ) : activeTab === "folio_details" ? (
        /* Folio Details Tab - KYC Information */
        <Card className="border-gray-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Folio Details & KYC Information</h3>
                <p className="text-sm text-gray-500">Joint holders, nominees, and contact details from Investor Master</p>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="secondary" className="bg-blue-100 text-blue-700">
                  {[...new Set(folioDetailsFilteredByPattern.map(h => h.folio_no))].length} folios
                </Badge>
                <Button variant="outline" size="sm" className="h-8 gap-1.5"
                  onClick={() => handleExportTabExcel("folio_details")}
                  data-testid="download-folio-details-xlsx">
                  <Download className="w-3.5 h-3.5" />
                  Download
                </Button>
              </div>
            </div>

            {/* Search Bar */}
            <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex items-center gap-3">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Search by folio, scheme, PAN, mobile, email, nominee..."
                    value={folioSearchTerm}
                    onChange={(e) => setFolioSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
                {folioSearchTerm && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setFolioSearchTerm("")}
                    className="text-gray-500"
                  >
                    Clear
                  </Button>
                )}
              </div>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-16">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
              </div>
            ) : folioDetailsFilteredByPattern.length === 0 ? (
              <div className="text-center py-16 text-gray-500">
                No folios found for this selection
              </div>
            ) : (
              <div className="space-y-4">
                {/* Group by unique folio numbers and sort by total AUM descending */}
                {(() => {
                  // Use investor_master data for KYC display
                  let filteredData = folioDetailsFilteredByPattern;
                  
                  // Apply search filter - searches all fields
                  if (folioSearchTerm) {
                    const term = folioSearchTerm.toLowerCase();
                    filteredData = filteredData.filter(h => (
                      h.folio_no?.toLowerCase().includes(term) ||
                      h.scheme_name?.toLowerCase().includes(term) ||
                      h.fund_desc?.toLowerCase().includes(term) ||
                      h.pan_1?.toLowerCase().includes(term) ||
                      h.mobile?.toLowerCase().includes(term) ||
                      h.email?.toLowerCase().includes(term) ||
                      h.nominee_1?.toLowerCase().includes(term) ||
                      h.nominee_2?.toLowerCase().includes(term) ||
                      h.first_holder?.toLowerCase().includes(term)
                    ));
                  }
                  
                  // Get AUM per folio from portfolio data
                  const folioAums = folioAumMap;
                  
                  // Get unique folios sorted by AUM descending
                  const sortedFolios = [...new Set(filteredData.map(h => h.folio_no))]
                    .sort((a, b) => (folioAums[b] || 0) - (folioAums[a] || 0));
                  
                  if (sortedFolios.length === 0 && folioSearchTerm) {
                    return (
                      <div className="text-center py-16 text-gray-500">
                        No folios match your search criteria
                      </div>
                    );
                  }
                  
                  return sortedFolios.map((folioNo, index) => {
                    // Get ALL schemes for this folio
                    const folioSchemes = filteredData.filter(h => h.folio_no === folioNo);
                    const holding = folioSchemes[0]; // Use first scheme for KYC details
                    if (!holding) return null;
                    
                    const folioAum = folioAums[folioNo] || 0;
                    
                    // Check for missing fields
                    const missingFields = [];
                    if (!holding.pan_1 && !holding.guardian_pan) missingFields.push("PAN");
                    if (!holding.mobile) missingFields.push("Mobile");
                    if (!holding.email) missingFields.push("Email");
                    if (!holding.nominee_1) missingFields.push("Nominee");
                    if (!holding.ckyc_no) missingFields.push("CKYC");
                    
                    // Build full address
                    const fullAddress = [holding.address1, holding.address2, holding.address3]
                      .filter(Boolean).join(', ');
                    const cityStatePin = [holding.city, holding.state, holding.pincode].filter(Boolean).join(', ');
                    
                    return (
                      <Card key={folioNo} className="border-gray-200 bg-white shadow-sm">
                        <CardContent className="p-5">
                          {/* Header with Folio and AUM */}
                          <div className="flex items-center justify-between mb-4 pb-3 border-b border-gray-200">
                            <div className="flex items-center gap-3">
                              <FileText className="w-5 h-5 text-blue-600" />
                              <span className="font-semibold text-gray-800 text-lg">Folio: {folioNo}</span>
                              <Badge variant="outline" className="text-sm bg-gray-100 text-gray-600">
                                {holding.source || 'N/A'}
                              </Badge>
                              <Badge variant="outline" className="text-sm bg-indigo-50 text-indigo-700 border-indigo-200">
                                {folioSchemes.length} {folioSchemes.length === 1 ? 'Scheme' : 'Schemes'}
                              </Badge>
                              <Badge 
                                variant="outline" 
                                className={`text-sm ${
                                  holding.holding_pattern === 'joint_holder' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                                  holding.holding_pattern === 'minor_guardian' ? 'bg-purple-50 text-purple-700 border-purple-200' :
                                  'bg-green-50 text-green-700 border-green-200'
                                }`}
                              >
                                {toTitleCase(holding.holding_description) || toTitleCase(holding.mode_of_hold) || 'Single Holder'}
                              </Badge>
                            </div>
                            <div className="text-right">
                              <div className="text-sm text-gray-500">Total AUM</div>
                              <div className="text-xl font-bold text-teal-700">{formatCurrency(folioAum)}</div>
                            </div>
                          </div>
                          
                          {/* Missing Fields Alert */}
                          {missingFields.length > 0 && (
                            <div className="mb-4 p-2 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 flex-wrap">
                              <AlertTriangle className="w-4 h-4 text-red-500" />
                              <span className="text-sm text-red-700">Missing:</span>
                              {missingFields.map(field => (
                                <Badge key={field} variant="outline" className="text-sm bg-red-100 text-red-700 border-red-300">
                                  {field}
                                </Badge>
                              ))}
                            </div>
                          )}

                          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                            {/* Holder Details Section - Combined Photo ID + Communication */}
                            <div className="bg-blue-50/50 rounded-lg p-4 border border-blue-100">
                              <div className="flex items-center gap-2 mb-3 pb-2 border-b border-blue-200">
                                <User className="w-4 h-4 text-blue-600" />
                                <span className="font-semibold text-blue-800">Holder Details</span>
                              </div>
                              
                              {/* Combined Holders Table - Photo ID + Communication */}
                              <div className="overflow-x-auto">
                                <table className="w-full text-sm border-collapse">
                                  <thead>
                                    <tr className="border-b border-blue-200 bg-blue-100/50">
                                      <th className="text-left py-2 px-2 text-gray-600 font-semibold w-28"></th>
                                      <th className="text-left py-2 px-2 text-blue-800 font-semibold">First Holder</th>
                                      <th className="text-left py-2 px-2 text-blue-800 font-semibold">Second Holder</th>
                                      <th className="text-left py-2 px-2 text-blue-800 font-semibold">Third Holder</th>
                                      <th className="text-left py-2 px-2 text-purple-800 font-semibold">Guardian</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {/* === PHOTO ID SECTION === */}
                                    <tr className="bg-blue-100/70">
                                      <td colSpan="5" className="py-1 px-2 text-blue-700 font-semibold text-xs uppercase tracking-wide">Photo ID Details</td>
                                    </tr>
                                    {/* Name Row */}
                                    <tr className="border-b border-blue-100">
                                      <td className="py-2 px-2 text-gray-600 font-medium bg-yellow-100">Name</td>
                                      <td className="py-2 px-2 text-gray-800">{toTitleCase(holding.first_holder) || '-'}</td>
                                      <td className="py-2 px-2 text-gray-700">{toTitleCase(holding.second_holder) || '-'}</td>
                                      <td className="py-2 px-2 text-gray-700">{toTitleCase(holding.third_holder) || '-'}</td>
                                      <td className="py-2 px-2 text-gray-700">{toTitleCase(holding.guardian_name) || '-'}</td>
                                    </tr>
                                    {/* PAN Row */}
                                    <tr className="border-b border-blue-100">
                                      <td className="py-2 px-2 text-gray-600 font-medium bg-yellow-100">PAN</td>
                                      <td className="py-2 px-2 font-mono text-gray-700">{holding.pan_1?.toUpperCase() || '-'}</td>
                                      <td className="py-2 px-2 font-mono text-gray-700">{holding.pan_2?.toUpperCase() || '-'}</td>
                                      <td className="py-2 px-2 font-mono text-gray-700">{holding.pan_3?.toUpperCase() || '-'}</td>
                                      <td className="py-2 px-2 font-mono text-gray-700">{holding.guardian_pan?.toUpperCase() || '-'}</td>
                                    </tr>
                                    {/* Date of Birth Row */}
                                    <tr className="border-b border-blue-100">
                                      <td className="py-2 px-2 text-gray-600 font-medium bg-yellow-100">Date of Birth</td>
                                      <td className="py-2 px-2 text-gray-700">{holding.dob ? formatDate(holding.dob) : '-'}</td>
                                      <td className="py-2 px-2 text-gray-700">{holding.dob_2 ? formatDate(holding.dob_2) : '-'}</td>
                                      <td className="py-2 px-2 text-gray-700">{holding.dob_3 ? formatDate(holding.dob_3) : '-'}</td>
                                      <td className="py-2 px-2 text-gray-700">{holding.guardian_dob ? formatDate(holding.guardian_dob) : '-'}</td>
                                    </tr>
                                    {/* Tax Category Row */}
                                    <tr className="border-b border-blue-100">
                                      <td className="py-2 px-2 text-gray-600 font-medium bg-yellow-100">Tax Category</td>
                                      <td className="py-2 px-2 text-gray-700">{toTitleCase(holding.category_desc) || holding.tax_category || '-'}</td>
                                      <td className="py-2 px-2 text-gray-400">-</td>
                                      <td className="py-2 px-2 text-gray-400">-</td>
                                      <td className="py-2 px-2 text-gray-400">-</td>
                                    </tr>
                                    {/* Tax Status Row */}
                                    <tr className="border-b border-blue-100">
                                      <td className="py-2 px-2 text-gray-600 font-medium bg-yellow-100">Tax Status</td>
                                      <td className="py-2 px-2 text-gray-700">{toTitleCase(holding.tax_status) || '-'}</td>
                                      <td className="py-2 px-2 text-gray-400">-</td>
                                      <td className="py-2 px-2 text-gray-400">-</td>
                                      <td className="py-2 px-2 text-gray-400">-</td>
                                    </tr>
                                    {/* CAN No Row */}
                                    <tr className="border-b border-blue-100">
                                      <td className="py-2 px-2 text-gray-600 font-medium bg-yellow-100">CAN No</td>
                                      <td className="py-2 px-2 font-mono text-gray-700">{holding.can_no || '-'}</td>
                                      <td className="py-2 px-2 text-gray-400">-</td>
                                      <td className="py-2 px-2 text-gray-400">-</td>
                                      <td className="py-2 px-2 text-gray-400">-</td>
                                    </tr>
                                    {/* CKYC Row */}
                                    <tr className="border-b border-blue-100">
                                      <td className="py-2 px-2 text-gray-600 font-medium bg-yellow-100">CKYC</td>
                                      <td className="py-2 px-2 font-mono text-gray-700">{holding.ckyc_first || '-'}</td>
                                      <td className="py-2 px-2 font-mono text-gray-700">{holding.ckyc_second || '-'}</td>
                                      <td className="py-2 px-2 font-mono text-gray-700">{holding.ckyc_third || '-'}</td>
                                      <td className="py-2 px-2 font-mono text-gray-700">{holding.guardian_ckyc || '-'}</td>
                                    </tr>
                                    
                                    {/* === COMMUNICATION SECTION === */}
                                    <tr className="bg-teal-100/70">
                                      <td colSpan="5" className="py-1 px-2 text-teal-700 font-semibold text-xs uppercase tracking-wide">Communication</td>
                                    </tr>
                                    {/* Address Row */}
                                    <tr className="border-b border-blue-100">
                                      <td className="py-2 px-2 text-gray-600 font-medium bg-yellow-100">Address</td>
                                      <td className="py-2 px-2 text-gray-700 text-xs" colSpan="4">
                                        {toTitleCase([holding.address1, holding.address2, holding.address3].filter(Boolean).join(', ')) || toTitleCase(holding.address) || '-'}
                                      </td>
                                    </tr>
                                    {/* City Row */}
                                    <tr className="border-b border-blue-100">
                                      <td className="py-2 px-2 text-gray-600 font-medium bg-yellow-100">City</td>
                                      <td className="py-2 px-2 text-gray-700" colSpan="4">{toTitleCase(holding.city) || '-'}</td>
                                    </tr>
                                    {/* Pincode Row */}
                                    <tr className="border-b border-blue-100">
                                      <td className="py-2 px-2 text-gray-600 font-medium bg-yellow-100">Pincode</td>
                                      <td className="py-2 px-2 font-mono text-gray-700" colSpan="4">{holding.pincode || '-'}</td>
                                    </tr>
                                    {/* State Row */}
                                    <tr className="border-b border-blue-100">
                                      <td className="py-2 px-2 text-gray-600 font-medium bg-yellow-100">State</td>
                                      <td className="py-2 px-2 text-gray-700" colSpan="4">{toTitleCase(holding.state) || '-'}</td>
                                    </tr>
                                    {/* Country Row */}
                                    <tr className="border-b border-blue-100">
                                      <td className="py-2 px-2 text-gray-600 font-medium bg-yellow-100">Country</td>
                                      <td className="py-2 px-2 text-gray-700" colSpan="4">{toTitleCase(holding.country) || '-'}</td>
                                    </tr>
                                    {/* Mobile Row */}
                                    <tr className="border-b border-blue-100">
                                      <td className="py-2 px-2 text-gray-600 font-medium bg-yellow-100">Mobile</td>
                                      <td className={`py-2 px-2 font-mono ${holding.mobile ? 'text-gray-700' : 'text-red-400'}`}>
                                        {holding.mobile || 'Not available'}
                                      </td>
                                      <td className="py-2 px-2 font-mono text-gray-700">{holding.mobile_2 || '-'}</td>
                                      <td className="py-2 px-2 font-mono text-gray-700">{holding.mobile_3 || '-'}</td>
                                      <td className="py-2 px-2 font-mono text-gray-700">{holding.guardian_mobile || '-'}</td>
                                    </tr>
                                    {/* Email Row */}
                                    <tr className="border-b border-blue-100">
                                      <td className="py-2 px-2 text-gray-600 font-medium bg-yellow-100">Email</td>
                                      <td className={`py-2 px-2 text-xs ${holding.email ? 'text-gray-700 break-all' : 'text-red-400'}`}>
                                        {holding.email?.toLowerCase() || 'Not available'}
                                      </td>
                                      <td className="py-2 px-2 text-gray-700 break-all text-xs">{holding.email_2?.toLowerCase() || '-'}</td>
                                      <td className="py-2 px-2 text-gray-700 break-all text-xs">{holding.email_3?.toLowerCase() || '-'}</td>
                                      <td className="py-2 px-2 text-gray-700 break-all text-xs">{holding.guardian_email?.toLowerCase() || '-'}</td>
                                    </tr>
                                    {/* Phone Row */}
                                    <tr>
                                      <td className="py-2 px-2 text-gray-600 font-medium bg-yellow-100">Phone</td>
                                      <td className="py-2 px-2 font-mono text-gray-700">{holding.phone || holding.tel_off || '-'}</td>
                                      <td className="py-2 px-2 font-mono text-gray-700">{holding.phone_2 || '-'}</td>
                                      <td className="py-2 px-2 font-mono text-gray-700">{holding.phone_3 || '-'}</td>
                                      <td className="py-2 px-2 font-mono text-gray-700">{holding.guardian_phone || '-'}</td>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>
                            </div>

                            {/* Nominee Details Section - Full details like Photo ID + Communication */}
                            <div className="bg-orange-50/50 rounded-lg p-4 border border-orange-100">
                              <div className="flex items-center gap-2 mb-3 pb-2 border-b border-orange-200">
                                <Target className="w-4 h-4 text-orange-600" />
                                <span className="font-semibold text-orange-800">Nominee Details</span>
                              </div>
                              
                              {/* Nominee Table - Photo ID + Communication combined */}
                              <div className="overflow-x-auto">
                                <table className="w-full text-sm border-collapse">
                                  <thead>
                                    <tr className="border-b border-orange-200 bg-orange-100/50">
                                      <th className="text-left py-2 px-2 text-gray-600 font-semibold w-28"></th>
                                      <th className="text-left py-2 px-2 text-orange-800 font-semibold">Nominee 1</th>
                                      <th className="text-left py-2 px-2 text-orange-800 font-semibold">Nominee 2</th>
                                      <th className="text-left py-2 px-2 text-orange-800 font-semibold">Nominee 3</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {/* === PHOTO ID SECTION === */}
                                    <tr className="bg-blue-50">
                                      <td colSpan="4" className="py-1 px-2 text-blue-700 font-semibold text-xs uppercase tracking-wide">Photo ID Details</td>
                                    </tr>
                                    {/* Name Row */}
                                    <tr className="border-b border-orange-100">
                                      <td className="py-2 px-2 text-gray-600 font-medium bg-yellow-100">Name</td>
                                      <td className={`py-2 px-2 ${holding.nominee_1 ? 'text-gray-700' : 'text-red-400 italic'}`}>
                                        {toTitleCase(holding.nominee_1) || 'Not registered'}
                                      </td>
                                      <td className="py-2 px-2 text-gray-700">{toTitleCase(holding.nominee_2) || '-'}</td>
                                      <td className="py-2 px-2 text-gray-700">{toTitleCase(holding.nominee_3) || '-'}</td>
                                    </tr>
                                    {/* PAN Row */}
                                    <tr className="border-b border-orange-100">
                                      <td className="py-2 px-2 text-gray-600 font-medium bg-yellow-100">PAN</td>
                                      <td className="py-2 px-2 font-mono text-gray-700">{holding.nominee_pan1?.toUpperCase() || '-'}</td>
                                      <td className="py-2 px-2 font-mono text-gray-700">{holding.nominee_pan2?.toUpperCase() || '-'}</td>
                                      <td className="py-2 px-2 font-mono text-gray-700">{holding.nominee_pan3?.toUpperCase() || '-'}</td>
                                    </tr>
                                    {/* DOB Row */}
                                    <tr className="border-b border-orange-100">
                                      <td className="py-2 px-2 text-gray-600 font-medium bg-yellow-100">Date of Birth</td>
                                      <td className="py-2 px-2 text-gray-700">{holding.nominee_dob1 ? formatDate(holding.nominee_dob1) : '-'}</td>
                                      <td className="py-2 px-2 text-gray-700">{holding.nominee_dob2 ? formatDate(holding.nominee_dob2) : '-'}</td>
                                      <td className="py-2 px-2 text-gray-700">{holding.nominee_dob3 ? formatDate(holding.nominee_dob3) : '-'}</td>
                                    </tr>
                                    {/* Relation Row */}
                                    <tr className="border-b border-orange-100">
                                      <td className="py-2 px-2 text-gray-600 font-medium bg-yellow-100">Relation</td>
                                      <td className="py-2 px-2 text-gray-700">{toTitleCase(holding.nominee_rel1) || '-'}</td>
                                      <td className="py-2 px-2 text-gray-700">{toTitleCase(holding.nominee_rel2) || '-'}</td>
                                      <td className="py-2 px-2 text-gray-700">{toTitleCase(holding.nominee_rel3) || '-'}</td>
                                    </tr>
                                    {/* Percentage Row */}
                                    <tr className="border-b border-orange-100">
                                      <td className="py-2 px-2 text-gray-600 font-medium bg-yellow-100">Percentage</td>
                                      <td className="py-2 px-2 text-gray-700">{holding.nominee_ratio1 || holding.nominee_1_pct || '-'}</td>
                                      <td className="py-2 px-2 text-gray-700">{holding.nominee_ratio2 || holding.nominee_2_pct || '-'}</td>
                                      <td className="py-2 px-2 text-gray-700">{holding.nominee_ratio3 || holding.nominee_3_pct || '-'}</td>
                                    </tr>
                                    {/* CKYC Row */}
                                    <tr className="border-b border-orange-100">
                                      <td className="py-2 px-2 text-gray-600 font-medium bg-yellow-100">CKYC</td>
                                      <td className="py-2 px-2 font-mono text-gray-700">{holding.nominee_ckyc1 || '-'}</td>
                                      <td className="py-2 px-2 font-mono text-gray-700">{holding.nominee_ckyc2 || '-'}</td>
                                      <td className="py-2 px-2 font-mono text-gray-700">{holding.nominee_ckyc3 || '-'}</td>
                                    </tr>
                                    
                                    {/* === COMMUNICATION SECTION === */}
                                    <tr className="bg-teal-50">
                                      <td colSpan="4" className="py-1 px-2 text-teal-700 font-semibold text-xs uppercase tracking-wide">Communication</td>
                                    </tr>
                                    {/* Address Row */}
                                    <tr className="border-b border-orange-100">
                                      <td className="py-2 px-2 text-gray-600 font-medium bg-yellow-100">Address</td>
                                      <td className="py-2 px-2 text-gray-700 text-xs">{toTitleCase(holding.nominee_addr1) || '-'}</td>
                                      <td className="py-2 px-2 text-gray-700 text-xs">{toTitleCase(holding.nominee_addr2) || '-'}</td>
                                      <td className="py-2 px-2 text-gray-700 text-xs">{toTitleCase(holding.nominee_addr3) || '-'}</td>
                                    </tr>
                                    {/* City Row */}
                                    <tr className="border-b border-orange-100">
                                      <td className="py-2 px-2 text-gray-600 font-medium bg-yellow-100">City</td>
                                      <td className="py-2 px-2 text-gray-700">{toTitleCase(holding.nominee_city1) || '-'}</td>
                                      <td className="py-2 px-2 text-gray-700">{toTitleCase(holding.nominee_city2) || '-'}</td>
                                      <td className="py-2 px-2 text-gray-700">{toTitleCase(holding.nominee_city3) || '-'}</td>
                                    </tr>
                                    {/* Pincode Row */}
                                    <tr className="border-b border-orange-100">
                                      <td className="py-2 px-2 text-gray-600 font-medium bg-yellow-100">Pincode</td>
                                      <td className="py-2 px-2 font-mono text-gray-700">{holding.nominee_pin1 || '-'}</td>
                                      <td className="py-2 px-2 font-mono text-gray-700">{holding.nominee_pin2 || '-'}</td>
                                      <td className="py-2 px-2 font-mono text-gray-700">{holding.nominee_pin3 || '-'}</td>
                                    </tr>
                                    {/* State Row */}
                                    <tr className="border-b border-orange-100">
                                      <td className="py-2 px-2 text-gray-600 font-medium bg-yellow-100">State</td>
                                      <td className="py-2 px-2 text-gray-700">{toTitleCase(holding.nominee_state1) || '-'}</td>
                                      <td className="py-2 px-2 text-gray-700">{toTitleCase(holding.nominee_state2) || '-'}</td>
                                      <td className="py-2 px-2 text-gray-700">{toTitleCase(holding.nominee_state3) || '-'}</td>
                                    </tr>
                                    {/* Mobile Row */}
                                    <tr className="border-b border-orange-100">
                                      <td className="py-2 px-2 text-gray-600 font-medium bg-yellow-100">Mobile</td>
                                      <td className="py-2 px-2 font-mono text-gray-700">{holding.nominee_mobile1 || '-'}</td>
                                      <td className="py-2 px-2 font-mono text-gray-700">{holding.nominee_mobile2 || '-'}</td>
                                      <td className="py-2 px-2 font-mono text-gray-700">{holding.nominee_mobile3 || '-'}</td>
                                    </tr>
                                    {/* Email Row */}
                                    <tr className="border-b border-orange-100">
                                      <td className="py-2 px-2 text-gray-600 font-medium bg-yellow-100">Email</td>
                                      <td className="py-2 px-2 text-gray-700 break-all text-xs">{holding.nominee_email1?.toLowerCase() || '-'}</td>
                                      <td className="py-2 px-2 text-gray-700 break-all text-xs">{holding.nominee_email2?.toLowerCase() || '-'}</td>
                                      <td className="py-2 px-2 text-gray-700 break-all text-xs">{holding.nominee_email3?.toLowerCase() || '-'}</td>
                                    </tr>
                                    
                                    {/* === GUARDIAN SECTION (for minor nominees) === */}
                                    <tr className="bg-purple-50">
                                      <td colSpan="4" className="py-1 px-2 text-purple-700 font-semibold text-xs uppercase tracking-wide">Guardian (if minor)</td>
                                    </tr>
                                    {/* Guardian Name Row */}
                                    <tr className="border-b border-orange-100">
                                      <td className="py-2 px-2 text-gray-600 font-medium bg-yellow-100">Guardian Name</td>
                                      <td className="py-2 px-2 text-gray-700">{toTitleCase(holding.nominee_guard1) || '-'}</td>
                                      <td className="py-2 px-2 text-gray-700">{toTitleCase(holding.nominee_guard2) || '-'}</td>
                                      <td className="py-2 px-2 text-gray-700">{toTitleCase(holding.nominee_guard3) || '-'}</td>
                                    </tr>
                                    {/* Guardian PAN Row */}
                                    <tr className="border-b border-orange-100">
                                      <td className="py-2 px-2 text-gray-600 font-medium bg-yellow-100">Guardian PAN</td>
                                      <td className="py-2 px-2 font-mono text-gray-700">{holding.nominee_guard_pan1?.toUpperCase() || '-'}</td>
                                      <td className="py-2 px-2 font-mono text-gray-700">{holding.nominee_guard_pan2?.toUpperCase() || '-'}</td>
                                      <td className="py-2 px-2 font-mono text-gray-700">{holding.nominee_guard_pan3?.toUpperCase() || '-'}</td>
                                    </tr>
                                    {/* Guardian Relation Row */}
                                    <tr>
                                      <td className="py-2 px-2 text-gray-600 font-medium bg-yellow-100">Guardian Relation</td>
                                      <td className="py-2 px-2 text-gray-700">{toTitleCase(holding.nominee_guard_rel1) || '-'}</td>
                                      <td className="py-2 px-2 text-gray-700">{toTitleCase(holding.nominee_guard_rel2) || '-'}</td>
                                      <td className="py-2 px-2 text-gray-700">{toTitleCase(holding.nominee_guard_rel3) || '-'}</td>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </div>

                          {/* Bank Details Section - Below the grid */}
                          <div className="mt-4 bg-purple-50/50 rounded-lg p-4 border border-purple-100">
                            <div className="flex items-center gap-2 mb-3 pb-2 border-b border-purple-200">
                              <Building2 className="w-4 h-4 text-purple-600" />
                              <span className="font-semibold text-purple-800">Bank Details</span>
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                              <div>
                                <span className="text-gray-500 block">Bank Name</span>
                                <span className={holding.bank_name ? 'text-gray-700 font-medium' : 'text-gray-400'}>
                                  {toTitleCase(holding.bank_name) || '-'}
                                </span>
                              </div>
                              <div>
                                <span className="text-gray-500 block">Account No</span>
                                <span className={`font-mono ${holding.bank_account ? 'text-gray-700' : 'text-gray-400'}`}>
                                  {holding.bank_account || '-'}
                                </span>
                              </div>
                              <div>
                                <span className="text-gray-500 block">Account Type</span>
                                <span className="text-gray-700">{toTitleCase(holding.bank_acc_type) || '-'}</span>
                              </div>
                              <div>
                                <span className="text-gray-500 block">IFSC Code</span>
                                <span className={`font-mono ${holding.bank_ifsc ? 'text-gray-700' : 'text-gray-400'}`}>
                                  {holding.bank_ifsc?.toUpperCase() || '-'}
                                </span>
                              </div>
                              <div>
                                <span className="text-gray-500 block">Branch</span>
                                <span className="text-gray-700">{toTitleCase(holding.bank_branch) || '-'}</span>
                              </div>
                            </div>
                          </div>

                          {/* Schemes in this Folio - at bottom */}
                          <div className="mt-4 pt-4 border-t border-gray-200">
                            <div className="flex items-center gap-2 mb-2">
                              <TrendingUp className="w-4 h-4 text-indigo-600" />
                              <span className="font-semibold text-gray-700">Schemes ({folioSchemes.length})</span>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {folioSchemes.map((scheme, idx) => (
                                <Badge key={idx} variant="outline" className="text-sm bg-white py-1.5 px-3">
                                  {scheme.bse_scheme_name || scheme.scheme_name || toTitleCase(scheme.fund_desc) || 'Unknown Scheme'}
                                </Badge>
                              ))}
                            </div>
                          </div>

                        </CardContent>
                      </Card>
                    );
                  });
                })()}
              </div>
            )}
          </CardContent>
        </Card>
      ) : activeTab === "provisional_sell" ? (
        /* Provisional Sell Intelligence — operates on filteredActiveUnits */
        <div className="space-y-4" data-testid="provisional-sell-tab">
          {/* Bucket Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
            {provisionalSellBuckets.map((b) => {
              const active = psBucket === b.key;
              return (
                <button
                  key={b.key}
                  data-testid={`ps-bucket-${b.key}`}
                  onClick={() => setPsBucket(active ? "all" : b.key)}
                  className={`text-left border rounded-lg p-3 transition-all ${b.color} ${active ? "ring-2 ring-offset-1 ring-teal-500" : "hover:shadow-md"}`}
                >
                  <div className="text-xs font-semibold uppercase tracking-wide opacity-80">{b.label}</div>
                  <div className="text-xl font-bold mt-1">{formatCurrency(b.current_value)}</div>
                  <div className="flex items-center justify-between text-xs mt-2 opacity-80">
                    <span>Lots: <b>{b.lots_count}</b></span>
                    <span>CAGR: <b>{b.avg_cagr != null ? `${b.avg_cagr.toFixed(2)}%` : "—"}</b></span>
                  </div>
                  <div className={`text-xs mt-1 font-medium ${b.gain >= 0 ? "text-green-700" : "text-red-700"}`}>
                    {b.gain >= 0 ? "▲" : "▼"} {formatCurrency(Math.abs(b.gain))} unrealized
                  </div>
                </button>
              );
            })}
          </div>

          {/* Ranked Lot Table + Sell Simulator side-by-side on large screens */}
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
            <Card className="xl:col-span-2 border-gray-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800">Ranked Active Lots</h3>
                    <p className="text-xs text-gray-500">FIFO-applied lots ordered worst-CAGR first. Click a bucket card above to filter.</p>
                  </div>
                  <Badge variant="outline">
                    {(psBucket === "all" ? provisionalSellLots : provisionalSellLots.filter(l => l.bucket === psBucket)).length} lots
                  </Badge>
                </div>
                <div className="overflow-x-auto max-h-[600px]">
                  <Table>
                    <TableHeader className="sticky top-0 bg-white z-10">
                      <TableRow className="bg-gray-50">
                        <TableHead className="text-xs">#</TableHead>
                        <TableHead className="text-xs">Bucket</TableHead>
                        <TableHead className="text-xs">Scheme</TableHead>
                        <TableHead className="text-xs">Folio</TableHead>
                        <TableHead className="text-xs">Purchase Date</TableHead>
                        <TableHead className="text-xs text-right">Yrs Held</TableHead>
                        <TableHead className="text-xs text-right">Remaining Units</TableHead>
                        <TableHead className="text-xs text-right">Buy NAV</TableHead>
                        <TableHead className="text-xs text-right">Cur NAV</TableHead>
                        <TableHead className="text-xs text-right">Current Value</TableHead>
                        <TableHead className="text-xs text-right">Gain</TableHead>
                        <TableHead className="text-xs text-right">CAGR</TableHead>
                        <TableHead className="text-xs">Tax</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(psBucket === "all" ? provisionalSellLots : provisionalSellLots.filter(l => l.bucket === psBucket))
                        .slice()
                        .sort((a, b) => a.overall_rank - b.overall_rank)
                        .slice(0, 500)
                        .map((l) => {
                          const cagrTone = l.cagr == null
                            ? "text-gray-500"
                            : l.cagr < 0 ? "text-rose-700 bg-rose-50"
                            : l.cagr < 8 ? "text-amber-700 bg-amber-50"
                            : l.cagr < 14 ? "text-teal-700 bg-teal-50"
                            : "text-emerald-700 bg-emerald-50";
                          const longTerm = l.is_equity ? l.holding_days >= 365 : l.holding_days >= 1095;
                          return (
                            <TableRow key={l.lot_id} className="hover:bg-gray-50" data-testid={`ps-lot-${l.lot_id}`}>
                              <TableCell className="text-xs font-mono text-gray-500">{l.overall_rank}</TableCell>
                              <TableCell className="text-xs">
                                <Badge variant="outline" className="text-[10px]">{l.bucket === "LT5" ? "5+" : l.bucket === "Y3_5" ? "3-5" : l.bucket === "Y2_3" ? "2-3" : l.bucket === "Y1_2" ? "1-2" : "<1"}</Badge>
                              </TableCell>
                              <TableCell className="text-xs max-w-[280px] truncate" title={toTitleCase(l.scheme_name) || l.scheme_name}>
                                {toTitleCase(l.scheme_name) || l.scheme_name}
                                {l.is_locked && <Badge className="ml-1 text-[10px] bg-amber-100 text-amber-700">ELSS Locked</Badge>}
                              </TableCell>
                              <TableCell className="text-xs font-mono">{l.folio_no}</TableCell>
                              <TableCell className="text-xs">{l.transaction_date}</TableCell>
                              <TableCell className="text-xs text-right">{l.holding_years.toFixed(2)}</TableCell>
                              <TableCell className="text-xs text-right font-mono">{l.remaining_units.toFixed(3)}</TableCell>
                              <TableCell className="text-xs text-right">₹{l.purchase_nav.toFixed(2)}</TableCell>
                              <TableCell className="text-xs text-right">₹{l.current_nav.toFixed(2)}</TableCell>
                              <TableCell className="text-xs text-right font-mono">{formatCurrency(l.current_value)}</TableCell>
                              <TableCell className={`text-xs text-right font-mono ${l.gain >= 0 ? "text-green-600" : "text-red-600"}`}>
                                {l.gain >= 0 ? "+" : ""}{formatCurrency(l.gain)}
                              </TableCell>
                              <TableCell className={`text-xs text-right font-mono px-2 ${cagrTone}`}>
                                {l.cagr == null ? "—" : `${l.cagr.toFixed(2)}%`}
                              </TableCell>
                              <TableCell className="text-xs">
                                <Badge className={`text-[10px] ${longTerm ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>
                                  {longTerm ? "LTCG" : "STCG"}
                                </Badge>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>

            {/* Sell Simulator */}
            <Card className="border-gray-200">
              <CardContent className="p-4 space-y-3">
                <div>
                  <h3 className="text-lg font-semibold text-gray-800">Sell Simulator</h3>
                  <p className="text-xs text-gray-500">Provisional FIFO-compliant redemption plan.</p>
                </div>

                {/* Already-realized FY gains shrink the ₹1.25 L equity LTCG exemption */}
                <div className="border rounded-md p-2.5 bg-blue-50/60 border-blue-200 text-[11px] space-y-1" data-testid="ps-fy-realized">
                  <div className="font-semibold text-blue-900">Already realized in {psFyRealized.fyLabel} ({psFyRealized.sales_count} sales)</div>
                  <div className="flex justify-between"><span>LTCG · Equity</span><b className={psFyRealized.ltcg_equity_gain >= 0 ? "text-green-700" : "text-red-700"}>{formatCurrency(psFyRealized.ltcg_equity_gain)}</b></div>
                  <div className="flex justify-between"><span>STCG · Equity</span><b className={psFyRealized.stcg_equity_gain >= 0 ? "text-green-700" : "text-red-700"}>{formatCurrency(psFyRealized.stcg_equity_gain)}</b></div>
                  <div className="flex justify-between"><span>LTCG · Debt</span><b className={psFyRealized.ltcg_debt_gain >= 0 ? "text-green-700" : "text-red-700"}>{formatCurrency(psFyRealized.ltcg_debt_gain)}</b></div>
                  <div className="flex justify-between border-t pt-1 mt-1">
                    <span>Eq LTCG exemption left (of ₹1.25 L)</span>
                    <b className="text-emerald-700">{formatCurrency(psFyRealized.remaining_eq_exempt)}</b>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs">Target Sell Amount (INR)</Label>
                  <Input
                    type="number"
                    inputMode="numeric"
                    placeholder="e.g. 1000000"
                    value={psSellAmount}
                    onChange={(e) => setPsSellAmount(e.target.value)}
                    data-testid="ps-target-amount"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-xs">Sell Mode</Label>
                  <Select value={psSellMode} onValueChange={setPsSellMode}>
                    <SelectTrigger data-testid="ps-mode"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bucket_priority">
                        Bucket Priority ({provisionalSellBuckets.filter(b => b.lots_count > 0).map(b => b.label).join(" → ") || "no lots"})
                      </SelectItem>
                      <SelectItem value="bucket_specific">Bucket Specific (use selected bucket only)</SelectItem>
                      <SelectItem value="tax_optimized">Tax Optimized (bucket order + losses-first within)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-[10px] text-gray-400 mt-1">
                    All modes drain longer-tenure buckets first. Within a bucket, debt &amp; equity lots are ranked together (worst CAGR first).
                  </p>
                </div>

                {psSellMode === "bucket_priority" && (
                  <div className="space-y-2">
                    <Label className="text-xs">Bucket Priority Order</Label>
                    <Input
                      value={psBucketPriority}
                      onChange={(e) => setPsBucketPriority(e.target.value)}
                      placeholder={provisionalSellBuckets.filter(b => b.lots_count > 0).map(b => b.key).join(",") || "LT5,Y3_5,Y2_3,Y1_2,LT1"}
                      data-testid="ps-priority"
                    />
                    <p className="text-[10px] text-gray-400">
                      Available buckets for this client:{" "}
                      <b>{provisionalSellBuckets.filter(b => b.lots_count > 0).map(b => b.label).join(" · ") || "none"}</b>
                    </p>
                    <p className="text-[10px] text-gray-400">Comma-separated. Within each bucket, worst-CAGR lot is sold first.</p>
                  </div>
                )}

                {psSellMode === "bucket_specific" && (
                  <div className="space-y-2">
                    <Label className="text-xs">Buckets to Sell From (multi-select)</Label>
                    <div className="grid grid-cols-2 gap-1.5">
                      {provisionalSellBuckets
                        .filter((b) => b.lots_count > 0)
                        .map((b) => (
                        <label key={b.key} className="flex items-center gap-1.5 text-xs text-gray-700 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={psBucketsSelected.includes(b.key)}
                            onChange={(e) => {
                              setPsBucketsSelected((prev) =>
                                e.target.checked ? [...prev, b.key] : prev.filter((x) => x !== b.key)
                              );
                            }}
                            data-testid={`ps-bucket-chk-${b.key}`}
                          />
                          {b.label}
                          <span className="text-[10px] text-gray-400">({b.lots_count})</span>
                        </label>
                      ))}
                    </div>
                    <p className="text-[10px] text-gray-400">
                      Longer-tenure buckets are drained first among the selected ones.
                    </p>
                  </div>
                )}

                <label className="flex items-start gap-2 text-xs text-gray-600">
                  <input
                    type="checkbox"
                    checked={psExcludeLocked}
                    onChange={(e) => setPsExcludeLocked(e.target.checked)}
                    data-testid="ps-exclude-locked"
                    className="mt-0.5"
                  />
                  <span>
                    Exclude ELSS lots still under 3-year lock-in
                    <span className="block text-[10px] text-gray-400 mt-0.5">
                      ELSS lots that have crossed 3 years are always eligible.
                    </span>
                  </span>
                </label>

                <div className="space-y-2">
                  <Label className="text-xs text-gray-600">
                    Tax Regime ({(() => {
                      // Auto-derive current Indian FY label from calendar
                      // so it stays accurate every April without a code change.
                      const _n = new Date();
                      const _sy = _n.getMonth() >= 3 ? _n.getFullYear() : _n.getFullYear() - 1;
                      return `FY ${_sy}-${String(_sy + 1).slice(-2)}`;
                    })()})
                  </Label>
                  <div className="flex gap-2" data-testid="ps-tax-regime-toggle">
                    <button
                      type="button"
                      onClick={() => { setPsTaxRegime("new"); setPsDebtSlabPct(30); }}
                      data-testid="ps-regime-new"
                      className={`flex-1 h-9 rounded-md text-xs font-medium transition-all border ${
                        psTaxRegime === "new"
                          ? "bg-teal-700 text-white border-teal-700"
                          : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"
                      }`}
                    >
                      New Regime
                    </button>
                    <button
                      type="button"
                      onClick={() => { setPsTaxRegime("old"); setPsDebtSlabPct(30); }}
                      data-testid="ps-regime-old"
                      className={`flex-1 h-9 rounded-md text-xs font-medium transition-all border ${
                        psTaxRegime === "old"
                          ? "bg-teal-700 text-white border-teal-700"
                          : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"
                      }`}
                    >
                      Old Regime
                    </button>
                  </div>
                </div>

                <div className="space-y-1">
                  <Label htmlFor="ps-debt-slab" className="text-xs text-gray-600">
                    Debt STCG — Marginal Slab Rate
                  </Label>
                  <Select
                    value={String(psDebtSlabPct)}
                    onValueChange={(v) => setPsDebtSlabPct(parseFloat(v))}
                  >
                    <SelectTrigger id="ps-debt-slab" className="h-9 text-sm" data-testid="ps-debt-slab-select">
                      <SelectValue placeholder="Select slab" />
                    </SelectTrigger>
                    <SelectContent>
                      {psTaxRegime === "new" ? (
                        <>
                          <SelectItem value="0">0% — Income ≤ ₹3 L</SelectItem>
                          <SelectItem value="5">5% — ₹3 L – ₹7 L</SelectItem>
                          <SelectItem value="10">10% — ₹7 L – ₹10 L</SelectItem>
                          <SelectItem value="15">15% — ₹10 L – ₹12 L</SelectItem>
                          <SelectItem value="20">20% — ₹12 L – ₹15 L</SelectItem>
                          <SelectItem value="30">30% — Income &gt; ₹15 L (top slab)</SelectItem>
                        </>
                      ) : (
                        <>
                          <SelectItem value="0">0% — Income ≤ ₹2.5 L</SelectItem>
                          <SelectItem value="5">5% — ₹2.5 L – ₹5 L</SelectItem>
                          <SelectItem value="20">20% — ₹5 L – ₹10 L</SelectItem>
                          <SelectItem value="30">30% — Income &gt; ₹10 L (top slab)</SelectItem>
                        </>
                      )}
                      <SelectItem value="33">33% — Top slab + 10% surcharge (₹50 L – ₹1 Cr)</SelectItem>
                      <SelectItem value="35.88">35.88% — Top slab + 15% surcharge + 4% cess (₹1 – 2 Cr)</SelectItem>
                      <SelectItem value="39">39% — Top slab + 25% surcharge + 4% cess (&gt; ₹2 Cr)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-[10px] text-gray-400">
                    Debt STCG (≤36 mo) is taxed at investor's marginal slab. LTCG Debt (&gt;36 mo) is fixed at 12.5% post-Budget-2024.
                  </p>
                </div>

                {/* ─── NRI Mode (TDS by AMC u/s 195) ──────────────────── */}
                {(() => {
                  const autoNri = !!holdingPatternCounts.isNriClient;
                  const effIsNri = (psNriOverride === null) ? autoNri : !!psNriOverride;
                  return (
                    <div className={`border rounded-lg p-3 space-y-2 ${effIsNri ? "border-orange-300 bg-orange-50/60" : "border-gray-200 bg-gray-50/40"}`} data-testid="ps-nri-block">
                      <div className="flex items-center justify-between">
                        <h4 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                          {effIsNri ? "🌐 NRI Mode — TDS by AMC" : "Resident Tax Mode"}
                          {effIsNri && autoNri && (
                            <Badge variant="outline" className="text-[9px] bg-white">Auto-detected</Badge>
                          )}
                        </h4>
                        <label className="flex items-center gap-1.5 text-[11px] text-gray-700 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={effIsNri}
                            onChange={(e) => setPsNriOverride(e.target.checked)}
                            data-testid="ps-nri-toggle"
                          />
                          <span>Treat as NRI</span>
                        </label>
                      </div>
                      {effIsNri && (
                        <>
                          <p className="text-[11px] text-orange-900/80">
                            For NRIs, the AMC deducts TDS at source on the entire capital gain.
                            The ₹1.25 L LTCG exemption is <b>not applied at TDS stage</b> — claim refund via ITR if final tax liability is lower.
                          </p>
                          <div className="space-y-1">
                            <Label htmlFor="ps-nri-surcharge" className="text-xs text-gray-700">Surcharge tier (per client income bracket)</Label>
                            <Select
                              value={String(psNriSurchargePct)}
                              onValueChange={(v) => setPsNriSurchargePct(parseFloat(v))}
                            >
                              <SelectTrigger id="ps-nri-surcharge" className="h-9 text-sm" data-testid="ps-nri-surcharge-select">
                                <SelectValue placeholder="Pick surcharge" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="0">No surcharge — Income ≤ ₹50 L</SelectItem>
                                <SelectItem value="10">10% surcharge — Income ₹50 L – ₹1 Cr</SelectItem>
                                <SelectItem value="15">15% surcharge — Income ₹1 Cr – ₹2 Cr</SelectItem>
                                <SelectItem value="25">25% surcharge — Income ₹2 Cr – ₹5 Cr (non-112A)</SelectItem>
                                <SelectItem value="37">37% surcharge — Income &gt; ₹5 Cr (non-112A)</SelectItem>
                              </SelectContent>
                            </Select>
                            <p className="text-[10px] text-gray-500">
                              4% Health-&-Education cess always applied. Effective LTCG TDS rate ={" "}
                              <b>{((0.125 * (1 + (Number(psNriSurchargePct) || 0) / 100) * 1.04) * 100).toFixed(2)}%</b>.
                            </p>
                          </div>
                        </>
                      )}
                    </div>
                  );
                })()}

                {/* ─── LTCG ₹1.25 L Harvest Recommendation ─────────────── */}
                <div className="border border-emerald-200 bg-emerald-50/60 rounded-lg p-3 space-y-2" data-testid="ps-ltcg-harvest-block">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-semibold text-emerald-900">📗 Book Profit upto ₹1.25 L (FY exemption)</h4>
                    <Badge variant="outline" className="text-[10px] bg-white">
                      Headroom: {formatCurrency(ltcgHarvest.headroom)}
                    </Badge>
                  </div>
                  <p className="text-[11px] text-emerald-900/70">
                    Suggests equity lots (&gt; 12 mo) to redeem so realized LTCG fills the remaining ₹1.25 L exemption for {psFyRealized?.fyLabel || ""}.
                    Already booked: <b>{formatCurrency(psFyRealized?.ltcg_equity_gain || 0)}</b>.
                    Sequence: 5y+ → 3-5y → 2-3y → 1-2y.
                  </p>
                  {((psNriOverride === null ? !!holdingPatternCounts.isNriClient : !!psNriOverride)) && ltcgHarvest.headroom > 0 && ltcgHarvest.picks.length > 0 && (
                    <div className="text-[11px] text-orange-800 bg-orange-100 border border-orange-300 rounded px-2 py-1">
                      <b>NRI note:</b> AMC will still deduct TDS on the full ₹{ltcgHarvest.harvested_gain.toLocaleString("en-IN", { maximumFractionDigits: 0 })} gain
                      ({((0.125 * (1 + (Number(psNriSurchargePct) || 0) / 100) * 1.04) * 100).toFixed(2)}% effective LTCG rate).
                      Headroom is recoverable via ITR refund — not at the redemption stage.
                    </div>
                  )}
                  {ltcgHarvest.headroom <= 0 ? (
                    <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                      ₹1.25 L equity-LTCG exemption already fully utilised this FY. No further tax-free harvest available.
                    </div>
                  ) : ltcgHarvest.picks.length === 0 ? (
                    <div className="text-xs text-gray-600">
                      No eligible equity lots (&gt; 12 months) with unrealised gain available to harvest.
                    </div>
                  ) : (
                    <>
                      <div className="grid grid-cols-3 gap-2 text-[11px]">
                        <div className="bg-white border border-emerald-200 rounded px-2 py-1">
                          <div className="text-gray-500">Lots</div>
                          <div className="font-semibold text-emerald-800">{ltcgHarvest.picks.length}</div>
                        </div>
                        <div className="bg-white border border-emerald-200 rounded px-2 py-1">
                          <div className="text-gray-500">Sell value</div>
                          <div className="font-semibold text-emerald-800">{formatCurrency(ltcgHarvest.sale_value)}</div>
                        </div>
                        <div className="bg-white border border-emerald-200 rounded px-2 py-1">
                          <div className="text-gray-500">LTCG harvested</div>
                          <div className="font-semibold text-emerald-800">{formatCurrency(ltcgHarvest.harvested_gain)}</div>
                        </div>
                      </div>
                      <div className="max-h-[220px] overflow-y-auto border border-emerald-200 bg-white rounded">
                        <Table>
                          <TableHeader className="bg-emerald-50 sticky top-0">
                            <TableRow>
                              <TableHead className="text-[10px] py-1">#</TableHead>
                              <TableHead className="text-[10px] py-1">Bucket</TableHead>
                              <TableHead className="text-[10px] py-1">Scheme</TableHead>
                              <TableHead className="text-[10px] py-1">Folio</TableHead>
                              <TableHead className="text-[10px] py-1 text-right">Units</TableHead>
                              <TableHead className="text-[10px] py-1 text-right">Sale</TableHead>
                              <TableHead className="text-[10px] py-1 text-right">Gain</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {ltcgHarvest.picks.map((p, i) => (
                              <TableRow key={`${p.lot_id}-${i}`} data-testid={`ltcg-pick-${i}`}>
                                <TableCell className="text-[10px] py-1">{i + 1}</TableCell>
                                <TableCell className="text-[10px] py-1">
                                  {p.bucket === "LT5" ? "5+y" : p.bucket === "Y3_5" ? "3-5y" : p.bucket === "Y2_3" ? "2-3y" : "1-2y"}
                                  {p.partial && <span className="ml-1 text-[9px] text-amber-600">(partial)</span>}
                                </TableCell>
                                <TableCell className="text-[10px] py-1 max-w-[140px] truncate" title={p.scheme_name}>{p.scheme_name}</TableCell>
                                <TableCell className="text-[10px] py-1">{p.folio_no}</TableCell>
                                <TableCell className="text-[10px] py-1 text-right">{formatNumber(p.units_to_sell, 3)}</TableCell>
                                <TableCell className="text-[10px] py-1 text-right">{formatCurrency(p.sale_value)}</TableCell>
                                <TableCell className="text-[10px] py-1 text-right text-emerald-700 font-medium">{formatCurrency(p.gain_realized)}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                      {ltcgHarvest.remaining_headroom > 100 && (
                        <p className="text-[10px] text-amber-700">
                          ⚠ Could not fill full headroom — only {formatCurrency(ltcgHarvest.harvested_gain)} of {formatCurrency(ltcgHarvest.headroom)} available from current eligible lots.
                        </p>
                      )}
                    </>
                  )}
                </div>

                <Button
                  className="w-full"
                  data-testid="ps-simulate-btn"
                  onClick={() => {
                    const target = parseFloat(psSellAmount) || 0;
                    if (target <= 0) {
                      toast.error("Enter a positive sell amount");
                      return;
                    }
                    if (psSellMode === "bucket_specific" && psBucketsSelected.length === 0) {
                      toast.error("Pick at least one bucket");
                      return;
                    }
                    const _effIsNri = (psNriOverride === null)
                      ? !!holdingPatternCounts.isNriClient
                      : !!psNriOverride;
                    const result = runSellSimulation(psSellMode, target, {
                      bucketPriority: psBucketPriority,
                      bucketsSelected: psBucketsSelected,
                      excludeLocked: psExcludeLocked,
                      fyExemptLeft: psFyRealized.remaining_eq_exempt,
                      debtSlabRate: psDebtSlabPct,
                      isNri: _effIsNri,
                      nriSurchargePct: psNriSurchargePct,
                    });
                    setPsSimulation(result);
                    setPsComparison(null);
                    toast.success(`Simulated ${result.picks.length} lot${result.picks.length === 1 ? "" : "s"} → ${formatCurrency(result.totalSold)}`, {
                      description: result.switchCount > 0 ? `Dynamic cross-fund switches: ${result.switchCount}` : undefined,
                    });
                  }}
                >
                  Run Simulation
                </Button>

                <Button
                  variant="outline"
                  className="w-full"
                  data-testid="ps-compare-modes-btn"
                  onClick={() => {
                    const target = parseFloat(psSellAmount) || 0;
                    if (target <= 0) {
                      toast.error("Enter a positive sell amount");
                      return;
                    }
                    const bucketsForSpecific = psBucketsSelected.length > 0
                      ? psBucketsSelected
                      : ["LT5", "Y3_5", "Y2_3", "Y1_2", "LT1"];
                    const modes = [
                      { key: "bucket_priority", label: "Bucket Priority" },
                      { key: "bucket_specific", label: "Bucket Specific" },
                      { key: "tax_optimized",   label: "Tax Optimized" },
                    ];
                    const _effIsNriCmp = (psNriOverride === null)
                      ? !!holdingPatternCounts.isNriClient
                      : !!psNriOverride;
                    const results = modes.map((m) => ({
                      ...m,
                      ...runSellSimulation(m.key, target, {
                        bucketPriority: psBucketPriority,
                        bucketsSelected: bucketsForSpecific,
                        excludeLocked: psExcludeLocked,
                        fyExemptLeft: psFyRealized.remaining_eq_exempt,
                        debtSlabRate: psDebtSlabPct,
                        isNri: _effIsNriCmp,
                        nriSurchargePct: psNriSurchargePct,
                      }),
                    }));
                    const bestNet = Math.max(...results.map((r) => r.netProceeds));
                    const worstNet = Math.min(...results.map((r) => r.netProceeds));
                    const lowestTax = Math.min(...results.map((r) => r.totalTax));
                    const highestTax = Math.max(...results.map((r) => r.totalTax));
                    const netTied = bestNet - worstNet < 1;
                    const taxTied = highestTax - lowestTax < 1;
                    results.forEach((r) => {
                      r.is_best_net = !netTied && r.netProceeds === bestNet;
                      r.is_lowest_tax = !taxTied && r.totalTax === lowestTax;
                    });
                    setPsComparison({ target, bucketsUsed: bucketsForSpecific, results, all_tied: netTied });
                    toast.success(`Compared ${results.length} sell modes — best net: ${formatCurrency(bestNet)}`);
                  }}
                >
                  Compare All Modes
                </Button>

                {psSimulation && (
                  <div className="border-t pt-3 space-y-2 text-xs">
                    {psSimulation.is_nri && (
                      <div className="text-[11px] text-orange-900 bg-orange-50 border border-orange-300 rounded px-2 py-1.5 font-medium">
                        🌐 NRI Mode · TDS deducted by AMC u/s 195 · {psSimulation.nri_surcharge_pct}% surcharge + 4% cess applied. No ₹1.25 L exemption at deduction stage — claim refund via ITR if final liability is lower.
                      </div>
                    )}
                    <div className="flex justify-between"><span>Target</span><b>{formatCurrency(psSimulation.target)}</b></div>
                    <div className="flex justify-between"><span>Plan covers</span><b>{formatCurrency(psSimulation.totalSold)}</b></div>
                    {psSimulation.shortfall > 1 && (
                      <div className="flex justify-between text-rose-600"><span>Shortfall</span><b>{formatCurrency(psSimulation.shortfall)}</b></div>
                    )}
                    <div className="flex justify-between"><span>Realized Gain</span><b className={psSimulation.totalGain >= 0 ? "text-green-700" : "text-red-700"}>{formatCurrency(psSimulation.totalGain)}</b></div>
                    <div className="flex justify-between text-rose-600">
                      <span>{psSimulation.is_nri ? "STCG TDS — Eq (20%" : "STCG Tax — Eq (20%"}
                        {psSimulation.is_nri ? `+${psSimulation.nri_surcharge_pct}%+4%)` : ")"}
                      </span>
                      <b>{formatCurrency(psSimulation.stcgEquityTax)}</b>
                    </div>
                    <div className="flex justify-between text-rose-600">
                      <span>{psSimulation.is_nri ? `STCG TDS — Debt (30%+${psSimulation.nri_surcharge_pct}%+4%)` : `STCG Tax — Debt (${psSimulation.debtSlabRatePct}% slab)`}
                        <span className="block text-[9px] text-gray-500 font-normal">
                          {psSimulation.is_nri ? "NRI: top-slab 30% applies — surcharge + cess on top" : "at investor's marginal income-tax slab"}
                        </span>
                      </span>
                      <b>{formatCurrency(psSimulation.stcgDebtTax)}</b>
                    </div>
                    <div className="flex justify-between text-rose-600">
                      <span>{psSimulation.is_nri ? `LTCG TDS — Eq (12.5%+${psSimulation.nri_surcharge_pct}%+4%)` : "LTCG Tax — Eq (12.5%)"}
                        <span className="block text-[9px] text-gray-500 font-normal">
                          {psSimulation.is_nri
                            ? "NRI: no exemption at TDS stage — refund via ITR"
                            : `after ${formatCurrency(psFyRealized.remaining_eq_exempt)} FY exemption`}
                        </span>
                      </span>
                      <b>{formatCurrency(psSimulation.ltcgEquityTax)}</b>
                    </div>
                    <div className="flex justify-between text-rose-600">
                      <span>{psSimulation.is_nri ? `LTCG TDS — Debt (12.5%+${psSimulation.nri_surcharge_pct}%+4%)` : "LTCG Tax — Debt (12.5%)"}</span>
                      <b>{formatCurrency(psSimulation.ltcgDebtTax)}</b>
                    </div>
                    <div className="flex justify-between border-t pt-1 font-semibold">
                      <span>{psSimulation.is_nri ? "Total TDS Deducted" : "Total Tax"}</span>
                      <b className="text-rose-700">{formatCurrency(psSimulation.totalTax)}</b>
                    </div>
                    <div className="flex justify-between font-semibold">
                      <span>{psSimulation.is_nri ? "Net Credit (after TDS)" : "Net Proceeds"}</span>
                      <b className="text-emerald-700">{formatCurrency(psSimulation.netProceeds)}</b>
                    </div>
                    {psSimulation.is_nri && psSimulation.ltcgGainEquity > 125000 && (
                      <div className="text-[10px] text-emerald-700 italic">
                        Potential ITR refund (₹1.25 L LTCG exemption × {((0.125 * psSimulation.effective_rate_multiplier) * 100).toFixed(2)}%) ≈ {formatCurrency(125000 * 0.125 * psSimulation.effective_rate_multiplier)}
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Compare All Modes — side-by-side panel */}
          {psComparison && (
            <Card className="border-indigo-200" data-testid="ps-comparison-card">
              <CardContent className="p-4">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800">Compare All Modes</h3>
                    <p className="text-xs text-gray-500">
                      Target: <b>{formatCurrency(psComparison.target)}</b> · same FIFO engine across modes ·
                      {psComparison.all_tied
                        ? <span className="text-amber-700"> All 3 modes produced the SAME plan on this target.</span>
                        : <><span className="text-emerald-700"> green badge</span> = best net proceeds · <span className="text-blue-700">blue badge</span> = lowest tax outflow</>
                      }
                    </p>
                  </div>
                  <button
                    onClick={() => setPsComparison(null)}
                    className="text-xs text-gray-500 hover:text-gray-700 underline"
                    data-testid="ps-comparison-close"
                  >Dismiss</button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {psComparison.results.map((r) => {
                    const borderClass = r.is_best_net ? "border-emerald-400 ring-1 ring-emerald-300" : "border-gray-200";
                    return (
                      <div
                        key={r.key}
                        className={`border-2 rounded-lg p-3 ${borderClass} bg-white`}
                        data-testid={`ps-comparison-card-${r.key}`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="font-semibold text-gray-800">{r.label}</div>
                          <div className="flex gap-1">
                            {r.is_best_net && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-300 font-medium">BEST NET</span>
                            )}
                            {r.is_lowest_tax && !r.is_best_net && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700 border border-blue-300 font-medium">LOW TAX</span>
                            )}
                          </div>
                        </div>
                        <div className="space-y-1.5 text-xs">
                          <div className="flex justify-between"><span className="text-gray-500">Plan covers</span><b>{formatCurrency(r.totalSold)}</b></div>
                          {r.shortfall > 1 && (
                            <div className="flex justify-between text-rose-600"><span>Shortfall</span><b>{formatCurrency(r.shortfall)}</b></div>
                          )}
                          <div className="flex justify-between">
                            <span className="text-gray-500">Realized Gain</span>
                            <b className={r.totalGain >= 0 ? "text-green-700" : "text-red-700"}>{formatCurrency(r.totalGain)}</b>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-500">Total Tax</span>
                            <b className="text-rose-700">{formatCurrency(r.totalTax)}</b>
                          </div>
                          <div className="flex justify-between text-[10px] text-gray-400 pl-2">
                            <span>STCG</span><span>{formatCurrency(r.stcgTax)}</span>
                          </div>
                          <div className="flex justify-between text-[10px] text-gray-400 pl-2">
                            <span>LTCG-Eq</span><span>{formatCurrency(r.ltcgEquityTax)}</span>
                          </div>
                          <div className="flex justify-between text-[10px] text-gray-400 pl-2">
                            <span>LTCG-Debt</span><span>{formatCurrency(r.ltcgDebtTax)}</span>
                          </div>
                          <div className="flex justify-between border-t pt-1.5 mt-1">
                            <span className="font-semibold">Net Proceeds</span>
                            <b className={r.is_best_net ? "text-emerald-700 text-base" : "text-emerald-700"}>{formatCurrency(r.netProceeds)}</b>
                          </div>
                          <div className="flex justify-between text-[11px] text-gray-500 pt-1">
                            <span>{r.schemes_count} scheme{r.schemes_count === 1 ? "" : "s"} · {r.picks.length} lot{r.picks.length === 1 ? "" : "s"}</span>
                            {r.switchCount > 0 && <span>↔ {r.switchCount} switches</span>}
                          </div>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full mt-3 text-xs"
                          data-testid={`ps-comparison-apply-${r.key}`}
                          onClick={() => {
                            setPsSellMode(r.key);
                            setPsSimulation(r);
                            toast.success(`Applied ${r.label} plan — see Recommended Redemption below`);
                          }}
                        >
                          Apply this plan
                        </Button>
                      </div>
                    );
                  })}
                </div>

                {(() => {
                  const sorted = [...psComparison.results].sort((a, b) => b.netProceeds - a.netProceeds);
                  const best = sorted[0];
                  const worst = sorted[sorted.length - 1];
                  const delta = best.netProceeds - worst.netProceeds;
                  if (delta < 1) return null;
                  return (
                    <div className="mt-3 text-[11px] text-gray-600 bg-indigo-50/60 border border-indigo-200 rounded-md p-2">
                      <b className="text-indigo-700">Insight:</b> {best.label} yields <b>{formatCurrency(delta)}</b> more in net proceeds than {worst.label} on the same ₹{formatCurrency(psComparison.target).replace("₹", "")} sell target.
                    </div>
                  );
                })()}
              </CardContent>
            </Card>
          )}

          {/* Simulated Lots Breakdown — clubbed by folio+scheme, expandable to per-lot detail */}
          {psSimulation && psSimulation.picks.length > 0 && (() => {
            // Build a map of TOTAL available units per (folio, scheme) across ALL active lots
            // (not just the picked ones) — so we can flag if a scheme is being fully exited.
            const availableByGroup = {};
            provisionalSellLots.forEach((l) => {
              const k = `${l.folio_no}::${l.scheme_code || l.isin || l.scheme_name}`;
              availableByGroup[k] = (availableByGroup[k] || 0) + l.remaining_units;
            });

            const groupsMap = {};
            psSimulation.picks.forEach((p) => {
              const k = `${p.folio_no}::${p.scheme_code || p.isin || p.scheme_name}`;
              if (!groupsMap[k]) {
                groupsMap[k] = {
                  key: k, folio_no: p.folio_no, scheme_name: p.scheme_name,
                  units: 0, sell_value: 0, gain: 0, buckets: new Set(),
                  is_equity: p.is_equity, lots: [],
                  available_units: availableByGroup[k] || 0,
                };
              }
              const g = groupsMap[k];
              g.units += p.units_to_sell;
              g.sell_value += p.sell_value;
              g.gain += p.gain_portion;
              g.buckets.add(p.bucket);
              g.lots.push(p);
            });
            const groups = Object.values(groupsMap).sort((a, b) => b.sell_value - a.sell_value);

            // Classify each group as full / partial
            groups.forEach((g) => {
              const ratio = g.available_units > 0 ? g.units / g.available_units : 0;
              g.is_full = ratio >= 0.995; // within 0.5%
            });

            const totalUnits = groups.reduce((s, g) => s + g.units, 0);
            const totalValue = groups.reduce((s, g) => s + g.sell_value, 0);
            const totalGain = groups.reduce((s, g) => s + g.gain, 0);
            const fullCount = groups.filter((g) => g.is_full).length;
            const partialCount = groups.length - fullCount;
            const bucketLabel = (b) => b === "LT5" ? "5+" : b === "Y3_5" ? "3-5" : b === "Y2_3" ? "2-3" : b === "Y1_2" ? "1-2" : "<1";

            // Download Recommended Redemption as XLSX (2 sheets: Summary + Transactions)
            const downloadRecommendedXLSX = () => {
              const sim = psSimulation;
              const clientName = (clients.find((c) => c.id === selectedClient)?.name) || selectedClient || "Client";
              const modeLabel = sim.mode === "bucket_priority" ? "Bucket Priority"
                              : sim.mode === "bucket_specific" ? "Bucket Specific"
                              : "Tax Optimized";

              // ─── Sheet 1 – Summary ───
              const summaryRows = [
                ["Provisional Sell — Recommended Redemption"],
                ["Generated", new Date().toLocaleString("en-IN")],
                ["Client", clientName],
                ["Sell Mode", modeLabel],
                ["Tax Regime", (() => {
                  // Same auto-derived FY label used in the UI toggle above,
                  // so the Excel export always reflects the current FY.
                  const _n = new Date();
                  const _sy = _n.getMonth() >= 3 ? _n.getFullYear() : _n.getFullYear() - 1;
                  const _lbl = `FY ${_sy}-${String(_sy + 1).slice(-2)}`;
                  return psTaxRegime === "new" ? `New Regime (${_lbl})` : `Old Regime (${_lbl})`;
                })()],
                ["Debt-STCG Marginal Slab", `${sim.debtSlabRatePct}%`],
                [],
                ["─── Plan Totals ───"],
                ["Target Sell Amount (INR)", sim.target],
                ["Plan Covers (INR)", sim.totalSold],
                ["Shortfall (INR)", sim.shortfall],
                ["Total Cost Sold (INR)", sim.totalCostSold],
                ["Realized Gain (INR)", sim.totalGain],
                ["Schemes Touched", sim.schemes_count],
                ["Lots Touched", sim.picks.length],
                ["Full Exits", fullCount],
                ["Partial Exits", partialCount],
                ["Switch Count (cross-fund)", sim.switchCount],
                [],
                ["─── Realized Gain Split ───"],
                ["STCG Gain — Equity", sim.stcgGainEquity],
                ["STCG Gain — Debt", sim.stcgGainDebt],
                ["LTCG Gain — Equity", sim.ltcgGainEquity],
                ["LTCG Gain — Debt", sim.ltcgGainDebt],
                [],
                ["─── Tax Computation ───"],
                ["STCG Tax — Equity (20%)", sim.stcgEquityTax],
                [`STCG Tax — Debt (${sim.debtSlabRatePct}% slab)`, sim.stcgDebtTax],
                ["LTCG Tax — Equity (12.5%, after FY exemption)", sim.ltcgEquityTax],
                ["FY Eq-LTCG exemption used (INR)", Math.max(0, (psFyRealized.remaining_eq_exempt || 0) - Math.max(0, sim.ltcgGainEquity - sim.ltcgEquityTax / 0.125))],
                ["LTCG Tax — Debt (12.5%)", sim.ltcgDebtTax],
                ["Total Tax (INR)", sim.totalTax],
                [],
                ["Net Proceeds (INR)", sim.netProceeds],
              ];
              // Convert AOA → object rows so the styled exporter can pick
              // up types & auto-widths. Empty rows act as section dividers.
              const summaryObjRows = summaryRows.map((r) => {
                if (!r || r.length === 0) return { "Metric": "", "Value": "" };
                return { "Metric": r[0] ?? "", "Value": r[1] == null ? "" : r[1] };
              });

              // ─── Sheet 2 – Transactions (lot-level) ───
              const txnHeader = [
                "S.No","Bucket","Folio","Scheme","ISIN","Purchase Date","Holding (yrs)",
                "Holding (days)","Equity?","Locked?","Original Units","Sell Units","Sell %",
                "NAV (Buy)","NAV (Current)","Sell Value (INR)","Cost (INR)","Gain (INR)",
                "Long Term?","CAGR %",
              ];
              const txnObjRows = sim.picks.map((p, i) => {
                const r = [
                  i + 1,
                  bucketLabel(p.bucket),
                  p.folio_no || "",
                  p.scheme_name || "",
                  p.isin || p.scheme_code || "",
                  p.transaction_date || "",
                  p.holding_years != null ? Number(p.holding_years.toFixed(2)) : "",
                  p.holding_days != null ? p.holding_days : "",
                  p.is_equity ? "Yes" : "No",
                  p.is_locked ? "Yes" : "No",
                  Number((p.remaining_units || 0).toFixed(4)),
                  Number((p.units_to_sell || 0).toFixed(4)),
                  p.remaining_units > 0 ? Number(((p.units_to_sell || 0) / p.remaining_units * 100).toFixed(2)) : "",
                  Number((p.purchase_nav || 0).toFixed(4)),
                  Number((p.current_nav || 0).toFixed(4)),
                  Number((p.sell_value || 0).toFixed(2)),
                  Number((p.cost_portion || 0).toFixed(2)),
                  Number((p.gain_portion || 0).toFixed(2)),
                  p.long_term ? "LTCG" : "STCG",
                  p.cagr != null && isFinite(p.cagr) ? Number(p.cagr.toFixed(2)) : "",
                ];
                const o = {};
                txnHeader.forEach((h, idx) => { o[h] = r[idx]; });
                return o;
              });

              // Filename is HARDCODED — date+time stamped, not user-editable.
              const dt = new Date();
              const stamp = `${dt.getFullYear()}${String(dt.getMonth() + 1).padStart(2, "0")}${String(dt.getDate()).padStart(2, "0")}_${String(dt.getHours()).padStart(2, "0")}${String(dt.getMinutes()).padStart(2, "0")}${String(dt.getSeconds()).padStart(2, "0")}`;
              const fname = `recommended_redemption_${stamp}.xlsx`;
              exportStyledXlsx(
                [
                  { name: "Summary", rows: summaryObjRows },
                  { name: "Transactions", rows: txnObjRows },
                ],
                fname,
              );
            };

            return (
              <Card className="border-gray-200">
                <CardContent className="p-4">
                  <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                    <h3 className="text-lg font-semibold text-gray-800">
                      Recommended Redemption — {groups.length} scheme{groups.length === 1 ? "" : "s"} · {psSimulation.picks.length} lot{psSimulation.picks.length === 1 ? "" : "s"}
                    </h3>
                    <div className="flex items-center gap-2 text-xs">
                      <span className="inline-flex items-center gap-1">
                        <span className="inline-block w-3 h-3 rounded bg-rose-200 border border-rose-300" />
                        Full exit ({fullCount})
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <span className="inline-block w-3 h-3 rounded bg-amber-100 border border-amber-300" />
                        Partial ({partialCount})
                      </span>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={downloadRecommendedXLSX}
                        data-testid="ps-download-recommended"
                        className="ml-2 gap-1.5 h-7 text-xs"
                      >
                        <Download className="w-3.5 h-3.5" />
                        Download (.xlsx)
                      </Button>
                    </div>
                  </div>

                  {/* Operational disclaimer banner — addresses real-world NAV slippage risk */}
                  <div className="mb-3 p-3 rounded-md border border-amber-200 bg-amber-50/70 text-[12px] leading-relaxed text-amber-900" data-testid="ps-nav-disclaimer">
                    <div className="font-semibold mb-1">⚠ Execution note — projected ₹ values are indicative only</div>
                    <ul className="list-disc pl-5 space-y-0.5">
                      <li><b>Full exits</b> should be placed as <b>units-based</b> orders (sell ALL units). Actual net receipt depends on the applicable NAV on the order-execution date — if NAV drops between simulation and cutoff, the client receives <i>less</i> than the projected ₹ above. The {fullCount} rose-coloured rows below carry this NAV-variation risk.</li>
                      <li><b>Partial sells</b> should be placed as <b>amount-based</b> orders to lock the rupee value. AMC will reject the order if the eligible folio balance × applicable NAV falls short of the requested amount — keep a small buffer or convert to units-based at cutoff if NAV slips.</li>
                      <li>Switch / STP / SWP triggered before the order-day cutoff will reduce available units and can cause partial rejections.</li>
                      <li><b>FIFO is enforced</b>: when a newer lot of a folio+scheme is picked, every older unpicked lot in that same folio+scheme is auto-included first (AMCs always redeem oldest-first). Rows tagged "FIFO-pulled" in the expansion show lots brought in by this rule.</li>
                    </ul>
                  </div>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-gray-50">
                          <TableHead className="text-xs w-8"></TableHead>
                          <TableHead className="text-xs">Buckets</TableHead>
                          <TableHead className="text-xs">Scheme</TableHead>
                          <TableHead className="text-xs">Folio</TableHead>
                          <TableHead className="text-xs text-right">Lots</TableHead>
                          <TableHead className="text-xs text-right">Units to Sell</TableHead>
                          <TableHead className="text-xs text-right">Available</TableHead>
                          <TableHead className="text-xs">Sale Type</TableHead>
                          <TableHead className="text-xs">Order Mode</TableHead>
                          <TableHead className="text-xs text-right">Sell Value</TableHead>
                          <TableHead className="text-xs text-right">Gain</TableHead>
                          <TableHead className="text-xs">Asset</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {groups.map((g) => {
                          const open = psExpandedGroups[g.key];
                          const groupBg = g.is_full ? "bg-rose-50 hover:bg-rose-100" : "bg-amber-50 hover:bg-amber-100";
                          const unitsTone = g.is_full ? "text-rose-800 font-semibold" : "text-amber-800 font-semibold";
                          return (
                            <Fragment key={g.key}>
                              <TableRow
                                className={`cursor-pointer ${groupBg}`}
                                onClick={() => setPsExpandedGroups(prev => ({ ...prev, [g.key]: !prev[g.key] }))}
                                data-testid={`ps-group-${g.key}`}
                              >
                                <TableCell className="text-xs">{open ? "▾" : "▸"}</TableCell>
                                <TableCell className="text-xs">
                                  {Array.from(g.buckets).sort().map(b => (
                                    <Badge key={b} variant="outline" className="text-[10px] mr-1">{bucketLabel(b)}</Badge>
                                  ))}
                                </TableCell>
                                <TableCell className="text-xs max-w-[320px] truncate" title={g.scheme_name}>{g.scheme_name}</TableCell>
                                <TableCell className="text-xs font-mono">{g.folio_no}</TableCell>
                                <TableCell className="text-xs text-right font-mono">{g.lots.length}</TableCell>
                                <TableCell className={`text-xs text-right font-mono ${unitsTone}`}>{g.units.toFixed(3)}</TableCell>
                                <TableCell className="text-xs text-right font-mono text-gray-600">{g.available_units.toFixed(3)}</TableCell>
                                <TableCell className="text-xs">
                                  <Badge className={`text-[10px] ${g.is_full ? "bg-rose-600 text-white" : "bg-amber-500 text-white"}`}>
                                    {g.is_full ? "FULL EXIT" : `PARTIAL (${((g.units / g.available_units) * 100).toFixed(1)}%)`}
                                  </Badge>
                                </TableCell>
                                <TableCell className="text-xs">
                                  <Badge variant="outline" className={`text-[10px] ${g.is_full ? "border-rose-300 text-rose-700" : "border-amber-300 text-amber-700"}`}>
                                    {g.is_full ? "Units-based" : "Amount-based"}
                                  </Badge>
                                </TableCell>
                                <TableCell className="text-xs text-right font-mono" title={g.is_full ? "Estimate at current NAV — actual receipt depends on cutoff-day NAV" : "Lock the rupee value via amount-based order"}>
                                  {g.is_full ? "~ " : ""}{formatCurrency(g.sell_value)}
                                </TableCell>
                                <TableCell className={`text-xs text-right font-mono ${g.gain >= 0 ? "text-green-700" : "text-red-700"}`}>
                                  {formatCurrency(g.gain)}
                                </TableCell>
                                <TableCell className="text-xs">
                                  <Badge className={`text-[10px] ${g.is_equity ? "bg-blue-100 text-blue-700" : "bg-purple-100 text-purple-700"}`}>
                                    {g.is_equity ? "Equity" : "Debt"}
                                  </Badge>
                                </TableCell>
                              </TableRow>
                              {open && g.lots.map((p, i) => {
                                const lotFull = p.units_to_sell >= p.remaining_units * 0.995;
                                const lotBg = lotFull ? "bg-rose-50/60" : "bg-amber-50/60";
                                const lotUnitsTone = lotFull ? "text-rose-700 font-semibold" : "text-amber-700 font-semibold";
                                return (
                                  <TableRow key={`${g.key}-lot-${i}`} className={lotBg}>
                                    <TableCell></TableCell>
                                    <TableCell colSpan={2} className="text-[11px] text-gray-600 pl-8">
                                      Purchase {p.transaction_date} · {p.holding_years.toFixed(2)} yrs · NAV ₹{p.purchase_nav.toFixed(2)} → ₹{p.current_nav.toFixed(2)}
                                      {p.fifo_forced && (
                                        <Badge className="ml-2 text-[9px] bg-indigo-100 text-indigo-700">FIFO-pulled</Badge>
                                      )}
                                    </TableCell>
                                    <TableCell className="text-[11px]">
                                      <Badge variant="outline" className="text-[9px]">{bucketLabel(p.bucket)}</Badge>
                                    </TableCell>
                                    <TableCell></TableCell>
                                    <TableCell className={`text-[11px] text-right font-mono ${lotUnitsTone}`}>{p.units_to_sell.toFixed(3)}</TableCell>
                                    <TableCell className="text-[11px] text-right font-mono text-gray-600">{p.remaining_units.toFixed(3)}</TableCell>
                                    <TableCell className="text-[11px]">
                                      <Badge className={`text-[9px] ${lotFull ? "bg-rose-600 text-white" : "bg-amber-500 text-white"}`}>
                                        {lotFull ? "FULL" : `${((p.units_to_sell / p.remaining_units) * 100).toFixed(1)}%`}
                                      </Badge>
                                    </TableCell>
                                    <TableCell className="text-[11px]">
                                      <Badge variant="outline" className={`text-[9px] ${lotFull ? "border-rose-300 text-rose-700" : "border-amber-300 text-amber-700"}`}>
                                        {lotFull ? "Units" : "Amount"}
                                      </Badge>
                                    </TableCell>
                                    <TableCell className="text-[11px] text-right font-mono" title={lotFull ? "Estimate — actual NAV-dependent" : "Lock via amount-based"}>
                                      {lotFull ? "~ " : ""}{formatCurrency(p.sell_value)}
                                    </TableCell>
                                    <TableCell className={`text-[11px] text-right font-mono ${p.gain_portion >= 0 ? "text-green-700" : "text-red-700"}`}>
                                      {formatCurrency(p.gain_portion)}
                                    </TableCell>
                                    <TableCell className="text-[11px]">
                                      <Badge className={`text-[9px] ${p.long_term ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>
                                        {p.long_term ? "LTCG" : "STCG"}
                                      </Badge>
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </Fragment>
                          );
                        })}
                        <TableRow className="bg-gray-100 font-semibold border-t-2 border-gray-300" data-testid="ps-recommend-total">
                          <TableCell></TableCell>
                          <TableCell colSpan={3} className="text-xs">TOTAL ({groups.length} schemes, {psSimulation.picks.length} lots · {fullCount} full / {partialCount} partial)</TableCell>
                          <TableCell className="text-xs text-right font-mono">{psSimulation.picks.length}</TableCell>
                          <TableCell className="text-xs text-right font-mono">{totalUnits.toFixed(3)}</TableCell>
                          <TableCell></TableCell>
                          <TableCell></TableCell>
                          <TableCell></TableCell>
                          <TableCell className="text-xs text-right font-mono">{formatCurrency(totalValue)}</TableCell>
                          <TableCell className={`text-xs text-right font-mono ${totalGain >= 0 ? "text-green-700" : "text-red-700"}`}>
                            {formatCurrency(totalGain)}
                          </TableCell>
                          <TableCell></TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            );
          })()}
        </div>
      ) : activeTab === "provisional_buy" ? (
        /* ──────────────────────────────────────────────────────────────
           Provisional Buy — Fresh or Additional purchase into a model.
           Fresh   → direct model deploy (uses /allocate route=direct).
           Additional → deficit-rebalance using the client's current
           holdings (server fetches from active_units by PAN).
           ────────────────────────────────────────────────────────────── */
        <ProvisionalBuyPanel
          selectedClient={selectedClient}
          selectedClientData={selectedClientData}
          formatCurrency={formatCurrency}
          api={api}
        />
      ) : activeTab === "exit_free" ? (
        /* ──────────────────────────────────────────────────────────────
           Exit Free Investments — lots redeemable without exit load AND
           outside ELSS lock-in. Built on top of `exitFreeLots` (memo).
           ────────────────────────────────────────────────────────────── */
        <div className="space-y-4" data-testid="exit-free-tab">
          {/* Hero summary row — live, responds to filter+search */}
          <Card className="border-emerald-200 bg-gradient-to-br from-emerald-50 via-white to-teal-50">
            <CardContent className="p-5">
              <div className="flex items-start gap-3 mb-4">
                <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="text-lg font-semibold text-gray-800">Exit Free Investments</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Lots held &gt; 1 year (past typical exit-load) AND outside any ELSS lock-in — redeemable today without penalty. Sequence: 5+y → 3-5y → 2-3y → 1-2y.
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <div className="bg-white border border-emerald-200 rounded-lg p-3" data-testid="ef-summary-lots">
                  <div className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold">Lots / Schemes</div>
                  <div className="text-2xl font-bold text-gray-900">{exitFreeSummary.lots} <span className="text-sm font-medium text-gray-500">/ {exitFreeSummary.schemes}</span></div>
                </div>
                <div className="bg-white border border-emerald-200 rounded-lg p-3" data-testid="ef-summary-current">
                  <div className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold">Current Value</div>
                  <div className="text-xl font-bold text-emerald-700">{formatCurrency(exitFreeSummary.current_value)}</div>
                </div>
                <div className="bg-white border border-emerald-200 rounded-lg p-3" data-testid="ef-summary-cost">
                  <div className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold">Cost Value</div>
                  <div className="text-xl font-bold text-gray-700">{formatCurrency(exitFreeSummary.cost_value)}</div>
                </div>
                <div className="bg-white border border-emerald-200 rounded-lg p-3" data-testid="ef-summary-gain">
                  <div className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold">Unrealised Gain</div>
                  <div className={`text-xl font-bold ${exitFreeSummary.gain >= 0 ? "text-green-700" : "text-red-700"}`}>
                    {exitFreeSummary.gain >= 0 ? "+" : ""}{formatCurrency(exitFreeSummary.gain)}
                  </div>
                  <div className="text-[10px] text-gray-500 mt-0.5">{exitFreeSummary.gain_pct.toFixed(1)}% absolute</div>
                </div>
                <div className="bg-white border border-emerald-200 rounded-lg p-3" data-testid="ef-summary-mix">
                  <div className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold">Asset Mix</div>
                  <div className="text-[11px] mt-0.5 space-y-0.5">
                    <div>Equity: <b className="text-blue-700">{exitFreeSummary.equity_count}</b></div>
                    <div>Debt: <b className="text-purple-700">{exitFreeSummary.debt_count}</b></div>
                    <div>ELSS: <b className="text-emerald-700">{exitFreeSummary.elss_count}</b></div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Bucket cards (unchanged but condensed) */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {exitFreeBuckets.map((b) => (
              <div
                key={b.key}
                data-testid={`exit-free-bucket-${b.key}`}
                className={`border rounded-lg p-3 ${b.color}`}
              >
                <div className="text-xs font-semibold uppercase tracking-wide opacity-80">{b.label}</div>
                <div className="text-xl font-bold mt-1">{formatCurrency(b.current_value)}</div>
                <div className="text-[11px] mt-1 opacity-80">Lots: <b>{b.lots_count}</b></div>
                <div className={`text-[11px] mt-1 font-medium ${b.gain >= 0 ? "text-green-700" : "text-red-700"}`}>
                  {b.gain >= 0 ? "▲" : "▼"} {formatCurrency(Math.abs(b.gain))}
                </div>
              </div>
            ))}
          </div>

          {/* Controls: Asset class radio + Search + Sort */}
          <Card className="border-gray-200">
            <CardContent className="p-4 space-y-3">
              <div className="flex flex-wrap items-center gap-4">
                {/* Asset class radio */}
                <div className="flex items-center gap-3" role="radiogroup" data-testid="ef-asset-class-radio">
                  <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Asset Class:</span>
                  {[
                    { v: "all",    lbl: "All",    cnt: exitFreeClassCounts.all },
                    { v: "equity", lbl: "Equity", cnt: exitFreeClassCounts.equity },
                    { v: "debt",   lbl: "Debt",   cnt: exitFreeClassCounts.debt },
                    { v: "elss",   lbl: "ELSS",   cnt: exitFreeClassCounts.elss },
                  ].map((o) => (
                    <label
                      key={o.v}
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded border cursor-pointer text-xs transition-colors ${
                        efAssetClass === o.v
                          ? "bg-emerald-100 border-emerald-400 text-emerald-900 font-medium"
                          : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
                      }`}
                      data-testid={`ef-asset-${o.v}`}
                    >
                      <input
                        type="radio"
                        name="ef_asset_class"
                        value={o.v}
                        checked={efAssetClass === o.v}
                        onChange={(e) => setEfAssetClass(e.target.value)}
                        className="h-3 w-3"
                      />
                      <span>{o.lbl}</span>
                      <Badge variant="outline" className="text-[9px] px-1 py-0">{o.cnt}</Badge>
                    </label>
                  ))}
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                {/* Search */}
                <div className="flex-1 min-w-[200px]">
                  <input
                    type="text"
                    value={efSearch}
                    onChange={(e) => setEfSearch(e.target.value)}
                    placeholder="🔍 Search scheme or folio…"
                    className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-300"
                    data-testid="ef-search-input"
                  />
                </div>
                {/* Sort */}
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Sort By:</span>
                  <select
                    value={efSortKey}
                    onChange={(e) => setEfSortKey(e.target.value)}
                    className="px-2 py-1 text-xs border border-gray-300 rounded"
                    data-testid="ef-sort-key"
                  >
                    <option value="bucket">Bucket (tenure)</option>
                    <option value="gain">Gain</option>
                    <option value="current_value">Current Value</option>
                    <option value="cagr">CAGR</option>
                    <option value="holding_years">Years Held</option>
                    <option value="scheme">Scheme Name</option>
                  </select>
                  <button
                    type="button"
                    onClick={() => setEfSortDir((d) => (d === "asc" ? "desc" : "asc"))}
                    className="px-2 py-1 text-xs border border-gray-300 rounded hover:bg-gray-50"
                    data-testid="ef-sort-dir"
                    title={`Currently ${efSortDir === "asc" ? "Ascending" : "Descending"} — click to flip`}
                  >
                    {efSortDir === "asc" ? "↑ Asc" : "↓ Desc"}
                  </button>
                </div>
                {/* Reset */}
                {(efAssetClass !== "all" || efSearch || efSortKey !== "bucket" || efSortDir !== "desc") && (
                  <button
                    type="button"
                    onClick={() => { setEfAssetClass("all"); setEfSearch(""); setEfSortKey("bucket"); setEfSortDir("desc"); }}
                    className="text-xs text-gray-600 hover:text-gray-900 underline"
                    data-testid="ef-reset"
                  >Reset</button>
                )}
              </div>
            </CardContent>
          </Card>

          <Card className="border-gray-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="text-lg font-semibold text-gray-800">Exit-Free Lots</h3>
                  <p className="text-xs text-gray-500">
                    Showing <b>{exitFreeGroups.length}</b> scheme/folio group{exitFreeGroups.length === 1 ? "" : "s"} ({exitFreeFilteredSorted.length} of {exitFreeLots.length} eligible lot{exitFreeLots.length === 1 ? "" : "s"}). Click the arrow to expand individual lots.
                  </p>
                </div>
                <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                  {formatCurrency(exitFreeSummary.current_value)} filtered
                </Badge>
              </div>
              <div className="overflow-x-auto max-h-[600px]">
                <Table>
                  <TableHeader className="sticky top-0 bg-white z-10">
                    <TableRow className="bg-gray-50">
                      <TableHead className="text-xs w-10"></TableHead>
                      <TableHead className="text-xs">#</TableHead>
                      <TableHead className="text-xs">Tenure</TableHead>
                      <TableHead className="text-xs">Scheme</TableHead>
                      <TableHead className="text-xs">Folio</TableHead>
                      <TableHead className="text-xs">Purchase Date</TableHead>
                      <TableHead className="text-xs text-right">Yrs Held</TableHead>
                      <TableHead className="text-xs text-right">Units</TableHead>
                      <TableHead className="text-xs text-right">Cost</TableHead>
                      <TableHead className="text-xs text-right">Current Value</TableHead>
                      <TableHead className="text-xs text-right">Gain</TableHead>
                      <TableHead className="text-xs text-right">CAGR</TableHead>
                      <TableHead className="text-xs">Type</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {exitFreeGroups.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={13} className="text-center py-6 text-gray-500 text-sm">
                          {exitFreeLots.length === 0
                            ? "No exit-free lots yet — all current holdings are within their exit-load window or ELSS lock-in."
                            : "No lots match the current filter. Try widening the asset class or clearing the search."}
                        </TableCell>
                      </TableRow>
                    ) : exitFreeGroups.map((g, i) => {
                      const open = !!expandedExitFreeGroups[g.key];
                      const tenureLabel = g.bucket === "LT5" ? "5+y" : g.bucket === "Y3_5" ? "3-5y" : g.bucket === "Y2_3" ? "2-3y" : "1-2y";
                      const purchaseLabel = g.lots.length === 1
                        ? g.earliest_date
                        : (g.earliest_date === g.latest_date ? g.earliest_date : `${g.earliest_date} … ${g.latest_date}`);
                      const yrsLabel = g.lots.length === 1
                        ? g.max_yrs.toFixed(2)
                        : (g.min_yrs.toFixed(2) === g.max_yrs.toFixed(2) ? g.max_yrs.toFixed(2) : `${g.min_yrs.toFixed(2)}–${g.max_yrs.toFixed(2)}`);
                      return (
                        <Fragment key={g.key}>
                          <TableRow
                            data-testid={`exit-free-group-${i}`}
                            className="cursor-pointer hover:bg-emerald-50/40"
                            onClick={() => setExpandedExitFreeGroups((prev) => ({ ...prev, [g.key]: !prev[g.key] }))}
                          >
                            <TableCell className="text-xs text-gray-500 select-none">{open ? "▼" : "▶"}</TableCell>
                            <TableCell className="text-xs">{i + 1}</TableCell>
                            <TableCell className="text-xs">
                              <Badge variant="outline" className="text-[10px]">{tenureLabel}</Badge>
                            </TableCell>
                            <TableCell className="text-xs max-w-[260px] truncate font-medium" title={g.scheme_name}>
                              {g.scheme_name}
                              {g.lots.length > 1 && (
                                <span className="ml-1 text-[10px] italic text-gray-500">· {g.lots.length} lots</span>
                              )}
                            </TableCell>
                            <TableCell className="text-xs">{g.folio_no}</TableCell>
                            <TableCell className="text-xs">{purchaseLabel}</TableCell>
                            <TableCell className="text-xs text-right">{yrsLabel}</TableCell>
                            <TableCell className="text-xs text-right">{formatNumber(g.remaining_units, 3)}</TableCell>
                            <TableCell className="text-xs text-right">{formatCurrency(g.cost_value)}</TableCell>
                            <TableCell className="text-xs text-right font-medium">{formatCurrency(g.current_value)}</TableCell>
                            <TableCell className={`text-xs text-right font-medium ${g.gain >= 0 ? "text-green-700" : "text-red-700"}`}>
                              {g.gain >= 0 ? "+" : ""}{formatCurrency(g.gain)}
                            </TableCell>
                            <TableCell className="text-xs text-right">{g.cagr != null ? `${g.cagr.toFixed(2)}%` : "—"}</TableCell>
                            <TableCell className="text-xs">
                              {g.asset_class === "elss" ? (
                                <Badge variant="outline" className="bg-emerald-50 text-emerald-700">ELSS</Badge>
                              ) : g.asset_class === "equity" ? (
                                <Badge variant="outline" className="bg-blue-50 text-blue-700">Equity</Badge>
                              ) : (
                                <Badge variant="outline" className="bg-purple-50 text-purple-700">Debt</Badge>
                              )}
                            </TableCell>
                          </TableRow>
                          {open && g.lots.map((l, li) => (
                            <TableRow key={l.lot_id} className="bg-gray-50/40">
                              <TableCell></TableCell>
                              <TableCell className="text-[10px] text-gray-500">↳ {li + 1}</TableCell>
                              <TableCell></TableCell>
                              <TableCell className="text-[11px] text-gray-600 max-w-[260px] truncate" title={l.scheme_name}>{l.scheme_name}</TableCell>
                              <TableCell className="text-[11px] text-gray-600">{l.folio_no}</TableCell>
                              <TableCell className="text-[11px] text-gray-600">{l.transaction_date}</TableCell>
                              <TableCell className="text-[11px] text-right text-gray-600">{l.holding_years.toFixed(2)}</TableCell>
                              <TableCell className="text-[11px] text-right text-gray-600">{formatNumber(l.remaining_units, 3)}</TableCell>
                              <TableCell className="text-[11px] text-right text-gray-600">{formatCurrency(l.cost_value)}</TableCell>
                              <TableCell className="text-[11px] text-right text-gray-700">{formatCurrency(l.current_value)}</TableCell>
                              <TableCell className={`text-[11px] text-right ${l.gain >= 0 ? "text-green-700" : "text-red-700"}`}>
                                {l.gain >= 0 ? "+" : ""}{formatCurrency(l.gain)}
                              </TableCell>
                              <TableCell className="text-[11px] text-right text-gray-600">{l.cagr != null ? `${l.cagr.toFixed(2)}%` : "—"}</TableCell>
                              <TableCell></TableCell>
                            </TableRow>
                          ))}
                        </Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </div>
      ) : activeTab === "reporting_prefs" ? (
        <ReportingPreferencesPanel api={api} />
      ) : activeTab === "scheme_breakdown" ? (
        /* Holdings Table (Scheme Wise Breakdown Tab) */
        <Card className="border-gray-200">
          <CardContent className="p-0">
          {/* Tab Navigation for Purchase/Sold Summary */}
          <div className="flex items-center border-b border-gray-200">
            {/* Show Purchase tab only for non-exited clients */}
            {!isExitedClient && (
              <button
                className={`flex items-center gap-2 px-6 py-3 text-sm font-medium transition-colors ${
                  summarySubTab === "purchase" 
                    ? "border-b-2 border-green-500 text-green-600 bg-green-50/50" 
                    : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                }`}
                onClick={() => setSummarySubTab("purchase")}
              >
                <TrendingUp className="w-4 h-4" />
                Active Units Summary
                <Badge variant="outline" className="bg-green-50 text-green-600 border-green-200 text-xs">
                  {sortedData.reduce((sum, h) => sum + (h.transaction_count || 1), 0)}
                </Badge>
              </button>
            )}
            <button
              className={`flex items-center gap-2 px-6 py-3 text-sm font-medium transition-colors ${
                summarySubTab === "sold" 
                  ? "border-b-2 border-red-500 text-red-600 bg-red-50/50" 
                  : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
              }`}
              onClick={() => setSummarySubTab("sold")}
            >
              <TrendingDown className="w-4 h-4" />
              {isExitedClient ? "Exited - Sold Units Summary" : "Sold Units Summary"}
              <Badge variant="outline" className="bg-red-50 text-red-600 border-red-200 text-xs">
                {soldFromAllTxn.length}
              </Badge>
            </button>
            <div className="flex-1" />
            {/* As-on-date picker — only meaningful on Active Units Summary */}
            {summarySubTab === "purchase" && (
              <div className="flex items-center gap-2 mr-4" data-testid="scheme-as-on-date-wrap">
                <span className="text-xs uppercase tracking-wide text-gray-500 whitespace-nowrap">As on date</span>
                <input
                  type="date"
                  className="h-8 rounded-md border border-gray-300 px-2 text-xs focus:outline-none focus:ring-1 focus:ring-teal-400 bg-white"
                  value={schemeAsOnDate}
                  max={new Date().toISOString().slice(0, 10)}
                  onChange={(e) => setSchemeAsOnDate(e.target.value)}
                  data-testid="scheme-as-on-date-input"
                />
                {schemeAsOnDate && (
                  <button
                    type="button"
                    className="h-8 px-2 rounded-md border border-gray-300 text-xs text-gray-600 hover:bg-gray-50"
                    onClick={() => setSchemeAsOnDate("")}
                    data-testid="scheme-as-on-date-clear"
                  >
                    Live
                  </button>
                )}
                {schemeAsOfLoading && (
                  <span className="text-[10px] text-teal-600">Loading NAVs…</span>
                )}
              </div>
            )}
            {/* Download XIRR buttons removed — XIRR data is included in the
                main Export Excel from the Portfolio header. */}
          </div>
          
          {/* Purchase Summary Tab Content */}
          {summarySubTab === "purchase" && (
            <div>
          {/* Search Bar */}
          <div className="p-4 bg-green-50/50 border-b border-green-100">
            <div className="flex items-center gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search by folio, scheme name, ISIN..."
                  value={purchaseSearchTerm}
                  onChange={(e) => setPurchaseSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              {purchaseSearchTerm && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setPurchaseSearchTerm("")}
                  className="text-gray-500"
                >
                  Clear
                </Button>
              )}
            </div>
          </div>
          
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600" />
            </div>
          ) : sortedData.length === 0 ? (
            <div className="text-center py-16 text-gray-500">
              {isExitedClient ? (
                <div>
                  <p className="text-lg font-medium text-orange-600 mb-2">Exited Client</p>
                  <p>This client has fully redeemed all holdings.</p>
                  <p className="mt-2">Check the <span className="font-semibold text-red-600">Sold Units Summary</span> tab above for redemption history.</p>
                </div>
              ) : (
                "No holdings found for this client"
              )}
            </div>
          ) : (
            <>
              {/* Mobile card list — vertical scroll only, no horizontal */}
              <div className="md:hidden">
                <MobileHoldingsCards rows={schemeRowsForRender} totalMarketValue={schemeTotalMarketValue} />
              </div>
              {/* Desktop / tablet table */}
              <div className="overflow-x-auto hidden md:block">
              <Table className="table-fixed w-full">
                <TableHeader>
                  <TableRow className="bg-gray-50/50">
                    <TableHead className="text-sm font-medium text-gray-500 w-[50px]">Accounts</TableHead>
                    <TableHead className="text-sm font-medium text-gray-500 w-[100px]">Linked Goals</TableHead>
                    <SortableHeader field="folio_no" className="w-[120px]">Folio No.</SortableHeader>
                    {showFamilyClientCol && (
                      <TableHead className="text-xs w-[140px]">Client</TableHead>
                    )}
                    <SortableHeader field="scheme_name" className="w-[280px]">Scheme Name</SortableHeader>
                    <SortableHeader field="units" className="justify-end text-right w-[110px]">Balance Units</SortableHeader>
                    <SortableHeader field="cost_value" className="justify-end text-right w-[140px]">Total Cost Value (INR)</SortableHeader>
                    <SortableHeader field="market_value" className="justify-end text-right w-[130px]">Current Value (INR)</SortableHeader>
                    <SortableHeader field="profit" className="justify-end text-right w-[110px]">Profit/Loss</SortableHeader>
                    <SortableHeader field="holdings_pct" className="justify-end text-right w-[80px]">% Holdings</SortableHeader>
                    <SortableHeader field="xirr" className="justify-end text-right w-[70px]">XIRR %</SortableHeader>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {schemeRowsForRender.map((holding, index) => {
                    const holdingsPct = schemeTotalMarketValue > 0 
                      ? ((holding.market_value || 0) / schemeTotalMarketValue * 100).toFixed(2) 
                      : 0;
                    return (
                      <TableRow key={`${holding.folio_no}-${index}`} className="hover:bg-gray-50/50">
                        {/* Accounts Column - holder type circle */}
                        <TableCell className="py-2">
                          <div 
                            className={`w-7 h-7 rounded-full ${getOwnershipColor(holding.holding_pattern)} flex items-center justify-center cursor-help`}
                            title={
                              holding.holding_pattern === 'joint_holder' 
                                ? `Joint Account\n2nd Holder: ${holding.second_holder || '-'}${holding.third_holder ? `\n3rd Holder: ${holding.third_holder}` : ''}`
                                : holding.holding_pattern === 'minor_guardian'
                                ? `Minor Account (as Guardian)\nGuardian: ${holding.guardian_name || '-'}`
                                : holding.holding_pattern === 'self_minor'
                                ? `Minor Account\nGuardian: ${holding.guardian_name || '-'}`
                                : 'Self (Single)'
                            }
                          >
                            {holding.holding_pattern === 'joint_holder' || holding.holding_pattern === 'as_joint' ? (
                              <Users className="w-3.5 h-3.5 text-white" />
                            ) : holding.holding_pattern === 'minor_guardian' || holding.holding_pattern === 'self_minor' ? (
                              <Baby className="w-3.5 h-3.5 text-white" />
                            ) : (
                              <User className="w-3.5 h-3.5 text-white" />
                            )}
                          </div>
                        </TableCell>
                        
                        {/* Linked Goals Column - goal capsules with popover */}
                        <TableCell className="py-2">
                          <Popover>
                            <PopoverTrigger asChild>
                              <div 
                                className="flex items-center gap-1 cursor-pointer min-h-[28px]"
                                data-testid={`goal-assign-${holding.folio_no}-${holding.isin}`}
                              >
                                {schemeGoals[getHoldingKey(holding.folio_no, holding.isin)]?.length > 0 ? (
                                  <>
                                    {schemeGoals[getHoldingKey(holding.folio_no, holding.isin)].map((sg) => {
                                      const color = getGoalColor(sg.goal.id);
                                      return (
                                        <div 
                                          key={sg.goal.id}
                                          className={`w-8 h-6 rounded-full text-[10px] font-bold ${color.bg} ${color.text} flex items-center justify-center`}
                                          title={sg.goal.name}
                                        >
                                          {sg.percentage}%
                                        </div>
                                      );
                                    })}
                                    {!isFullyAllocated(getHoldingKey(holding.folio_no, holding.isin)) && (
                                      <div className="w-8 h-6 rounded-full text-[9px] bg-gray-100 text-gray-400 flex items-center justify-center" title="Unallocated">
                                        +{getRemainingAllocation(getHoldingKey(holding.folio_no, holding.isin))}%
                                      </div>
                                    )}
                                  </>
                                ) : (
                                  <div className="text-gray-400 text-[10px] flex items-center gap-0.5 hover:text-gray-600">
                                    <Plus className="w-3 h-3" />
                                    <span>Add</span>
                                  </div>
                                )}
                              </div>
                            </PopoverTrigger>
                            <PopoverContent className="w-80 p-0" align="start" sideOffset={5}>
                              <div>
                                {/* Header */}
                                <div className="flex items-center justify-between px-3 py-2 border-b bg-gray-50">
                                  <p className="text-sm font-medium text-gray-700">Assign to Goal</p>
                                  <span className={`text-sm ${isFullyAllocated(getHoldingKey(holding.folio_no, holding.isin)) ? 'text-red-500 font-medium' : 'text-gray-500'}`}>
                                    {isFullyAllocated(getHoldingKey(holding.folio_no, holding.isin)) ? '100% allocated' : `${getRemainingAllocation(getHoldingKey(holding.folio_no, holding.isin))}% remaining`}
                                  </span>
                                </div>
                                
                                {goals.length === 0 ? (
                                  <div className="text-sm text-gray-500 p-3">
                                    No goals yet.
                                    <Button 
                                      variant="link" 
                                      size="sm" 
                                      className="h-auto p-0 ml-1 text-purple-600"
                                      onClick={() => setShowCreateGoalDialog(true)}
                                    >
                                      Create one
                                    </Button>
                                  </div>
                                ) : (
                                  <table className="w-full">
                                    <thead>
                                      <tr className="border-b bg-gray-50/50">
                                        <th className="text-left text-sm font-medium text-gray-500 px-3 py-2">Goals</th>
                                        <th className="text-center text-sm font-medium text-gray-500 px-3 py-2 w-32">Allocation%</th>
                                        <th className="w-16"></th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {goals.map((goal, goalIdx) => {
                                        const holdingKey = getHoldingKey(holding.folio_no, holding.isin);
                                        const existingAlloc = schemeGoals[holdingKey]?.find(sg => sg.goal.id === goal.id);
                                        const color = goalColors[goalIdx % goalColors.length];
                                        const canAssign = !isFullyAllocated(holdingKey) || existingAlloc;
                                        const maxAlloc = existingAlloc 
                                          ? getRemainingAllocation(holdingKey) + existingAlloc.percentage 
                                          : getRemainingAllocation(holdingKey);
                                        
                                        return (
                                          <tr 
                                            key={goal.id}
                                            className={`border-b last:border-b-0 ${existingAlloc ? color.bg : ''} ${!canAssign && !existingAlloc ? 'opacity-50' : ''}`}
                                          >
                                            <td className="px-3 py-2">
                                              <p className={`text-sm font-medium ${existingAlloc ? color.text : 'text-gray-800'}`}>
                                                {goal.name}
                                              </p>
                                              <p className="text-[10px] text-gray-500">{goal.expected_date}</p>
                                            </td>
                                            <td className="px-3 py-2">
                                              {canAssign ? (
                                                <Input
                                                  type="number"
                                                  min="1"
                                                  max={maxAlloc}
                                                  defaultValue={existingAlloc?.percentage || Math.min(100, maxAlloc)}
                                                  className="h-7 w-full text-center text-sm"
                                                  onClick={(e) => e.stopPropagation()}
                                                  id={`alloc-${goal.id}-${holding.folio_no}-${holding.isin}`}
                                                />
                                              ) : (
                                                <span className="text-sm text-gray-400">-</span>
                                              )}
                                            </td>
                                            <td className="px-2 py-2">
                                              {canAssign ? (
                                                <div className="flex items-center gap-1">
                                                  <Button
                                                    size="sm"
                                                    className={`h-7 px-2 text-sm ${existingAlloc ? color.badge : 'bg-teal-600 hover:bg-teal-700 text-white'}`}
                                                    onClick={async (e) => {
                                                      e.stopPropagation();
                                                      const input = document.getElementById(`alloc-${goal.id}-${holding.folio_no}-${holding.isin}`);
                                                      const pct = parseInt(input?.value) || 0;
                                                      if (pct > 0 && pct <= maxAlloc) {
                                                        await handleAssignGoal(goal.id, holding.folio_no, holding.isin, pct);
                                                      }
                                                    }}
                                                  >
                                                    {existingAlloc ? '✓' : '+'}
                                                  </Button>
                                                  {existingAlloc && (
                                                    <Button
                                                      variant="ghost"
                                                      size="sm"
                                                      className="h-7 w-7 p-0 text-red-400 hover:text-red-600"
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleUnassignGoal(goal.id, holding.folio_no, holding.isin);
                                                      }}
                                                    >
                                                      <Trash2 className="w-3 h-3" />
                                                    </Button>
                                                  )}
                                                </div>
                                              ) : null}
                                            </td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                )}
                              </div>
                            </PopoverContent>
                          </Popover>
                        </TableCell>
                        <TableCell className="py-2 font-mono text-sm text-gray-600">
                          {holding.folio_no || "-"}
                        </TableCell>
                        {showFamilyClientCol && (
                          <TableCell
                            className="py-2 text-xs text-gray-700"
                            data-testid={`scheme-row-client-${holding.folio_no}-${index}`}
                          >
                            <span
                              className="truncate block max-w-[130px]"
                              title={resolveRowClientName(holding) || "-"}
                            >
                              {resolveRowClientName(holding) || "-"}
                            </span>
                          </TableCell>
                        )}
                        <TableCell className="py-2 max-w-[300px]">
                          <div 
                            className="truncate text-sm cursor-help" 
                            title={`${toTitleCase(holding.scheme_name) || toTitleCase(holding.fund_desc) || 'Unknown Scheme'}\nISIN: ${holding.isin || '-'}\nCode: ${(holding.scheme_code || holding.product_code)?.toUpperCase() || '-'}`}
                          >
                            {toTitleCase(holding.scheme_name) || toTitleCase(holding.fund_desc) || (holding.scheme_code || holding.product_code)?.toUpperCase() || "Unknown"}
                          </div>
                        </TableCell>
                        <TableCell className="py-2 text-right font-mono text-sm">
                          {(() => {
                            // Check if there's a unit mismatch for this holding
                            // Mirror the product_code-first join used by computedUnitMismatch
                            // so IDCW-variant ISIN switches (e.g. 128TSDP K01149 ↔ K01156) don't
                            // appear as phantom mismatches on this view.
                            const key = `${holding.folio_no}:${holding.scheme_code || holding.isin}`;
                            const mismatchItem = computedUnitMismatch?.mismatched?.find(m => 
                              `${m.folio_no}:${m.scheme_code || m.isin}` === key
                            );
                            const hasMismatch = mismatchItem && Math.abs(mismatchItem.difference) >= 0.01;

                            // Bucket active-unit lots by exit-load eligibility.
                            // Equity (non-ELSS): 365 days; ELSS: 1,096 days
                            // (3-yr lock-in). Non-equity thresholds per the
                            // user-defined fund-category windows below — we
                            // check most-specific tokens first so "ultra short"
                            // doesn't get short-circuited by the "short" check.
                            const lots = activeUnitsCostMap?.[key]?.transactions || [];
                            const schemeNameLc = (holding.scheme_name || holding.fund_desc || "").toLowerCase();
                            const isElss = schemeNameLc.includes("elss") || schemeNameLc.includes("tax saver") || schemeNameLc.includes("tax-saver");
                            const debtish = ["debt","bond","gilt","income"].some(k => schemeNameLc.includes(k));

                            // Returns [thresholdDays, label] for non-equity, non-ELSS schemes.
                            const resolveDebtThreshold = () => {
                              if (schemeNameLc.includes("overnight")) return [1, "Overnight · 1-day exit-load window"];
                              if (schemeNameLc.includes("money market")) return [1, "Money Market · 1-day exit-load window"];
                              if (schemeNameLc.includes("treasury")) return [1, "Treasury · 1-day exit-load window"];
                              if (schemeNameLc.includes("ultra short") || schemeNameLc.includes("ultra-short")) return [30, "Ultra Short · 30-day exit-load window"];
                              if (schemeNameLc.includes("liquid")) return [7, "Liquid · 7-day exit-load window"];
                              if (schemeNameLc.includes("short term") || schemeNameLc.includes("short-term")) return [90, "Short Term · 90-day exit-load window"];
                              if (schemeNameLc.includes("low duration")) return [90, "Low Duration · 90-day exit-load window"];
                              return [365, "Debt · 365-day exit-load window"];
                            };
                            const liquidish = (
                              schemeNameLc.includes("liquid") ||
                              schemeNameLc.includes("overnight") ||
                              schemeNameLc.includes("ultra short") ||
                              schemeNameLc.includes("ultra-short") ||
                              schemeNameLc.includes("short term") ||
                              schemeNameLc.includes("short-term") ||
                              schemeNameLc.includes("low duration") ||
                              schemeNameLc.includes("money market") ||
                              schemeNameLc.includes("treasury")
                            );
                            const lotsAreEquity = lots.length > 0
                              ? lots.some(l => l.is_equity === true)
                              : !(liquidish || debtish || isElss);
                            const isEquityScheme = isElss ? false : (lotsAreEquity || (!liquidish && !debtish));

                            let threshold, categoryLabel;
                            if (isElss) {
                              threshold = 1096;
                              categoryLabel = "ELSS · 1,096-day lock-in";
                            } else if (isEquityScheme) {
                              threshold = 365;
                              categoryLabel = "Equity · 365-day exit-load window";
                            } else {
                              [threshold, categoryLabel] = resolveDebtThreshold();
                            }

                            const parsePD = (s) => {
                              if (!s) return null;
                              const t = String(s).trim();
                              const m = t.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})/);
                              if (m) return new Date(+m[3], +m[2] - 1, +m[1]);
                              if (/^\d{8}$/.test(t)) return new Date(+t.substring(0,4), +t.substring(4,6) - 1, +t.substring(6,8));
                              if (/^\d{4}-\d{1,2}-\d{1,2}/.test(t)) return new Date(t);
                              return null;
                            };
                            const today = new Date();
                            let exitFreeUnits = 0, exitLoadUnits = 0, persistingUnits = 0;
                            lots.forEach(l => {
                              const pd = parsePD(l.transaction_date || l.trade_date);
                              if (!pd) return;
                              const days = Math.floor((today - pd) / 86400000);
                              const u = l.remaining_units != null
                                ? Math.max(0, parseFloat(l.remaining_units) || 0)
                                : Math.max(0, Math.abs(parseFloat(l.units || 0)) - Math.abs(parseFloat(l.units_sold || l.sold_units || 0)));
                              if (u <= 0) return;
                              if (isElss && days < 1096) persistingUnits += u;
                              else if (days >= threshold) exitFreeUnits += u;
                              else exitLoadUnits += u;
                            });

                            const fmt = (n) => Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 4, maximumFractionDigits: 4 });
                            const tooltipLines = [
                              categoryLabel,
                              `Exit-Free Units: ${fmt(exitFreeUnits)}`,
                              `Exit-Load Units: ${fmt(exitLoadUnits)}`,
                            ];
                            if (isElss) tooltipLines.push(`Persisting (Lock-In): ${fmt(persistingUnits)}`);
                            const exitTooltip = tooltipLines.join("\n");

                            return (
                              <span className={`flex items-center justify-end gap-1 cursor-help ${hasMismatch ? 'text-amber-600' : ''}`} title={exitTooltip}>
                                {formatNumber(holding.units || 0, 4)}
                                {hasMismatch && (
                                  <span 
                                    className="inline-flex items-center justify-center w-4 h-4 bg-amber-100 rounded-full" 
                                    title={`Unit Mismatch! Holdings: ${formatNumber(mismatchItem.holding_units, 4)}, Active Units: ${formatNumber(mismatchItem.active_units, 4)}, Diff: ${formatNumber(mismatchItem.difference, 4)}`}
                                  >
                                    <AlertTriangle className="w-3 h-3 text-amber-600" />
                                  </span>
                                )}
                              </span>
                            );
                          })()}
                        </TableCell>
                        <TableCell className="py-2 text-right text-sm">
                          {(() => {
                            // Check if there's a unit mismatch - don't show cost if mismatched
                            const key = `${holding.folio_no}:${holding.scheme_code || holding.isin}`;
                            const mismatchItem = computedUnitMismatch?.mismatched?.find(m => 
                              `${m.folio_no}:${m.scheme_code || m.isin}` === key
                            );
                            const hasMismatch = mismatchItem && Math.abs(mismatchItem.difference) >= 0.01;
                            
                            if (hasMismatch) {
                              // Still show cost value from Active Units even with mismatch
                              return (
                                <span 
                                  className="font-mono text-blue-600 cursor-help" 
                                  title={`Unit Mismatch! Holdings: ${formatNumber(mismatchItem.holding_units, 4)}, Active Units: ${formatNumber(mismatchItem.active_units, 4)}\nCost from Active Units transactions`}
                                >
                                  {holding.cost_value ? formatCurrency(holding.cost_value) : <span className="text-gray-400">-</span>}
                                </span>
                              );
                            }
                            
                            return holding.cost_value ? (
                              <span 
                                className="font-mono text-blue-600 cursor-help" 
                                title={`Avg Purchase NAV: ${holding.cost_value && holding.units ? (holding.cost_value / holding.units).toFixed(4) : '-'}`}
                              >
                                {formatCurrency(holding.cost_value)}
                              </span>
                            ) : (
                              <span className="text-gray-400">-</span>
                            );
                          })()}
                        </TableCell>
                        <TableCell className="py-2 text-right font-medium">
                          {holding.market_value ? (
                            <span 
                              className={`cursor-help ${holding.nav_is_stale ? 'text-amber-600' : ''}`}
                              title={`NAV: ₹${holding.nav ? holding.nav.toFixed(4) : '-'}${holding.nav_date ? ` (as of ${holding.nav_date})` : ''}${holding.nav_is_stale ? ' - Last available NAV' : ''}`}
                            >
                              {formatCurrency(holding.market_value)}
                              {holding.nav_is_stale && <span className="text-xs ml-1">*</span>}
                            </span>
                          ) : "-"}
                        </TableCell>
                        <TableCell className="py-2 text-right text-sm">
                          {(() => {
                            // Check if there's a unit mismatch
                            const key = `${holding.folio_no}:${holding.scheme_code || holding.isin}`;
                            const mismatchItem = computedUnitMismatch?.mismatched?.find(m => 
                              `${m.folio_no}:${m.scheme_code || m.isin}` === key
                            );
                            const hasMismatch = mismatchItem && Math.abs(mismatchItem.difference) >= 0.01;
                            
                            if (hasMismatch) {
                              // Show P/L from Active Units cost even with mismatch
                              return holding.cost_value && holding.market_value ? (
                                <span className={`font-mono font-medium ${(holding.market_value - holding.cost_value) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                  {(holding.market_value - holding.cost_value) >= 0 ? '+' : ''}{formatCurrency(holding.market_value - holding.cost_value)}
                                </span>
                              ) : (
                                <span className="text-gray-400">-</span>
                              );
                            }
                            
                            return holding.cost_value && holding.market_value ? (
                              <span className={`font-mono font-medium ${(holding.market_value - holding.cost_value) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {(holding.market_value - holding.cost_value) >= 0 ? '+' : ''}{formatCurrency(holding.market_value - holding.cost_value)}
                              </span>
                            ) : (
                              <span className="text-gray-400">-</span>
                            );
                          })()}
                        </TableCell>
                        <TableCell className="py-2 text-right text-sm text-gray-600">
                          {holdingsPct}%
                        </TableCell>
                        <TableCell className="py-2 text-right text-sm">
                          {holding.xirr !== null && holding.xirr !== undefined ? (
                            <span className={`font-mono font-medium ${holding.xirr >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {holding.xirr >= 0 ? '+' : ''}{holding.xirr.toFixed(2)}%
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {/* Total Row */}
                  {(() => {
                    // Calculate overall XIRR for purchases (weighted average by cost)
                    let totalCostForXirr = 0;
                    let weightedXirrSum = 0;
                    schemeRowsForRender.forEach(h => {
                      const cost = h.cost_value || 0;
                      const xirr = h.xirr || 0;
                      if (cost > 0 && xirr !== null && xirr !== undefined) {
                        totalCostForXirr += cost;
                        weightedXirrSum += cost * xirr;
                      }
                    });
                    const overallPurchaseXirr = totalCostForXirr > 0 ? weightedXirrSum / totalCostForXirr : null;
                    const totalCost = schemeRowsForRender.reduce((sum, h) => sum + (h.cost_value || 0), 0);
                    const totalProfit = schemeTotalMarketValue - totalCost;
                    
                    return (
                    <TableRow className="bg-teal-50 border-t-2 border-teal-200 font-semibold">
                      <TableCell className="py-3"></TableCell>
                      <TableCell className="py-3"></TableCell>
                      <TableCell className="py-3"></TableCell>
                      <TableCell className="py-3 text-sm text-teal-800">Total ({schemeRowsForRender.length} schemes){schemeAsOnDate ? ` · as on ${schemeAsOnDate}` : ""}</TableCell>
                      <TableCell className="py-3 text-right text-sm text-gray-500">-</TableCell>
                      <TableCell className="py-3 text-right text-sm text-blue-600 font-mono">
                        {formatCurrency(totalCost)}
                      </TableCell>
                      <TableCell className="py-3 text-right text-teal-700 font-bold">
                        {formatCurrency(schemeTotalMarketValue)}
                      </TableCell>
                      <TableCell className={`py-3 text-right text-sm font-bold ${
                        totalProfit >= 0 ? 'text-green-700' : 'text-red-700'
                      }`}>
                        {totalProfit >= 0 ? '+' : ''}
                        {formatCurrency(totalProfit)}
                      </TableCell>
                      <TableCell className="py-3 text-right text-sm text-teal-700">100%</TableCell>
                      <TableCell className={`py-3 text-right text-sm font-mono font-bold ${
                        (overallPurchaseXirr || 0) >= 0 ? 'text-green-700' : 'text-red-700'
                      }`}>
                        {overallPurchaseXirr !== null ? `${overallPurchaseXirr >= 0 ? '+' : ''}${overallPurchaseXirr.toFixed(2)}%` : '-'}
                      </TableCell>
                    </TableRow>
                    );
                  })()}
                </TableBody>
              </Table>
            </div>
            </>
          )}
          </div>
          )}

          {/* Sold Units Summary Tab Content */}
          {summarySubTab === "sold" && soldFromAllTxn.length > 0 && (
            <div>
              {/* Search Bar */}
              <div className="mb-4 p-4 bg-red-50/50 rounded-lg border border-red-100">
                <div className="flex items-center gap-3">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      placeholder="Search by folio, scheme name, transaction date..."
                      value={soldSearchTerm}
                      onChange={(e) => setSoldSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  {soldSearchTerm && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSoldSearchTerm("")}
                      className="text-gray-500"
                    >
                      Clear
                    </Button>
                  )}
                </div>
              </div>
              
              {allTransactionsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-red-600" />
                </div>
              ) : (
                <div className="overflow-x-auto">
                  {(() => {
                    // Filter sold transactions based on search - now from allTransactions
                    let filteredSold = soldFromAllTxn;
                    if (soldSearchTerm) {
                      const term = soldSearchTerm.toLowerCase();
                      filteredSold = soldFromAllTxn.filter(txn => (
                        txn.folio_no?.toLowerCase().includes(term) ||
                        txn.scheme_name?.toLowerCase().includes(term) ||
                        txn.isin?.toLowerCase().includes(term) ||
                        txn.scheme_code?.toLowerCase().includes(term) ||
                        txn.investor_name?.toLowerCase().includes(term)
                      ));
                    }
                    
                    // Group by folio + scheme (ISIN)
                    const groupedSoldMap = {};
                    filteredSold.forEach(txn => {
                      const key = `${txn.folio_no}|${txn.isin || txn.scheme_code || txn.product_code || txn.amfi_code}`;
                      if (!groupedSoldMap[key]) {
                        groupedSoldMap[key] = {
                          folio_no: txn.folio_no,
                          scheme_name: txn.scheme_name,
                          scheme_code: txn.scheme_code || txn.product_code || txn.amfi_code,
                          isin: txn.isin,
                          investor_name: txn.investor_name,
                          units: 0,
                          total_cost_basis: 0,
                          sale_amount: 0,
                          gain_loss: 0,
                          transaction_count: 0,
                          cashflows: [],
                          first_transaction_date: txn.trade_date || txn.transaction_date,
                          last_transaction_date: txn.trade_date || txn.transaction_date
                        };
                      }
                      const saleAmt = Math.abs(parseFloat(txn.amount) || 0);
                      const costBasis = parseFloat(txn.fifo_cost_value) || 0;
                      groupedSoldMap[key].units += Math.abs(parseFloat(txn.units) || 0);
                      groupedSoldMap[key].total_cost_basis += costBasis;
                      groupedSoldMap[key].sale_amount += saleAmt;
                      groupedSoldMap[key].gain_loss += (saleAmt - costBasis);
                      groupedSoldMap[key].transaction_count += 1;
                      
                      // Collect cashflows for XIRR: buy dates as outflows, sell date as inflow
                      const parseDDMMYYYY = (ds) => {
                        if (!ds) return null;
                        const parts = ds.split('-');
                        if (parts.length === 3 && parts[0].length === 2) return new Date(parts[2], parts[1] - 1, parts[0]);
                        return new Date(ds);
                      };
                      if (txn.fifo_details) {
                        txn.fifo_details.forEach(fd => {
                          const buyDate = parseDDMMYYYY(fd.buy_date);
                          const buyAmt = parseFloat(fd.buy_amount) || 0;
                          if (buyDate && !isNaN(buyDate.getTime()) && buyAmt > 0) {
                            groupedSoldMap[key].cashflows.push({ date: buyDate, amount: -buyAmt });
                          }
                        });
                      }
                      const sellDate = parseDDMMYYYY(txn.trade_date || txn.transaction_date);
                      if (sellDate && !isNaN(sellDate.getTime()) && saleAmt > 0) {
                        groupedSoldMap[key].cashflows.push({ date: sellDate, amount: saleAmt });
                      }
                      
                      // Track date range
                      const txnDate = txn.trade_date || txn.transaction_date || "";
                      if (txnDate < groupedSoldMap[key].first_transaction_date) {
                        groupedSoldMap[key].first_transaction_date = txnDate;
                      }
                      if (txnDate > groupedSoldMap[key].last_transaction_date) {
                        groupedSoldMap[key].last_transaction_date = txnDate;
                      }
                    });
                    
                    const groupedSold = Object.values(groupedSoldMap);
                    
                    // Calculate total sale value for % Share calculation
                    const totalSaleValueForCalc = groupedSold.reduce((sum, t) => sum + Math.abs(t.sale_amount || 0), 0);
                    
                    // Pre-calculate sale_pct and xirr for each grouped item
                    groupedSold.forEach(item => {
                      // Calculate % Share
                      item.sale_pct = totalSaleValueForCalc > 0 ? (Math.abs(item.sale_amount || 0) / totalSaleValueForCalc) * 100 : 0;
                      
                      // Calculate XIRR from cashflows (buy dates as outflows, sell dates as inflows)
                      let calculatedXirr = null;
                      if (item.cashflows && item.cashflows.length >= 2) {
                        const outflows = item.cashflows.filter(c => c.amount < 0);
                        const inflows = item.cashflows.filter(c => c.amount > 0);
                        if (outflows.length > 0 && inflows.length > 0) {
                          const totalCost = outflows.reduce((s, c) => s + Math.abs(c.amount), 0);
                          const totalSale = inflows.reduce((s, c) => s + c.amount, 0);
                          // Weighted average buy date
                          let weightedDays = 0, totalWeight = 0;
                          // Use last sell date as reference
                          const lastSellDate = inflows.reduce((latest, c) => c.date > latest ? c.date : latest, inflows[0].date);
                          outflows.forEach(c => {
                            const days = (lastSellDate - c.date) / (1000 * 60 * 60 * 24);
                            weightedDays += days * Math.abs(c.amount);
                            totalWeight += Math.abs(c.amount);
                          });
                          const avgDays = totalWeight > 0 ? weightedDays / totalWeight : 0;
                          const years = avgDays / 365.25;
                          if (years > 0 && totalCost > 0) {
                            calculatedXirr = (Math.pow(totalSale / totalCost, 1 / years) - 1) * 100;
                            if (isNaN(calculatedXirr) || !isFinite(calculatedXirr)) calculatedXirr = null;
                          }
                        }
                      }
                      item.xirr = calculatedXirr;
                    });
                    
                    // Sort grouped transactions
                    const sortedSold = [...groupedSold].sort((a, b) => {
                      let aVal, bVal;
                      switch (soldSortField) {
                        case 'folio_no':
                          aVal = a.folio_no || '';
                          bVal = b.folio_no || '';
                          break;
                        case 'scheme_name':
                          aVal = a.scheme_name || '';
                          bVal = b.scheme_name || '';
                          break;
                        case 'units':
                          aVal = Math.abs(a.units || 0);
                          bVal = Math.abs(b.units || 0);
                          break;
                        case 'total_cost_basis':
                          aVal = a.total_cost_basis || 0;
                          bVal = b.total_cost_basis || 0;
                          break;
                        case 'sale_amount':
                          aVal = Math.abs(a.sale_amount || a.amount || 0);
                          bVal = Math.abs(b.sale_amount || b.amount || 0);
                          break;
                        case 'gain_loss':
                          aVal = a.gain_loss || 0;
                          bVal = b.gain_loss || 0;
                          break;
                        case 'sale_pct':
                          aVal = a.sale_pct || 0;
                          bVal = b.sale_pct || 0;
                          break;
                        case 'xirr':
                          aVal = a.xirr || 0;
                          bVal = b.xirr || 0;
                          break;
                        case 'transaction_date':
                        default:
                          aVal = a.transaction_date || '';
                          bVal = b.transaction_date || '';
                          break;
                      }
                      if (typeof aVal === 'string') {
                        return soldSortDir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
                      }
                      return soldSortDir === 'asc' ? aVal - bVal : bVal - aVal;
                    });
                    
                    // Sortable header component for sold table - matching Purchase Unit Summary style
                    const SoldSortIcon = ({ field }) => {
                      if (soldSortField !== field) return <ArrowUpDown className="w-3 h-3 ml-1 text-gray-300" />;
                      return soldSortDir === "asc" 
                        ? <ArrowUp className="w-3 h-3 ml-1 text-red-600" /> 
                        : <ArrowDown className="w-3 h-3 ml-1 text-red-600" />;
                    };
                    
                    const SoldSortHeader = ({ field, children, className = "" }) => {
                      // Extract width classes for TableHead
                      const widthClasses = className.split(' ').filter(c => c.startsWith('w-') || c.startsWith('min-w-')).join(' ');
                      const flexClasses = className.split(' ').filter(c => !c.startsWith('w-') && !c.startsWith('min-w-')).join(' ');
                      
                      return (
                        <TableHead 
                          className={`text-sm font-medium text-gray-500 cursor-pointer hover:bg-red-100/50 select-none whitespace-nowrap ${widthClasses}`}
                          onClick={() => {
                            if (soldSortField === field) {
                              setSoldSortDir(soldSortDir === 'asc' ? 'desc' : 'asc');
                            } else {
                              setSoldSortField(field);
                              setSoldSortDir('desc');
                            }
                          }}
                        >
                          <div className={`flex items-center gap-1 ${flexClasses}`}>
                            {children}
                            <SoldSortIcon field={field} />
                          </div>
                        </TableHead>
                      );
                    };
                    
                    return (
                  <Table className="table-fixed w-full">
                    <TableHeader>
                      <TableRow className="bg-red-50/50">
                        <TableHead className="text-sm font-medium text-gray-500 w-[50px]">Accounts</TableHead>
                        <TableHead className="text-sm font-medium text-gray-500 w-[100px]">Linked Goals</TableHead>
                        <SoldSortHeader field="folio_no" className="w-[120px]">Folio No.</SoldSortHeader>
                        {showFamilyClientCol && (
                          <TableHead className="text-xs w-[140px]">Client</TableHead>
                        )}
                        <SoldSortHeader field="scheme_name" className="w-[280px]">Scheme Name</SoldSortHeader>
                        <SoldSortHeader field="units" className="justify-end text-right w-[110px]">Units Sold</SoldSortHeader>
                        <SoldSortHeader field="total_cost_basis" className="justify-end text-right w-[140px]">Total Cost Value (INR)</SoldSortHeader>
                        <SoldSortHeader field="sale_amount" className="justify-end text-right w-[130px]">Sale Value (INR)</SoldSortHeader>
                        <SoldSortHeader field="gain_loss" className="justify-end text-right w-[110px]">Profit/Loss</SoldSortHeader>
                        <SoldSortHeader field="sale_pct" className="justify-end text-right w-[80px]">% Share</SoldSortHeader>
                        <SoldSortHeader field="xirr" className="justify-end text-right w-[70px]">XIRR %</SoldSortHeader>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sortedSold.map((txn, txnIndex) => {
                        // Use pre-calculated values from grouping
                        const saleValue = Math.abs(txn.sale_amount || txn.amount || 0);
                        const costBasis = txn.total_cost_basis || 0;
                        const gainLoss = txn.gain_loss || (saleValue - costBasis);
                        const salePct = txn.sale_pct?.toFixed(2) || '0.00';
                        const calculatedXirr = txn.xirr;
                        
                        // Get holding pattern for the folio
                        const folioHolding = portfolioData.find(h => h.folio_no === txn.folio_no);
                        const holdingPattern = folioHolding?.holding_pattern || 'self_single';
                        
                        // Get holding key for goal lookup (folio + isin)
                        const soldHoldingKey = getHoldingKey(txn.folio_no, txn.isin);
                        
                        return (
                          <TableRow key={`sold-${txn.folio_no}-${txn.isin || txn.scheme_code}-${txnIndex}`} className="hover:bg-red-50/30">
                            {/* Accounts Column */}
                            <TableCell className="py-2">
                              <div 
                                className={`w-7 h-7 rounded-full ${getOwnershipColor(holdingPattern)} flex items-center justify-center cursor-help`}
                                title={holdingPattern === 'joint_holder' ? 'Joint Holder' : holdingPattern === 'minor_guardian' ? 'Minor (Guardian)' : 'Self'}
                              >
                                {holdingPattern === 'minor_guardian' || holdingPattern === 'self_minor' ? (
                                  <Baby className="w-3.5 h-3.5 text-white" />
                                ) : holdingPattern === 'joint_holder' || holdingPattern === 'as_joint' ? (
                                  <Users className="w-3.5 h-3.5 text-white" />
                                ) : (
                                  <User className="w-3.5 h-3.5 text-white" />
                                )}
                              </div>
                            </TableCell>
                            {/* Linked Goals Column with Add Button */}
                            <TableCell className="py-2">
                              <Popover>
                                <PopoverTrigger asChild>
                                  <div 
                                    className="flex items-center gap-1 cursor-pointer min-h-[28px]"
                                    data-testid={`sold-goal-assign-${txn.folio_no}-${txn.isin}`}
                                  >
                                    {schemeGoals[soldHoldingKey]?.length > 0 ? (
                                      <>
                                        {schemeGoals[soldHoldingKey].map((sg) => {
                                          const color = getGoalColor(sg.goal.id);
                                          return (
                                            <div 
                                              key={sg.goal.id}
                                              className={`w-8 h-6 rounded-full text-[10px] font-bold ${color.bg} ${color.text} flex items-center justify-center`}
                                              title={sg.goal.name}
                                            >
                                              {sg.percentage}%
                                            </div>
                                          );
                                        })}
                                        {!isFullyAllocated(soldHoldingKey) && (
                                          <div className="w-8 h-6 rounded-full text-[9px] bg-gray-100 text-gray-400 flex items-center justify-center" title="Unallocated">
                                            +{getRemainingAllocation(soldHoldingKey)}%
                                          </div>
                                        )}
                                      </>
                                    ) : (
                                      <div className="text-gray-400 text-[10px] flex items-center gap-0.5 hover:text-gray-600">
                                        <Plus className="w-3 h-3" />
                                        <span>Add</span>
                                      </div>
                                    )}
                                  </div>
                                </PopoverTrigger>
                                <PopoverContent className="w-80 p-0" align="start" sideOffset={5}>
                                  <div>
                                    {/* Header */}
                                    <div className="flex items-center justify-between px-3 py-2 border-b bg-red-50">
                                      <p className="text-sm font-medium text-gray-700">Tag to Goal (Sold)</p>
                                      <span className={`text-sm ${isFullyAllocated(soldHoldingKey) ? 'text-red-500 font-medium' : 'text-gray-500'}`}>
                                        {isFullyAllocated(soldHoldingKey) ? '100% allocated' : `${getRemainingAllocation(soldHoldingKey)}% remaining`}
                                      </span>
                                    </div>
                                    
                                    {goals.length === 0 ? (
                                      <div className="text-sm text-gray-500 p-3">
                                        No goals yet.
                                        <Button 
                                          variant="link" 
                                          size="sm" 
                                          className="h-auto p-0 ml-1 text-purple-600"
                                          onClick={() => setShowCreateGoalDialog(true)}
                                        >
                                          Create one
                                        </Button>
                                      </div>
                                    ) : (
                                      <table className="w-full">
                                        <thead>
                                          <tr className="border-b bg-gray-50/50">
                                            <th className="text-left text-sm font-medium text-gray-500 px-3 py-2">Goals</th>
                                            <th className="text-center text-sm font-medium text-gray-500 px-3 py-2 w-32">Allocation%</th>
                                            <th className="w-16"></th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {goals.map((goal, goalIdx) => {
                                            const existingAlloc = schemeGoals[soldHoldingKey]?.find(sg => sg.goal.id === goal.id);
                                            const color = goalColors[goalIdx % goalColors.length];
                                            const canAssign = !isFullyAllocated(soldHoldingKey) || existingAlloc;
                                            const maxAlloc = existingAlloc 
                                              ? getRemainingAllocation(soldHoldingKey) + existingAlloc.percentage 
                                              : getRemainingAllocation(soldHoldingKey);
                                            
                                            return (
                                              <tr 
                                                key={goal.id}
                                                className={`border-b last:border-b-0 ${existingAlloc ? color.bg : ''} ${!canAssign && !existingAlloc ? 'opacity-50' : ''}`}
                                              >
                                                <td className="px-3 py-2">
                                                  <p className={`text-sm font-medium ${existingAlloc ? color.text : 'text-gray-800'}`}>
                                                    {goal.name}
                                                  </p>
                                                  <p className="text-[10px] text-gray-500">{goal.expected_date}</p>
                                                </td>
                                                <td className="px-3 py-2">
                                                  {canAssign ? (
                                                    <Input
                                                      type="number"
                                                      min="1"
                                                      max={maxAlloc}
                                                      defaultValue={existingAlloc?.percentage || Math.min(100, maxAlloc)}
                                                      className="h-7 text-center text-sm w-20"
                                                      id={`sold-alloc-${txn.folio_no}-${txn.isin}-${goal.id}`}
                                                    />
                                                  ) : (
                                                    <span className="text-xs text-gray-400">-</span>
                                                  )}
                                                </td>
                                                <td className="px-3 py-2">
                                                  {existingAlloc ? (
                                                    <div className="flex gap-1">
                                                      <Button 
                                                        variant="ghost" 
                                                        size="sm" 
                                                        className="h-7 w-7 p-0 text-green-600 hover:text-green-700 hover:bg-green-50"
                                                        onClick={() => {
                                                          const input = document.getElementById(`sold-alloc-${txn.folio_no}-${txn.isin}-${goal.id}`);
                                                          const pct = parseInt(input?.value) || existingAlloc.percentage;
                                                          handleAssignGoal(goal.id, txn.folio_no, txn.isin, pct);
                                                        }}
                                                      >
                                                        <Check className="w-4 h-4" />
                                                      </Button>
                                                      <Button 
                                                        variant="ghost" 
                                                        size="sm" 
                                                        className="h-7 w-7 p-0 text-red-500 hover:text-red-600 hover:bg-red-50"
                                                        onClick={() => handleUnassignGoal(goal.id, txn.folio_no, txn.isin)}
                                                      >
                                                        <Trash2 className="w-3.5 h-3.5" />
                                                      </Button>
                                                    </div>
                                                  ) : canAssign ? (
                                                    <Button 
                                                      variant="ghost" 
                                                      size="sm" 
                                                      className="h-7 px-2 text-xs text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                                                      onClick={() => {
                                                        const input = document.getElementById(`sold-alloc-${txn.folio_no}-${txn.isin}-${goal.id}`);
                                                        const pct = parseInt(input?.value) || Math.min(100, maxAlloc);
                                                        handleAssignGoal(goal.id, txn.folio_no, txn.isin, pct);
                                                      }}
                                                    >
                                                      <Plus className="w-3 h-3 mr-1" />
                                                      Add
                                                    </Button>
                                                  ) : null}
                                                </td>
                                              </tr>
                                            );
                                          })}
                                        </tbody>
                                      </table>
                                    )}
                                  </div>
                                </PopoverContent>
                              </Popover>
                            </TableCell>
                            <TableCell className="py-2 text-sm font-mono">{txn.folio_no}</TableCell>
                            <TableCell 
                              className="py-2 text-sm max-w-[200px] truncate cursor-help" 
                              title={`${toTitleCase(txn.scheme_name)}\nISIN: ${txn.isin || '-'}\nCode: ${txn.scheme_code || '-'}${txn.transaction_count > 1 ? `\n${txn.transaction_count} redemptions` : ''}`}
                            >
                              {toTitleCase(txn.scheme_name)}
                            </TableCell>
                            <TableCell className="py-2 text-sm text-right font-mono text-red-600">
                              {Math.abs(txn.units || 0).toLocaleString('en-IN', { minimumFractionDigits: 3 })}
                            </TableCell>
                            <TableCell className="py-2 text-sm text-right font-mono text-blue-600">
                              {formatCurrency(costBasis)}
                            </TableCell>
                            <TableCell className="py-2 text-sm text-right font-mono">
                              {formatCurrency(saleValue)}
                            </TableCell>
                            <TableCell className={`py-2 text-sm text-right font-mono font-medium ${gainLoss >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {gainLoss >= 0 ? '+' : ''}{formatCurrency(gainLoss)}
                            </TableCell>
                            <TableCell className="py-2 text-sm text-right font-mono text-gray-600">
                              {salePct}%
                            </TableCell>
                            <TableCell className={`py-2 text-sm text-right font-mono font-medium ${(calculatedXirr || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {calculatedXirr !== null ? `${calculatedXirr >= 0 ? '+' : ''}${calculatedXirr.toFixed(2)}%` : '-'}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {/* Totals Row */}
                      {sortedSold.length > 0 && (() => {
                        // Calculate overall XIRR for sold units (weighted average by cost)
                        let totalCostForXirr = 0;
                        let weightedXirrSum = 0;
                        sortedSold.forEach(t => {
                          const cost = t.total_cost_basis || 0;
                          const xirr = t.xirr || 0;
                          if (cost > 0 && xirr !== null) {
                            totalCostForXirr += cost;
                            weightedXirrSum += cost * xirr;
                          }
                        });
                        const overallSoldXirr = totalCostForXirr > 0 ? weightedXirrSum / totalCostForXirr : null;
                        const totalGL = sortedSold.reduce((sum, t) => sum + (t.gain_loss || 0), 0);
                        const totalUnitsSold = sortedSold.reduce((sum, t) => sum + Math.abs(t.units || 0), 0);
                        
                        return (
                        <TableRow className="bg-red-50 border-t-2 border-red-200 font-semibold">
                          <TableCell className="py-3"></TableCell>
                          <TableCell className="py-3"></TableCell>
                          <TableCell className="py-3"></TableCell>
                          <TableCell className="py-3 text-sm text-red-800">Total ({groupedSold.length} schemes)</TableCell>
                          <TableCell className="py-3 text-right text-sm font-mono text-red-700 font-bold">
                            {totalUnitsSold.toLocaleString('en-IN', { minimumFractionDigits: 3 })}
                          </TableCell>
                          <TableCell className="py-3 text-right text-sm font-mono text-blue-600">
                            {formatCurrency(sortedSold.reduce((sum, t) => sum + (t.total_cost_basis || 0), 0))}
                          </TableCell>
                          <TableCell className="py-3 text-right text-sm font-mono text-red-700 font-bold">
                            {formatCurrency(sortedSold.reduce((sum, t) => sum + Math.abs(t.sale_amount || t.amount || 0), 0))}
                          </TableCell>
                          <TableCell className={`py-3 text-right text-sm font-mono font-bold ${
                            totalGL >= 0 ? 'text-green-700' : 'text-red-700'
                          }`}>
                            {(totalGL >= 0 ? '+' : '') + formatCurrency(totalGL)}
                          </TableCell>
                          <TableCell className="py-3 text-right text-sm text-red-700">100%</TableCell>
                          <TableCell className={`py-3 text-right text-sm font-mono font-bold ${
                            (overallSoldXirr || 0) >= 0 ? 'text-green-700' : 'text-red-700'
                          }`}>
                            {overallSoldXirr !== null ? `${overallSoldXirr >= 0 ? '+' : ''}${overallSoldXirr.toFixed(2)}%` : '-'}
                          </TableCell>
                        </TableRow>
                        );
                      })()}
                    </TableBody>
                  </Table>
                    );
                  })()}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
      ) : null}

      {/* Create Goal Dialog */}
      <Dialog open={showCreateGoalDialog} onOpenChange={setShowCreateGoalDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Target className="w-5 h-5 text-purple-600" />
              Create Investment Goal
            </DialogTitle>
            <DialogDescription>
              Create a goal to track your target investments
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="goal-name" className="flex items-center gap-1">
                Goal Name <span className="text-red-500">*</span>
              </Label>
              <Input 
                id="goal-name"
                placeholder="e.g., Retirement Fund, Child Education"
                value={newGoalName}
                onChange={(e) => setNewGoalName(e.target.value)}
                data-testid="goal-name-input"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="goal-amount" className="flex items-center gap-1">
                Target Amount (₹) <span className="text-red-500">*</span>
              </Label>
              <Input 
                id="goal-amount"
                type="number"
                placeholder="e.g., 5000000"
                value={newGoalAmount}
                onChange={(e) => setNewGoalAmount(e.target.value)}
                data-testid="goal-amount-input"
                min="1"
                required
              />
              {newGoalAmount && (
                <p className="text-sm text-gray-500">
                  ₹{parseFloat(newGoalAmount).toLocaleString('en-IN')}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="goal-date" className="flex items-center gap-1">
                Target Date <span className="text-red-500">*</span>
              </Label>
              <Input 
                id="goal-date"
                type="date"
                value={newGoalDate}
                onChange={(e) => setNewGoalDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                data-testid="goal-date-input"
                required
              />
            </div>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setShowCreateGoalDialog(false);
                setNewGoalName("");
                setNewGoalDate("");
                setNewGoalAmount("");
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleCreateGoal}
              disabled={creatingGoal || !newGoalName.trim() || !newGoalDate || !newGoalAmount || parseFloat(newGoalAmount) <= 0}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="save-goal-btn"
            >
              {creatingGoal ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save Goal"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Goal Dialog */}
      <Dialog open={showEditGoalDialog} onOpenChange={(open) => {
        setShowEditGoalDialog(open);
        if (!open) {
          setEditingGoal(null);
          setNewGoalName("");
          setNewGoalDate("");
          setNewGoalAmount("");
        }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit2 className="w-5 h-5 text-purple-600" />
              Edit Goal
            </DialogTitle>
            <DialogDescription>
              Update goal details and target amount
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-goal-name" className="flex items-center gap-1">
                Goal Name <span className="text-red-500">*</span>
              </Label>
              <Input 
                id="edit-goal-name"
                placeholder="e.g., Retirement Fund, Child Education"
                value={newGoalName}
                onChange={(e) => setNewGoalName(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-goal-amount" className="flex items-center gap-1">
                Target Amount (₹) <span className="text-red-500">*</span>
              </Label>
              <Input 
                id="edit-goal-amount"
                type="number"
                placeholder="e.g., 5000000"
                value={newGoalAmount}
                onChange={(e) => setNewGoalAmount(e.target.value)}
                min="1"
                required
              />
              {newGoalAmount && (
                <p className="text-sm text-gray-500">
                  ₹{parseFloat(newGoalAmount).toLocaleString('en-IN')}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-goal-date" className="flex items-center gap-1">
                Target Date <span className="text-red-500">*</span>
              </Label>
              <Input 
                id="edit-goal-date"
                type="date"
                value={newGoalDate}
                onChange={(e) => setNewGoalDate(e.target.value)}
                required
              />
            </div>
            {/* Current Progress Info */}
            {editingGoal && (
              <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                <p className="text-sm font-medium text-gray-700">Current Progress</p>
                <div className="text-sm space-y-1">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Assigned Schemes:</span>
                    <span className="font-medium">{editingGoal.schemes?.length || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Current Value:</span>
                    <span className="font-medium text-green-600">
                      {formatCurrency(calculateGoalProgress(editingGoal).currentValue)}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setShowEditGoalDialog(false);
                setEditingGoal(null);
                setNewGoalName("");
                setNewGoalDate("");
                setNewGoalAmount("");
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleEditGoal}
              disabled={creatingGoal || !newGoalName.trim() || !newGoalDate || !newGoalAmount || parseFloat(newGoalAmount) <= 0}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {creatingGoal ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                "Update Goal"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};


// ─── Provisional Buy Panel ───────────────────────────────────────────────
const ProvisionalBuyPanel = ({ selectedClient, selectedClientData, formatCurrency, api }) => {
  const [models, setModels] = useState([]);
  const [modelId, setModelId] = useState("");
  const [amount, setAmount] = useState(1000000);
  const [equityPct, setEquityPct] = useState(70);
  const [debtPct, setDebtPct] = useState(30);
  const [buyType, setBuyType] = useState("fresh");
  const [route, setRoute] = useState("direct");
  const [stpFreq, setStpFreq] = useState("monthly");
  const [stpDur, setStpDur] = useState(12);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showRationale, setShowRationale] = useState(false);

  const pan = (selectedClientData?.pan_no
    || selectedClientData?.first_holder_pan
    || selectedClientData?.pan
    || selectedClientData?.client_pan
    || selectedClient
    || "").toUpperCase();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/portfolio-strategy/list?status=active");
        const items = (data.items || []).filter(
          (m) => m.version_no >= 1 && m.show_in_provisional_buy !== false
        );
        setModels(items);
        if (items.length && !modelId) setModelId(items[0].id);
      } catch (e) { /* swallow */ }
    })();
    // eslint-disable-next-line
  }, [api]);

  const totalRatio = (Number(equityPct) || 0) + (Number(debtPct) || 0);
  const ratioOk = Math.abs(totalRatio - 100) < 0.01;

  const run = async () => {
    if (!modelId) { toast.error("Pick a model portfolio first"); return; }
    if (!ratioOk) { toast.error("Equity % + Debt % must equal 100"); return; }
    setBusy(true);
    try {
      const payload = {
        investment_amount: Number(amount),
        equity_pct: Number(equityPct),
        debt_pct: Number(debtPct),
        buy_type: buyType,
      };
      if (buyType === "fresh") {
        payload.route = route;
        if (route === "liquid_stp") {
          payload.stp_frequency = stpFreq;
          payload.stp_duration_months = Number(stpDur);
        }
      } else if (buyType === "additional") {
        if (pan) payload.client_pan = pan;
      }
      const { data } = await api.post(`/portfolio-strategy/${modelId}/provisional-buy`, payload);
      setResult(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Compute failed");
      setResult(null);
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="provisional-buy-tab">
      <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3 text-sm">
        <b>Provisional Buy</b> — pick a model portfolio + enter the investment amount and the client's Equity:Debt ratio.
        <span className="text-gray-600 block mt-0.5 text-xs">
          Fresh: directly deploys per model weights. Additional: rebalances using current holdings (auto-fetched for the selected client) so the COMBINED portfolio moves toward target weights.
        </span>
      </div>

      <div className="grid grid-cols-2 gap-4 bg-white border border-gray-200 rounded-md p-4">
        <div>
          <label className="text-xs text-gray-600 font-medium mb-1 block">Model Portfolio *</label>
          <select value={modelId} onChange={(e) => setModelId(e.target.value)}
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm" data-testid="pb-model-select">
            {models.length === 0 && <option value="">No active portfolios yet</option>}
            {models.map((m) => (
              <option key={m.id} value={m.id}>{m.portfolio_name} ({m.portfolio_code}) v{m.version_no}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs text-gray-600 font-medium mb-1 block">Investment Amount</label>
          <input type="number" min="0" value={amount} onChange={(e) => setAmount(Number(e.target.value))}
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm" data-testid="pb-amount" />
        </div>
        <div>
          <label className="text-xs text-gray-600 font-medium mb-1 block">Equity %</label>
          <input type="number" min="0" max="100" value={equityPct} onChange={(e) => setEquityPct(Number(e.target.value))}
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm" data-testid="pb-equity-pct" />
        </div>
        <div>
          <label className="text-xs text-gray-600 font-medium mb-1 block">Debt %</label>
          <input type="number" min="0" max="100" value={debtPct} onChange={(e) => setDebtPct(Number(e.target.value))}
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm" data-testid="pb-debt-pct" />
        </div>
        <div className="col-span-2">
          <div className="text-xs text-gray-600 font-medium mb-2">Buy Type</div>
          <div className="flex gap-3 text-sm">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="radio" name="buy-type" checked={buyType === "fresh"} onChange={() => setBuyType("fresh")} data-testid="pb-type-fresh" />
              <span>Fresh Buy <span className="text-gray-500 text-xs">(direct model deploy)</span></span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="radio" name="buy-type" checked={buyType === "additional"} onChange={() => setBuyType("additional")} data-testid="pb-type-additional" />
              <span>Additional Buy <span className="text-gray-500 text-xs">(rebalance with current holdings)</span></span>
            </label>
          </div>
        </div>
        {buyType === "fresh" && (
          <div className="col-span-2 bg-gray-50 p-3 rounded">
            <div className="text-xs text-gray-600 font-medium mb-2">First-time investor route</div>
            <div className="flex gap-3 text-sm">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name="pb-route" checked={route === "direct"} onChange={() => setRoute("direct")} data-testid="pb-route-direct" />
                <span>Direct</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name="pb-route" checked={route === "liquid_stp"} onChange={() => setRoute("liquid_stp")} data-testid="pb-route-liquid" />
                <span>Liquid + STP</span>
              </label>
            </div>
            {route === "liquid_stp" && (
              <div className="grid grid-cols-2 gap-3 mt-2">
                <div>
                  <label className="text-xs text-gray-600 mb-1 block">STP Frequency</label>
                  <select value={stpFreq} onChange={(e) => setStpFreq(e.target.value)} className="w-full border border-gray-300 rounded px-2 py-2 text-sm">
                    <option value="daily">Daily</option><option value="weekly">Weekly</option><option value="monthly">Monthly</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-gray-600 mb-1 block">STP Duration (months)</label>
                  <input type="number" min="1" value={stpDur} onChange={(e) => setStpDur(Number(e.target.value))} className="w-full border border-gray-300 rounded px-2 py-2 text-sm" />
                </div>
              </div>
            )}
          </div>
        )}
        {buyType === "additional" && (
          <div className="col-span-2 text-xs text-gray-600 bg-amber-50 border border-amber-200 rounded px-3 py-2">
            Current holdings will be auto-fetched for PAN <b>{pan || "(missing)"}</b> from active_units.
          </div>
        )}
        <div className="col-span-2 flex items-center gap-3">
          <button onClick={run} disabled={busy || !ratioOk}
            className="bg-emerald-700 hover:bg-emerald-800 text-white px-4 py-2 text-sm rounded inline-flex items-center gap-2 disabled:opacity-60"
            data-testid="pb-run">
            {busy ? "Computing..." : "Compute Allocation"}
          </button>
          {!ratioOk && <span className="text-xs text-rose-600">Equity + Debt = {totalRatio}% (must equal 100)</span>}
        </div>
      </div>

      {result && (
        <div className="border border-gray-200 rounded-md overflow-hidden bg-white" data-testid="pb-result">
          <div className="bg-gray-50 px-3 py-2 text-xs text-gray-600 flex flex-wrap gap-x-4 items-center">
            <span>Type: <b className={result.buy_type === "additional" ? "text-amber-700" : "text-emerald-700"}>{result.buy_type}</b></span>
            <span>Investment: <b>{formatCurrency(result.investment_amount)}</b></span>
            {result.buy_type === "additional" && (
              <>
                <span>Current Portfolio: <b>{formatCurrency(result.current_portfolio_value)}</b></span>
                <span>New Portfolio: <b>{formatCurrency(result.new_portfolio_value)}</b></span>
              </>
            )}
            <span>Equity Pool: <b>{formatCurrency(result.new_equity_pool ?? result.equity_pool)}</b></span>
            <span>Debt Pool: <b>{formatCurrency(result.new_debt_pool ?? result.debt_pool)}</b></span>
            <button
              type="button"
              onClick={() => setShowRationale(true)}
              className="ml-auto text-emerald-700 underline hover:text-emerald-900 text-xs"
              data-testid="pb-view-rationale"
            >
              View Rationale →
            </button>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs text-gray-600">
              <tr>
                <th className="text-left px-3 py-2">Asset Class</th>
                <th className="text-left px-3 py-2">Scheme</th>
                {result.buy_type === "additional" && <th className="text-right px-3 py-2">Current Value</th>}
                {result.buy_type === "additional" && <th className="text-right px-3 py-2">Target Value</th>}
                {result.buy_type === "additional" && <th className="text-right px-3 py-2">Deficit</th>}
                {result.buy_type === "fresh" && <th className="text-right px-3 py-2">Weight</th>}
                <th className="text-right px-3 py-2">Suggested Buy</th>
              </tr>
            </thead>
            <tbody>
              {result.splits.map((s, i) => (
                <tr key={i} className="border-t border-gray-100">
                  <td className="px-3 py-2 text-xs">{s.asset_class}</td>
                  <td className="px-3 py-2 text-xs">{s.scheme_name}<div className="text-[10px] text-gray-500">{s.amc_name} {s.amc_name && s.scheme_code ? "-" : ""} {s.scheme_code}</div></td>
                  {result.buy_type === "additional" && <td className="px-3 py-2 text-right">{formatCurrency(s.current_value)}</td>}
                  {result.buy_type === "additional" && <td className="px-3 py-2 text-right">{formatCurrency(s.target_value)}</td>}
                  {result.buy_type === "additional" && <td className={`px-3 py-2 text-right ${s.deficit > 0 ? "text-rose-600" : "text-gray-400"}`}>{formatCurrency(s.deficit)}</td>}
                  {result.buy_type === "fresh" && <td className="px-3 py-2 text-right text-xs">{(s.normalized_weight_pct ?? 0).toFixed(2)}%</td>}
                  <td className="px-3 py-2 text-right font-semibold text-emerald-700">{formatCurrency(s.suggested_amount ?? s.allocation_amount)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-gray-50 border-t border-gray-200">
              <tr>
                <td colSpan={result.buy_type === "additional" ? 5 : 3} className="px-3 py-2 text-xs text-gray-600 font-medium">Total</td>
                <td className="px-3 py-2 text-right font-semibold text-emerald-700">{formatCurrency(result.total_suggested ?? result.total_allocated)}</td>
              </tr>
            </tfoot>
          </table>
          {result.deployment?.route === "liquid_stp" && (
            <div className="bg-amber-50 border-t border-amber-200 px-3 py-3 text-xs">
              <div className="font-semibold text-amber-900 mb-1">STP Schedule {result.deployment.stp_frequency} x {result.deployment.stp_duration_months} months. Parking {formatCurrency(result.deployment.parking_amount)}</div>
              <ul className="space-y-1 mt-1">
                {result.deployment.stp_targets.map((t, i) => (
                  <li key={i} className="flex justify-between"><span>{t.scheme_name}</span><span className="text-gray-500">{t.normalized_weight_pct.toFixed(2)}% / {formatCurrency(t.total_amount)}</span></li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {showRationale && result && (
        <RationaleDrawer result={result} formatCurrency={formatCurrency} onClose={() => setShowRationale(false)} />
      )}
    </div>
  );
};

// ─── Rationale Drawer — transparent step-by-step derivation of the
// Provisional Buy allocation (Fresh or Additional). All values come from
// the API response (no client-side recomputation), so what the user sees
// is provably equal to what the engine returned.
const RationaleDrawer = ({ result, formatCurrency, onClose }) => {
  const isAdditional = result.buy_type === "additional";
  const eqSplits = (result.splits || []).filter((s) => s.asset_class === "Equity");
  const dbSplits = (result.splits || []).filter((s) => s.asset_class === "Debt");
  const liqSplits = (result.splits || []).filter((s) => s.asset_class === "Liquid (Parking)");

  const computeBranch = (rows, newPool) => {
    if (!rows.length) return null;
    const totalDef = rows.reduce((s, r) => s + (r.deficit || 0), 0);
    if (totalDef > newPool + 0.01) return { branch: "prorate", totalDef };
    return { branch: "fill_plus_spread", totalDef, leftover: newPool - totalDef };
  };

  const Section = ({ n, title, children }) => (
    <div className="mb-4">
      <div className="font-semibold text-gray-800 mb-2">
        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-emerald-700 text-white text-xs mr-2">{n}</span>
        {title}
      </div>
      <div className="ml-8 text-xs">{children}</div>
    </div>
  );

  const renderAssetClassBlock = (label, rows, newPool, curPool) => {
    if (!rows.length || newPool <= 0) return null;
    const branch = computeBranch(rows, newPool);
    const totalW = rows.reduce((s, r) => s + (r.model_weight_pct || 0), 0) || 1;
    return (
      <div className="bg-gray-50 border border-gray-200 rounded p-3 mb-3">
        <div className="font-medium text-gray-800 mb-2">{label} slice — pool {formatCurrency(newPool)}{isAdditional ? ` (existing ${formatCurrency(curPool)})` : ""}</div>
        {isAdditional && (
          <>
            <div className="text-gray-600 mb-1">target_pool = current_pool + new_pool = {formatCurrency(curPool + newPool)}</div>
            <div className="text-gray-600 mb-2">for each scheme:  target_value = target_pool × (weight ÷ 100);  deficit = max(0, target − current)</div>
            <table className="w-full text-[11px] mb-2">
              <thead className="bg-white text-gray-500">
                <tr>
                  <th className="text-left px-2 py-1">Scheme</th>
                  <th className="text-right px-2 py-1">Weight</th>
                  <th className="text-right px-2 py-1">Target = pool × w</th>
                  <th className="text-right px-2 py-1">Current</th>
                  <th className="text-right px-2 py-1">Deficit</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} className="border-t border-gray-200">
                    <td className="px-2 py-1">{r.scheme_name}</td>
                    <td className="px-2 py-1 text-right">{r.model_weight_pct}%</td>
                    <td className="px-2 py-1 text-right">{formatCurrency(r.target_value)}</td>
                    <td className="px-2 py-1 text-right">{formatCurrency(r.current_value)}</td>
                    <td className={`px-2 py-1 text-right ${r.deficit > 0 ? "text-rose-600" : "text-gray-400"}`}>{formatCurrency(r.deficit)}</td>
                  </tr>
                ))}
                <tr className="border-t border-gray-300 bg-white">
                  <td colSpan={4} className="px-2 py-1 font-medium text-right">Σ deficit</td>
                  <td className="px-2 py-1 text-right font-semibold">{formatCurrency(branch?.totalDef || 0)}</td>
                </tr>
              </tbody>
            </table>
            {branch?.branch === "prorate" ? (
              <div className="bg-rose-50 border border-rose-200 rounded p-2 mb-1 text-rose-900">
                <b>Branch A — Prorate.</b> Σ deficit ({formatCurrency(branch.totalDef)}) {">"} new pool ({formatCurrency(newPool)}).
                Each scheme gets <code>new_pool × (deficit ÷ Σ deficit)</code>.
              </div>
            ) : (
              <div className="bg-emerald-50 border border-emerald-200 rounded p-2 mb-1 text-emerald-900">
                <b>Branch B — Fill + Spread.</b> Σ deficit ({formatCurrency(branch.totalDef)}) ≤ new pool ({formatCurrency(newPool)}).
                Each scheme is filled to target; the leftover {formatCurrency(branch.leftover)} is spread by model weight (<code>w ÷ Σ w</code>).
              </div>
            )}
          </>
        )}
        {!isAdditional && (
          <div className="text-gray-600 mb-2">for each scheme: <code>allocation = new_pool × (weight ÷ Σ weights)</code></div>
        )}
        <table className="w-full text-[11px]">
          <thead className="bg-white text-gray-500">
            <tr>
              <th className="text-left px-2 py-1">Scheme</th>
              <th className="text-right px-2 py-1">Model w</th>
              {isAdditional && <th className="text-right px-2 py-1">Fill (deficit)</th>}
              {isAdditional && branch?.branch === "fill_plus_spread" && <th className="text-right px-2 py-1">Spread = leftover × w÷Σw</th>}
              {isAdditional && branch?.branch === "prorate" && <th className="text-right px-2 py-1">Prorate = pool × def÷Σdef</th>}
              {!isAdditional && <th className="text-right px-2 py-1">Normalized w</th>}
              <th className="text-right px-2 py-1">Suggested</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => {
              const sharePr = branch?.totalDef > 0 ? (r.deficit || 0) / branch.totalDef : 0;
              const spreadAmt = (branch?.leftover || 0) * (r.model_weight_pct || 0) / totalW;
              return (
                <tr key={i} className="border-t border-gray-200">
                  <td className="px-2 py-1">{r.scheme_name}</td>
                  <td className="px-2 py-1 text-right">{r.model_weight_pct}%</td>
                  {isAdditional && <td className="px-2 py-1 text-right">{formatCurrency(r.deficit)}</td>}
                  {isAdditional && branch?.branch === "fill_plus_spread" && <td className="px-2 py-1 text-right">{formatCurrency(spreadAmt)}</td>}
                  {isAdditional && branch?.branch === "prorate" && <td className="px-2 py-1 text-right">{formatCurrency(newPool * sharePr)}</td>}
                  {!isAdditional && <td className="px-2 py-1 text-right">{(r.normalized_weight_pct ?? 0).toFixed(2)}%</td>}
                  <td className="px-2 py-1 text-right font-semibold text-emerald-700">{formatCurrency(r.suggested_amount ?? r.allocation_amount)}</td>
                </tr>
              );
            })}
            <tr className="border-t border-gray-300 bg-white">
              <td colSpan={isAdditional ? 4 : 2} className="px-2 py-1 font-medium text-right">Σ for {label}</td>
              <td className="px-2 py-1 text-right font-semibold text-emerald-700">
                {formatCurrency(rows.reduce((s, r) => s + (r.suggested_amount ?? r.allocation_amount ?? 0), 0))}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-stretch justify-end" onClick={onClose} data-testid="pb-rationale-drawer">
      <div className="bg-white w-full max-w-3xl h-full overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b sticky top-0 bg-white flex items-center justify-between z-10">
          <div>
            <div className="text-xs text-gray-500 uppercase tracking-wide">Calculation Rationale</div>
            <div className="text-lg font-semibold text-gray-800">
              {isAdditional ? "Additional Buy — deficit-based rebalance" : "Fresh Buy — direct model deploy"}
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-xl leading-none" aria-label="Close">×</button>
        </div>

        <div className="p-6 space-y-1 text-sm text-gray-700">
          <Section n={1} title="Inputs">
            <div className="grid grid-cols-2 gap-1.5">
              <div><span className="text-gray-500">Investment</span>: <b>{formatCurrency(result.investment_amount)}</b></div>
              <div><span className="text-gray-500">Model</span>: <b>{result.portfolio_name}</b></div>
              <div><span className="text-gray-500">Equity %</span>: <b>{result.equity_pct}%</b></div>
              <div><span className="text-gray-500">Debt %</span>: <b>{result.debt_pct}%</b></div>
              {isAdditional && <div><span className="text-gray-500">Current Equity</span>: <b>{formatCurrency(result.current_equity_value)}</b></div>}
              {isAdditional && <div><span className="text-gray-500">Current Debt</span>: <b>{formatCurrency(result.current_debt_value)}</b></div>}
              {!isAdditional && <div><span className="text-gray-500">Route</span>: <b>{result.route}</b></div>}
            </div>
          </Section>

          <Section n={2} title="Asset-class pools (split from new money)">
            <div className="bg-gray-50 border border-gray-200 rounded p-3">
              <div>equity_pool = investment × (equity% ÷ 100) = {formatCurrency(result.investment_amount)} × {result.equity_pct}% = <b>{formatCurrency(result.new_equity_pool ?? result.equity_pool)}</b></div>
              <div>debt_pool   = investment × (debt% ÷ 100)   = {formatCurrency(result.investment_amount)} × {result.debt_pct}%   = <b>{formatCurrency(result.new_debt_pool ?? result.debt_pool)}</b></div>
              {isAdditional && (
                <div className="mt-2 pt-2 border-t border-gray-200 text-gray-600">
                  <div>combined_portfolio = current + new = {formatCurrency(result.current_portfolio_value)} + {formatCurrency(result.investment_amount)} = <b>{formatCurrency(result.new_portfolio_value)}</b></div>
                </div>
              )}
            </div>
          </Section>

          <Section n={3} title={isAdditional ? "Per asset-class: target → deficit → branch → suggested" : "Per asset-class: pool × normalized weight"}>
            {/* TODO: JSX below was truncated in a prior session — placeholder
                keeps the module compilable while preserving the intent for a
                future implementation of the per-asset-class breakdown block. */}
            <div className="text-xs text-gray-500 italic">Per asset-class breakdown pending.</div>
          </Section>
        </div>
      </div>
    </div>
  );
};

export default Portfolio;
